import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  CheckCircle, LogOut, Clock, Play, Coffee, Flag, Gauge,
  AlertCircle, Edit3, AlertTriangle, Send, XCircle, MapPin, Navigation, QrCode, Camera, Video,
  BarChart2, Package, Route, ChevronDown, ChevronUp, School, ShoppingBag, Users, HelpCircle,
  RefreshCw, UserPlus, CheckSquare, Truck, Download, Trash2, Plus,
  Calendar, Activity, FileText, ImageIcon, FolderOpen,
  ClipboardCheck, Lock, BookOpen, GraduationCap, ScanLine, LayoutGrid, Headphones,
  ShieldAlert, Search, ChevronLeft, ChevronRight, Smartphone,
  Wrench, Signpost, PackageSearch, PackageCheck, Accessibility,
} from 'lucide-react';
import { ApplicantsView } from '../components/ApplicantsManager';
import LeaveRequestPortal from '../components/LeaveRequestPortal';
import LeaseSignCard from '../components/LeaseSignCard';
import ContractorAgreementCard from '../components/ContractorAgreementCard';
import AdvanceAgreementCard from '../components/AdvanceAgreementCard';
import DriverDetailsCard from '../components/DriverDetailsCard';
import DocUploadRequestCard from '../components/DocUploadRequestCard';
import DoECoverCard from '../components/DoECoverCard';
import MyDocsPortalTab from '../components/MyDocsPortalTab';
import VevoConsentPrompt from '../components/VevoConsentPrompt';
import BagEoiCard, { bagEoiPending } from '../components/BagEoiCard';
import CupNoticeCard from '../components/CupNoticeCard';
import TrainingPortalTab from '../components/TrainingPortalTab';
import DeliveryProceduresTab from '../components/DeliveryProceduresTab';
import { SafetyFocusCard, SafetyTab, SafetyBriefGate, checkBriefGate, getSafetyLang } from '../components/SafetyPortal';
import { SecurityDeclarationGate, checkSecurityGate } from '../components/SecurityDeclaration';
import ChuteLayoutTab from './ChuteLayoutTab';
import { PortalAddressScan } from './DepotAddressScan';
import DepotMapPage from './DepotMapPage';
import ParcelAuditPage from './ParcelAuditPage';
import { SelfAvatar } from '../components/DriverPhoto';
import SignedLeasesCard from '../components/SignedLeasesCard';
import ToDoList from '../components/ToDoList';
import RctiHistoryView from '../components/RctiHistoryView';
import CameraCapture from '../components/CameraCapture';
import { IssueVideoList, AddIssueVideo, PendingVideoPicker, uploadPendingVideos, VideoProgress } from '../components/IssueVideos';
import CaseQuestionnaire from '../components/CaseQuestionnaire';
// The customer-case cards, the portal-cases API and their small helpers live in their own
// module so the office's "Driver Portal View" can render the driver's real card — see
// components/CustomerCasePortalCard.
import {
  CustomerCaseCard, AssistCaseCard, DeliveryRequestCard, ccApi, ccDue, CC_LABELS, previewVerdict,
} from '../components/CustomerCasePortalCard';
import PushAlertsCard from '../components/PushAlertsCard';
import StandingOrderCard, { soApi } from '../components/StandingOrderCard';
import { registerSW, syncAlerts, setTileBadge } from '../utils/pushAlerts';
import { pendingQuestions } from '../utils/caseQuestionnaire';
import { caseDueMeta } from '../utils/caseDue';
import { checkOdometer, checkDayDistance } from '../utils/odoCheck';
import { watchStanding, watchTarget } from '../utils/vehicleWatch';
import { uprightJpegBytes } from '../utils/imageOrient';
import { fieldByKey, HIT_OPTIONS, missingForClaim, openRequest, draftRecord, valueOf } from '../utils/accidentClaim';
import { detectPeriodEnd } from '../utils/periods';

const SESSION_KEY    = 'hp-shift-session';
const QR_SESSION_KEY = 'hp-depot-qr';
const ODO_PENDING_KEY = 'hp-odo-pending';

// Encoded ceiling for the odometer photo. Netlify's function body limit is 6 MB and
// base64 is what's counted, not the file; 4 MB leaves room for the JSON around it.
const MAX_ODO_B64 = 4_000_000;

// A reading the camera has read but the driver hasn't confirmed yet. Held outside the
// shift session (which only ever carries a CONFIRMED odoStart) so that if the phone
// reloads the page between the photo and the tap — iOS discards tabs whenever it likes,
// and the camera is exactly when it's most likely to — the driver comes back to their
// number instead of an empty field and a second trip to the dashboard.
const stashPendingOdo = (reading) => {
  try { localStorage.setItem(ODO_PENDING_KEY, JSON.stringify({ date: localDateStr(), reading })); } catch {}
};
const clearPendingOdo = () => { try { localStorage.removeItem(ODO_PENDING_KEY); } catch {} };
const readPendingOdo = () => {
  try {
    const p = JSON.parse(localStorage.getItem(ODO_PENDING_KEY) || 'null');
    return (p && p.date === localDateStr() && p.reading > 0) ? p.reading : null;
  } catch { return null; }
};

// Admin logins — see all-driver summary instead of personal history
const ADMIN_PORTAL_LOGINS = new Set(['BOUVENGJ', 'BOUVENGP2']);

// ── Home-hub navigation (PILOT — not for drivers yet) ────────────────────────
// Option 2 of the portal navigation study. Instead of one horizontally scrolling
// strip (17 tabs for a driver, 29 for a portal admin, ~2,070px wide — so any red
// dot past the right edge is invisible), the portal opens on a home screen of
// grouped tiles and every screen pushes with a Back bar.
// ONLY the logins listed here see it; everyone else keeps the tab strip exactly
// as it is. The pilot gets a Hub/Tabs switch in the header so both can be felt on
// the same phone, and the choice is remembered per device.
// Pilot: Paul (BOUVENGP2), Jayda (BOUVENGJ), Billy Dong (DONGB3) — 21 Aug 2026,
// gathering opinions before this goes anywhere near the drivers.
const NAV_HUB_LOGINS = new Set(['BOUVENGP2', 'BOUVENGJ', 'DONGB3']);
const NAV_MODE_KEY   = 'hp_portal_nav_mode';
// Tile groups in the order they appear on the hub; every tab carries a `group`.
const HUB_GROUPS = [
  ['today', 'Today'],
  ['me',    'Me'],
  ['know',  'Know-how'],
  ['team',  'My Team'],
  ['admin', 'Admin'],
];
// "Sort to outlet" scan tool (Chute Layout) — office/oversight logins who help at the
// sort frame: Paul, Jayda + Valeria (Broadway). Mirror this list in _shared/admin-auth.js
// (SORT_SCAN_USERS), which gates the backend nsGetSortGuide + ocr-sort-label calls.
const SORT_SCAN_LOGINS = new Set(['BOUVENGP2', 'BOUVENGJ', 'ASTORGACV']);
// Document Management System (DMS) — the "Your documents on file" section
// (compliance docs: change details + upload renewals) is folded into the single
// "My Docs" tab and shown to ALL active contractors. It self-hides for anyone with
// no required documents, and only ever shows a driver their REQUIRED items.
// The Morning Van Check is STANDARD PRACTICE (Paul, 27 Aug 2026) — every driver
// sees the tab and every Onboard Scan is gated behind it. There is no trial roll
// any more: the enrolment list was one settings row that, when it went missing,
// took 38 drivers' van checks with it. Nothing to lose now, and nobody to add.
//
// The one exception is a WALKER: no vehicle, so no van to check and no odometer
// to read, and the gate would strand them on the Onboard tab every morning. Name
// them here — deliberately, one login at a time. Do NOT replace this with "has no
// van on file": LANDERR has no Fleet row either and drives a van daily, so an
// inferred exemption would quietly stop recording a real driver's kilometres.
const VAN_CHECK_EXEMPT = new Set(['PHUNGL']);

// Team-leader / admin aggregated history. Each entry scopes the period totals to
// a set of areas and the volume metrics that login should see.
//   areas: 'ALL' or array of area codes (CITY/BWAY/DARL)
//   metrics: which of parcels/express/depots/doe to display
const TEAM_HISTORY = {
  SOTOCRUZL:  { scope: 'All Teams',     areas: 'ALL',    metrics: ['parcels', 'express', 'depots'] },
  SOTOASTOL2: { scope: 'Broadway',      areas: ['BWAY'], metrics: ['parcels', 'depots'] },
  SOTOASTOA:  { scope: 'Broadway',      areas: ['BWAY'], metrics: ['parcels', 'depots'] },
  DONGB3:     { scope: 'Darlinghurst',  areas: ['DARL'], metrics: ['parcels', 'express', 'depots'] },
  BOUVENGP2:  { scope: 'All Contracts', areas: 'ALL',    metrics: ['parcels', 'express', 'depots', 'doe'] },
  BOUVENGJ:   { scope: 'All Contracts', areas: 'ALL',    metrics: ['parcels', 'express', 'depots', 'doe'] },
};
const METRIC_META = {
  parcels: { label: 'Parcels',    color: 'var(--hp-gold)' },
  express: { label: 'Express',    color: '#f97316' },
  depots:  { label: 'Depot Bags', color: '#60a5fa' },
  doe:     { label: 'DoE',        color: '#a78bfa' },
};

// ── Period helpers ────────────────────────────────────────────────────────────
const PERIOD_ANCHOR = '2026-05-16';
function addDays(iso, n) {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d + n);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
}
function isoToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
// Count out from TODAY, never search a fixed window around the anchor: the old
// i ≤ 6 loop ran out on 31 Oct 2026 and every portal fell back to Apr–May 2026.
function currentPeriodDates() {
  const end = detectPeriodEnd(isoToday(), PERIOD_ANCHOR);
  return { start: addDays(end, -27), end };
}
function previousPeriodDates() {
  const { start } = currentPeriodDates();
  const end   = addDays(start, -1);
  const pStart = addDays(end, -27);
  return { start: pStart, end };
}

// ── Wage-run helpers ─────────────────────────────────────────────────────────
// Two pay calendars exist in this business. Contractors bill on the 28-day RCTI
// grid above; a WAGE login is paid MONTHLY, the 15th → the 14th. Their history
// has to be cut on their own calendar, or the "this period" total they read here
// is not the run they're actually paid for.
const WAGE_RUN_LOGINS = new Set(['BOUVENGJ']);
function wageRunDates(back = 0) {
  const t = new Date();
  // The run containing today opens on the 15th of this month — or last month's
  // 15th if we haven't reached the 15th yet — and closes on the 14th following.
  const m = t.getMonth() - (t.getDate() >= 15 ? 0 : 1) - back;
  const s = new Date(t.getFullYear(), m, 15);
  const e = new Date(t.getFullYear(), m + 1, 14);
  const iso = d => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
  return { start: iso(s), end: iso(e) };
}
// The one place that decides which calendar a login's history is cut on.
function historyPeriods(login) {
  if (WAGE_RUN_LOGINS.has((login || '').toString().toUpperCase())) {
    return { wageRun: true, current: wageRunDates(0), previous: wageRunDates(1) };
  }
  return { wageRun: false, current: currentPeriodDates(), previous: previousPeriodDates() };
}
// Returns the last working day (Mon–Fri), skipping weekends.
// Mon → previous Friday, Sun → previous Friday, otherwise yesterday.
function lastWorkingDay() {
  const d = new Date();
  const day = d.getDay(); // 0=Sun, 1=Mon, 6=Sat
  const daysBack = day === 0 ? 2 : day === 1 ? 3 : 1; // Sun→2, Mon→3, else 1
  d.setDate(d.getDate() - daysBack);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function weekStartFor(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  const day = dt.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day; // shift to Monday
  const mon = new Date(dt);
  mon.setDate(dt.getDate() + diff);
  return `${mon.getFullYear()}-${String(mon.getMonth()+1).padStart(2,'0')}-${String(mon.getDate()).padStart(2,'0')}`;
}
function formatShortDate(iso) {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}
function totalParcels(p) {
  if (!p) return 0;
  return Math.round((p.parcel || 0) + (p.express || 0) + (p.doe || 0) + (p.depot || 0));
}

// ── Depot geofence config ─────────────────────────────────────────────────────
// NB: the QR gate is split by audience — a separate DRIVER gate (`driverQrRequired`)
// and NIGHT-SORT gate (`sorterQrRequired`), toggled independently in admin Settings.
// This page maps the driver gate into `qrRequired` (see loadGeofenceSettings); the
// sorter gate never reaches here. Default off until an admin turns the driver gate on.
const GEOFENCE_DEFAULTS = { geofenceRadiusM: 150, geofenceEnabled: true, qrRequired: false };
// A GPS fix reports how confident it is via coords.accuracy (metres). Privacy
// browsers (e.g. Brave) and indoor cold-starts often hand back a coarse network
// fix that's off by hundreds of metres, which falsely reads as "out of range"
// for a driver standing at the depot. Give the fix the benefit of its own stated
// error margin — but cap that leniency so a genuinely-distant (or absurdly fuzzy
// IP-level) fix can't unlock the geofence from anywhere.
const GEO_ACCURACY_SLACK_MAX_M = 500;
const FINISH_ZONE_DEFAULTS = { finishZoneEnabled: false, finishZoneLat: -33.882, finishZoneLng: 151.195, finishZoneRadiusM: 4000 };

// Logins that may finish a shift WITHOUT a scanner photo (team leaders + admins/
// others who don't always run a handheld scanner).
const SCANNER_OPTIONAL_LOGINS = new Set(['SOTOCRUZL', 'SOTOASTOL2', 'DONGB3', 'BOUVENGJ', 'BOUVENGP2', 'ASTORGACV']);
// Logins exempt from the start/finish geofence + QR scan (e.g. owners who log on/
// off from a different location such as Bayview 2104).
// PEREAVEGD (Daniel Perea Vega): his Brave browser refuses geolocation even with
// both OS + site location permissions on ("Location access denied"), so the depot
// geofence hard-blocks his start/finish. Exempt until he's on a browser that
// returns a location (Chrome), then remove.
const GEOFENCE_EXEMPT_LOGINS = new Set(['BOUVENGJ', 'BOUVENGP2', 'PEREAVEGD']);
// Logins exempt from the FINISH (log-off) zone only — the start geofence + QR still
// apply. ASTORGACV (Valeria): finishes her shift away from the depot.
const FINISH_GEOFENCE_EXEMPT_LOGINS = new Set(['ASTORGACV']);
// Logins for whom the odometer is OPTIONAL — they may start/finish without a km
// reading (don't always drive), but can still enter start/finish km when they
// convey parcels so total km is recorded.
const ODO_OPTIONAL_LOGINS = new Set(['ASTORGACV', 'BOUVENGJ', 'BOUVENGP2', 'PHUNGL']);

// No metro delivery run covers anywhere near this in a day — a larger total
// means a mistyped odometer (e.g. an extra digit: 121706 → 1217441).
const MAX_DAILY_KM = 600;

// ── Carry-over parcels report (end of shift) ─────────────────────────────────
// Every driver answers Yes/No at finish. 'photo: true' reasons require a photo
// before submit; 'note: true' requires free text. Incorrect Address shows the
// transfer-to-Post-Office instruction and needs an acknowledgement tick.
const CARRY_REASONS = [
  { key: 'refused',  label: 'Customer Refused' },
  { key: 'carded',   label: 'Carded Items — Post Office Closed' },
  { key: 'address',  label: 'Incorrect Address' },
  { key: 'damaged',  label: 'Damaged', photo: true },
  { key: 'misssort', label: 'Miss Sort', photo: true },
  { key: 'other',    label: 'Other Reason', note: true },
  // A parcel the driver has lost on the run. Besides the carry_over row it files an
  // incident report (carryover-sb reportMissingParcel) that goes to AP as its own card,
  // with the scanner photo of the barcode. Label must match INCIDENT_TYPES[0] server-side.
  { key: 'missing',  label: 'Missing / lost parcel', photo: true, note: true, incident: true },
];

// Pull the consignment + article ID off the OCR text of a scanner screen photo
// (Handover to Retail / Task List / article details). Best guess only: the driver
// sees both fields and can correct them.
function parseScannerBarcode(text) {
  const toks = (text || '').toUpperCase().split(/[^A-Z0-9]+/).filter(Boolean);
  const article = toks
    .filter(t => t.length >= 18 && (t.match(/\d/g) || []).length >= 10)
    .sort((a, b) => b.length - a.length)[0] || '';
  const consignment = toks.find(t => t !== article && (/^[A-Z]{2,4}\d{6,10}$/.test(t) || /^\d{2}[A-Z]{3}\d{7}$/.test(t))) || '';
  return { consignment, articleId: article };
}

// Logins who can view the daily carry-over report and email it to Australia
// Post from the portal ("Report" tab). Recipient/auto-send settings are
// editable by ADMIN_PORTAL_LOGINS only.
const CARRY_REPORT_LOGINS = new Set(['SOTOCRUZL', 'BOUVENGJ', 'BOUVENGP2', 'ASTORGACV']);
// Fallback pins only — the live pins come from geofence-settings (admin-set GPS
// captures). Keep these matching the saved overrides: a stale fallback strands
// any driver whose settings fetch fails (Botany was 619 m off → nobody could start).
const DEPOTS = [
  { name: 'Botany',     address: '1a Hale St, Botany',             lat: -33.95026,   lng: 151.1945    },
  { name: 'Alexandria', address: '12 Ralph St, Alexandria',        lat: -33.915597,  lng: 151.197872  },
  { name: 'Newtown',    address: '21-25 Enmore Rd, Newtown',       lat: -33.8997,    lng: 151.1754   },
];

// Haversine formula — returns distance in metres between two GPS points
function haversineDistance(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const toRad = deg => deg * Math.PI / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function localDateStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function formatTimeStr(isoStr) {
  if (!isoStr) return '--:--';
  return new Date(isoStr).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
}

function formatDuration(ms) {
  if (!ms || ms < 0) return '0h 0m';
  const totalMin = Math.floor(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m}m`;
}

function formatDateDisplay(dateStr) {
  if (!dateStr) return '';
  const [y, mo, d] = dateStr.split('-').map(Number);
  return new Date(y, mo - 1, d).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
}

// Calculate total worked hours from HH:MM strings
function calcTotalHoursFromHHMM(shiftStart, finishTime, breakStart, breakEnd) {
  function toMs(hhmm) {
    if (!hhmm || !hhmm.includes(':')) return null;
    const [h, m] = hhmm.split(':').map(Number);
    return (h * 60 + m) * 60000;
  }
  const startMs = toMs(shiftStart);
  const endMs   = toMs(finishTime);
  if (startMs === null || endMs === null) return 0;
  let shiftMs = endMs - startMs;
  if (shiftMs < 0) shiftMs += 24 * 3600000;
  let breakMs = 0;
  const bStartMs = toMs(breakStart);
  const bEndMs   = toMs(breakEnd);
  if (bStartMs !== null && bEndMs !== null) {
    breakMs = bEndMs - bStartMs;
    if (breakMs < 0) breakMs += 24 * 3600000;
  }
  return Math.round(Math.max(0, shiftMs - breakMs) / 3600000 * 100) / 100;
}

// ── Multiple breaks ───────────────────────────────────────────────────────────
// A shift can have several breaks, stored as shift.breaks = [{start, end}].
// The legacy single breakStart/breakEnd are mirrored (first start / last end) for
// the Driver_Logs record + admin display. Old sessions (only breakStart/breakEnd)
// are read as a one-element list so nothing breaks.
function getBreaks(s) {
  if (s && Array.isArray(s.breaks)) return s.breaks.filter(b => b && b.start);
  if (s && s.breakStart) return [{ start: s.breakStart, end: s.breakEnd || null }];
  return [];
}
// Sum of break durations in ms. Completed breaks always count; an in-progress
// break counts up to nowTs only when nowTs is supplied.
function sumBreakMs(breaks, nowTs) {
  return breaks.reduce((total, b) => {
    if (b.start && b.end)        return total + (new Date(b.end) - new Date(b.start));
    if (b.start && !b.end && nowTs) return total + (nowTs - new Date(b.start));
    return total;
  }, 0);
}

function statusToShiftState(status) {
  if (status === 'APPROVED') return 'approved';
  if (status === 'REJECTED')  return 'rejected';
  return 'pending_review'; // PENDING or anything else
}

// ── Styles ────────────────────────────────────────────────────────────────────
const S = {
  page: {
    minHeight: '100vh',
    background: 'var(--hp-black)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '0 0 40px',
    fontFamily: 'var(--font-body)',
  },
  header: {
    width: '100%',
    background: 'var(--hp-surface)',
    borderBottom: '1px solid var(--hp-border)',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  brand: {
    fontFamily: 'var(--font-display)',
    fontSize: '1.4rem',
    color: 'var(--hp-gold)',
    letterSpacing: '0.06em',
  },
  card: {
    background: 'var(--hp-surface)',
    border: '1px solid var(--hp-border)',
    borderRadius: 'var(--radius-lg)',
    padding: '28px 24px',
    width: '100%',
    maxWidth: 440,
  },
  label: {
    display: 'block',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: 'var(--hp-dim)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginBottom: 8,
  },
  sectionLabel: {
    fontSize: '0.7rem',
    fontWeight: 700,
    color: 'var(--hp-dim)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    marginBottom: 8,
  },
  input: {
    width: '100%',
    background: 'var(--hp-card)',
    border: '1px solid var(--hp-border)',
    borderRadius: 'var(--radius)',
    color: 'var(--hp-text)',
    fontSize: '1.1rem',
    padding: '14px 16px',
    outline: 'none',
    fontFamily: 'var(--font-body)',
    WebkitAppearance: 'none',
  },
  timeInput: {
    width: '100%',
    background: 'var(--hp-card)',
    border: '1px solid var(--hp-border)',
    borderRadius: 'var(--radius)',
    color: 'var(--hp-text)',
    fontSize: '1.2rem',
    padding: '14px 16px',
    outline: 'none',
    fontFamily: 'var(--font-mono)',
    WebkitAppearance: 'none',
    colorScheme: 'dark',
  },
  bigBtn: (color) => ({
    width: '100%',
    padding: '20px',
    borderRadius: 'var(--radius-lg)',
    border: 'none',
    fontSize: '1.1rem',
    fontWeight: 700,
    fontFamily: 'var(--font-body)',
    letterSpacing: '0.04em',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    transition: 'opacity 150ms',
    background: color === 'green'  ? 'var(--hp-green)'
              : color === 'amber'  ? 'var(--hp-amber)'
              : color === 'red'    ? 'var(--hp-red)'
              : color === 'gold'   ? 'var(--hp-gold)'
              : color === 'purple' ? '#7c3aed'
              : 'var(--hp-muted)',
    color: 'var(--hp-black)',
  }),
  metaRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid var(--hp-border)',
  },
  metaLabel: {
    fontSize: '0.8rem',
    color: 'var(--hp-dim)',
    fontWeight: 500,
  },
  metaValue: {
    fontSize: '0.95rem',
    color: 'var(--hp-text)',
    fontWeight: 600,
  },
  errorBox: {
    background: 'rgba(239,68,68,0.12)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: 'var(--radius)',
    padding: '12px 16px',
    color: 'var(--hp-red)',
    fontSize: '0.875rem',
    display: 'flex',
    gap: 8,
    alignItems: 'flex-start',
  },
  timerDisplay: {
    textAlign: 'center',
    padding: '20px 0',
  },
  timerNum: {
    fontFamily: 'var(--font-mono)',
    fontSize: '2.6rem',
    fontWeight: 700,
    color: 'var(--hp-green)',
    letterSpacing: '0.04em',
  },
  timerLabel: {
    fontSize: '0.75rem',
    color: 'var(--hp-dim)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    marginTop: 4,
  },
};

const WELCOME_KEY = 'hp-portal-welcomed';

// ── Night Sheet scan (BOUVENGJ / BOUVENGP2 only) ──────────────────────────────
// Photograph the Botany PDC paper sign-in sheet from the portal — it uploads
// through the same ap-signsheet-sb pipeline as the admin AP Sheets tab (store →
// OCR → roster reconcile), so the sheet lands in Night Sorters → AP Sheets with
// the shift night worked out. The function's siteLogin gate already accepts the
// BOUVENG* portal session cookie; this is purely the phone-friendly front door.
function NightSheetScanTab() {
  const [capture, setCapture] = useState(null);   // dataUrl of the photo
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [recent, setRecent] = useState(null);

  const card = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 14 };
  const apApi = (action, payload) => fetch('/.netlify/functions/ap-signsheet-sb', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }),
  }).then(async r => {
    const text = await r.text();
    try { return JSON.parse(text); }
    catch { throw new Error('The server took too long — the sheet may still have been stored. Check "Recent sheets" below in a minute before scanning again.'); }
  });

  const loadRecent = useCallback(async () => {
    try { const r = await apApi('apSheetList', {}); setRecent(r.success ? (r.sheets || []).slice(0, 5) : []); }
    catch { setRecent([]); }
  }, []);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  async function send() {
    if (!capture || busy) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const base64 = (capture.split(',')[1] || '');
      const r = await apApi('apSheetUpload', { base64, contentType: 'image/jpeg', filename: 'night-sheet.jpg' });
      if (!r.success) throw new Error(r.error || 'Upload failed');
      setResult(r); setCapture(null); loadRecent();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  const fmtNight = (iso) => iso ? new Date(iso + 'T00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
  const sum = result && result.reconciliation && result.reconciliation.summary;

  return (
    <div style={{ padding: '16px' }}>
      <div style={card}>
        <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Camera size={17} color="var(--hp-gold)" /> Scan the night sort sign-in sheet
        </div>
        <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '0 0 12px', lineHeight: 1.5 }}>
          Photograph the Botany PDC paper sheet — it's read automatically, checked against the sorter roster,
          and filed under <strong>Night Sorters → AP Sheets</strong>. The paper's date is the morning the shift ends;
          the shift night is worked out for you.
        </p>

        <CameraCapture
          nativeFirst
          guide="document"
          maxEdge={2200}
          quality={0.85}
          value={capture}
          onCapture={dataUrl => { setCapture(dataUrl); setResult(null); setError(''); }}
          onClear={() => setCapture(null)}
        />

        {capture && (
          <button onClick={send} disabled={busy}
            style={{ marginTop: 12, width: '100%', padding: '14px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.95rem', cursor: busy ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {busy ? <><RefreshCw size={16} style={{ animation: 'spin 0.9s linear infinite' }} /> Reading the sheet… (~15s)</> : <><Send size={16} /> Send to AP Sheets</>}
          </button>
        )}

        {error && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 'var(--radius)', color: '#ef4444', fontSize: '0.82rem', display: 'flex', gap: 8 }}>
            <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} /> {error}
          </div>
        )}

        {result && (
          <div style={{ marginTop: 12, padding: '12px 14px', background: result.ocrError ? 'rgba(245,158,11,0.1)' : 'rgba(52,211,153,0.08)', border: `1px solid ${result.ocrError ? 'rgba(245,158,11,0.5)' : 'rgba(52,211,153,0.4)'}`, borderRadius: 'var(--radius)', fontSize: '0.84rem' }}>
            {result.ocrError ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <AlertTriangle size={16} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Sheet <strong>stored safely</strong>, but the automatic read didn't complete — open Night Sorters → AP Sheets and press <strong>Re-read</strong>.</span>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 8, fontWeight: 700, marginBottom: 4 }}>
                  <CheckCircle size={16} color="#34d399" style={{ flexShrink: 0, marginTop: 1 }} />
                  Received — shift night {fmtNight(result.shiftDate)}{result.sheetDate ? ` (paper dated ${fmtNight(result.sheetDate)})` : ''}
                </div>
                {sum && (
                  <div style={{ color: 'var(--hp-dim)', fontSize: '0.78rem' }}>
                    {sum.signedOn} signed on · {sum.rosteredCount} rostered
                    {sum.headcountCircled != null && ` · AP circled ${sum.headcountCircled} ${sum.headcountMatches ? '✓' : '≠'}`}
                    {sum.sheetMisses && sum.sheetMisses.length > 0 && ` · ${sum.sheetMisses.length} rostered but not on the sheet`}
                    {' — full reconciliation is in Night Sorters → AP Sheets.'}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div style={card}>
        <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>Recent sheets</div>
        {recent === null && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>Loading…</div>}
        {recent && recent.length === 0 && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>No sheets scanned yet.</div>}
        {(recent || []).map(s => (
          <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 0', borderTop: '1px solid var(--hp-border)', fontSize: '0.84rem' }}>
            <span style={{ fontWeight: 700 }}>{fmtNight(s.shiftDate)}</span>
            <span style={{ color: 'var(--hp-dim)', fontSize: '0.76rem' }}>
              {s.summary ? `${s.summary.signedOn ?? '—'} signed on` : '—'}
            </span>
            <span style={{ fontSize: '0.72rem', fontWeight: 700, color: s.status === 'Reviewed' ? '#34d399' : '#f59e0b' }}>{s.status === 'Reviewed' ? '✓ Reviewed' : 'To review'}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Onboard summary scan (all drivers) ───────────────────────────────────────
// After accepting all parcels the device shows a SUMMARY screen; the driver
// photographs it here instead of their camera roll. onboard-sb stores the image
// (recallable any time), stamps SERVER time (depot-departure evidence) and OCRs
// the tally lines. Admin reviews the whole team on the Tallies tab.
const OB_API = '/.netlify/functions/onboard-sb';
const obApi = (action, payload) => fetch(OB_API, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }),
}).then(async r => {
  const text = await r.text();
  try { return JSON.parse(text); }
  catch { throw new Error('The server took too long — the photo may still have been stored. Check your summaries list in a minute before scanning again.'); }
});
const obTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }) : '—';
const obDay  = (iso) => iso ? new Date(iso).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' }) : '—';
const obN    = (v) => (v == null ? '—' : v);
const obEparcel = (s) => (s.eparcelInitial == null && s.eparcelSubsequent == null) ? null : (s.eparcelInitial || 0) + (s.eparcelSubsequent || 0);

// ── Morning vehicle inspection (pre-onboard gate) ────────────────────────────
// A ~30-second pre-trip van check the driver does before their Onboard Scan.
// Daily items every morning; weekly/fortnightly/monthly items surface only when
// due (tracked per item from history). Everything is assumed good — the driver
// taps only what's WRONG, which needs a photo + note and alerts the depot
// (SMS + email) but does NOT block the run. vehicle-inspection-sb stores it all.
const INSP_API = '/.netlify/functions/vehicle-inspection-sb';
const inspApi = (action, payload) => fetch(INSP_API, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }),
}).then(r => r.json());

// ── Mail stoppers (Customs / ABF / Police interception requests) ─────────────
// A driver sees only the active stoppers assigned to them: a heads-up on the shift
// start screen, and a Yes/No "did you receive any parcels?" gate before Onboard.
const MS_API = '/.netlify/functions/portal-mail-stoppers';
const msApi = (action, payload) => fetch(MS_API, {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }),
}).then(r => r.json());

// ── Route issues (the shared Issue Register — driver-issues-sb) ───────────────
// Problems from the run — no access, abusive customers, broken intercoms, a
// concierge refusing parcels. Drivers log their own here and see the office's
// updates; the office side lives on the admin Issue Register page.
const riApi = (action, payload) => fetch('/.netlify/functions/driver-issues-sb', {
  method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }),
}).then(r => r.json());

const INSPECTION_ITEMS = [
  { id: 'tyres',    name: 'Tyres',                        hint: 'flats, cuts, bulges',     freq: 'daily' },
  { id: 'lights',   name: 'Lights & indicators',         hint: 'head, brake, indicators', freq: 'daily' },
  { id: 'brakes',   name: 'Brakes & handbrake',          hint: 'firm pedal, holds',       freq: 'daily' },
  { id: 'glass',    name: 'Windscreen, wipers & washers', hint: 'cracks, fluid',          freq: 'daily' },
  { id: 'dash',     name: 'Dash warning lights',         hint: 'none stay on',            freq: 'daily' },
  { id: 'leaks',    name: 'Leaks under the van',         hint: 'oil, coolant',            freq: 'daily' },
  { id: 'fuel',     name: 'Fuel / AdBlue',               hint: 'enough for the run',      freq: 'daily' },
  { id: 'body',     name: 'New body damage',             hint: 'dents, scratches',        freq: 'daily' },
  { id: 'load',     name: 'Load doors & security',       hint: 'latch, secure',           freq: 'daily' },
  { id: 'tread',    name: 'Tyre tread & pressure',       hint: 'gauge, incl. spare',      freq: 'weekly' },
  { id: 'fluids',   name: 'Oil, coolant & brake fluid',  hint: 'levels between marks',    freq: 'weekly' },
  { id: 'washer',   name: 'Washer fluid',                hint: 'topped up',               freq: 'weekly' },
  { id: 'regochk',  name: 'Registration currency',       hint: 'not expiring',            freq: 'weekly' },
  { id: 'battery',  name: 'Battery',                     hint: 'terminals clean & tight', freq: 'fortnightly' },
  { id: 'spare',    name: 'Spare, jack & tools',         hint: 'present, serviceable',    freq: 'fortnightly' },
  { id: 'aircon',   name: 'A/C & heating',               hint: 'blows hot & cold',        freq: 'fortnightly' },
  { id: 'locks',    name: 'Locks & door seals',          hint: 'central locking, seals',  freq: 'fortnightly' },
  { id: 'walk',     name: 'Full photo walkaround',       hint: 'each side + interior',    freq: 'monthly' },
  { id: 'svcchk',   name: 'Service-due check',           hint: 'odometer vs interval',    freq: 'monthly' },
  { id: 'wipers',   name: 'Wiper blades',                hint: 'replace if smearing',     freq: 'monthly' },
  { id: 'under',    name: 'Underbody & exhaust',         hint: 'no damage or leaks',      freq: 'monthly' },
  { id: 'insure',   name: 'Insurance & rego confirm',    hint: 'both current',            freq: 'monthly' },
  { id: 'interior', name: 'Interior clean',              hint: 'cab + load area',         freq: 'monthly' },
];
const INSP_FREQ_DAYS  = { weekly: 7, fortnightly: 14, monthly: 28 };
const INSP_FREQ_LABEL = { weekly: 'Weekly', fortnightly: 'Fortnightly', monthly: 'Monthly' };
const inspNum  = (n) => (n == null || isNaN(n)) ? '—' : Number(n).toLocaleString('en-AU');
const inspFmtD = (iso) => { try { return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return iso || '—'; } };
// Whole days since an ISO date (YYYY-MM-DD); Infinity if never done → always due.
const inspDaysSince = (iso) => iso ? Math.floor((Date.now() - new Date(iso + 'T00:00:00').getTime()) / 86400000) : Infinity;

// Odometer sanity warning, shared by the van check, the shift-start field and the
// finish screen. Never blocks on its own: the driver either takes the suggested
// reading or says the one they typed is right, and then carries on. See
// utils/odoCheck for why (a refused reading at 5am is worse than a bad row).
function OdoWarning({ check, onUse, onKeep, kept }) {
  if (!check || check.ok) return null;
  if (kept) {
    return (
      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 5 }}>
        Reading confirmed as typed.
      </div>
    );
  }
  return (
    <div style={{ marginTop: 10, padding: '12px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.35)', borderRadius: 'var(--radius)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
        <AlertTriangle size={16} color="var(--hp-amber)" style={{ flexShrink: 0, marginTop: 1 }} />
        <div style={{ fontSize: '0.82rem', color: 'var(--hp-text)', lineHeight: 1.45 }}>{check.message}</div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {check.suggestion > 0 && onUse && (
          <button onClick={() => onUse(check.suggestion)}
            style={{ flex: 1, minWidth: 140, padding: '9px', background: 'var(--hp-green)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
            Use {check.suggestion.toLocaleString('en-AU')} km
          </button>
        )}
        <button onClick={onKeep}
          style={{ flex: 1, minWidth: 140, padding: '9px', background: 'none', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-dim)', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
          No, that's what it reads
        </button>
      </div>
    </div>
  );
}

// Maintenance reminders the office has set on this van ("brakes in ~5,000–10,000 km").
// Shown on Van Check and My Van so the driver knows before it's urgent — more eyes on it.
// Read-only here: the office closes a reminder when the work is done. `odo` (optional) is a
// reading the driver has just typed, so "km to go" moves with it.
function VanWatchCard({ rego, odo }) {
  const [list, setList] = useState([]);
  useEffect(() => {
    if (!rego) { setList([]); return; }
    let alive = true;
    fetch('/.netlify/functions/vehicle-watch-sb', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'forVan', rego }) })
      .then(r => r.json()).then(d => { if (alive) setList(d && d.success ? (d.watches || []) : []); })
      .catch(() => { if (alive) setList([]); });
    return () => { alive = false; };
  }, [rego]);
  if (!list.length) return null;
  const today = new Date().toLocaleDateString('en-CA');
  const tone = { watch: '#94a3b8', soon: '#f59e0b', due: '#f59e0b', over: '#ef4444' };
  const word = { watch: 'Coming up', soon: 'Due soon', due: 'Due now', over: 'Overdue' };
  return (
    <div style={{ background: 'var(--hp-surface)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#f59e0b', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
        <Wrench size={13} /> Maintenance coming up on {rego}
      </div>
      <p style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', margin: '0 0 10px', lineHeight: 1.4 }}>
        The office is watching these and will book them in. If one starts to feel or sound wrong before then, mark it <strong style={{ color: 'var(--hp-text)' }}>Action</strong> on your van check.
      </p>
      {list.map(w => {
        const st = odo ? watchStanding(w, odo, today) : (w.standing || watchStanding(w, 0, today));
        const c = tone[st.stage] || tone.watch;
        return (
          <div key={w.id} style={{ padding: 11, borderRadius: 10, marginBottom: 8, border: `1px solid ${c}66`, background: `${c}14` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, fontSize: '0.9rem', fontWeight: 700 }}>{w.item}</div>
              <span style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: c }}>{word[st.stage]}</span>
            </div>
            <div style={{ fontSize: '0.8rem', marginTop: 3 }}>
              {w.fromKm ? 'At ' : 'Due '}{watchTarget(w)}{st.label && <span style={{ color: c, fontWeight: 700 }}> · {st.label}</span>}
            </div>
            {w.note && <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 3 }}>{w.note}</div>}
          </div>
        );
      })}
    </div>
  );
}

function InspectionTab({ driver, onDone, odoStart, setOdoStart }) {
  // Odometer is the SAME value the driver records at Start Shift (odoStart) — one
  // reading, shared, so the two can never disagree. Entering it here fills the
  // shift's start-km and vice versa.
  const [van, setVan] = useState(null);        // { rego, make, model, nextServiceKm, serviceDue, curOdo }
  const [status, setStatus] = useState(null);  // { doneToday, lastByItem }
  const [marks, setMarks] = useState({});      // itemId -> { note, photo(dataUrl) }
  const [faultItem, setFaultItem] = useState(null);
  const [draftPhoto, setDraftPhoto] = useState(null);
  const [draftNote, setDraftNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [redo, setRedo] = useState(false);
  const [error, setError] = useState('');
  const [openIssues, setOpenIssues] = useState([]);   // carried from earlier days
  const [resolveId, setResolveId] = useState(null);    // issue id whose remedy box is open
  const [remedyText, setRemedyText] = useState('');
  const [resolveBusy, setResolveBusy] = useState(false);
  const [odoKept, setOdoKept] = useState(false);   // driver stood by a flagged reading

  const today = new Date().toLocaleDateString('en-CA');
  const card = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 14 };
  const cap  = { fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--hp-dim)' };
  const svcVal = { fontSize: '0.95rem', fontWeight: 800, marginTop: 3 };
  const svcSub = { fontSize: '0.72rem', color: 'var(--hp-dim)', fontWeight: 600, marginTop: 2 };

  const loadStatus = useCallback(() => {
    inspApi('inspectionStatus', {})
      .then(r => setStatus(r && r.success ? r : { doneToday: false, lastByItem: {} }))
      .catch(() => setStatus({ doneToday: false, lastByItem: {} }));
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  useEffect(() => {
    if (!driver) return;
    let alive = true;
    fetch('/.netlify/functions/appsscript', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getMyVan', login: driver }),
    }).then(r => r.json()).then(d => {
      if (!alive) return;
      const v = (d && d.vehicle) || {};
      setVan({
        rego: v.rego || '', make: v.make || '', model: v.model || '',
        nextServiceKm: (d && d.nextServiceKm) || 0,
        serviceDue: v.serviceDue || '',
        curOdo: (d && d.latestOdo && d.latestOdo.odometer) || 0,
        curOdoDate: (d && d.latestOdo && d.latestOdo.date) || '',
      });
    }).catch(() => { if (alive) setVan({ rego: '', nextServiceKm: 0, serviceDue: '', curOdo: 0, curOdoDate: '' }); });
    return () => { alive = false; };
  }, [driver]);

  // Open issues carried over from earlier days for this van — shown so the driver
  // doesn't re-report; each stays until resolved with a remedy.
  const loadOpenIssues = useCallback((rego) => {
    if (!rego) { setOpenIssues([]); return; }
    inspApi('issuesForVan', { rego })
      .then(r => setOpenIssues(r && r.success ? (r.issues || []) : []))
      .catch(() => setOpenIssues([]));
  }, []);
  useEffect(() => { if (van && van.rego) loadOpenIssues(van.rego); }, [van && van.rego, loadOpenIssues]);

  async function resolveIssue(id) {
    if (resolveBusy) return;
    setResolveBusy(true);
    try {
      const r = await inspApi('issueResolve', { id, remedy: remedyText.trim() });
      if (r && r.success) { setOpenIssues(list => list.filter(i => i.id !== id)); setResolveId(null); setRemedyText(''); }
      else setError((r && r.error) || 'Could not resolve — try again');
    } catch { setError('Could not resolve — try again'); }
    finally { setResolveBusy(false); }
  }

  const lastByItem = (status && status.lastByItem) || {};
  const dueItems = INSPECTION_ITEMS.filter(it => it.freq === 'daily'
    || inspDaysSince(lastByItem[it.id]) >= INSP_FREQ_DAYS[it.freq]);
  const dailyItems = dueItems.filter(it => it.freq === 'daily');
  const stagedItems = dueItems.filter(it => it.freq !== 'daily');
  const carriedIds = new Set(openIssues.map(i => i.itemId));  // items with a known open issue
  const actionCount = Object.keys(marks).filter(id => marks[id] && marks[id].action).length;
  // An item counts as done if the driver marked it, OR it's a carried known issue.
  const resolvedCount = dueItems.filter(it => marks[it.id] || carriedIds.has(it.id)).length;
  const allResolved = dueItems.length > 0 && resolvedCount >= dueItems.length;
  const odoRaw = (odoStart || '').toString().replace(/[^0-9]/g, '');
  const odoValue = odoRaw ? parseInt(odoRaw, 10) : null;
  // Sanity-check the reading against what this van last read. Amber warning with a
  // suggested correction; the driver can confirm their number and carry on.
  const odoChk = checkOdometer({
    reading: odoValue, lastKnown: van && van.curOdo, lastDate: van && van.curOdoDate,
    date: today, rego: van && van.rego,
  });
  const odoBlocked = !odoChk.ok && !odoKept;
  // Editing the reading withdraws any confirmation of the previous one.
  useEffect(() => { setOdoKept(false); }, [odoValue]);

  const nextKm = (van && van.nextServiceKm) || 0;
  const kmLeft = (nextKm > 0 && odoValue) ? nextKm - odoValue : null;
  const svcDays = (van && van.serviceDue) ? Math.ceil((new Date(van.serviceDue) - new Date(today)) / 86400000) : null;

  function markGood(item) { setMarks({ ...marks, [item.id]: 'good' }); }
  function openAction(item) {
    const ex = marks[item.id];
    const wasAction = ex && ex.action;
    setFaultItem(item); setDraftPhoto(wasAction ? ex.photo : null); setDraftNote(wasAction ? ex.note : ''); setError('');
  }
  function saveAction() {
    if (!draftPhoto) return;
    setMarks({ ...marks, [faultItem.id]: { action: true, note: draftNote.trim(), photo: draftPhoto } });
    setFaultItem(null); setDraftPhoto(null); setDraftNote('');
  }
  function removeAction(id) { const m = { ...marks }; delete m[id]; setMarks(m); setFaultItem(null); }

  async function submit() {
    if (busy) return;
    if (odoValue == null) { setError('Enter the odometer reading first.'); return; }
    if (!allResolved) { setError('Mark every item Good or Action first.'); return; }
    setBusy(true); setError('');
    try {
      const items = dueItems.map(it => {
        if (carriedIds.has(it.id)) return { id: it.id, name: it.name, freq: it.freq, status: 'carried', note: '' };
        const m = marks[it.id];
        const isAction = m && m.action;
        return {
          id: it.id, name: it.name, freq: it.freq,
          status: isAction ? 'fault' : 'ok', note: isAction ? m.note : '',
          photoBase64: (isAction && m.photo) ? (m.photo.split(',')[1] || '') : '',
        };
      });
      const r = await inspApi('inspectionSubmit', {
        rego: van ? van.rego : '', odometer: odoValue,
        nextServiceKm: nextKm, nextServiceDate: van ? van.serviceDue : '',
        items,
      });
      if (!r.success) throw new Error(r.error || 'Could not save the check');
      if (van && van.rego && odoValue) {
        fetch('/.netlify/functions/appsscript', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'logOdometer', rego: van.rego, odometer: odoValue, date: today, driver, source: 'Morning inspection', notes: 'Vehicle inspection check' }),
        }).catch(() => {});
      }
      setDone({ faults: actionCount, at: r.inspectedAt || new Date().toISOString() });
      loadStatus();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  // ── Completed view (just submitted, or already done earlier today) ──────────
  const showDone = done || (status && status.doneToday && !redo);
  if (showDone) {
    const n = done ? done.faults : 0;
    return (
      <div style={{ padding: '16px 0' }}>
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(52,211,153,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
            <CheckCircle size={28} color="#34d399" />
          </div>
          <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>
            {done && n > 0 ? `Logged with ${n} fault${n > 1 ? 's' : ''}` : 'Van check complete'}
          </div>
          <p style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', margin: '6px auto 14px', maxWidth: 300, lineHeight: 1.5 }}>
            {done && n > 0
              ? 'The depot has been alerted. Your Onboard Scan is unlocked — you’re right to go.'
              : 'Your Onboard Scan is unlocked. Have a good run.'}
          </p>
          <button onClick={onDone}
            style={{ width: '100%', padding: '14px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            <Package size={16} /> Go to Onboard Scan
          </button>
          <button onClick={() => { setDone(null); setRedo(true); setMarks({}); }}
            style={{ marginTop: 10, background: 'none', border: 'none', color: 'var(--hp-dim)', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' }}>
            Run the check again
          </button>
        </div>
      </div>
    );
  }

  const ItemRow = (it) => {
    // Carried known issue — shown but not re-checked; resolve it in the panel above.
    if (carriedIds.has(it.id)) {
      return (
        <div key={it.id} style={{ padding: 12, borderRadius: 11, marginBottom: 9, border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.08)', display: 'flex', alignItems: 'center', gap: 10 }}>
          <AlertTriangle size={15} color="#f59e0b" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '0.9rem', fontWeight: 650 }}>{it.name}</div>
            <div style={{ fontSize: '0.72rem', color: '#f59e0b' }}>Known issue — already reported, still open</div>
          </div>
        </div>
      );
    }
    const m = marks[it.id];
    const isGood = m === 'good';
    const isAction = m && m.action;
    return (
      <div key={it.id} style={{ padding: 12, borderRadius: 11, marginBottom: 9,
        border: `1px solid ${isAction ? 'rgba(239,68,68,0.5)' : isGood ? 'rgba(52,211,153,0.45)' : 'var(--hp-border)'}`,
        background: isAction ? 'rgba(239,68,68,0.08)' : isGood ? 'rgba(52,211,153,0.08)' : 'var(--hp-black)' }}>
        <div style={{ marginBottom: 9 }}>
          <div style={{ fontSize: '0.9rem', fontWeight: 650 }}>{it.name}</div>
          <div style={{ fontSize: '0.72rem', color: isAction ? '#ef4444' : 'var(--hp-dim)' }}>
            {isAction ? (m.note || 'Action needed') : it.hint}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => markGood(it)}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 10, borderRadius: 9, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit',
              border: `1px solid ${isGood ? '#34d399' : 'var(--hp-border)'}`, background: isGood ? '#34d399' : 'var(--hp-surface)', color: isGood ? '#0a1f14' : 'var(--hp-dim)' }}>
            <CheckCircle size={15} /> Good
          </button>
          <button onClick={() => openAction(it)}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 10, borderRadius: 9, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit',
              border: `1px solid ${isAction ? '#ef4444' : 'var(--hp-border)'}`, background: isAction ? '#ef4444' : 'var(--hp-surface)', color: isAction ? '#fff' : 'var(--hp-dim)' }}>
            <Camera size={15} /> Action
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '16px 0' }}>
      {/* header: van + next service */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <SelfAvatar login={driver} size={30} editable={false} />
              <ClipboardCheck size={17} color="var(--hp-gold)" /> Morning van check
            </div>
            <p style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', margin: '4px 0 0', lineHeight: 1.45 }}>
              Check each item — mark it <strong style={{ color: 'var(--hp-text)' }}>Good</strong>, or <strong style={{ color: 'var(--hp-text)' }}>Action</strong> if it needs attention (add a photo). Every item needs a tap before you leave the depot.
            </p>
          </div>
          {van && van.rego ? (
            <div style={{ textAlign: 'right' }}>
              <div style={cap}>Rego</div>
              <div style={{ fontWeight: 800, fontSize: '0.95rem', letterSpacing: '0.06em', border: '1px solid var(--hp-gold)', borderRadius: 6, padding: '3px 8px', marginTop: 2 }}>{van.rego}</div>
            </div>
          ) : null}
        </div>
        <div style={{ display: 'flex', borderTop: '1px solid var(--hp-border)', paddingTop: 12, marginTop: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={cap}>Next service · kms</div>
            <div style={svcVal}>{nextKm ? inspNum(nextKm) + ' km' : '—'}</div>
            <div style={{ ...svcSub, color: kmLeft != null && kmLeft < 0 ? '#ef4444' : 'var(--hp-dim)' }}>
              {kmLeft == null ? 'enter reading' : (kmLeft >= 0 ? inspNum(kmLeft) + ' km to go' : inspNum(Math.abs(kmLeft)) + ' km overdue')}
            </div>
          </div>
          <div style={{ width: 1, background: 'var(--hp-border)', margin: '0 14px' }} />
          <div style={{ flex: 1 }}>
            <div style={cap}>Next service · date</div>
            <div style={svcVal}>{van && van.serviceDue ? inspFmtD(van.serviceDue) : '—'}</div>
            <div style={{ ...svcSub, color: svcDays != null && svcDays < 0 ? '#ef4444' : 'var(--hp-dim)' }}>
              {svcDays == null ? '—' : (svcDays >= 0 ? 'in ' + svcDays + ' days' : Math.abs(svcDays) + ' days overdue')}
            </div>
          </div>
        </div>
      </div>

      <VanWatchCard rego={van && van.rego} odo={odoValue || (van && van.curOdo) || 0} />

      {/* open issues carried from earlier days — resolve when fixed */}
      {openIssues.length > 0 && (
        <div style={card}>
          <div style={{ ...cap, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8, color: '#f59e0b' }}>
            <AlertTriangle size={13} /> Open issues on your van ({openIssues.length})
          </div>
          <p style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', margin: '0 0 12px', lineHeight: 1.4 }}>
            Still being fixed — no need to report these again. Tap <strong style={{ color: 'var(--hp-text)' }}>Resolved</strong> once one's sorted.
          </p>
          {openIssues.map(iss => (
            <div key={iss.id} style={{ padding: 11, borderRadius: 10, marginBottom: 8, border: '1px solid rgba(245,158,11,0.4)', background: 'rgba(245,158,11,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '0.88rem', fontWeight: 650 }}>{iss.itemName}</div>
                  {iss.problem && <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)' }}>{iss.problem}</div>}
                  <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginTop: 2 }}>
                    Reported {(() => { try { return new Date(iss.reportedDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }); } catch { return iss.reportedDate; } })()}
                    {iss.reportedBy && iss.reportedBy !== driver ? ' by ' + iss.reportedBy : ''}
                  </div>
                </div>
                {iss.photoUrl && <a href={iss.photoUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.72rem', color: 'var(--hp-gold)', fontWeight: 700, flexShrink: 0 }}>Photo</a>}
              </div>
              {resolveId === iss.id ? (
                <div style={{ marginTop: 10 }}>
                  <textarea value={remedyText} onChange={e => setRemedyText(e.target.value)} rows={2} placeholder="What was done to fix it? e.g. new tyre fitted"
                    style={{ width: '100%', border: '1px solid var(--hp-border)', borderRadius: 9, padding: 10, background: 'var(--hp-black)', color: 'var(--hp-text)', fontSize: '0.85rem', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button onClick={() => { setResolveId(null); setRemedyText(''); }}
                      style={{ flex: 1, padding: '10px', borderRadius: 9, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>Cancel</button>
                    <button onClick={() => resolveIssue(iss.id)} disabled={!remedyText.trim() || resolveBusy}
                      style={{ flex: 1, padding: '10px', borderRadius: 9, border: 'none', background: '#34d399', color: '#0a1f14', fontWeight: 800, fontSize: '0.82rem', cursor: (remedyText.trim() && !resolveBusy) ? 'pointer' : 'default', opacity: (remedyText.trim() && !resolveBusy) ? 1 : 0.5 }}>Confirm resolved</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => { setResolveId(iss.id); setRemedyText(''); }}
                  style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, border: '1px solid #34d399', background: 'transparent', color: '#34d399', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
                  <CheckCircle size={13} style={{ verticalAlign: '-2px', marginRight: 4 }} /> Resolved
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* odometer — the same reading recorded at Start Shift */}
      <div style={card}>
        <div style={{ ...cap, marginBottom: 8 }}>Odometer — start of shift</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--hp-border)', borderRadius: 10, background: 'var(--hp-black)', padding: '4px 14px' }}>
          <input value={odoValue != null ? odoValue.toLocaleString('en-AU') : ''}
            onChange={e => setOdoStart(e.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="0" aria-label="Odometer reading"
            style={{ flex: 1, border: 'none', background: 'none', color: 'var(--hp-text)', fontSize: '1.35rem', fontWeight: 800, padding: '9px 0', outline: 'none', width: '100%' }} />
          <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--hp-dim)' }}>km</span>
        </div>
        <OdoWarning check={odoChk} kept={odoKept}
          onUse={(km) => { setOdoStart(String(km)); setOdoKept(false); }}
          onKeep={() => setOdoKept(true)} />
        <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 8 }}>
          This is your <strong style={{ color: 'var(--hp-text)' }}>start-of-shift reading</strong> — it's shared with your shift record, so enter it once.
          {van && van.curOdo > 0 && <> Last recorded: {inspNum(van.curOdo)} km.</>}
        </div>
      </div>

      {/* daily */}
      <div style={card}>
        <div style={{ ...cap, marginBottom: 10 }}>Daily check</div>
        {dailyItems.map(ItemRow)}
      </div>

      {/* staged (due today) */}
      {stagedItems.length > 0 && (
        <div style={card}>
          <div style={{ ...cap, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            Also due today
            <span style={{ fontSize: '0.58rem', padding: '1px 7px', borderRadius: 20, background: 'rgba(96,165,250,0.15)', color: '#60a5fa', fontWeight: 700 }}>
              {[...new Set(stagedItems.map(s => INSP_FREQ_LABEL[s.freq]))].join(' · ')}
            </span>
          </div>
          {stagedItems.map(ItemRow)}
        </div>
      )}

      {error && (
        <div style={{ marginBottom: 12, padding: '10px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 'var(--radius)', color: '#ef4444', fontSize: '0.82rem', display: 'flex', gap: 8 }}>
          <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} /> {error}
        </div>
      )}
      {!allResolved && (
        <div style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
          {resolvedCount} of {dueItems.length} checked{actionCount > 0 ? ` · ${actionCount} action${actionCount > 1 ? 's' : ''}` : ''}
        </div>
      )}
      {allResolved && actionCount > 0 && (
        <div style={{ textAlign: 'center', fontSize: '0.78rem', color: '#ef4444', marginBottom: 8 }}>
          <strong>{actionCount} action{actionCount > 1 ? 's' : ''}</strong> flagged — you can still start your run
        </div>
      )}
      <button onClick={submit} disabled={busy || odoValue == null || !allResolved || odoBlocked}
        style={{ width: '100%', padding: '15px', border: actionCount > 0 ? '1px solid #ef4444' : 'none',
          background: actionCount > 0 ? 'var(--hp-surface)' : 'var(--hp-gold)', color: actionCount > 0 ? 'var(--hp-text)' : 'var(--hp-black)',
          borderRadius: 'var(--radius)', fontWeight: 800, fontSize: '0.95rem', cursor: (busy || odoValue == null || !allResolved || odoBlocked) ? 'default' : 'pointer',
          opacity: (busy || odoValue == null || !allResolved || odoBlocked) ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
        {busy ? <><RefreshCw size={16} style={{ animation: 'spin 0.9s linear infinite' }} /> Saving…</>
          : odoBlocked ? 'Check the odometer reading'
          : odoValue == null ? 'Enter odometer to continue'
          : !allResolved ? `${resolvedCount} of ${dueItems.length} checked`
          : actionCount > 0 ? <>Continue to Onboard ({actionCount} action{actionCount > 1 ? 's' : ''})</>
          : <><CheckCircle size={16} /> All good — unlock Onboard</>}
      </button>

      {/* fault panel — drops down from the top */}
      {faultItem && (
        <div onClick={() => setFaultItem(null)} style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.55)' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ position: 'absolute', top: 0, left: 0, right: 0, maxWidth: 620, margin: '0 auto', maxHeight: '94vh', overflowY: 'auto',
              background: 'var(--hp-surface)', borderBottomLeftRadius: 18, borderBottomRightRadius: 18, borderBottom: '1px solid var(--hp-border)', padding: '18px 16px' }}>
            <div style={{ fontWeight: 800, fontSize: '1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={17} color="#ef4444" /> {faultItem.name}
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '4px 0 12px', lineHeight: 1.45 }}>
              Add a photo and a short note about what needs attention.
            </p>
            <CameraCapture nativeFirst guide="document" maxEdge={1600} quality={0.8}
              value={draftPhoto} onCapture={setDraftPhoto} onClear={() => setDraftPhoto(null)} />
            <textarea value={draftNote} onChange={e => setDraftNote(e.target.value)} rows={2}
              placeholder="What's wrong? e.g. front left tyre low"
              style={{ width: '100%', marginTop: 12, border: '1px solid var(--hp-border)', borderRadius: 10, padding: 12, background: 'var(--hp-black)', color: 'var(--hp-text)', fontSize: '0.9rem', fontFamily: 'inherit', resize: 'none', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              {marks[faultItem.id] && marks[faultItem.id].action && (
                <button onClick={() => removeAction(faultItem.id)}
                  style={{ flex: '0 0 auto', padding: '13px 16px', borderRadius: 10, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-dim)', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                  Remove
                </button>
              )}
              <button onClick={() => setFaultItem(null)}
                style={{ flex: 1, padding: '13px', borderRadius: 10, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={saveAction} disabled={!draftPhoto}
                style={{ flex: 1, padding: '13px', borderRadius: 10, border: 'none', background: '#ef4444', color: '#fff', fontWeight: 800, fontSize: '0.88rem', cursor: draftPhoto ? 'pointer' : 'default', opacity: draftPhoto ? 1 : 0.45 }}>
                Save action
              </button>
            </div>
            <div style={{ marginTop: 12, fontSize: '0.72rem', color: 'var(--hp-dim)', lineHeight: 1.45 }}>
              This flags the action to the depot right away. You can still start your run.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// What the driver should hold, from the article types (they deliver parcels, so lead
// with parcels; never say "all mail" when only specific types are targeted).
function holdPhrase(s) {
  const t = (s.article_types || []).join(', ').toLowerCase();
  if (/parcel/.test(t)) return 'Hold parcels for this address';
  if (/letter/.test(t)) return 'Hold mail for this address';
  return 'Hold mail for this address';
}
const articleLine = (s) => (s.article_types || []).join(', ');
// "Mail monitor" (record what arrives) vs "Mail stopper" (intercept it), and what the
// driver is looking out for — a monitor covering letters is about all mail, not parcels.
const msKind = (s) => (s.request_type === 'monitor' ? 'Mail monitor' : 'Mail stopper');
const msWhat = (s) => (/letter/i.test(articleLine(s)) ? 'mail' : 'parcels');
// Who the mail is addressed to — the name the driver will actually see on the label.
function MsAddressee({ s, size = '0.9rem' }) {
  if (!s.addressee) return null;
  return (
    <div style={{ fontSize: size, marginTop: 4 }}>
      <span style={{ color: 'var(--hp-dim)' }}>Addressed to:</span> <strong>{s.addressee}</strong>
    </div>
  );
}

// Warning chips (do not scan / do not endorse) shared by the banner + gate.
function MsWarnings({ s }) {
  const chip = { display: 'flex', alignItems: 'center', gap: 6, background: 'var(--hp-black)', border: '1px solid rgba(239,68,68,0.5)', borderRadius: 8, padding: '6px 10px', fontSize: '0.82rem', fontWeight: 700, color: '#f87171' };
  if (!s.doNotScan && !s.doNotEndorse) return null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
      {s.doNotScan && <div style={chip}><ScanLine size={15} /> Do NOT scan the parcel</div>}
      {s.doNotEndorse && <div style={chip}><AlertTriangle size={15} /> Do NOT write on the parcel</div>}
    </div>
  );
}

// Shift-start heads-up: active mail stoppers assigned to this driver. Non-blocking.
// A stopper a team leader is covering for their area (assigned to another driver).
// Awareness-only — not gated at Onboard — with an optional "record a parcel" action
// so the leader can log a catch when the regular driver is away.
function LeaderStopperCard({ s, onRecorded }) {
  const [open, setOpen] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [barcodes, setBarcodes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(!!(s.answeredToday && s.received));

  const submit = async () => {
    if (photos.length === 0) { setError('Please add at least one photo of the parcel and its barcode.'); return; }
    setBusy(true); setError('');
    try {
      const payloadPhotos = photos.map(d => ({ base64: (d.split(',')[1] || ''), contentType: 'image/jpeg' }));
      const r = await msApi('submitCheck', { stopper_id: s.id, received: true, barcodes, photos: payloadPhotos });
      if (!r.success) throw new Error(r.error || 'Failed to save');
      setDone(true); setOpen(false); setPhotos([]); setBarcodes('');
      onRecorded && onRecorded();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.5)', borderRadius: 'var(--radius)', padding: '13px 15px', marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: '#f59e0b', fontSize: '0.9rem' }}>
        <AlertTriangle size={16} /> Mail stopper — team area
      </div>
      <div style={{ fontSize: '0.98rem', fontWeight: 700, marginTop: 6 }}>{s.address}</div>
      <MsAddressee s={s} />
      <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 4 }}>
        Regular driver: <strong>{s.assignedDriver}</strong>
      </div>
      {articleLine(s) && <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 4 }}>Watching for: {articleLine(s)}</div>}
      <MsWarnings s={s} />
      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 10, lineHeight: 1.5 }}>
        You cover this area. If {s.assignedDriver} is away, hold any parcel for this address and record it below.
      </div>

      {done ? (
        <div style={{ marginTop: 10, fontSize: '0.82rem', fontWeight: 700, color: '#22c55e' }}>✓ Parcel recorded — office notified.</div>
      ) : !open ? (
        <button onClick={() => setOpen(true)}
          style={{ marginTop: 12, padding: '10px 14px', background: 'transparent', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-text)', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
          I received a parcel for this address
        </button>
      ) : (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', marginBottom: 8, lineHeight: 1.5 }}>
            Photograph the parcel and its barcode. Do <strong>not</strong> scan it and do <strong>not</strong> write on it — just hold it and tell the office.
          </div>
          {photos.length > 0 && (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
              {photos.map((p, i) => (
                <div key={i} style={{ position: 'relative' }}>
                  <img src={p} alt={`parcel ${i + 1}`} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hp-border)' }} />
                  <button onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))}
                    style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 'none', background: '#ef4444', color: '#fff', fontSize: 12, cursor: 'pointer', lineHeight: 1 }}>×</button>
                </div>
              ))}
            </div>
          )}
          <CameraCapture nativeFirst guide="document" maxEdge={2000} quality={0.85}
            value={null} onCapture={d => { setPhotos(ps => [...ps, d]); setError(''); }} />
          <input value={barcodes} onChange={e => setBarcodes(e.target.value)}
            placeholder="Barcode number(s) — optional"
            style={{ width: '100%', marginTop: 10, boxSizing: 'border-box', border: '1px solid var(--hp-border)', borderRadius: 10, padding: 12, background: 'var(--hp-black)', color: 'var(--hp-text)', fontSize: '0.9rem' }} />
          {error && <div style={{ marginTop: 10, color: '#f87171', fontSize: '0.82rem' }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            <button onClick={() => { setOpen(false); setError(''); }} disabled={busy}
              style={{ padding: '11px 14px', background: 'transparent', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-text)', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
              Cancel
            </button>
            <button onClick={submit} disabled={busy}
              style={{ flex: 1, padding: '11px 14px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.9rem', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
              {busy ? 'Saving…' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Required training, on the home screen ────────────────────────────────────
// A retraining topic assigned to a driver used to live only behind the Training tab, so a
// driver who never opened that tab never knew it was waiting — which is no use when the
// office has assigned it in answer to a customer case. This card puts it on the first
// screen of the shift: the brief to read, then the quiz. It disappears the moment the
// quiz is passed, and stays silent (renders nothing) when there is nothing outstanding.
function TrainingDueCard({ driver, onCountChange, onGoTraining }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch('/.netlify/functions/driver-training-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'myOutstanding' }),
    })
      .then(r => r.json())
      .then(d => { if (alive && d && d.success) { setItems(d.items || []); onCountChange && onCountChange(d.count || 0); } })
      .catch(() => {});
    return () => { alive = false; };
  }, [driver, onCountChange]);

  if (!items || !items.length) return null;
  return (
    <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid var(--hp-gold)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: 'var(--hp-gold)', fontSize: '0.78rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        <GraduationCap size={15} /> Training to complete
      </div>
      {items.map(it => (
        <div key={it.id} style={{ marginTop: 10 }}>
          <div style={{ fontWeight: 800, fontSize: '1.02rem', color: 'var(--hp-text)' }}>{it.topic}</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 3, lineHeight: 1.5 }}>
            {it.guideUrl ? 'Read the brief, then answer the questions' : 'Answer the questions'}
            {it.questions ? ` · ${it.questions} question${it.questions === 1 ? '' : 's'}` : ''}
            {it.caseNumber ? ` · from customer case ${it.caseNumber}` : ''}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            {it.guideUrl && (
              <a href={it.guideUrl} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 'var(--radius)', textDecoration: 'none', fontWeight: 800, fontSize: '0.84rem', border: '1px solid var(--hp-gold)', color: 'var(--hp-gold)' }}>
                <BookOpen size={14} /> Read the brief
              </a>
            )}
            {it.practiceUrl && (
              <a href={it.practiceUrl} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 'var(--radius)', textDecoration: 'none', fontWeight: 800, fontSize: '0.84rem', border: '1px solid var(--hp-border)', color: 'var(--hp-text)' }}>
                <Smartphone size={14} /> Practice
              </a>
            )}
            <button onClick={onGoTraining}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 16px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontWeight: 800, fontSize: '0.84rem', background: 'var(--hp-gold)', color: '#1a1a2e' }}>
              Start the quiz →
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function MailStopperBanner({ driver, onGo }) {
  const [stoppers, setStoppers] = useState([]);
  const load = useCallback(() => {
    msApi('list', {}).then(r => { if (r && r.success) setStoppers(r.stoppers || []); }).catch(() => {});
  }, []);
  useEffect(() => {
    let alive = true;
    const fetchNow = () => msApi('list', {}).then(r => { if (alive && r && r.success) setStoppers(r.stoppers || []); }).catch(() => {});
    fetchNow();
    // A stopper can be assigned after the driver has opened the app (the office confirms
    // the driver mid-morning), so look again whenever they come back to it.
    const onVis = () => { if (document.visibilityState === 'visible') fetchNow(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { alive = false; document.removeEventListener('visibilitychange', onVis); };
  }, [driver]);
  if (!stoppers.length) return null;
  const mine = stoppers.filter(s => s.role !== 'leader');
  const leader = stoppers.filter(s => s.role === 'leader');
  return (
    <div style={{ marginBottom: 16 }}>
      {mine.map(s => (
        <div key={s.id} style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.5)', borderRadius: 'var(--radius)', padding: '13px 15px', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: '#ef4444', fontSize: '0.9rem' }}>
            <AlertTriangle size={16} /> {msKind(s)} — {holdPhrase(s).toLowerCase()}
          </div>
          <div style={{ fontSize: '0.98rem', fontWeight: 700, marginTop: 6 }}>{s.address}</div>
          <MsAddressee s={s} />
          {articleLine(s) && <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 4 }}>Watching for: {articleLine(s)}</div>}
          <MsWarnings s={s} />
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 10, lineHeight: 1.5 }}>
            {!s.answeredToday ? `Record today whether any ${msWhat(s)} arrived for this address.`
              : s.received ? `✓ Recorded today — ${s.photoCount} photo${s.photoCount === 1 ? '' : 's'}. Add anything else that turns up.`
              : `✓ Nothing arrived today — record it if something turns up later.`}
          </div>
          {onGo && (
            <button onClick={onGo}
              style={{ marginTop: 10, padding: '10px 14px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer' }}>
              Open Mail Monitor →
            </button>
          )}
        </div>
      ))}
      {leader.map(s => (
        <LeaderStopperCard key={s.id} s={s} onRecorded={load} />
      ))}
    </div>
  );
}

// Slim, non-blocking reminder shown on the shift-start / onboard / finish screens. It does
// NOT fetch — it reads the up-front count (csPending) — and taps through to the Customer
// Service tab where the work happens. Keeps those screens light (the perf constraint).
function CustomerCaseNudge({ count, onGo }) {
  if (!count) return null;
  return (
    <button onClick={onGo} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.5)', borderRadius: 'var(--radius)', padding: '11px 14px', marginBottom: 16, cursor: 'pointer', color: 'var(--hp-text)', textAlign: 'left' }}>
      <AlertTriangle size={16} style={{ color: '#ef4444', flexShrink: 0 }} />
      <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>You have {count} open customer case{count > 1 ? 's' : ''} to answer</span>
      <span style={{ marginLeft: 'auto', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>Open →</span>
    </button>
  );
}

// Slim, non-blocking reminder for a driver who is well into the shift with no onboard
// summary scanned. Deliberately LATE: the scan only happens once the whole load has been
// accepted, and across Jul-Aug the median gap from shift start to first scan was ~105
// minutes (p90 ~156), so a nudge any earlier nags nine drivers in ten for nothing. At
// ONBOARD_NUDGE_MINS about 90% have already scanned and dismissed it by arriving, while
// the ones who never scan still have hours of shift left to put it right.
// scannedToday: null = not known yet (stay quiet), 0 = nothing today, >0 = done.
const ONBOARD_NUDGE_MINS = 150;

function OnboardNudge({ shiftStart, now, scannedToday, onGo }) {
  if (scannedToday !== 0 || !shiftStart) return null;
  if ((now - new Date(shiftStart)) / 60000 < ONBOARD_NUDGE_MINS) return null;
  return (
    <button onClick={onGo} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, background: 'rgba(229,161,58,0.08)', border: '1px solid rgba(229,161,58,0.5)', borderRadius: 'var(--radius)', padding: '11px 14px', marginBottom: 16, cursor: 'pointer', color: 'var(--hp-text)', textAlign: 'left' }}>
      <Package size={16} style={{ color: '#e5a13a', flexShrink: 0 }} />
      <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>You haven't scanned your onboard summary today</span>
      <span style={{ marginLeft: 'auto', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.82rem', whiteSpace: 'nowrap' }}>Scan it &rarr;</span>
    </button>
  );
}

// The Customer Service tab. Multi-function: Cases now, Route Issues next phase. Self-fetches
// on mount (lazy — only when the tab is opened) so it never slows the rest of the portal.
// The driver's own case rate vs the 1-per-1000 target, with a plain-English summary.
function MyStatsPanel({ driver }) {
  const [days, setDays] = useState(90);
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    ccApi('stats', { days }).then(r => { if (r && r.success) setS(r.stats); }).catch(() => {}).finally(() => setLoading(false));
  }, [days, driver]);
  if (loading || !s) return <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center' }}>Loading…</div>;
  const me = s.me || { parcels: 0, cases: 0, atFault: 0, notAtFault: 0, casesPer1000: null, atFaultPer1000: null, okReasons: {}, faultReasons: {} };
  const color = (v) => (v == null ? 'var(--hp-dim)' : v <= 1 ? 'var(--hp-green)' : v <= 2 ? '#f59e0b' : '#ef4444');
  const reasons = (obj) => Object.entries(obj || {}).sort((a, b) => b[1] - a[1]).map(([r, n]) => `${r} (${n})`).join(', ');
  const metric = (label, val) => (
    <div style={{ flex: '1 1 140px', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14 }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--hp-dim)', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: '1.8rem', fontWeight: 800, color: color(val) }}>{val == null ? '—' : val}</span>
        <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>/1000 · target ≤1</span>
      </div>
    </div>
  );
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[[30, '30d'], [90, '90d'], [180, '180d'], [365, '12m']].map(([d, l]) => (
          <button key={d} onClick={() => setDays(d)} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem', background: days === d ? 'var(--hp-gold)' : 'var(--hp-surface)', color: days === d ? 'var(--hp-black)' : 'var(--hp-dim)' }}>{l}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {metric('Cases / 1000 parcels', me.casesPer1000)}
        {metric('At-fault / 1000', me.atFaultPer1000)}
      </div>
      <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14, fontSize: '0.88rem', lineHeight: 1.6 }}>
        {me.cases === 0 ? (
          <div>No customer cases in this window — {me.parcels.toLocaleString()} parcels delivered. 🎉</div>
        ) : (
          <>
            <div>You received <strong>{me.cases}</strong> case{me.cases !== 1 ? 's' : ''} over <strong>{me.parcels.toLocaleString()}</strong> parcels delivered.</div>
            <div style={{ marginTop: 4 }}><strong>{me.notAtFault}</strong> with no error to you{reasons(me.okReasons) ? ` — ${reasons(me.okReasons)}` : ''}.</div>
            {me.atFault > 0 && <div style={{ marginTop: 2, color: '#ef4444' }}><strong>{me.atFault}</strong> at fault — {reasons(me.faultReasons) || '—'}.</div>}
          </>
        )}
        <div style={{ marginTop: 8, fontSize: '0.74rem', color: 'var(--hp-dim)' }}>{s.from} → {s.to} · target is 1 case per 1000 parcels.</div>
      </div>
    </div>
  );
}

// Route Issues — the driver's window onto the shared Issue Register. Lists their
// own issues (logged by them OR recorded by the office, e.g. ones we email AP
// about), lets them log a new one, and add follow-up comments the office sees.
const RI_CATEGORIES = [
  ['access', 'No access / entry refused'],
  ['customer', 'Abusive or difficult customer'],
  ['intercom', 'Broken intercom / buzzer'],
  ['refusal', 'Concierge / building refusing parcels'],
  ['animal', 'Dog or animal hazard'],
  ['safety', 'Safety hazard'],
  ['other', 'Other'],
];
const riCatLabel = (k) => (RI_CATEGORIES.find(([c]) => c === k) || [k, k])[1];
const RI_STATUS = {
  open: { label: 'OPEN', color: '#ef4444' },
  monitoring: { label: 'MONITORING', color: '#d97706' },
  resolved: { label: 'RESOLVED', color: '#16a34a' },
};

// Thumbnails with a remove button. Shared by the new-issue form (data URLs) and the
// issue card (signed URLs), so both read the same.
function IssuePhotoStrip({ photos, onRemove }) {
  if (!photos || !photos.length) return null;
  return (
    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 8 }}>
      {photos.map((p, i) => {
        const src = typeof p === 'string' ? p : p.url;
        if (!src) return null;
        return (
          <div key={i} style={{ position: 'relative' }}>
            <a href={src} target="_blank" rel="noreferrer">
              <img src={src} alt={`Photo ${i + 1}`}
                style={{ width: 74, height: 74, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hp-border)', display: 'block' }} />
            </a>
            {onRemove && (
              <button onClick={() => onRemove(i)} aria-label="Remove photo"
                style={{ position: 'absolute', top: -6, right: -6, width: 22, height: 22, borderRadius: 999, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: '0.72rem', lineHeight: 1 }}>×</button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function RouteIssueCard({ issue, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  // Adding a photo to an issue already sent — the driver who logged it at the gate and
  // photographed it on the way back.
  const [addingPhoto, setAddingPhoto] = useState(false);
  const [photoKey, setPhotoKey] = useState(0);
  const [photoBusy, setPhotoBusy] = useState(false);

  const sendPhoto = async (dataUrl) => {
    const b64 = (dataUrl || '').split(',')[1];
    if (!b64) return;
    setPhotoBusy(true);
    const r = await riApi('addPhotos', { id: issue.id, photos: [b64] });
    setPhotoBusy(false);
    setPhotoKey(k => k + 1);          // remount so another photo can go straight on
    if (r && r.success) onChanged();
  };
  const st = RI_STATUS[issue.status] || RI_STATUS.open;
  const when = issue.issue_date ? new Date(`${issue.issue_date}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  const sendComment = async () => {
    if (!comment.trim() || busy) return;
    setBusy(true);
    const r = await riApi('comment', { id: issue.id, text: comment.trim() });
    setBusy(false);
    if (r && r.success) { setComment(''); onChanged(); }
  };

  return (
    <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 10 }}>
      <div onClick={() => setExpanded(!expanded)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>{issue.title || riCatLabel(issue.category)}</div>
          <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 2 }}>{when}{issue.location ? ` · ${issue.location}` : ''}</div>
        </div>
        <span style={{ fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.05em', color: st.color, border: `1px solid ${st.color}`, borderRadius: 5, padding: '2px 6px', whiteSpace: 'nowrap' }}>{st.label}</span>
        {expanded ? <ChevronUp size={16} style={{ color: 'var(--hp-dim)' }} /> : <ChevronDown size={16} style={{ color: 'var(--hp-dim)' }} />}
      </div>
      {expanded && (
        <div style={{ marginTop: 10, borderTop: '1px solid var(--hp-border)', paddingTop: 10 }}>
          {issue.description && <div style={{ fontSize: '0.85rem', lineHeight: 1.5, whiteSpace: 'pre-wrap', marginBottom: 8 }}>{issue.description}</div>}
          <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginBottom: 8 }}>
            {riCatLabel(issue.category)}{issue.source === 'admin' ? ' · recorded by the office' : ''}
            {issue.ap_notified ? ' · Australia Post has been notified' : ''}
          </div>
          <IssuePhotoStrip photos={issue.photos} />
          <IssueVideoList videos={issue.videos} />
          {issue.status !== 'resolved' && <AddIssueVideo api={riApi} issue={issue} onAdded={onChanged} />}
          {issue.status !== 'resolved' && (issue.photos || []).length < 12 && (
            addingPhoto ? (
              <div style={{ marginBottom: 10 }}>
                <CameraCapture key={photoKey} nativeFirst guide="document" maxEdge={1280} quality={0.78} onCapture={sendPhoto} />
                <button onClick={() => setAddingPhoto(false)}
                  style={{ marginTop: 6, background: 'none', border: 'none', color: 'var(--hp-dim)', fontSize: '0.76rem', cursor: 'pointer', padding: 0 }}>
                  {photoBusy ? 'Uploading…' : 'Done adding photos'}
                </button>
              </div>
            ) : (
              <button onClick={() => setAddingPhoto(true)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginBottom: 10, background: 'none', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-gold)', padding: '7px 12px', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                <Camera size={13} /> {(issue.photos || []).length ? 'Add another photo' : 'Add a photo'}
              </button>
            )
          )}
          {(issue.notes || []).length > 0 && (
            <div style={{ borderLeft: '2px solid var(--hp-border)', paddingLeft: 10, marginBottom: 10 }}>
              {issue.notes.map((n, idx) => (
                <div key={idx} style={{ marginBottom: 6, fontSize: '0.8rem' }}>
                  <div style={{ color: 'var(--hp-dim)', fontSize: '0.7rem' }}>{n.at ? new Date(n.at).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''} · {n.by}</div>
                  <div style={{ whiteSpace: 'pre-wrap' }}>{n.text}</div>
                </div>
              ))}
            </div>
          )}
          {issue.status === 'resolved' && issue.resolution && (
            <div style={{ fontSize: '0.8rem', color: '#16a34a', marginBottom: 8 }}>Resolved — {issue.resolution}</div>
          )}
          {issue.status !== 'resolved' && (
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendComment()}
                placeholder="Add an update for the office…"
                style={{ flex: 1, background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '8px 10px', color: 'var(--hp-text)', fontSize: '0.85rem' }} />
              <button onClick={sendComment} disabled={busy || !comment.trim()}
                style={{ border: 'none', borderRadius: 8, padding: '8px 12px', cursor: 'pointer', background: 'var(--hp-gold)', color: 'var(--hp-black)', opacity: busy || !comment.trim() ? 0.5 : 1 }}>
                <Send size={14} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RouteIssuesPanel({ driver }) {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ issue_date: new Date().toLocaleDateString('en-CA'), category: 'access', location: '', description: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Photos taken while logging the issue. CameraCapture is remounted with a new key
  // after every shot so the "take a photo" control comes straight back — a blocked dock
  // or a broken intercom usually needs two or three.
  const [photos, setPhotos] = useState([]);
  const [captureKey, setCaptureKey] = useState(0);
  // Videos go up after the issue is saved (they need its id). If one fails, the form
  // stays open against that same issue so "Retry" sends just the videos — never a
  // second copy of the issue.
  const [videos, setVideos] = useState([]);
  const [createdId, setCreatedId] = useState(null);
  const [progress, setProgress] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    riApi('mine', {}).then(r => { if (r && r.success) setIssues(r.issues || []); }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load, driver]);

  const submit = async () => {
    if (!form.description.trim()) { setError('Please describe what happened.'); return; }
    if (busy) return;
    setBusy(true); setError('');
    let id = createdId;
    if (!id) {
      const r = await riApi('submit', { ...form, photos: photos.map(p => p.split(',')[1]).filter(Boolean) }).catch(() => null);
      if (!r || !r.success) { setBusy(false); setError((r && r.error) || 'Could not send — try again.'); return; }
      id = r.issue.id;
      setCreatedId(id);
      setPhotos([]);
    }
    if (videos.length) {
      const { left, error: vErr } = await uploadPendingVideos(riApi, id, videos, (text, frac) => setProgress({ text, frac }));
      setProgress(null);
      if (left.length) {
        setBusy(false);
        setVideos(left);
        load();
        setError(`Your issue was sent, but ${left.length > 1 ? `${left.length} videos` : 'the video'} didn't upload (${vErr}). Tap Retry, or Close and add it from the issue later.`);
        return;
      }
    }
    setBusy(false);
    closeForm();
    load();
  };
  const closeForm = () => {
    setShowForm(false); setError(''); setPhotos([]); setVideos([]); setCreatedId(null); setProgress(null);
    setForm({ issue_date: new Date().toLocaleDateString('en-CA'), category: 'access', location: '', description: '' });
  };

  const input = { width: '100%', boxSizing: 'border-box', background: 'var(--hp-bg)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '9px 10px', color: 'var(--hp-text)', fontSize: '0.88rem' };
  const label = { display: 'block', fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-dim)', marginBottom: 4 };
  const current = issues.filter(i => i.status !== 'resolved');
  const resolved = issues.filter(i => i.status === 'resolved');

  return (
    <div>
      {!showForm && (
        <button onClick={() => setShowForm(true)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, background: 'var(--hp-gold)', color: 'var(--hp-black)', border: 'none', borderRadius: 'var(--radius)', padding: '12px 14px', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', marginBottom: 14 }}>
          <Plus size={16} /> Log an Issue
        </button>
      )}
      {showForm && (
        <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 14 }}>
          <div style={{ fontWeight: 800, color: 'var(--hp-text)', marginBottom: 10 }}>Log an Issue</div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            <div style={{ flex: '0 0 46%' }}>
              <label style={label}>When</label>
              <input type="date" style={input} value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={label}>What kind of issue</label>
              <select style={input} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {RI_CATEGORIES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={label}>Where (address / building)</label>
            <input style={input} value={form.location} placeholder="e.g. 388 George Street" onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={label}>What happened</label>
            <textarea style={{ ...input, minHeight: 80 }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={label}><Camera size={12} style={{ verticalAlign: -2 }} /> Photos ({photos.length})</label>
            <IssuePhotoStrip photos={photos} onRemove={(i) => setPhotos(ps => ps.filter((_, j) => j !== i))} />
            {photos.length < 12 && (
              <CameraCapture key={captureKey} nativeFirst guide="document" maxEdge={1280} quality={0.78}
                onCapture={(d) => { setPhotos(ps => [...ps, d]); setCaptureKey(k => k + 1); }} />
            )}
            <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 6 }}>
              A photo settles it faster than a description. Take as many as you need.
            </div>
          </div>
          {!createdId || videos.length ? (
            <div style={{ marginBottom: 10 }}>
              <label style={label}><Video size={12} style={{ verticalAlign: -2 }} /> Videos ({videos.length})</label>
              <PendingVideoPicker files={videos} setFiles={setVideos} />
              <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 6 }}>
                Keep it short — about 30 seconds of the gate, the intercom, the dog. Uploads best on wifi or good signal.
              </div>
            </div>
          ) : null}
          {progress && <div style={{ marginBottom: 8 }}><VideoProgress text={`${progress.text} — keep this screen open`} frac={progress.frac} /></div>}
          {error && <div style={{ color: '#ef4444', fontSize: '0.82rem', marginBottom: 8 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => { closeForm(); if (createdId) load(); }} disabled={busy} style={{ flex: 1, background: 'var(--hp-bg)', color: 'var(--hp-dim)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '10px 0', fontWeight: 700, cursor: 'pointer' }}>{createdId ? 'Close' : 'Cancel'}</button>
            <button onClick={submit} disabled={busy} style={{ flex: 2, background: 'var(--hp-gold)', color: 'var(--hp-black)', border: 'none', borderRadius: 8, padding: '10px 0', fontWeight: 800, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>{busy ? (progress ? 'Uploading…' : 'Sending…') : createdId ? 'Retry video upload' : 'Send to the office'}</button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center' }}>Loading…</div>
      ) : issues.length === 0 ? (
        <div style={{ background: 'var(--hp-surface)', border: '1px dashed var(--hp-border)', borderRadius: 'var(--radius)', padding: 22, textAlign: 'center', color: 'var(--hp-dim)' }}>
          <Flag size={26} style={{ color: 'var(--hp-gold)', marginBottom: 8 }} />
          <div style={{ fontWeight: 700, color: 'var(--hp-text)', marginBottom: 6 }}>No issues on record</div>
          <div style={{ fontSize: '0.85rem', lineHeight: 1.5 }}>Hit a problem on your run — no access, an abusive customer, a broken intercom, a concierge refusing parcels? Log it here so the office can follow it up.</div>
        </div>
      ) : (
        <>
          {current.map(i => <RouteIssueCard key={i.id} issue={i} onChanged={load} />)}
          {resolved.length > 0 && (
            <>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--hp-dim)', margin: '14px 0 8px' }}>Resolved</div>
              {resolved.map(i => <RouteIssueCard key={i.id} issue={i} onChanged={load} />)}
            </>
          )}
        </>
      )}
    </div>
  );
}

// The leader's window onto their driver's phone. Same component the driver has open, same
// data the portal serves them (portal-customer-cases → teamCase → _shared/case-driver-view),
// `preview` on so nothing a leader taps can answer for their driver. The office reads the
// identical view from the Customer Cases drawer — one card, two readers.
const PEEK_TONE = { live: 'var(--hp-green)', history: 'var(--hp-dim)', unmatched: '#f59e0b', nodriver: '#ef4444' };
function TeamCasePeek({ caseId, driver, onHide }) {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    ccApi('teamCase', { id: caseId })
      .then((r) => { if (!alive) return; if (r && r.success) setRes(r); else setErr((r && r.error) || 'Could not load their screen'); })
      .catch(() => { if (alive) setErr('Network error — try again'); });
    return () => { alive = false; };
  }, [caseId]);

  const verdict = res ? previewVerdict(res.visibility, res.driver || driver) : null;
  return (
    <div style={{ border: '1px solid var(--hp-border)', borderRadius: 10, padding: 10, background: 'var(--hp-black)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Smartphone size={13} style={{ color: 'var(--hp-gold)' }} />
        <span style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--hp-dim)' }}>
          {(res && res.driver) || driver}&rsquo;s screen
        </span>
        <button onClick={onHide} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--hp-dim)', fontSize: '0.78rem', textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>Hide</button>
      </div>
      {err && <div style={{ color: '#ef4444', fontSize: '0.8rem' }}>{err}</div>}
      {!res && !err && <div style={{ color: 'var(--hp-dim)', fontSize: '0.82rem' }}>Loading their screen…</div>}
      {res && (
        <>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: PEEK_TONE[verdict.state] || 'var(--hp-dim)', marginBottom: 8 }}>{verdict.text}</div>
          <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginBottom: 8 }}>Read-only — the buttons are your driver&rsquo;s. Tapping one here sends nothing.</div>
          {res.case
            ? <CustomerCaseCard c={res.case} preview startOpen />
            : <div style={{ fontSize: '0.84rem', color: 'var(--hp-dim)' }}>Nothing on this case has reached their portal.</div>}
          {res.assistCase && (
            <>
              <div style={{ fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--hp-dim)', margin: '12px 0 6px' }}>
                The recovery · {res.assistDriver}&rsquo;s screen
              </div>
              <AssistCaseCard c={res.assistCase} preview />
            </>
          )}
        </>
      )}
    </div>
  );
}

// One of my team's open cases, seen by the team leader. Read-only detail plus a message
// into the thread — the leader chases their driver or tells the office what they know.
// Triage, the fault call and the reply to Australia Post stay with the office.
function TeamCaseCard({ c, onChanged }) {
  const [open, setOpen] = useState(false);
  const [peek, setPeek] = useState(false);   // "see what my driver sees"
  const [msg, setMsg] = useState('');
  const [to, setTo] = useState('driver');    // whose phone the note rings: 'driver' | 'assist' | 'both'
  const [sent, setSent] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const due = ccDue(c.due_at);
  const chip = { fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 5, border: '1px solid' };
  const answered = c.stage === 'answered';
  // A settled case is a record, not a job: the server refuses a post to it, so the box that
  // would take one isn't offered (a composer that always fails is worse than none).
  const settled = !!c.status && c.status !== 'open';
  // The recovery driver, while the office still has one out looking.
  const helper = (c.assist && c.assist.driver) ? String(c.assist.driver).toUpperCase() : '';

  const post = async () => {
    if (!msg.trim()) return;
    setBusy(true); setErr(''); setSent('');
    try {
      const r = await ccApi('teamPost', { id: c.id, message: msg.trim(), audience: helper ? to : 'driver' });
      if (r && r.success) {
        setMsg('');
        // Who actually got a phone alert. A leader who is told only "sent" has no way to know
        // their driver is unreachable until the job doesn't get done.
        const said = [];
        if (r.driverNotified) said.push(r.alerted ? `${c.driver_login} alerted` : r.smsSent ? `${c.driver_login} texted` : `${c.driver_login} not reachable`);
        if (r.assistLogin) said.push(r.assistAlerted ? `${r.assistLogin} alerted` : r.assistSms ? `${r.assistLogin} texted` : `${r.assistLogin} not reachable`);
        setSent(said.length ? `Posted — ${said.join(' · ')}` : 'Posted to the case');
        onChanged && onChanged();
      }
      else setErr((r && r.error) || 'Something went wrong');
    } catch { setErr('Network error — try again'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ background: 'var(--hp-surface)', border: `1px solid ${answered ? 'rgba(52,211,153,0.5)' : 'rgba(239,68,68,0.5)'}`, borderRadius: 'var(--radius)', padding: 13, marginBottom: 8 }}>
      <div onClick={() => setOpen((o) => !o)} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flexWrap: 'wrap' }}>
        <span style={{ ...chip, color: '#ef4444', borderColor: 'rgba(239,68,68,0.5)' }}>{CC_LABELS[c.category] || 'Enquiry'}</span>
        <span style={{ fontSize: '0.78rem', fontWeight: 800, fontFamily: 'var(--font-mono)' }}>{c.driver_login}</span>
        {/* On a settled case the fault call IS the outcome — it is what the driver was
            given, and the first thing a leader looks for when they open one. */}
        {c.fault === 'no' && <span style={{ ...chip, color: 'var(--hp-green)', borderColor: 'var(--hp-green)' }}>No fault</span>}
        {c.fault === 'yes' && <span style={{ ...chip, color: '#ef4444', borderColor: '#ef4444' }}>Driver fault</span>}
        {settled && <span style={{ ...chip, color: 'var(--hp-dim)', borderColor: 'var(--hp-border)' }}>{c.status}</span>}
        {c.assist && <span style={{ ...chip, color: '#3b82f6', borderColor: '#3b82f6' }}>Recovery · {c.assist.driver}</span>}
        {answered && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.7rem', color: '#34d399' }}><CheckCircle size={12} /> answered</span>}
        {!settled && due && due.show && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.7rem', fontWeight: 700, color: due.color, fontFamily: 'var(--font-mono)' }}><Clock size={11} /> {due.label}</span>}
        <span style={{ marginLeft: 'auto', color: 'var(--hp-dim)' }}>{open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
      </div>
      <div style={{ fontSize: '0.88rem', fontWeight: 700, marginTop: 8 }}>{c.case_number ? `Case ${c.case_number}` : 'Customer case'}{c.address ? ` · ${c.address}` : ''}</div>
      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 3 }}>{c.summary}</div>

      {open && (
        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* The same note the driver was sent — nothing more. A leader chasing their driver
              about AP wording the driver never saw is the old confusion one level up. */}
          {c.driver_note && (
            <div style={{ border: '1px solid rgba(212,175,55,0.45)', background: 'rgba(212,175,55,0.07)', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--hp-gold)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>Note from the office</div>
              <div style={{ fontSize: '0.84rem', whiteSpace: 'pre-wrap' }}>{c.driver_note}</div>
            </div>
          )}
          {c.assist && (
            <div style={{ border: '1px solid rgba(59,130,246,0.5)', background: 'rgba(59,130,246,0.08)', borderRadius: 8, padding: '10px 12px', fontSize: '0.84rem' }}>
              <div style={{ fontSize: '0.7rem', fontWeight: 800, color: '#3b82f6', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Recovery with {c.assist.driver}</div>
              {c.assist.task || 'Asked to search the address for the parcel.'}
            </div>
          )}
          {/* The peek below carries this same thread inside the driver's own card. Two
              copies of it on one phone screen is just scrolling, so the leader's copy
              stands down while the driver's card is open. */}
          {(c.messages || []).length > 0 && !peek && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {c.messages.map((m) => (
                <div key={m.id} style={{ alignSelf: m.who === 'you' ? 'flex-end' : 'flex-start', maxWidth: '85%', background: m.who === 'you' ? 'rgba(212,175,55,0.12)' : 'var(--hp-black)', border: '1px solid var(--hp-border)', borderRadius: 10, padding: '7px 10px', fontSize: '0.82rem' }}>
                  <div style={{ fontSize: '0.64rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>{m.who === 'you' ? 'You' : m.who === 'office' ? 'Office' : (m.driver_login || 'Driver')}</div>
                  {m.outcome && <div style={{ fontWeight: 700 }}>{m.outcome}</div>}
                  {m.message && <div style={{ whiteSpace: 'pre-wrap' }}>{m.message}</div>}
                  {(m.photos || []).map((src, i) => (
                    <a key={i} href={src} target="_blank" rel="noreferrer"><img src={src} alt="" style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6, marginTop: 5 }} /></a>
                  ))}
                </div>
              ))}
            </div>
          )}
          {/* What the office actually put in front of this driver, and what is still being
              asked of them. A leader chasing a driver over a case they can only see the
              office's version of is the old confusion one level up. */}
          {peek
            ? <TeamCasePeek caseId={c.id} driver={c.driver_login} onHide={() => setPeek(false)} />
            : (
              <button onClick={() => setPeek(true)}
                style={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '7px 11px', color: 'var(--hp-text)', fontSize: '0.8rem', fontWeight: 700, cursor: 'pointer' }}>
                <Smartphone size={13} style={{ color: 'var(--hp-gold)' }} /> See what {c.driver_login} sees
              </button>
            )}
          {settled ? (
            <div style={{ padding: 11, background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.4)', borderRadius: 8, fontSize: '0.84rem', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <CheckCircle size={15} style={{ color: '#34d399', flexShrink: 0, marginTop: 1 }} />
              <span>
                This case is {c.status}
                {c.fault === 'yes' ? ` — the office called it a driver fault${c.fault_reason ? `: ${c.fault_reason}` : ''}.`
                  : c.fault === 'no' ? ` — the office found no error by ${c.driver_login}${c.fault_reason ? `: ${c.fault_reason}` : ''}.`
                  : ' — the office has not recorded a fault call on it.'}
                {' '}If something has changed on it, tell the office.
              </span>
            </div>
          ) : (
            <>
              {/* Two drivers on one case: your own driver, who answers for the delivery, and
                  whoever is at the address today looking for the parcel. Both read the whole
                  thread — this picks whose phone rings. */}
              {helper && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--hp-dim)' }}>Alert</span>
                  {[['driver', c.driver_login], ['assist', `${helper} (recovery)`], ['both', 'Both']].map(([k, lbl]) => (
                    <button key={k} onClick={() => setTo(k)}
                      style={{ fontSize: '0.76rem', fontWeight: 700, padding: '5px 11px', borderRadius: 14, cursor: 'pointer', border: `1px solid ${to === k ? 'var(--hp-gold)' : 'var(--hp-border)'}`, background: to === k ? 'rgba(212,175,55,0.15)' : 'transparent', color: 'var(--hp-text)' }}>{lbl}</button>
                  ))}
                </div>
              )}
              <textarea value={msg} onChange={(e) => setMsg(e.target.value)}
                placeholder={helper && to === 'assist' ? `Message ${helper} about the recovery…`
                  : helper && to === 'both' ? `Message ${c.driver_login} and ${helper}…`
                  : "Add what you know, or what you've asked your driver to do…"}
                style={{ width: '100%', minHeight: 54, background: 'var(--hp-black)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', padding: 9, fontSize: '0.85rem', fontFamily: 'inherit' }} />
              <button disabled={busy || !msg.trim()} onClick={post} style={{ padding: 11, borderRadius: 8, border: 'none', background: 'var(--hp-gold)', color: 'var(--hp-black)', fontWeight: 800, cursor: 'pointer' }}>
                <Send size={14} /> {helper && to === 'assist' ? `Send to ${helper}` : 'Post to the case'}
              </button>
              {sent && <div style={{ fontSize: '0.76rem', color: /not reachable/.test(sent) ? '#f59e0b' : 'var(--hp-green)' }}>{sent}</div>}
              <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)' }}>
                The office answers Australia Post and makes the fault call — this puts your note on the case
                {helper ? ' and rings the phone you picked.' : ' and alerts your driver.'}
              </div>
            </>
          )}
          {err && <div style={{ color: '#ef4444', fontSize: '0.8rem' }}>{err}</div>}
        </div>
      )}
    </div>
  );
}

// The team leader's view of every open case on their area's drivers.
function TeamCasesPanel() {
  const [scope, setScope] = useState('current');   // 'current' | 'resolved'
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  // quiet = refresh in place. A full reload swaps the list for a spinner, which unmounts the
  // card the leader just posted from and takes the "MEDINACOK alerted" line with it — the one
  // thing they needed to read.
  const load = useCallback((quiet) => {
    if (!quiet) setLoading(true);
    ccApi('teamCases', { scope }).then(r => { if (r && r.success) setCases(r.cases || []); })
      .catch(() => {}).finally(() => { if (!quiet) setLoading(false); });
  }, [scope]);
  useEffect(() => { load(); }, [load]);
  const pill = (active) => ({ flex: 1, padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', background: active ? 'var(--hp-gold)' : 'var(--hp-surface)', color: active ? 'var(--hp-black)' : 'var(--hp-dim)' });
  return (
    <>
      {/* Open answers "what is outstanding"; settled answers "how did the last one go, and
          what was my driver told" — which is most of what a leader is actually asked. */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
        <button style={pill(scope === 'current')} onClick={() => setScope('current')}>Open</button>
        <button style={pill(scope === 'resolved')} onClick={() => setScope('resolved')}>Settled</button>
      </div>
      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 10 }}>
        {scope === 'current'
          ? <>Open customer cases sitting with your drivers. You&rsquo;ll get a text whenever one lands on your team, or when the office sends a recovery to another driver.</>
          : <>Your team&rsquo;s finished cases, newest first, with the fault call the office made on each. Nothing here is waiting on anyone.</>}
      </div>
      {loading ? (
        <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center' }}>Loading…</div>
      ) : !cases.length ? (
        <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center', fontSize: '0.88rem' }}>
          {scope === 'current' ? 'No open cases on your team right now. 🎉' : 'No settled cases on your team yet.'}
        </div>
      ) : (
        cases.map(c => <TeamCaseCard key={c.id} c={c} onChanged={() => load(true)} />)
      )}
    </>
  );
}

// One driver, opened out of the team table. The rate says something is wrong; this says
// WHAT — every case behind the number, what it was about, and the fault call the office made
// on it. That is the part a leader can act on: three "delivered to incorrect address" faults
// in a row is a conversation about addressing, not about trying harder. Each case can be
// opened as the driver's own screen, so the leader can see exactly what was asked of them.
function TeamDriverDrill({ login, days, onPeek, peekId }) {
  const [res, setRes] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    setRes(null); setErr('');
    ccApi('teamDriver', { login, days })
      .then((r) => { if (!alive) return; if (r && r.success) setRes(r); else setErr((r && r.error) || 'Could not load this driver'); })
      .catch(() => { if (alive) setErr('Network error — try again'); });
    return () => { alive = false; };
  }, [login, days]);

  if (err) return <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: 8 }}>{err}</div>;
  if (!res) return <div style={{ color: 'var(--hp-dim)', fontSize: '0.82rem', marginTop: 8 }}>Loading…</div>;

  const faultRows = Object.entries((res.row && res.row.faultReasons) || {}).sort((a, b) => b[1] - a[1]);
  const okRows = Object.entries((res.row && res.row.okReasons) || {}).sort((a, b) => b[1] - a[1]);
  const faultChip = (c) => (c.fault === 'yes'
    ? { label: 'At fault', color: '#ef4444' }
    : c.fault === 'no' ? { label: 'No fault', color: 'var(--hp-green)' }
    : { label: 'Awaiting fault call', color: '#f59e0b' });

  return (
    <div style={{ marginTop: 10, borderTop: '1px solid var(--hp-border)', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Why they were at fault, in the office's own words. This is the remedial list. */}
      {faultRows.length > 0 && (
        <div>
          <div style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#ef4444', marginBottom: 5 }}>At fault for</div>
          {faultRows.map(([r, n]) => (
            <div key={r} style={{ fontSize: '0.82rem', display: 'flex', gap: 8 }}>
              <span style={{ fontWeight: 800, fontFamily: 'var(--font-mono)' }}>{n}×</span><span>{r}</span>
            </div>
          ))}
        </div>
      )}
      {okRows.length > 0 && (
        <div>
          <div style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--hp-green)', marginBottom: 5 }}>Cleared because</div>
          {okRows.map(([r, n]) => (
            <div key={r} style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', display: 'flex', gap: 8 }}>
              <span style={{ fontWeight: 800, fontFamily: 'var(--font-mono)' }}>{n}×</span><span>{r}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--hp-dim)' }}>
        The {res.cases.length} case{res.cases.length === 1 ? '' : 's'} behind this
      </div>
      {res.cases.length === 0 && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>No cases in this window — a clean record.</div>}
      {res.cases.map((c) => {
        const f = faultChip(c);
        return (
          <div key={c.id} style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 5, border: '1px solid rgba(239,68,68,0.5)', color: '#ef4444' }}>{CC_LABELS[c.category] || 'Enquiry'}</span>
              <span style={{ fontSize: '0.7rem', fontWeight: 800, color: f.color, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{f.label}</span>
              <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{c.date}</span>
            </div>
            <div style={{ fontSize: '0.84rem', fontWeight: 700, marginTop: 5 }}>{c.case_number ? `Case ${c.case_number}` : 'Customer case'}</div>
            {c.address && <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 2 }}>{c.address}</div>}
            {c.fault_reason && <div style={{ fontSize: '0.8rem', color: c.fault === 'yes' ? '#ef4444' : 'var(--hp-dim)', marginTop: 4 }}>{c.fault_reason}</div>}
            {c.driver_note && <div style={{ fontSize: '0.8rem', marginTop: 5, whiteSpace: 'pre-wrap' }}>{c.driver_note}</div>}
            <div style={{ marginTop: 8 }}>
              {peekId === c.id
                ? <TeamCasePeek caseId={c.id} driver={login} onHide={() => onPeek('')} />
                : (
                  <button onClick={() => onPeek(c.id)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '6px 10px', color: 'var(--hp-text)', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }}>
                    <Smartphone size={12} style={{ color: 'var(--hp-gold)' }} /> See what {login} saw
                  </button>
                )}
            </div>
          </div>
        );
      })}
      <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{res.from} → {res.to}</div>
    </div>
  );
}

// ── Standing orders ──────────────────────────────────────────────────────────
// Parcels addressed to one place that a customer has arranged to be delivered to another
// (the office's Standing Orders page). The banner puts an unread one on the shift screen in
// full; the tab keeps every order in force on their phone for the day they are standing at
// the wrong door with a parcel in their hand.
function useStandingOrders(onInfo) {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    soApi('list', {}).then((r) => setOrders((r && r.success && r.orders) || [])).catch(() => setOrders([]));
  }, []);
  useEffect(() => {
    if (orders && onInfo) onInfo({ total: orders.length, pending: orders.filter((o) => !o.acked_at).length });
  }, [orders, onInfo]);
  const onAcked = (id, at) => setOrders((l) => (l || []).map((o) => (o.id === id ? { ...o, acked_at: at } : o)));
  return [orders, onAcked];
}

// An Authority to Leave a driver confirms every day is back here each morning until they
// tap Got it — before they load and head out (Paul, 20 Oct 2026).
function StandingOrdersBanner({ onInfo }) {
  const [orders, onAcked] = useStandingOrders(onInfo);
  const pending = (orders || []).filter((o) => !o.acked_at);
  if (!pending.length) return null;
  const atl = pending.filter((o) => o.kind === 'atl').length;
  const care = pending.filter((o) => o.kind === 'care').length;
  const head = atl === pending.length
    ? (atl === 1 ? 'Authority to Leave on your round today' : `${atl} Authority to Leave addresses today`)
    : care === pending.length
      ? (care === 1 ? 'Take special care on your round today' : `${care} addresses to take special care at today`)
      : pending.length === 1 ? 'A standing order for your round' : `${pending.length} standing orders for your round`;
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#ef4444', marginBottom: 7, display: 'flex', alignItems: 'center', gap: 5 }}>
        <AlertTriangle size={13} /> {head}
      </div>
      {pending.map((o) => <StandingOrderCard key={o.id} o={o} onAcked={onAcked} />)}
    </div>
  );
}

function StandingOrdersPanel({ onInfo, leader }) {
  const [orders, onAcked] = useStandingOrders(onInfo);
  if (!orders) return <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center' }}>Loading…</div>;
  return (
    <div>
      <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: 12, lineHeight: 1.5 }}>
        Customer arrangements on your round. <span style={{ color: '#22c55e', fontWeight: 700 }}>Authority to Leave</span>: leave every parcel in the receptacle — confirm it each morning. <span style={{ color: '#ef4444', fontWeight: 700 }}>Take special care</span>: a customer we have let down — deliver by the book, confirm each morning. Deliver elsewhere: if a parcel is addressed to a <span style={{ color: '#ef4444', fontWeight: 700 }}>red</span> address, take it to the <span style={{ color: '#22c55e', fontWeight: 700 }}>green</span> one.
      </div>
      {!orders.length && <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center', fontSize: '0.88rem' }}>No standing orders on your round.</div>}
      {orders.filter((o) => !o.acked_at).map((o) => <StandingOrderCard key={o.id} o={o} onAcked={onAcked} />)}
      {orders.filter((o) => o.acked_at).map((o) => <StandingOrderCard key={o.id} o={o} onAcked={onAcked} />)}
      {leader && (
        <div style={{ marginTop: 22 }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--hp-gold)', marginBottom: 8 }}>Team leader register — every address</div>
          <LeaderStandingRegister />
        </div>
      )}
    </div>
  );
}

// ── Team leader register ─────────────────────────────────────────────────────
// Every standing arrangement in force, under the three headings the office uses: customers
// with a disability, Authority to Leave, deliver elsewhere. All of them, not only the
// leader's team — a leader covering a round looks up whatever address is in their hand.
// Their own team's come first and say so; each shows whether its drivers have confirmed it
// (today, for an ATL).
const LEADER_SECTIONS = [
  { key: 'disability', label: 'Customers with a disability', color: '#60a5fa', Icon: Accessibility },
  { key: 'atl', label: 'Authority to Leave', color: '#22c55e', Icon: PackageCheck },
  { key: 'care', label: 'Take special care', color: '#ef4444', Icon: ShieldAlert },
  { key: 'redirect', label: 'Deliver elsewhere', color: 'var(--hp-gold)', Icon: Signpost },
];
function LeaderStandingRegister() {
  const [orders, setOrders] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => { soApi('team', {}).then((r) => setOrders((r && r.success && r.orders) || [])).catch(() => setOrders([])); }, []);
  if (!orders) return <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center' }}>Loading…</div>;
  const n = q.trim().toLowerCase();
  const shown = orders.filter((o) => !n || [o.customer, o.from.address, o.from.name, o.to.address, o.to.name, o.receptacle, o.case_number, ...o.drivers.map((d) => d.login)].join(' ').toLowerCase().includes(n));
  const sectionOf = (o) => (o.kind === 'atl' ? (o.disability ? 'disability' : 'atl') : o.kind === 'care' ? 'care' : 'redirect');
  const short = (a) => (a || '').split(',')[0].trim();
  return (
    <>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search an address, customer or driver"
        style={{ width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-surface)', color: 'var(--hp-text)', marginBottom: 12, fontSize: '0.9rem' }} />
      {LEADER_SECTIONS.map(({ key, label, color, Icon }) => {
        const list = shown.filter((o) => sectionOf(o) === key);
        return (
          <div key={key} style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color, borderBottom: `2px solid ${color}`, paddingBottom: 4, marginBottom: 8 }}>
              <Icon size={14} /> {label} <span style={{ color: 'var(--hp-dim)', fontWeight: 600 }}>{list.length}</span>
            </div>
            {!list.length && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', padding: '4px 0 8px' }}>None.</div>}
            {list.map((o) => (
              <div key={o.id} style={{ background: 'var(--hp-surface)', border: `1px solid ${o.my_team ? color : 'var(--hp-border)'}`, borderRadius: 'var(--radius)', padding: 12, marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  {o.my_team && <span style={{ fontSize: '0.64rem', fontWeight: 800, textTransform: 'uppercase', padding: '1px 6px', borderRadius: 5, border: '1px solid #f59e0b', color: '#f59e0b' }}>Your team</span>}
                  {o.customer && <span style={{ fontSize: '0.9rem', fontWeight: 800 }}>{o.customer}</span>}
                  {o.case_number && <span style={{ fontSize: '0.7rem', color: 'var(--hp-dim)' }}>case {o.case_number}</span>}
                </div>
                {o.kind === 'care' ? (
                  <div style={{ fontSize: '0.92rem', fontWeight: 700, marginTop: 5, color: '#ef4444' }}>{o.from.name ? `${o.from.name}, ` : ''}{o.from.address}</div>
                ) : o.kind === 'atl' ? (
                  <>
                    <div style={{ fontSize: '0.92rem', fontWeight: 700, marginTop: 5, color: '#22c55e' }}>{o.from.name ? `${o.from.name}, ` : ''}{o.from.address}</div>
                    <div style={{ fontSize: '0.84rem', marginTop: 3 }}>Leave every parcel in {o.receptacle || 'the receptacle'}{o.include_signature ? ' — signature items included, never card them.' : '.'}</div>
                  </>
                ) : (
                  <div style={{ fontSize: '0.88rem', marginTop: 5 }}>
                    <span style={{ color: '#ef4444', fontWeight: 700, textDecoration: 'line-through' }}>{o.from.name ? `${o.from.name}, ` : ''}{short(o.from.address)}</span>
                    <span style={{ color: 'var(--hp-gold)', fontWeight: 800 }}> → </span>
                    <span style={{ color: '#22c55e', fontWeight: 700 }}>{o.to.name ? `${o.to.name}, ` : ''}{short(o.to.address)}</span>
                  </div>
                )}
                {o.instruction && <div style={{ fontSize: '0.82rem', marginTop: 5, whiteSpace: 'pre-wrap' }}>{o.instruction}</div>}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 7 }}>
                  {o.drivers.map((d) => (
                    <span key={d.login} style={{ fontSize: '0.72rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: d.acked_at ? '#34d399' : '#ef4444' }}>
                      {d.first ? `${d.first} ` : ''}{d.login} {d.acked_at ? (d.daily ? '✓ today' : '✓') : (d.daily ? '— not today' : '— not read')}
                    </span>
                  ))}
                  {!o.drivers.length && <span style={{ fontSize: '0.72rem', color: '#f59e0b' }}>No driver named</span>}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </>
  );
}

// The shift-start gate: red until every standing request has been acknowledged. It never
// blocks the shift (Paul chose must-acknowledge, non-blocking) — a 5am gate on a busy
// morning has bitten us before — but it does not go away on its own either.
function DeliveryRequestsBanner({ onCountChange }) {
  const [reqs, setReqs] = useState(null);
  const load = useCallback(() => {
    ccApi('requests', {}).then((r) => { if (r && r.success) setReqs(r.requests || []); }).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);
  const pending = (reqs || []).filter((r) => !r.acked_at);
  useEffect(() => { onCountChange && onCountChange(pending.length); }, [pending.length, onCountChange]);
  if (!pending.length) return null;
  const onAcked = (id, at) => setReqs((l) => (l || []).map((r) => (r.id === id ? { ...r, acked_at: at } : r)));
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#ef4444', marginBottom: 7, display: 'flex', alignItems: 'center', gap: 5 }}>
        <AlertTriangle size={13} /> {pending.length === 1 ? 'A customer has asked for something' : `${pending.length} customer requests to read`}
      </div>
      {pending.map((r) => <DeliveryRequestCard key={r.id} r={r} onAcked={onAcked} />)}
    </div>
  );
}

// Retention: everything they have been asked to do, acknowledged or not, kept and searchable.
// This is the half that makes it worth acknowledging — an instruction you cannot look up
// again is one you follow once.
function DeliveryRequestsPanel() {
  const [reqs, setReqs] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => { ccApi('requests', {}).then((r) => { if (r && r.success) setReqs(r.requests || []); }).catch(() => setReqs([])); }, []);
  const onAcked = (id, at) => setReqs((l) => (l || []).map((r) => (r.id === id ? { ...r, acked_at: at } : r)));
  if (!reqs) return <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center' }}>Loading…</div>;
  const n = q.trim().toLowerCase();
  const shown = n ? reqs.filter((r) => `${r.address} ${r.request}`.toLowerCase().includes(n)) : reqs;
  const pending = shown.filter((r) => !r.acked_at);
  const done = shown.filter((r) => r.acked_at);
  return (
    <div>
      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 10 }}>
        What customers on your round have asked us to do at their address. These stay here — check one before you deliver if you are not sure.
      </div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a street or a word…"
        style={{ width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '10px 12px', color: 'var(--hp-text)', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: 12 }} />
      {!shown.length && (
        <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center', fontSize: '0.88rem' }}>
          {reqs.length ? 'Nothing matches that.' : 'No customer requests on your round.'}
        </div>
      )}
      {pending.map((r) => <DeliveryRequestCard key={r.id} r={r} onAcked={onAcked} />)}
      {done.length > 0 && (
        <>
          {pending.length > 0 && <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--hp-dim)', margin: '14px 0 8px' }}>Confirmed</div>}
          {done.map((r) => <DeliveryRequestCard key={r.id} r={r} onAcked={onAcked} />)}
        </>
      )}
    </div>
  );
}

// ── The team's case rate, on the leader's phone ──────────────────────────────
// The same arithmetic as the office's Stats page (_shared/case-stats), scoped by the server
// to this leader's own drivers. It was always available to them in the admin app; a number
// you have to open a laptop for is a number you check monthly, and this is the one they are
// judged on. Per-driver rows sit under the team total because "the team is at 1.4" is a
// conversation with nobody — the rows say who to have it with.
function TeamStatsPanel() {
  const [days, setDays] = useState(90);
  const [drill, setDrill] = useState('');   // the driver row opened out
  const [peek, setPeek] = useState('');     // a case inside it, shown as the driver's screen
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    ccApi('teamStats', { days })
      .then((r) => { if (alive && r && r.success) setRes(r); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  if (loading) return <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center' }}>Loading…</div>;
  if (!res || !res.stats) return <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center', fontSize: '0.88rem' }}>Couldn’t load your team’s stats — pull down and try again.</div>;
  const s = res.stats;
  const t = s.totals;
  // Same thresholds as the driver's own stats and the office page: at or under the
  // 1-per-1000 target is green, up to twice it amber, beyond that red.
  const color = (v) => (v == null ? 'var(--hp-dim)' : v <= 1 ? 'var(--hp-green)' : v <= 2 ? '#f59e0b' : '#ef4444');
  const reasons = (obj) => Object.entries(obj || {}).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([r, n]) => `${r} (${n})`).join(', ');
  const metric = (label, val) => (
    <div style={{ flex: '1 1 140px', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14 }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--hp-dim)', marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: '1.8rem', fontWeight: 800, color: color(val) }}>{val == null ? '—' : val}</span>
        <span style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>/1000 · target ≤1</span>
      </div>
    </div>
  );
  // Drivers who delivered nothing in the window have no rate to stand on — they sit at the
  // bottom rather than reading as a clean record.
  const rows = (s.rows || []).filter((r) => r.parcels > 0 || r.cases > 0);

  return (
    <div>
      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 10 }}>
        Your team&rsquo;s customer-case rate — {(res.areas || []).join(' · ') || 'your area'}. The same numbers the office sees.
      </div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[[30, '30d'], [90, '90d'], [180, '180d'], [365, '12m']].map(([d, l]) => (
          <button key={d} onClick={() => setDays(d)} style={{ padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem', background: days === d ? 'var(--hp-gold)' : 'var(--hp-surface)', color: days === d ? 'var(--hp-black)' : 'var(--hp-dim)' }}>{l}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {metric('Team cases / 1000', t.casesPer1000)}
        {metric('At-fault / 1000', t.atFaultPer1000)}
      </div>
      <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14, fontSize: '0.88rem', lineHeight: 1.6, marginBottom: 12 }}>
        {t.cases === 0 ? (
          <div>No customer cases on your team in this window — {t.parcels.toLocaleString()} parcels delivered. 🎉</div>
        ) : (
          <>
            <div>Your {t.drivers} driver{t.drivers !== 1 ? 's' : ''} took <strong>{t.cases}</strong> case{t.cases !== 1 ? 's' : ''} over <strong>{t.parcels.toLocaleString()}</strong> parcels.</div>
            <div style={{ marginTop: 4 }}><strong>{t.notAtFault}</strong> with no error to the driver.</div>
            {t.atFault > 0 && <div style={{ marginTop: 2, color: '#ef4444' }}><strong>{t.atFault}</strong> at fault.</div>}
            {/* Unclassified cases are still in triage — they are NOT a clean record yet, and
                a leader reading the total as settled would be reading it early. */}
            {t.unclassified > 0 && <div style={{ marginTop: 2, color: '#f59e0b' }}><strong>{t.unclassified}</strong> still with the office for a fault call.</div>}
          </>
        )}
        <div style={{ marginTop: 8, fontSize: '0.74rem', color: 'var(--hp-dim)' }}>{s.from} → {s.to} · target is 1 case per 1000 parcels.</div>
      </div>

      <div style={{ fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--hp-dim)', marginBottom: 8 }}>Driver by driver</div>
      {rows.length === 0 ? (
        <div style={{ color: 'var(--hp-dim)', fontSize: '0.85rem' }}>No parcels recorded for your team in this window.</div>
      ) : rows.map((r) => {
        const openRow = drill === r.login;
        return (
          <div key={r.login} style={{ background: 'var(--hp-surface)', border: `1px solid ${openRow ? 'var(--hp-gold)' : 'var(--hp-border)'}`, borderRadius: 'var(--radius)', padding: '10px 12px', marginBottom: 6 }}>
            {/* The whole row opens: a leader reads a rate and immediately asks "which cases?" */}
            <div onClick={() => { setDrill(openRow ? '' : r.login); setPeek(''); }} style={{ cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 800, fontFamily: 'var(--font-mono)' }}>{r.login}</span>
                {r.name && <span style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>}
                <span style={{ marginLeft: 'auto', fontSize: '1rem', fontWeight: 800, color: color(r.casesPer1000), fontFamily: 'var(--font-mono)' }}>{r.casesPer1000 == null ? '—' : r.casesPer1000}</span>
                <span style={{ color: 'var(--hp-dim)', display: 'flex' }}>{openRow ? <ChevronUp size={15} /> : <ChevronDown size={15} />}</span>
              </div>
              <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 3 }}>
                {r.cases} case{r.cases !== 1 ? 's' : ''} · {r.parcels.toLocaleString()} parcels
                {r.atFault > 0 ? ` · ${r.atFault} at fault` : ''}
                {r.unclassified > 0 ? ` · ${r.unclassified} awaiting a fault call` : ''}
              </div>
              {!openRow && r.atFault > 0 && reasons(r.faultReasons) && (
                <div style={{ fontSize: '0.74rem', color: '#ef4444', marginTop: 3 }}>{reasons(r.faultReasons)}</div>
              )}
            </div>
            {openRow && <TeamDriverDrill login={r.login} days={days} peekId={peek} onPeek={setPeek} />}
          </div>
        );
      })}
    </div>
  );
}

// The leader's copy: their drivers' standing requests, and whether each driver has actually
// said they have it. They cover the round when the driver is off, so an instruction only the
// absent driver knows about is the same failure one step along.
function TeamRequestsPanel() {
  const [reqs, setReqs] = useState(null);
  useEffect(() => { ccApi('teamRequests', {}).then((r) => setReqs((r && r.success && r.requests) || [])).catch(() => setReqs([])); }, []);
  if (!reqs) return <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center' }}>Loading…</div>;
  if (!reqs.length) return <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center', fontSize: '0.88rem' }}>No standing customer requests on your team.</div>;
  return (
    <>
      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 10 }}>
        What customers have asked for at addresses on your team&rsquo;s rounds, and whether your driver has confirmed they have it.
      </div>
      {reqs.map((r) => (
        <div key={r.id} style={{ background: 'var(--hp-surface)', border: `1px solid ${r.acked_at ? 'var(--hp-border)' : '#ef4444'}`, borderRadius: 'var(--radius)', padding: 12, marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 800, fontFamily: 'var(--font-mono)' }}>{r.driver_login}</span>
            {r.acked_at
              ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: '0.7rem', color: '#34d399' }}><CheckCircle size={12} /> confirmed {new Date(r.acked_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}</span>
              : <span style={{ fontSize: '0.7rem', fontWeight: 800, color: '#ef4444', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Not read yet</span>}
          </div>
          <div style={{ fontSize: '0.88rem', fontWeight: 700, marginTop: 6 }}>{r.address}</div>
          <div style={{ fontSize: '0.85rem', marginTop: 4, whiteSpace: 'pre-wrap' }}>{r.request}</div>
          {(r.photos || []).length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 7 }}>
              {r.photos.map((src, i) => <a key={i} href={src} target="_blank" rel="noreferrer"><img src={src} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hp-border)' }} /></a>)}
            </div>
          )}
          {r.case_number && <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 6 }}>From case {r.case_number}</div>}
        </div>
      ))}
    </>
  );
}

// The leader's My Team tab: their drivers' open cases, and how the team is tracking.
function TeamPanel() {
  const [view, setView] = useState('cases');
  const pill = (active) => ({ flex: 1, padding: '7px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem', background: active ? 'var(--hp-gold)' : 'var(--hp-surface)', color: active ? 'var(--hp-black)' : 'var(--hp-dim)' });
  return (
    <>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <button style={pill(view === 'cases')} onClick={() => setView('cases')}>Open cases</button>
        <button style={pill(view === 'stats')} onClick={() => setView('stats')}>Team stats</button>
        <button style={pill(view === 'requests')} onClick={() => setView('requests')}>Requests</button>
        <button style={pill(view === 'standing')} onClick={() => setView('standing')}>ATL &amp; orders</button>
      </div>
      {view === 'cases' ? <TeamCasesPanel /> : view === 'requests' ? <TeamRequestsPanel /> : view === 'standing' ? <LeaderStandingRegister /> : <TeamStatsPanel />}
    </>
  );
}

function CustomerServiceTab({ driver, onCount, initialSub, msCount = 0, hasMonitors = false, onMonitors }) {
  const [sub, setSub] = useState(initialSub || 'cases');  // 'cases' | 'team' | 'requests' | 'stats' | 'issues' | 'mail'
  const [scope, setScope] = useState('current');     // 'current' | 'resolved'
  const [q, setQ] = useState('');
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [team, setTeam] = useState(null);            // {leader, teamCount} — team leaders only

  // Is this login a team leader? Cheap, and it decides whether the My Team pill exists.
  useEffect(() => {
    let alive = true;
    ccApi('count', {}).then(r => { if (alive && r && r.success && r.leader) setTeam({ leader: true, teamCount: r.teamCount || 0 }); }).catch(() => {});
    return () => { alive = false; };
  }, [driver]);

  const load = useCallback(() => {
    setLoading(true);
    ccApi('list', { scope }).then(r => {
      if (r && r.success) {
        setCases(r.cases || []);
        if (scope === 'current' && onCount) onCount((r.cases || []).length);
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, [scope, onCount]);
  useEffect(() => { load(); }, [load, driver]);

  const filtered = useMemo(() => {
    const n = q.trim().toLowerCase();
    if (!n) return cases;
    return cases.filter(c => `${c.case_number} ${c.address} ${c.summary}`.toLowerCase().includes(n));
  }, [cases, q]);

  const pill = (active) => ({ flex: 1, padding: '8px 10px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', background: active ? 'var(--hp-gold)' : 'var(--hp-surface)', color: active ? 'var(--hp-black)' : 'var(--hp-dim)' });

  return (
    <div style={{ maxWidth: 640, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--hp-text)', letterSpacing: '0.03em', marginBottom: 14 }}>
        <Headphones size={20} style={{ color: 'var(--hp-gold)' }} /> CUSTOMER SERVICE
      </div>

      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        <button style={pill(sub === 'cases')} onClick={() => setSub('cases')}>Cases</button>
        {team && team.leader && (
          <button style={pill(sub === 'team')} onClick={() => setSub('team')}>My Team{team.teamCount ? ` (${team.teamCount})` : ''}</button>
        )}
        <button style={pill(sub === 'requests')} onClick={() => setSub('requests')}>Requests</button>
        <button style={pill(sub === 'stats')} onClick={() => setSub('stats')}>My Stats</button>
        <button style={pill(sub === 'issues')} onClick={() => setSub('issues')}>Route Issues</button>
      </div>
      {(hasMonitors || sub === 'mail') && (
        <button onClick={() => setSub('mail')}
          style={{ ...pill(sub === 'mail'), width: '100%', marginTop: -6, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
          <AlertTriangle size={14} /> Mail Monitor
          {msCount > 0 && <span style={{ background: 'var(--hp-red)', color: '#fff', borderRadius: 10, padding: '0 7px', fontSize: '0.72rem', fontWeight: 800 }}>{msCount}</span>}
        </button>
      )}

      {sub === 'cases' ? (
        <>
          <PushAlertsCard app="driver" />
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            <button style={pill(scope === 'current')} onClick={() => setScope('current')}>Current</button>
            <button style={pill(scope === 'resolved')} onClick={() => setScope('resolved')}>Resolved</button>
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search case number or address…"
            style={{ width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '10px 12px', color: 'var(--hp-text)', fontSize: '0.9rem', boxSizing: 'border-box', marginBottom: 12 }} />
          {loading ? (
            <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center' }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center', fontSize: '0.88rem' }}>
              {scope === 'current' ? 'No open cases — you’re all clear. 🎉' : 'No resolved cases yet.'}
            </div>
          ) : (
            filtered.map(c => (c.isAssist
              ? <AssistCaseCard key={c.id} c={c} onChanged={load} />
              : <CustomerCaseCard key={c.id} c={c} onChanged={load} startOpen={false} />))
          )}
        </>
      ) : sub === 'team' ? (
        <TeamPanel />
      ) : sub === 'requests' ? (
        <DeliveryRequestsPanel />
      ) : sub === 'stats' ? (
        <MyStatsPanel driver={driver} />
      ) : sub === 'mail' ? (
        <MailMonitorTab onChanged={onMonitors} />
      ) : (
        <RouteIssuesPanel driver={driver} />
      )}
    </div>
  );
}

// Customer Service → Mail Monitor. Where a driver keeps the daily record for every
// stopper/monitor on their route: today's answer (if the Onboard gate hasn't taken it),
// and any item that turns up later in the day — the Onboard question is asked once, but
// mail keeps arriving. A covering team leader gets the same card to log a catch.
function MailMonitorTab({ onChanged }) {
  const [stoppers, setStoppers] = useState(null);
  const load = useCallback(() => {
    msApi('list', {})
      .then(r => { setStoppers(r && r.success ? (r.stoppers || []) : []); onChanged && onChanged(r && r.success ? r.stoppers || [] : null); })
      .catch(() => setStoppers([]));
  }, [onChanged]);
  useEffect(() => { load(); }, [load]);

  if (stoppers === null) return <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center' }}>Loading…</div>;
  if (!stoppers.length) {
    return <div style={{ color: 'var(--hp-dim)', padding: 20, textAlign: 'center', fontSize: '0.88rem' }}>No mail monitors or stoppers on your route right now.</div>;
  }
  return (
    <div>
      {stoppers.map(s => <MonitorCard key={s.id} s={s} onSaved={load} />)}
    </div>
  );
}

function MonitorCard({ s, onSaved }) {
  const leader = s.role === 'leader';
  const [mode, setMode] = useState(null);            // null | 'record' (Yes / another item)
  const [photos, setPhotos] = useState([]);
  const [barcodes, setBarcodes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const accent = leader ? '#f59e0b' : '#ef4444';
  const what = msWhat(s);

  const save = async (received) => {
    if (received && photos.length === 0) { setError('Please add at least one photo of the item and its barcode.'); return; }
    setBusy(true); setError('');
    try {
      const payloadPhotos = photos.map(d => ({ base64: (d.split(',')[1] || ''), contentType: 'image/jpeg' }));
      const r = await msApi('submitCheck', { stopper_id: s.id, received, barcodes, photos: payloadPhotos });
      if (!r.success) throw new Error(r.error || 'Failed to save');
      setMode(null); setPhotos([]); setBarcodes('');
      setMsg(received ? 'Saved — the office has been told.' : 'Saved — nothing arrived today.');
      onSaved && onSaved();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const fmtDay = (iso) => new Date(iso + 'T00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
  const btn = (primary) => ({ flex: 1, padding: '12px', borderRadius: 'var(--radius)', cursor: busy ? 'default' : 'pointer', fontWeight: 800, fontSize: '0.9rem', opacity: busy ? 0.6 : 1,
    border: primary ? 'none' : '1px solid var(--hp-border)', background: primary ? 'var(--hp-gold)' : 'var(--hp-black)', color: primary ? 'var(--hp-black)' : 'var(--hp-text)' });

  return (
    <div style={{ background: 'var(--hp-surface)', border: `1px solid ${accent}80`, borderRadius: 'var(--radius)', padding: '14px 15px', marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: accent, fontSize: '0.9rem' }}>
        <AlertTriangle size={16} /> {msKind(s)}{leader ? ' — team area' : ''}
      </div>
      <div style={{ fontSize: '1rem', fontWeight: 700, marginTop: 6 }}>{s.address}</div>
      <MsAddressee s={s} />
      {leader && <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 4 }}>Regular driver: <strong>{s.assignedDriver}</strong></div>}
      {articleLine(s) && <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 4 }}>Watching for: {articleLine(s)}</div>}
      {s.to_date && <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 2 }}>Until {fmtDay(s.to_date)}</div>}
      <MsWarnings s={s} />

      {/* Today */}
      <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--hp-border)' }}>
        <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 8 }}>Today</div>
        {s.answeredToday ? (
          <div style={{ fontSize: '0.88rem', fontWeight: 700, color: s.received ? accent : 'var(--hp-green)' }}>
            {s.received ? `✓ ${what === 'mail' ? 'Mail' : 'Parcel'} recorded — ${s.photoCount} photo${s.photoCount === 1 ? '' : 's'}` : '✓ Nothing arrived'}
            {s.barcodes && <div style={{ fontSize: '0.76rem', fontWeight: 400, color: 'var(--hp-dim)', marginTop: 2 }}>Barcodes: {s.barcodes}</div>}
          </div>
        ) : leader ? (
          <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>If {s.assignedDriver} is away, record anything that arrives for this address.</div>
        ) : (
          <div style={{ fontSize: '0.88rem', fontWeight: 600 }}>Did any {what} arrive for this address today?</div>
        )}
        {msg && <div style={{ fontSize: '0.8rem', color: 'var(--hp-green)', marginTop: 6 }}>{msg}</div>}

        {mode !== 'record' && (
          <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
            <button style={btn(true)} disabled={busy} onClick={() => { setMode('record'); setMsg(''); setError(''); }}>
              {s.answeredToday || leader ? 'Record an item' : 'Yes — record it'}
            </button>
            {!s.answeredToday && !leader && (
              <button style={btn(false)} disabled={busy} onClick={() => save(false)}>{busy ? 'Saving…' : 'No — nothing today'}</button>
            )}
          </div>
        )}

        {mode === 'record' && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', marginBottom: 8, lineHeight: 1.5 }}>
              Photograph the item so the name, sender and barcode can be read. Do <strong>not</strong> scan it and do <strong>not</strong> write on it{s.request_type === 'stopper' ? ' — hold it and tell the office' : ''}.
            </div>
            {photos.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {photos.map((p, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img src={p} alt={`item ${i + 1}`} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hp-border)' }} />
                    <button onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))}
                      style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 'none', background: '#ef4444', color: '#fff', fontSize: 12, cursor: 'pointer', lineHeight: 1 }}>×</button>
                  </div>
                ))}
              </div>
            )}
            <CameraCapture nativeFirst guide="document" maxEdge={2000} quality={0.85}
              value={null} onCapture={d => { setPhotos(ps => [...ps, d]); setError(''); }} />
            <input value={barcodes} onChange={e => setBarcodes(e.target.value)}
              placeholder="Barcode number(s) — optional"
              style={{ width: '100%', marginTop: 10, boxSizing: 'border-box', border: '1px solid var(--hp-border)', borderRadius: 10, padding: 12, background: 'var(--hp-black)', color: 'var(--hp-text)', fontSize: '0.9rem' }} />
            {error && <div style={{ marginTop: 10, color: '#f87171', fontSize: '0.82rem' }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              <button style={{ ...btn(false), flex: '0 0 auto', padding: '12px 16px' }} disabled={busy} onClick={() => { setMode(null); setPhotos([]); setError(''); }}>Cancel</button>
              <button style={btn(true)} disabled={busy} onClick={() => save(true)}>{busy ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        )}
      </div>

      {/* The record so far */}
      {(s.history || []).length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--hp-border)' }}>
          <div style={{ fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--hp-dim)', marginBottom: 8 }}>Your record</div>
          {s.history.map(h => (
            <div key={h.date} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', padding: '4px 0' }}>
              <span style={{ color: 'var(--hp-dim)' }}>{fmtDay(h.date)}</span>
              <span style={{ fontWeight: 700, color: h.received ? accent : 'var(--hp-green)' }}>
                {h.received ? `Received · ${h.photoCount} photo${h.photoCount === 1 ? '' : 's'}` : 'Nothing arrived'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Pre-Onboard gate: the driver must answer, per assigned stopper, whether any parcels
// arrived for that address today; a Yes needs a photo of the parcel + barcode. Mirrors
// the van-check gate — Onboard stays locked until every stopper is answered.
function MailStopperGate({ stoppers, onAllDone }) {
  const [idx, setIdx] = useState(0);
  const [received, setReceived] = useState(null); // true | false | null
  const [photos, setPhotos] = useState([]); // array of dataUrls — parcel + barcode may be separate shots
  const [barcodes, setBarcodes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const s = stoppers[idx];
  const card = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 14 };

  const advance = () => {
    if (idx + 1 < stoppers.length) { setIdx(idx + 1); setReceived(null); setPhotos([]); setBarcodes(''); setError(''); }
    else onAllDone();
  };
  const submit = async () => {
    if (received === null) { setError('Please choose Yes or No.'); return; }
    if (received && photos.length === 0) { setError('Please add at least one photo of the parcel and its barcode.'); return; }
    setBusy(true); setError('');
    try {
      const payloadPhotos = photos.map(d => ({ base64: (d.split(',')[1] || ''), contentType: 'image/jpeg' }));
      const r = await msApi('submitCheck', { stopper_id: s.id, received, barcodes, photos: payloadPhotos });
      if (!r.success) throw new Error(r.error || 'Failed to save');
      advance();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  if (!s) return null;

  const choiceBtn = (on, label) => (
    <button onClick={() => { setReceived(on); setError(''); }}
      style={{ flex: 1, padding: '13px', borderRadius: 'var(--radius)', cursor: 'pointer', fontWeight: 800, fontSize: '0.95rem',
        border: `1px solid ${received === on ? 'var(--hp-gold)' : 'var(--hp-border)'}`,
        background: received === on ? 'var(--hp-gold)' : 'var(--hp-black)',
        color: received === on ? 'var(--hp-black)' : 'var(--hp-text)' }}>
      {label}
    </button>
  );

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: '#ef4444', fontSize: '0.95rem' }}>
          <AlertTriangle size={17} /> {msKind(s)} — before you onboard
        </div>
        <div style={{ fontSize: '1rem', fontWeight: 700, marginTop: 8 }}>{s.address}</div>
        <MsAddressee s={s} />
        {articleLine(s) && <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 4 }}>Watching for: {articleLine(s)}</div>}
        <MsWarnings s={s} />
        <div style={{ fontSize: '0.9rem', marginTop: 16, marginBottom: 10, fontWeight: 600 }}>
          Did you receive any {msWhat(s)} for this address today?
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {choiceBtn(true, 'Yes')}
          {choiceBtn(false, 'No')}
        </div>

        {received === true && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', marginBottom: 8, lineHeight: 1.5 }}>
              Photograph the parcel and its barcode. Do <strong>not</strong> scan it and do <strong>not</strong> write on it — just hold it and tell the office.
              You can add more than one photo if the barcode won't fit in the same shot.
            </div>
            {photos.length > 0 && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                {photos.map((p, i) => (
                  <div key={i} style={{ position: 'relative' }}>
                    <img src={p} alt={`parcel ${i + 1}`} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--hp-border)' }} />
                    <button onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))}
                      style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', border: 'none', background: '#ef4444', color: '#fff', fontSize: 12, cursor: 'pointer', lineHeight: 1 }}>×</button>
                  </div>
                ))}
              </div>
            )}
            <CameraCapture nativeFirst guide="document" maxEdge={2000} quality={0.85}
              value={null} onCapture={d => { setPhotos(ps => [...ps, d]); setError(''); }} />
            <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginTop: 6 }}>
              {photos.length === 0 ? 'Add a photo — parcel first, then the barcode if separate.' : `${photos.length} photo${photos.length > 1 ? 's' : ''} added · tap above to add another`}
            </div>
            <input value={barcodes} onChange={e => setBarcodes(e.target.value)}
              placeholder="Barcode number(s) — optional"
              style={{ width: '100%', marginTop: 10, boxSizing: 'border-box', border: '1px solid var(--hp-border)', borderRadius: 10, padding: 12, background: 'var(--hp-black)', color: 'var(--hp-text)', fontSize: '0.9rem' }} />
          </div>
        )}

        {error && <div style={{ marginTop: 12, color: '#f87171', fontSize: '0.82rem' }}>{error}</div>}

        <button onClick={submit} disabled={busy}
          style={{ width: '100%', marginTop: 16, padding: '14px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.95rem', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>
          {busy ? 'Saving…' : received === false ? 'Confirm — no parcels' : 'Submit and continue'}
        </button>
        {stoppers.length > 1 && (
          <div style={{ textAlign: 'center', fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 10 }}>Stopper {idx + 1} of {stoppers.length}</div>
        )}
      </div>
    </div>
  );
}

function OnboardScanTab({ driver, onGoInspection, exempt }) {
  const [capture, setCapture] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [recent, setRecent] = useState(null);
  // FIRST gate of all: Australia Post's security declaration. It sits ahead of even the
  // safety brief because it is not a briefing — it is a signed instrument, and four of
  // its clauses describe criminal offences. A driver who has not signed it should not be
  // carrying AP's mail today. Annual, so on all but one morning a year this is one cheap
  // call that returns { blocked:false }. Fails OPEN, like every other gate here.
  const [secDecl, setSecDecl] = useState(null);
  // SECOND gate: a safety brief the office has launched must be read before anything else
  // this morning. Deliberately ahead of the van check — it is the morning briefing, and
  // reading it with fresh eyes is the whole reason it moved off the end-of-shift screen.
  // null = still checking; { blocked:false } = clear. Fails OPEN on any error.
  const [brief, setBrief] = useState(null);
  // Gate: the Onboard Scan is locked until this morning's van check is done.
  // Fail OPEN on a backend error so a service hiccup never blocks the actual run.
  const [inspOk, setInspOk] = useState(null);
  // Second gate: unanswered mail stoppers for today. null=loading, []=none/done.
  // Fail OPEN (→ []) on error/timeout so it can never strand the Onboard Scan.
  const [msPending, setMsPending] = useState(null);

  const card = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 14 };

  const loadBrief = useCallback(async () => {
    const d = await checkBriefGate(getSafetyLang());
    setBrief(d && d.success ? d : { blocked: false });
  }, []);
  useEffect(() => { loadBrief(); }, [loadBrief]);

  const loadSecDecl = useCallback(async () => {
    const d = await checkSecurityGate(getSafetyLang());
    setSecDecl(d && d.success ? d : { blocked: false });
  }, []);
  useEffect(() => { loadSecDecl(); }, [loadSecDecl]);

  useEffect(() => {
    if (exempt) { setInspOk(true); return undefined; }   // walker — no van to check
    let alive = true;
    // Fail OPEN: a status endpoint that can't answer must never hold up a run.
    inspApi('inspectionStatus', {})
      .then(r => { if (alive) setInspOk(r && r.success ? !!r.doneToday : true); })
      .catch(() => { if (alive) setInspOk(true); });
    return () => { alive = false; };
  }, [exempt]);

  useEffect(() => {
    let alive = true;
    // Only stoppers assigned directly to this driver gate the Onboard scan. Team-leader
    // area stoppers (role 'leader') are awareness-only and handled from the banner.
    msApi('list', {})
      .then(r => { if (alive) setMsPending(r && r.success ? (r.stoppers || []).filter(s => s.role !== 'leader' && !s.answeredToday) : []); })
      .catch(() => { if (alive) setMsPending([]); });
    const t = setTimeout(() => { if (alive) setMsPending(p => (p === null ? [] : p)); }, 4000);
    return () => { alive = false; clearTimeout(t); };
  }, []);

  const loadRecent = useCallback(async () => {
    try { const r = await obApi('onboardList', {}); setRecent(r.success ? (r.scans || []) : []); }
    catch { setRecent([]); }
  }, []);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  // Summaries already scanned today — used to warn the driver before they save a
  // second photo of the same load (the "wasn't sure the first one saved" retake).
  const todayIso = new Date().toLocaleDateString('en-CA');
  const todays = (recent || []).filter(s => s.scanDate === todayIso)
    .sort((a, b) => (a.scannedAt < b.scannedAt ? 1 : -1)); // newest first
  const lastToday = todays[0];

  async function send() {
    if (!capture || busy) return;
    if (todays.length > 0) {
      const ok = window.confirm(
        "You've already scanned an onboard summary today" +
        (lastToday && lastToday.scannedAt ? ' at ' + obTime(lastToday.scannedAt) : '') +
        (lastToday && lastToday.items != null ? ' (' + lastToday.items + ' items)' : '') +
        ".\n\nOnly scan again if you picked up MORE parcels later in your shift. " +
        'Save this photo as well?'
      );
      if (!ok) return;
    }
    setBusy(true); setError(''); setResult(null);
    try {
      const base64 = (capture.split(',')[1] || '');
      const r = await obApi('onboardUpload', { base64, contentType: 'image/jpeg' });
      if (!r.success) throw new Error(r.error || 'Upload failed');
      setResult(r.scan || {}); setCapture(null); loadRecent();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  const tallyCell = (label, value, big) => (
    <div style={{ textAlign: 'center', padding: '8px 4px', background: 'var(--hp-black)', borderRadius: 8 }}>
      <div style={{ fontSize: big ? '1.5rem' : '1.05rem', fontWeight: 800, color: big ? 'var(--hp-gold)' : 'var(--hp-text)' }}>{obN(value)}</div>
      <div style={{ fontSize: '0.62rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 2 }}>{label}</div>
    </div>
  );

  // The security declaration outranks everything else on this screen. Nothing renders
  // behind it — no safety brief, no van check, no mail stoppers, no camera.
  if (secDecl === null) {
    return <div style={{ padding: 26, textAlign: 'center', color: 'var(--hp-dim)' }}>
      <RefreshCw size={18} style={{ animation: 'spin 0.9s linear infinite' }} /> Checking your security declaration…
    </div>;
  }
  if (secDecl.blocked) {
    return (
      <div style={{ padding: '16px 0' }}>
        <SecurityDeclarationGate
          payload={secDecl}
          lang={secDecl.declaration ? secDecl.declaration.lang : undefined}
          onLangChange={loadSecDecl}
          onCleared={loadSecDecl}
        />
      </div>
    );
  }

  // Safety brief next. Nothing else on this screen renders until it is acknowledged —
  // no van check, no mail stoppers, no camera.
  if (brief === null) {
    return <div style={{ padding: 26, textAlign: 'center', color: 'var(--hp-dim)' }}>
      <RefreshCw size={18} style={{ animation: 'spin 0.9s linear infinite' }} /> Checking your safety brief…
    </div>;
  }
  if (brief.blocked && brief.brief) {
    return (
      <div style={{ padding: '16px 0' }}>
        <SafetyBriefGate
          brief={brief.brief}
          outstanding={brief.outstanding}
          lang={brief.lang}
          onLangChange={loadBrief}
          onCleared={loadBrief}
        />
      </div>
    );
  }

  // Gate stays closed while the check status is still loading, so the scan UI
  // never flashes before the lock. Only a confirmed done-today (or a backend
  // error → fail-open) reveals the scanner below.
  if (inspOk !== true) {
    const loading = inspOk === null;
    return (
      <div style={{ padding: '16px 0' }}>
        <div style={{ ...card, textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(203,165,76,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px' }}>
            {loading ? <RefreshCw size={22} color="var(--hp-gold)" style={{ animation: 'spin 0.9s linear infinite' }} /> : <Lock size={26} color="var(--hp-gold)" />}
          </div>
          <div style={{ fontWeight: 800, fontSize: '1rem' }}>{loading ? 'Checking your van check…' : 'Onboard Scan is locked'}</div>
          {!loading && (
            <>
              <p style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', margin: '6px auto 14px', maxWidth: 300, lineHeight: 1.5 }}>
                Do your morning van check first — it only takes about 30 seconds. Then your Onboard Scan unlocks.
              </p>
              <button onClick={onGoInspection}
                style={{ width: '100%', padding: '14px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                <ClipboardCheck size={16} /> Start the van check
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // Answer any mail stoppers before the scanner unlocks (after the van check).
  if (msPending && msPending.length > 0) {
    return <MailStopperGate stoppers={msPending} onAllDone={() => setMsPending([])} />;
  }

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={card}>
        <div style={{ fontWeight: 800, fontSize: '1rem', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
          <SelfAvatar login={driver} size={30} editable={false} />
          <Camera size={17} color="var(--hp-gold)" /> Scan your onboard summary
        </div>
        <p style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '0 0 12px', lineHeight: 1.5 }}>
          After you've accepted all parcels, photograph the device <strong>SUMMARY</strong> screen here
          (instead of your camera roll). It's read automatically, filed with the time, and you can
          open any past summary from the list below. <strong>Onboard more parcels later in your shift?</strong>{' '}
          Scan the new summary too — every capture is kept as your record.
        </p>

        {todays.length > 0 && !result && (
          <div style={{ marginBottom: 12, padding: '10px 12px', background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.4)', borderRadius: 'var(--radius)', fontSize: '0.8rem', display: 'flex', gap: 8 }}>
            <CheckCircle size={15} color="#34d399" style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              You've <strong>already captured</strong> today's onboard summary
              {lastToday && lastToday.scannedAt ? ' at ' + obTime(lastToday.scannedAt) : ''}
              {lastToday && lastToday.items != null ? ` (${lastToday.items} items)` : ''}. It's saved — no need to redo it.
              Only scan again if you picked up <strong>more parcels</strong> later.
            </span>
          </div>
        )}

        <CameraCapture
          nativeFirst
          guide="document"
          maxEdge={2000}
          quality={0.85}
          value={capture}
          onCapture={dataUrl => { setCapture(dataUrl); setResult(null); setError(''); }}
          onClear={() => setCapture(null)}
        />

        {capture && (
          <button onClick={send} disabled={busy}
            style={{ marginTop: 12, width: '100%', padding: '14px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.95rem', cursor: busy ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
            {busy ? <><RefreshCw size={16} style={{ animation: 'spin 0.9s linear infinite' }} /> Reading the summary… (~10s)</> : <><Send size={16} /> Submit summary</>}
          </button>
        )}

        {error && (
          <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 'var(--radius)', color: '#ef4444', fontSize: '0.82rem', display: 'flex', gap: 8 }}>
            <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} /> {error}
          </div>
        )}

        {result && (
          <div style={{ marginTop: 12, padding: '12px 14px', background: (result.ocrError || result.indoor?.flag) ? 'rgba(245,158,11,0.1)' : 'rgba(52,211,153,0.08)', border: `1px solid ${(result.ocrError || result.indoor?.flag) ? 'rgba(245,158,11,0.5)' : 'rgba(52,211,153,0.4)'}`, borderRadius: 'var(--radius)', fontSize: '0.84rem' }}>
            {result.ocrError ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <AlertTriangle size={16} color="#f59e0b" style={{ flexShrink: 0, marginTop: 1 }} />
                <span>Photo <strong>stored safely</strong> at {obTime(result.scannedAt)}, but the automatic read didn't complete — admin can re-read it, nothing to redo.</span>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: 8, fontWeight: 700, marginBottom: 10 }}>
                  <CheckCircle size={16} color="#34d399" style={{ flexShrink: 0, marginTop: 1 }} />
                  Onboard recorded at {obTime(result.scannedAt)}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 6 }}>
                  {tallyCell('Items', result.items, true)}
                  {tallyCell('Held', result.heldArticles)}
                </div>
                <div style={{ marginTop: 8, fontSize: '0.74rem', color: 'var(--hp-dim)' }}>
                  Pick up more parcels later? Scan that summary too — it's added to today's record.
                </div>
                {result.indoor?.flag && (
                  <div style={{ marginTop: 10, padding: '10px 11px', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.5)', borderRadius: 8 }}>
                    <div style={{ display: 'flex', gap: 7, fontWeight: 800, color: '#f59e0b', fontSize: '0.8rem', marginBottom: 4 }}>
                      <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
                      {result.indoor.flag === 'NO_LOGON' ? 'You are not logged on' : 'Check your log-on time'}
                    </div>
                    <div style={{ fontSize: '0.78rem', lineHeight: 1.5 }}>{result.indoor.message}</div>
                    <div style={{ fontSize: '0.78rem', lineHeight: 1.5, marginTop: 6, fontWeight: 700 }}>
                      {result.indoor.flag === 'NO_LOGON'
                        ? 'Your parcels are recorded, but your shift is not. Tell the office what time you started — today, while you remember.'
                        : 'Your parcels are recorded and safe. If you actually started earlier, tell the office today so your hours are right — it cannot be worked out later.'}
                    </div>
                  </div>
                )}
                {result.loginMismatch && (
                  <div style={{ marginTop: 8, color: '#f59e0b', fontSize: '0.76rem', display: 'flex', gap: 6 }}>
                    <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} /> The device shows {result.deviceLogin} — make sure you scanned your own device.
                  </div>
                )}
                {result.checksumOk === false && (
                  <div style={{ marginTop: 8, color: '#f59e0b', fontSize: '0.76rem' }}>
                    The lines don't add up to #Items — a number may have been misread; admin will double-check the photo.
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div style={card}>
        <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>My summaries</div>
        {recent === null && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>Loading…</div>}
        {recent && recent.length === 0 && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>No summaries scanned yet.</div>}
        {(recent || []).map(s => (
          <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '9px 0', borderTop: '1px solid var(--hp-border)', fontSize: '0.84rem' }}>
            <div>
              <div style={{ fontWeight: 700 }}>{obDay(s.scannedAt)}</div>
              <div style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>{obTime(s.scannedAt)}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontWeight: 800, color: 'var(--hp-gold)' }}>{obN(s.items)}</span>
              <span style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}> items</span>
            </div>
            {s.viewUrl
              ? <a href={s.viewUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--hp-gold)', fontSize: '0.78rem', fontWeight: 700, textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}><ImageIcon size={14} /> View</a>
              : <span style={{ color: 'var(--hp-dim)', fontSize: '0.78rem' }}>—</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Onboard team tallies (admin) — the whole team's position for a day ───────
const OB_AREA_ORDER = ['DARL', 'CITY', 'BWAY'];
const OB_AREA_LABEL = { DARL: 'Darlinghurst', CITY: 'City', BWAY: 'Broadway' };

function OnboardTeamView() {
  const todayIso = new Date().toLocaleDateString('en-CA');
  const [date, setDate] = useState(todayIso);
  const [scans, setScans] = useState(null);
  const [loginRows, setLoginRows] = useState(null);   // who logged on that day (baseline for "not scanned")
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');

  const card = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 14 };

  const load = useCallback(async (d) => {
    setScans(null); setError(''); setLoginRows(null);
    try { const r = await obApi('onboardTeam', { date: d }); if (!r.success) throw new Error(r.error); setScans(r.scans || []); }
    catch (e) { setError(e.message); setScans([]); }
    // Who logged on / worked that day — the baseline against which we flag "logged
    // on but never scanned". Independent of the scans fetch: a failure just hides
    // the section rather than breaking the tally view.
    try {
      const lc = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getLoginCheck', date: d }),
      }).then(x => x.json());
      setLoginRows(lc && lc.success ? (lc.rows || []) : []);
    } catch { setLoginRows([]); }
  }, []);
  useEffect(() => { load(date); }, [date, load]);

  const shiftDay = (days) => {
    const d = new Date(date + 'T12:00'); d.setDate(d.getDate() + days);
    setDate(d.toLocaleDateString('en-CA'));
  };

  async function reocr(id) {
    setBusyId(id);
    try { const r = await obApi('onboardReocr', { id }); if (!r.success) throw new Error(r.error); load(date); }
    catch (e) { window.alert(e.message); setBusyId(''); }
  }
  async function remove(id, driver) {
    if (!window.confirm('Delete ' + driver + "'s scan? The photo goes too.")) return;
    setBusyId(id);
    try { await obApi('onboardDelete', { id }); load(date); }
    catch (e) { window.alert(e.message); setBusyId(''); }
  }

  // Each capture is a separate acceptance batch — a big depot load first, then
  // smaller top-ups later in the shift — NOT a retake of the same load. So the
  // headline figure is the LARGEST capture (the main onboard), and any other
  // images for the day nest under it, each still viewable.
  const byDriver = {};
  (scans || []).forEach(s => { (byDriver[s.driver] = byDriver[s.driver] || []).push(s); });
  const mainOf = (list) => list.slice().sort((a, b) => {
    const ai = a.items == null ? -1 : a.items, bi = b.items == null ? -1 : b.items;
    return bi !== ai ? bi - ai : (a.scannedAt < b.scannedAt ? -1 : 1);
  })[0];
  const main = {};    // driver -> largest capture (the headline tally)
  const extras = {};  // driver -> the other captures, chronological
  Object.keys(byDriver).forEach(d => {
    const m = mainOf(byDriver[d]);
    main[d] = m;
    extras[d] = byDriver[d].filter(s => s.id !== m.id).sort((a, b) => (a.scannedAt < b.scannedAt ? -1 : 1));
  });
  const drivers = Object.values(main);

  // Likely DUPLICATE (not a genuine later top-up): a second photo of the SAME
  // load, taken because the driver wasn't sure the first one saved. Signals:
  // shot within a few minutes of the main capture, or reading the same #Items.
  // A real top-up load comes back much later with a different, smaller count.
  const DUP_WINDOW_MIN = 10;
  const minsApart = (a, b) => {
    const ta = Date.parse(a), tb = Date.parse(b);
    return (isNaN(ta) || isNaN(tb)) ? Infinity : Math.abs(ta - tb) / 60000;
  };
  const isDupe = (e, m) => !!m && (minsApart(e.scannedAt, m.scannedAt) <= DUP_WINDOW_MIN
    || (e.items != null && m.items != null && e.items === m.items));
  const dupeList = [];
  Object.keys(extras).forEach(d => {
    extras[d].forEach(e => { if (isDupe(e, main[d])) dupeList.push({ driver: d, main: main[d], extra: e }); });
  });
  const dupeIds = new Set(dupeList.map(x => x.extra.id));

  // Impossible indoor times — a driver who logged on seconds before photographing a
  // full load, so the shift start cannot be right. Judged server-side (onboard-sb's
  // indoor guard) against the shift start for THIS date, so looking back at an older
  // day still shows what was wrong with it. Raised here as well as on the driver's
  // phone because the driver may not read their screen; caught on the day, the start
  // can simply be asked about and fixed, which weeks later it cannot.
  const indoorFlags = drivers.filter(s => s.indoor && s.indoor.flag)
    .sort((a, b) => (a.driver < b.driver ? -1 : 1));

  const areas = [...OB_AREA_ORDER, ...[...new Set(drivers.map(s => s.area || 'UNASSIGNED'))].filter(a => !OB_AREA_ORDER.includes(a))];
  const sum = (rows, key) => rows.reduce((a, s) => a + (key === 'eparcel' ? (obEparcel(s) || 0) : (s[key] || 0)), 0);

  // On shift but not yet onboarded — the live catch-them list. The summary is
  // meant to be photographed at the depot at pickup, so a driver who's logged on
  // and out on the road with no scan is the one to chase now. Staff excluded;
  // finished shifts drop off (the window to onboard has passed).
  const scannedSet = new Set(Object.keys(main));
  const onShift = (loginRows || []).filter(r => !r.isStaff && r.status === 'ON_SHIFT');
  const missing = onShift.filter(r => !scannedSet.has((r.login || '').toUpperCase()));
  const missAreas = [...OB_AREA_ORDER, ...[...new Set(missing.map(m => m.area || 'UNASSIGNED'))].filter(a => !OB_AREA_ORDER.includes(a))];

  const numCell = (v, w, style) => <span style={{ width: w, textAlign: 'right', flexShrink: 0, ...style }}>{obN(v)}</span>;

  return (
    <div style={{ padding: '16px 0', width: '100%' }}>
      {/* Date nav */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <button onClick={() => shiftDay(-1)} style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', padding: '8px 14px', cursor: 'pointer', fontWeight: 800 }}>‹</button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: 800, fontSize: '1rem' }}>{obDay(date + 'T12:00')}</div>
          {date !== todayIso && (
            <button onClick={() => setDate(todayIso)} style={{ background: 'none', border: 'none', color: 'var(--hp-gold)', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', padding: 2 }}>Back to today</button>
          )}
        </div>
        <button onClick={() => shiftDay(1)} style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', padding: '8px 14px', cursor: 'pointer', fontWeight: 800 }}>›</button>
      </div>

      {error && <div style={{ ...card, color: '#ef4444', fontSize: '0.84rem' }}>{error}</div>}
      {scans === null && <div style={{ ...card, color: 'var(--hp-dim)', fontSize: '0.84rem' }}>Loading…</div>}

      {scans !== null && (
        <>
          {/* Team total */}
          <div style={{ ...card, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Team onboard</div>
              <div style={{ fontSize: '2rem', fontWeight: 800, color: 'var(--hp-gold)', lineHeight: 1.2 }}>{sum(drivers, 'items')}</div>
              <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>
                {drivers.length} driver{drivers.length === 1 ? '' : 's'} scanned · {sum(drivers, 'heldArticles')} held
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: '0.76rem', color: 'var(--hp-dim)', lineHeight: 1.7 }}>
              {areas.map(a => {
                const rows = drivers.filter(s => (s.area || 'UNASSIGNED') === a);
                return rows.length ? <div key={a}><strong style={{ color: 'var(--hp-text)' }}>{OB_AREA_LABEL[a] || a}</strong> {sum(rows, 'items')}</div> : null;
              })}
            </div>
          </div>

          {/* Impossible indoor time — fix the start today, while it can still be asked about */}
          {indoorFlags.length > 0 && (
            <div style={{ ...card, border: '1px solid #ef444455' }}>
              <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#ef4444', marginBottom: 4 }}>
                Log-on time can't be right ({indoorFlags.length})
              </div>
              <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginBottom: 6 }}>
                Logged on moments before accepting a full load — the sort could not have happened in the time recorded.
                Ask what time they actually started and correct the shift on Shift Approvals <strong>today</strong>; once the
                pay period closes the hours can only be estimated.
              </div>
              {indoorFlags.map(s => (
                <div key={s.id} style={{ borderTop: '1px solid var(--hp-border)', padding: '7px 0', fontSize: '0.8rem' }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontWeight: 700 }}>{s.driver}</span>
                    <span style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>{OB_AREA_LABEL[s.area] || s.area}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ color: '#ef4444', fontWeight: 800, fontSize: '0.72rem' }}>
                      {s.indoor.flag === 'NO_LOGON' ? 'no log-on' : (s.indoor.pph ? Math.round(s.indoor.pph).toLocaleString() + '/hr' : 'start after scan')}
                    </span>
                  </div>
                  <div style={{ color: 'var(--hp-dim)', fontSize: '0.72rem', marginTop: 2 }}>{s.indoor.message}</div>
                </div>
              ))}
            </div>
          )}

          {/* On shift but not yet onboarded — the live catch-them list */}
          {loginRows !== null && missing.length > 0 && (
            <div style={{ ...card, border: '1px solid #f59e0b55' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
                <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#f59e0b' }}>On shift, not yet onboarded</div>
                <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)' }}>
                  {missing.length} of {onShift.length} still out
                </div>
              </div>
              <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginBottom: 6 }}>
                Logged on and out on the road but haven't photographed the device summary — catch them now to onboard, or flag for training.
              </div>
              {missAreas.map(a => {
                const rows = missing.filter(m => (m.area || 'UNASSIGNED') === a)
                  .sort((x, y) => ((x.login || '') < (y.login || '') ? -1 : 1));
                if (!rows.length) return null;
                return (
                  <div key={a}>
                    <div style={{ fontSize: '0.68rem', fontWeight: 800, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '6px 0 2px' }}>{OB_AREA_LABEL[a] || a}</div>
                    {rows.map(m => (
                      <div key={m.login} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem', borderTop: '1px solid var(--hp-border)', padding: '6px 0' }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ fontWeight: 700 }}>{m.login}</span>
                          {m.name && m.name !== m.login && <span style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}> {m.name}</span>}
                        </span>
                        <span style={{ fontSize: '0.68rem', color: 'var(--hp-dim)' }}>
                          on shift{m.shiftStart ? ' · since ' + m.shiftStart : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
          {loginRows !== null && onShift.length > 0 && missing.length === 0 && (
            <div style={{ ...card, border: '1px solid #22c55e55', color: '#22c55e', fontSize: '0.82rem', fontWeight: 700 }}>
              ✓ All {onShift.length} driver{onShift.length === 1 ? '' : 's'} currently out have onboarded.
            </div>
          )}

          {/* Possible duplicate photos — a driver shot the same load twice (unsure
              the first saved). Compare, then remove the extra; the tally already
              ignores it, so removing just tidies the record. */}
          {dupeList.length > 0 && (
            <div style={{ ...card, border: '1px solid #f59e0b55' }}>
              <div style={{ fontWeight: 800, fontSize: '0.9rem', color: '#f59e0b', marginBottom: 4 }}>
                Possible duplicate photo{dupeList.length === 1 ? '' : 's'} ({dupeList.length})
              </div>
              <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', marginBottom: 6 }}>
                Same load photographed twice within minutes. Compare the two, then remove the duplicate — the headline tally already keeps only the main capture.
              </div>
              {dupeList.map(({ driver: dd, main: m, extra: e }) => (
                <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', borderTop: '1px solid var(--hp-border)', padding: '7px 0' }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 700 }}>{dd}</span>
                    <span style={{ color: 'var(--hp-dim)', fontSize: '0.72rem' }}>
                      {' '}kept {obTime(m.scannedAt)} ({obN(m.items)}) · duplicate {obTime(e.scannedAt)} ({e.ocrError ? 'read failed' : obN(e.items)})
                    </span>
                  </span>
                  {m.viewUrl && <a href={m.viewUrl} target="_blank" rel="noreferrer" title="View kept photo" style={{ color: 'var(--hp-dim)' }}><ImageIcon size={14} /></a>}
                  {e.viewUrl && <a href={e.viewUrl} target="_blank" rel="noreferrer" title="View duplicate" style={{ color: 'var(--hp-dim)' }}><ImageIcon size={14} /></a>}
                  <button onClick={() => remove(e.id, dd)} disabled={busyId === e.id} title="Remove the duplicate photo"
                    style={{ background: 'none', border: '1px solid #f59e0b', borderRadius: 6, color: '#f59e0b', fontSize: '0.68rem', fontWeight: 700, padding: '2px 10px', cursor: 'pointer' }}>
                    {busyId === e.id ? '…' : 'Remove'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {drivers.length === 0 && <div style={{ ...card, color: 'var(--hp-dim)', fontSize: '0.84rem' }}>No onboard summaries scanned this day yet.</div>}

          {areas.map(a => {
            const rows = drivers.filter(s => (s.area || 'UNASSIGNED') === a).sort((x, y) => (x.scannedAt < y.scannedAt ? -1 : 1));
            if (!rows.length) return null;
            return (
              <div key={a} style={{ ...card, padding: '12px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                  <div style={{ fontWeight: 800, fontSize: '0.9rem' }}>{OB_AREA_LABEL[a] || a}</div>
                  <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)' }}>
                    <strong style={{ color: 'var(--hp-gold)', fontSize: '0.9rem' }}>{sum(rows, 'items')}</strong> items · {rows.length} driver{rows.length === 1 ? '' : 's'}
                  </div>
                </div>
                {/* Column headers */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.6rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', padding: '4px 0' }}>
                  <span style={{ flex: 1 }}>Driver</span>
                  {numCell('Items', 38)}{numCell('Held', 32)}
                  <span style={{ width: 22 }} />
                </div>
                {rows.map(s => (
                  <div key={s.id} style={{ borderTop: '1px solid var(--hp-border)', padding: '7px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.8rem' }}>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ fontWeight: 700 }}>{s.driver}</span>
                        <span style={{ color: 'var(--hp-dim)', fontSize: '0.7rem' }}> {obTime(s.scannedAt)}{extras[s.driver] && extras[s.driver].length ? ' · main load' : ''}</span>
                      </span>
                      {numCell(s.items, 38, { fontWeight: 800, color: 'var(--hp-gold)' })}
                      {numCell(s.heldArticles, 32)}
                      <span style={{ width: 22, textAlign: 'right' }}>
                        {s.viewUrl && <a href={s.viewUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--hp-dim)' }}><ImageIcon size={14} /></a>}
                      </span>
                    </div>
                    {/* Other captures this day — later top-ups, nested under the main load. */}
                    {extras[s.driver] && extras[s.driver].length > 0 && extras[s.driver].map(e => (
                      <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', color: 'var(--hp-dim)', padding: '3px 0 3px 12px', borderLeft: '2px solid var(--hp-border)', marginTop: 3 }}>
                        <span style={{ flex: 1, minWidth: 0 }}>
                          {dupeIds.has(e.id)
                            ? <span style={{ color: '#f59e0b', fontWeight: 700 }}>possible duplicate {obTime(e.scannedAt)}</span>
                            : <>+ later {obTime(e.scannedAt)}</>}
                          {e.ocrError ? ' · read failed' : ''}
                        </span>
                        {numCell(e.items, 38)}
                        {numCell(e.heldArticles, 32)}
                        <span style={{ textAlign: 'right', display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                          {e.ocrError && (
                            <button onClick={() => reocr(e.id)} disabled={busyId === e.id} title="Re-read"
                              style={{ background: 'none', border: '1px solid #f59e0b', borderRadius: 5, color: '#f59e0b', fontSize: '0.64rem', fontWeight: 700, padding: '1px 6px', cursor: 'pointer' }}>
                              {busyId === e.id ? '…' : 'Re-read'}
                            </button>
                          )}
                          {e.viewUrl && <a href={e.viewUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--hp-dim)' }}><ImageIcon size={13} /></a>}
                          <button onClick={() => remove(e.id, s.driver)} disabled={busyId === e.id} title="Delete this capture"
                            style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', padding: 0 }}>
                            <Trash2 size={12} />
                          </button>
                        </span>
                      </div>
                    ))}
                    {(s.ocrError || s.checksumOk === false || (s.deviceLogin && s.deviceLogin !== s.driver)) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4, fontSize: '0.7rem', color: '#f59e0b' }}>
                        <AlertTriangle size={12} style={{ flexShrink: 0 }} />
                        <span style={{ flex: 1 }}>
                          {s.ocrError ? 'Photo stored, read failed' :
                            s.checksumOk === false ? "Lines don't add up to #Items — check the photo" :
                            `Device shows ${s.deviceLogin}`}
                        </span>
                        {s.ocrError && (
                          <button onClick={() => reocr(s.id)} disabled={busyId === s.id}
                            style={{ background: 'none', border: '1px solid #f59e0b', borderRadius: 6, color: '#f59e0b', fontSize: '0.68rem', fontWeight: 700, padding: '2px 8px', cursor: 'pointer' }}>
                            {busyId === s.id ? '…' : 'Re-read'}
                          </button>
                        )}
                        <button onClick={() => remove(s.id, s.driver)} disabled={busyId === s.id}
                          style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', padding: 0 }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

export default function DriverLogPage() {
  // ── Auth ──────────────────────────────────────────────────────────────────
  const [authState, setAuthState]     = useState('login');
  const [showWelcome, setShowWelcome] = useState(false);
  const [loginForm, setLoginForm]     = useState({ login: '', pin: '' });
  const [loginError, setLoginError]   = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // ── Shift state ───────────────────────────────────────────────────────────
  const [shift, setShift]             = useState(null);
  const [shiftState, setShiftState]   = useState('ready');

  // ── Live timer ────────────────────────────────────────────────────────────
  const [now, setNow] = useState(Date.now());

  // ── Finish form ───────────────────────────────────────────────────────────
  const [odoStart, setOdoStart]       = useState('');
  const [odoEnd, setOdoEnd]           = useState('');
  // A flagged reading the driver has confirmed is what the dash actually says.
  const [odoStartKept, setOdoStartKept] = useState(false);
  const [odoEndKept, setOdoEndKept]     = useState(false);
  // Mirror the latest start odometer so backup/persist can never send a blank.
  const odoStartRef = useRef('');
  useEffect(() => { if (odoStart) odoStartRef.current = String(odoStart); }, [odoStart]);
  const [submitting, setSubmitting]   = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Pending lease agreements for this driver — drives the red dot on the My Van tab.
  const [leasePending, setLeasePending] = useState(0);
  // Pending "complete your details" request — drives the red dot on the My Shift tab.
  const [detailsPending, setDetailsPending] = useState(0);
  const [accidentPending, setAccidentPending] = useState(0); // office asked for more on an accident report
  const [assetCheck, setAssetCheck] = useState(0);      // outstanding scanner asset check
  // Open customer cases confirmed to this driver — drives the red dot on the My Shift tab.
  const [csPending, setCsPending] = useState(0); // open customer cases → Customer Service tab red dot
  // Mail monitors/stoppers on my route: how many there are (shows the Mail Monitor pill) and
  // how many of MINE still need today's answer (joins the Customer Service red dot).
  const [msInfo, setMsInfo] = useState({ any: false, todo: 0 });
  const [csSub, setCsSub] = useState(null);      // open Customer Service on a given sub-tab
  const applyMs = useCallback((list) => {
    if (!list) return;
    setMsInfo({ any: list.length > 0, todo: list.filter(s => s.role !== 'leader' && !s.answeredToday).length });
  }, []);
  const [briefsDue, setBriefsDue] = useState(0); // compulsory safety briefs not yet read
  // Standing customer requests they have not acknowledged — its own count, because it is not
  // a case waiting on an answer; it is something to take in. Drives the Requests tab dot.
  const [reqPending, setReqPending] = useState(0);
  // Standing orders naming this driver (parcels for one address delivered to another).
  // `total` decides whether the tab exists at all — most drivers have none, and an empty
  // tab on every phone is clutter; `pending` is the ones not yet read in their current wording.
  const [soInfo, setSoInfo] = useState({ total: 0, pending: 0 });
  const [soLeader, setSoLeader] = useState(false);   // team leader: the tab carries the whole register
  // Onboard summaries scanned today — drives the late-in-the-shift nudge on the shift
  // screen. null until the count comes back, so a slow/failed check stays silent rather
  // than telling a driver who HAS scanned that they haven't.
  const [obToday, setObToday] = useState(null);
  // Unread safety hazards reaching this driver → red dot on the Safety tab. Fed by
  // SafetyFocusCard, which is always mounted on the home screen.
  const [safetyMustRead, setSafetyMustRead] = useState(0);
  // Unopened documents pushed to this driver — drives the red dot on the My Docs tab.
  const [docsUnread, setDocsUnread] = useState(0);
  const [finesUnread, setFinesUnread] = useState(0);   // unopened penalty notices → home banner
  const [tollsToPay, setTollsToPay] = useState([]);    // toll notices the driver pays, not yet paid → home banner until paid
  const [docBanners, setDocBanners] = useState([]);    // My Docs letters the office pinned to the home screen for a date window
  const [trainingDue, setTrainingDue] = useState(0);
  // Outstanding "please upload X" requests from the office — banner + My Docs red dot.
  const [docReqPending, setDocReqPending] = useState(0);
  // DMS documents needing the driver's attention (expiring/expired/renewal-requested)
  // — drives the red dot on the "My Documents" tab.
  const [dmsAttention, setDmsAttention] = useState(0);
  // Delivery bag expression of interest not yet answered — My Docs red dot + the hub's
  // "Needs your attention" row. Read once on sign-in; the cards update it on answer.
  const [bagPending, setBagPending] = useState(0);
  useEffect(() => {
    if (authState !== 'authed') return;
    let alive = true;
    bagEoiPending().then(n => { if (alive) setBagPending(n); });
    return () => { alive = false; };
  }, [authState, shift?.driver]);

  // ── Vehicle for the shift (My Van default + mid-shift swap) ────────────────
  const [shiftVehicle, setShiftVehicle] = useState(null);  // {rego, make, model} default from Fleet
  const [showVehChange, setShowVehChange] = useState(false);
  const [vehChangeRego, setVehChangeRego] = useState('');
  const [vehChangeOdo, setVehChangeOdo]   = useState('');
  const [vehChanging, setVehChanging]     = useState(false);

  // ── Scanner photo at sign-off ─────────────────────────────────────────────
  const [scannerCapture, setScannerCapture]   = useState(null);    // { dataUrl, file } — captured, uploaded at submit
  const [scannerDone, setScannerDone]         = useState(false);   // uploaded OK
  const [scannerSkipped, setScannerSkipped]   = useState(false);   // team leader: no scanner used
  // A phone that cannot produce a photo must not hold a shift — and a driver's day
  // — hostage. GALLEGUIL's Android could not, and his 2 Sep shift sat unsubmitted
  // and unpaid for it. This only ever unlocks AFTER a capture has genuinely failed:
  // it is a way past a broken camera, not a way past the requirement.
  const [scannerFailure, setScannerFailure]   = useState('');      // why the camera couldn't
  const [scannerNoPhoto, setScannerNoPhoto]   = useState(false);   // driver chose to finish without it
  // Some logins don't always use a scanner — they may finish with no photo.
  const driverLogin = (shift?.driver || '').toUpperCase();
  const scannerOptional = SCANNER_OPTIONAL_LOGINS.has(driverLogin);
  // Owners who log on/off from a different location skip the depot geofence + QR.
  const geoExempt = GEOFENCE_EXEMPT_LOGINS.has(driverLogin);
  const finishGeoExempt = geoExempt || FINISH_GEOFENCE_EXEMPT_LOGINS.has(driverLogin);
  // Some logins don't always drive — odometer is optional (can still enter km).
  const odoOptional = ODO_OPTIONAL_LOGINS.has(driverLogin);
  // Submit is allowed once a photo is taken (it's uploaded when they tap SUBMIT SHIFT),
  // already sent, or a scanner-optional driver marks "no scanner used".
  const scannerSatisfied = scannerDone || !!scannerCapture || (scannerOptional && scannerSkipped) || scannerNoPhoto;
  // Scroll targets so a blocked submit can point at the missing requirement.
  const scannerSectionRef = useRef(null);
  const carrySectionRef   = useRef(null);


  // A scanner photo already uploaded today (e.g. via the Scanner tab) counts
  // toward the finish requirement. Drivers were getting stuck because a
  // Scanner-tab upload didn't satisfy the finish screen's own photo gate —
  // they believed they'd finished while SUBMIT stayed disabled.
  useEffect(() => {
    if (shiftState !== 'finishing' || scannerDone || !shift?.driver) return;
    fetch(SCANNER_API, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'list', driver: shift.driver, from: localDateStr(), to: localDateStr() }),
    })
      .then(r => r.json())
      .then(d => { if (d.ok && (d.photos || []).length > 0) setScannerDone(true); })
      .catch(() => {});
  }, [shiftState, scannerDone, shift?.driver]);

  // ── Carry-over parcels report (end of shift) ──────────────────────────────
  const [carryOver, setCarryOver]   = useState(null); // null | 'no' | 'yes'
  const [carrySel, setCarrySel]     = useState({});   // reason key → { count, note, photoUrls[], uploading, photoError }
  const [carryPoAck, setCarryPoAck] = useState(false); // Incorrect Address: PO-transfer acknowledged
  const carryEntries = CARRY_REASONS.filter(r => carrySel[r.key]);
  // Submit gate: 'no' answers immediately; 'yes' needs ≥1 reason, a parcel count
  // for each, required photos uploaded, an 'other' note, and the PO-transfer tick.
  const carrySatisfied =
    carryOver === 'no' ||
    (carryOver === 'yes' && carryEntries.length > 0 &&
      carryEntries.every(r => {
        const e = carrySel[r.key];
        return Number(e.count) > 0 &&
          (!r.photo || (e.photoUrls && e.photoUrls.length > 0)) &&
          (!r.note || (e.note || '').trim());
      }) &&
      (!carrySel.address || carryPoAck));

  // ── Odometer camera capture ───────────────────────────────────────────────
  const [odoCapturing, setOdoCapturing]         = useState(false); // 'finish' | false
  const [startOdoCapturing, setStartOdoCapturing] = useState(false); // inline camera for start
  const [odoOcrLoading, setOdoOcrLoading]       = useState(false);
  const [odoOcrResult, setOdoOcrResult]         = useState(null);
  const [odoOcrError, setOdoOcrError]           = useState('');
  const odoFileRef = useCallback(node => { if (node) node.click(); }, []);

  // ── Existing submission / amendment ───────────────────────────────────────
  const [existingSub, setExistingSub] = useState(null);
  const [amendForm, setAmendForm] = useState({
    shiftStart: '', breakStart: '', breakEnd: '', finishTime: '',
    odoStart: '', odoEnd: '', reason: '',
  });
  const [amendSubmitting, setAmendSubmitting] = useState(false);
  const [amendError, setAmendError] = useState('');

  // ── Geofence ──────────────────────────────────────────────────────────────
  // geoState: 'idle' | 'checking' | 'in_range' | 'out_of_range' | 'denied' | 'error'
  const [geoState, setGeoState]       = useState('idle');
  const [geoDistance, setGeoDistance] = useState(null);  // metres to nearest depot
  const [nearestDepot, setNearestDepot] = useState(null);

  // ── QR verification ───────────────────────────────────────────────────────
  // qrState: 'idle' | 'checking' | 'verified' | 'invalid' | 'error' | 'override'
  const [qrState, setQrState] = useState('idle');
  const [qrDepot, setQrDepot] = useState(null);

  // ── DoE / Depot tasks (shared by Tasks tab + close-out gate) ───────────────
  const [depotActive, setDepotActive]       = useState(false);   // login on active depot roster
  // Re-render trigger when the sheet-driven DoE assignments load (DOE_POINTS_LIVE).
  const [, bumpDoeAssignVersion]             = useState(0);
  const [doeRelief, setDoeRelief]            = useState([]);      // today's relief assignments [{school,qr,relief,original}]
  const [doeConfirmed, setDoeConfirmed]      = useState(null);    // Map school → {collected, reason} for today (null = not loaded)
  const [finishBlockMsg, setFinishBlockMsg]  = useState('');      // close-out gate message

  // ── Portal tab ────────────────────────────────────────────────────────────
  const [portalTab, setPortalTab] = useState('shift'); // 'shift' | 'history' | 'routes' | 'tasks' | 'scanner' | 'todos'
  // Home-hub pilot: which navigation this device is using ('hub' | 'tabs').
  const [navMode, setNavMode] = useState(() => {
    try { return localStorage.getItem(NAV_MODE_KEY) === 'tabs' ? 'tabs' : 'hub'; } catch { return 'hub'; }
  });
  // Land on the hub rather than the shift screen (and bounce off 'hub' again if the
  // pilot switches back to tabs, so the tab strip never renders with nothing selected).
  useEffect(() => {
    if (authState !== 'authed') return;
    const up = (shift?.driver || '').toUpperCase();
    if (NAV_HUB_LOGINS.has(up) && navMode === 'hub') setPortalTab(t => (t === 'shift' ? 'hub' : t));
    else setPortalTab(t => (t === 'hub' ? 'shift' : t));
  }, [authState, shift?.driver, navMode]);
  const [todoAccess, setTodoAccess] = useState(null);  // { owner, allowed: [] } — who may use the personal to-do list
  // Cheap up-front count for the Customer Service tab red dot (id-only query, no threads),
  // refreshed on tab change so it drops as the driver answers.
  useEffect(() => {
    if (authState !== 'authed' || !shift?.driver) return;
    let alive = true;
    ccApi('count', {}).then(r => {
      if (!alive || !r || !r.success) return;
      setCsPending(r.count || 0);
      // Counted up-front with the cases so the Customer Service tab can carry the dot before
      // anything is opened — but kept separate from it: a case waits on an ANSWER, a request
      // waits on being read.
      setReqPending(r.requestsPending || 0);
    }).catch(() => {});
    msApi('list', {}).then(r => { if (alive && r && r.success) applyMs(r.stoppers || []); }).catch(() => {});
    return () => { alive = false; };
  }, [authState, shift?.driver, portalTab, applyMs]);
  // A deep link into a Customer Service sub-tab is used once, then forgotten.
  useEffect(() => { if (portalTab !== 'customerservice') setCsSub(null); }, [portalTab]);
  const goMailMonitor = useCallback(() => { setCsSub('mail'); setPortalTab('customerservice'); }, []);
  // Push alerts: start the service worker once the driver is in, and quietly re-register
  // this phone if its subscription has lapsed (a re-installed Home Screen icon voids it
  // without telling anyone). Both are no-ops where the device can't do push.
  useEffect(() => {
    if (authState !== 'authed') return;
    registerSW();
    syncAlerts('driver');
  }, [authState]);
  // The red number on the Home Screen tile mirrors the Customer Service red dot. This
  // covers the app being open; while it's closed the service worker sets the badge as
  // each push arrives, because iOS never wakes a web app to check for itself.
  useEffect(() => {
    if (authState !== 'authed') return;
    // The tile number is everything waiting on this driver, not only cases: a compulsory
    // brief they have not read blocks their onboard scan just as surely. Counting only
    // cases meant a launched brief left the icon clean.
    setTileBadge(csPending + briefsDue + accidentPending + soInfo.pending);
  }, [authState, csPending, briefsDue, accidentPending, soInfo.pending]);
  // Whether this driver has any standing orders at all — the tab only exists when they do,
  // so this has to be known from sign-in, not from when the shift-screen banner mounts.
  useEffect(() => {
    if (authState !== 'authed') return;
    soApi('list', {}).then((r) => {
      if (r && r.success) setSoInfo({ total: (r.orders || []).length, pending: r.pending || 0 });
    }).catch(() => {});
    soApi('team', {}).then((r) => { if (r && r.success) setSoLeader(true); }).catch(() => {});
  }, [authState]);
  // Where a tapped alert lands. A push carries ?tab=… ; the service worker either opens the
  // portal at that URL (app was closed) or messages the running app (app was already open,
  // so nothing reloads under a driver mid-shift). Deliberately an allowlist — an unknown
  // tab name would render a blank screen.
  useEffect(() => {
    if (authState !== 'authed') return;
    const ALLOWED = new Set(['customerservice', 'procedures', 'myvan', 'standingorders', 'mydocs']);
    const go = (raw) => {
      const t = (raw || '').toString();
      if (ALLOWED.has(t)) setPortalTab(t);
    };
    const params = new URLSearchParams(window.location.search);
    if (params.get('tab')) {
      go(params.get('tab'));
      params.delete('tab');
      const q = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (q ? '?' + q : ''));
    }
    if (!('serviceWorker' in navigator)) return;
    const onMsg = (e) => {
      if (!e.data || e.data.type !== 'hp-open') return;
      try { go(new URL(e.data.url, window.location.origin).searchParams.get('tab')); } catch (err) { /* ignore */ }
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, [authState]);
  useEffect(() => {
    if (authState !== 'authed') return;
    fetch('/.netlify/functions/todos-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getToDoAccess' }),
    }).then(r => r.json()).then(d => { if (d.success) setTodoAccess(d); }).catch(() => {});
  }, [authState]);

  // ── Today's onboard scan count (drives OnboardNudge) ──────────────────────
  // Only asked while the shift screen is open — that is the only place the nudge can
  // show — and it stops asking for the rest of the day once a scan is in, so a driver
  // who scans and comes back never pays for the check twice.
  const obDoneForRef = useRef('');   // shift date the driver has already scanned on
  useEffect(() => {
    if (authState !== 'authed' || !shift?.driver || portalTab !== 'shift') return undefined;
    if (shift.date && obDoneForRef.current === shift.date) return undefined;
    let cancelled = false;
    obApi('onboardToday', {})
      .then(r => {
        if (cancelled || !r || !r.success) return;
        setObToday(r.count);
        if (r.count > 0) obDoneForRef.current = shift.date || '';
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [authState, shift?.driver, shift?.date, portalTab]);

  // ── Team-leader feature access (nominated in Settings → Team Leader Access) ──
  const [featureAccess, setFeatureAccess] = useState({}); // { LOGIN: { afternoon_check, logged_on, leave_calendar, driver_routes, status } }
  const [accessUpdated, setAccessUpdated] = useState(false); // shows the "access updated" refresh banner
  const featureAccessRef = useRef('');
  useEffect(() => {
    if (authState !== 'authed') return undefined;
    let cancelled = false;
    const loadAccess = (isPoll) => {
      fetch('/.netlify/functions/feature-access-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getFeatureAccess' }),
      }).then(r => r.json()).then(d => {
        if (cancelled || !d.success) return;
        const me = (shift?.driver || '').toUpperCase();
        const mine = JSON.stringify((d.map || {})[me] || {});
        if (isPoll && featureAccessRef.current && mine !== featureAccessRef.current) {
          setAccessUpdated(true); // permissions changed mid-session → prompt a refresh
        }
        featureAccessRef.current = mine;
        setFeatureAccess(d.map || {});
      }).catch(() => {});
    };
    loadAccess(false);
    const id = setInterval(() => loadAccess(true), 180000); // re-check every 3 min
    return () => { cancelled = true; clearInterval(id); };
  }, [authState, shift?.driver]);

  const applyAccessUpdate = () => {
    setAccessUpdated(false);
    fetch('/.netlify/functions/feature-access-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getFeatureAccess' }),
    }).then(r => r.json()).then(d => {
      if (!d.success) return;
      const me = (shift?.driver || '').toUpperCase();
      featureAccessRef.current = JSON.stringify((d.map || {})[me] || {});
      setFeatureAccess(d.map || {});
    }).catch(() => {});
  };

  // If the active tab is a team-leader function that's been revoked, drop to My Shift.
  useEffect(() => {
    const f = featureAccess[(shift?.driver || '').toUpperCase()] || {};
    const tlTabs = {
      tl_afternoon: !!f.afternoon_check,
      tl_loggedon:  !!f.logged_on,
      tl_leave:     !!f.leave_calendar,
      tl_routes:    ['all', 'team', 'driver'].includes(f.driver_routes),
      tl_status:    !!f.status,
    };
    if (Object.prototype.hasOwnProperty.call(tlTabs, portalTab) && !tlTabs[portalTab]) {
      setPortalTab('shift');
    }
  }, [featureAccess, portalTab, shift?.driver]);
  const [historyView, setHistoryView] = useState('yesterday'); // 'yesterday' | 'week' | 'period' | 'lastperiod'
  const [historyMode, setHistoryMode] = useState('mine');      // team-history logins: 'mine' (own per-day) | 'team'
  const [historyData, setHistoryData] = useState(null); // { shifts, parcelsByDate }
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  // ── Geofence settings from sheet ──────────────────────────────────────────
  const [geofenceSettings, setGeofenceSettings] = useState(GEOFENCE_DEFAULTS);
  const [finishZone, setFinishZone]             = useState(FINISH_ZONE_DEFAULTS);
  const [settingsLoaded, setSettingsLoaded]     = useState(false);

  // ── Finish geofence state ────────────────────────────────────────────────
  const [finishGeoState, setFinishGeoState]   = useState('idle'); // idle | checking | ok | outside | denied | error
  const [finishGeoDistance, setFinishGeoDistance] = useState(null);
  const [finishGpsCoords, setFinishGpsCoords] = useState(null); // { lat, lng } recorded at finish

  // ── Load geofence settings from sheet ────────────────────────────────────
  // ── PWA manifest swap ───────────────────────────────────────────────────────
  // While the driver portal is open, point the <link rel="manifest"> at the
  // driver manifest (start_url=/driver-log) so "Add to Home Screen" installs an
  // app that launches into the portal — not the engine's default /dashboard.
  // Also retitle the iOS home-screen app. Restored on unmount.
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    const prevManifest = link ? link.getAttribute('href') : null;
    if (link) link.setAttribute('href', '/manifest-driver.json');

    const titleMeta = document.querySelector('meta[name="apple-mobile-web-app-title"]');
    const prevTitle = titleMeta ? titleMeta.getAttribute('content') : null;
    if (titleMeta) titleMeta.setAttribute('content', 'HP Driver');

    return () => {
      if (link && prevManifest) link.setAttribute('href', prevManifest);
      if (titleMeta && prevTitle) titleMeta.setAttribute('content', prevTitle);
    };
  }, []);

  // ── New-version detection ──────────────────────────────────────────────────
  // Phones can keep running an old cached bundle long after a deploy (this hid
  // the Tasks tab from a newly-assigned DoE driver). Compare the bundle name in
  // a freshly-fetched app shell against the one actually running: auto-reload
  // once while still on the login screen, otherwise show a tap-to-update banner
  // (a reload is safe mid-shift — state is in localStorage — but shouldn't
  // happen under the driver without warning).
  const [updateAvailable, setUpdateAvailable] = useState(false);
  useEffect(() => {
    const running = ([...document.scripts].map(x => x.src).find(src => /\/static\/js\/main\.[^/]*\.js/.test(src)) || '').match(/main\.[^/]*\.js/)?.[0];
    if (!running) return;
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch(window.location.pathname, { cache: 'no-store', headers: { Accept: 'text/html' } });
        const html = await res.text();
        const latest = html.match(/main\.[^"'/]*\.js/)?.[0];
        if (cancelled || !latest || latest === running) return;
        const authed = !!localStorage.getItem(SESSION_KEY);
        const alreadyTried = sessionStorage.getItem('hp-update-reload') === latest;
        if (!authed && !alreadyTried) {
          // Not logged in — safe to refresh silently (once per new version).
          try { sessionStorage.setItem('hp-update-reload', latest); } catch {}
          window.location.reload();
        } else {
          setUpdateAvailable(true);
        }
      } catch {}
    };
    check();
    const t = setInterval(check, 10 * 60 * 1000);
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancelled = true; clearInterval(t); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  // The one-shot fetch at load can fail silently on a flaky mobile connection,
  // stranding the driver on the hardcoded defaults (wrong radius, QR flag and —
  // worst — depot pins). Track whether live settings ever arrived so every
  // geofence re-check can retry the fetch until they do.
  const settingsLiveRef = useRef(false);
  const loadGeofenceSettings = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/geofence-settings');
      const data = await res.json();
      if (data.success) {
        settingsLiveRef.current = true;
        // The QR gate is split by audience. This page only honours the DRIVER gate
        // (`driverQrRequired`) — the sorter gate must never lock out the driver fleet.
        // Map it into the `qrRequired` field the rest of this page already keys on.
        const driverSettings = { ...data.settings, qrRequired: !!data.settings.driverQrRequired };
        setGeofenceSettings(driverSettings);
        if (data.settings.finishZone) setFinishZone(data.settings.finishZone);
        return driverSettings;
      }
    } catch { /* offline / cold start — checkGeofence retries */ }
    return null;
  }, []);

  useEffect(() => {
    loadGeofenceSettings().finally(() => setSettingsLoaded(true));
  }, [loadGeofenceSettings]);

  // ── Tick every 30s ────────────────────────────────────────────────────────
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // Refresh the sheet-driven DoE assignments (DOE_POINTS_LIVE). Called at login,
  // whenever the Tasks tab opens, and before the finish close-out gate — so a
  // permanent driver swap made mid-session still reaches an already-open portal.
  // Never rejects; on failure the current (or hardcoded fallback) list stands.
  const refreshDoeAssignments = useCallback(async () => {
    try {
      const res = await fetch('/.netlify/functions/doe-assign-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getDoEAssignments' }),
      });
      const d = await res.json();
      const pts = (d && d.points) || [];
      if (pts.length) { DOE_POINTS_LIVE = pts; bumpDoeAssignVersion(v => v + 1); }
    } catch {}
  }, []);

  // ── Load depot roster + today's DoE confirmations once driver is authed ────
  // Drives the Tasks-tab visibility, the Depot Bags gate, and the close-out gate.
  useEffect(() => {
    const driver = shift?.driver;
    if (authState !== 'authed' || !driver) return;
    const today = localDateStr();

    fetch('/.netlify/functions/depot-roster-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getDepotRoster' }),
    })
      .then(r => r.json())
      .then(d => {
        const active = (d && d.activeLogins) || [];
        setDepotActive(active.map(x => String(x).toUpperCase()).includes(driver.toUpperCase()));
      })
      .catch(() => {});

    // Load the live (sheet-driven) DoE assignments FIRST so permanent driver
    // swaps apply before the schedule/relief logic runs, then today's relief —
    // the effective DoE schedule decides whether to load confirmations (covers
    // relief drivers not in the base assignments).
    refreshDoeAssignments()
      .finally(() => {
        fetch('/.netlify/functions/doe-assign-sb', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getDoERelief', date: today }),
        })
          .then(r => r.json())
          .then(d => {
            const relief = (d && d.rows) || [];
            setDoeRelief(relief);
            if (effectiveDoESchools(driver, today, relief).length > 0) {
              return fetch('/.netlify/functions/doe-sb', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'getDoEConfirmations', driver, date: today }),
              })
                .then(r => r.json())
                .then(d2 => setDoeConfirmed(new Map(((d2 && d2.rows) || []).map(r => [r.school, { collected: (r.quantity || 0) > 0, reason: r.reason || '' }]))));
            }
            setDoeConfirmed(new Map());
          })
          .catch(() => { setDoeRelief([]); setDoeConfirmed(new Map()); });
      });
  }, [authState, shift?.driver, refreshDoeAssignments]);

  // Opening the Tasks tab re-checks the assignments, so a permanent swap made
  // while the portal is already open shows up without a reload.
  useEffect(() => {
    if (portalTab === 'tasks' && authState === 'authed') refreshDoeAssignments();
  }, [portalTab, authState, refreshDoeAssignments]);

  // ── Restore saved session ─────────────────────────────────────────────────
  useEffect(() => {
    const today = localDateStr();
    let restoredFromLocal = false;
    let savedState = null, savedDriver = null, savedSession = null;
    try {
      const saved = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
      savedSession = saved;
      if (saved && saved.date === today) {
        savedState = saved.shiftState || 'ready';
        savedDriver = saved.driver || null;
        setShift(saved);
        setShiftState(savedState);
        if (saved.odoStart) setOdoStart(String(saved.odoStart));
        if (saved._sub)     setExistingSub(saved._sub);
        setAuthState('authed');
        restoredFromLocal = true;
        // A reading photographed but not yet confirmed before the page reloaded.
        if (!saved.odoStart) {
          const pending = readPendingOdo();
          if (pending) { setOdoOcrResult(pending); setStartOdoCapturing(true); }
        }
      }
    } catch {}

    // Reconcile with the server whenever we DON'T already hold a started shift
    // locally — i.e. nothing saved, OR only a pre-start 'ready' session. This
    // adopts an in-progress shift that's backed up server-side (e.g. after the
    // update-banner reload, or if the local session was lost/stale), so a driver
    // who'd logged on isn't bounced back to "ready" or the login screen.
    if (!restoredFromLocal || savedState === 'ready') {
      const loginHint = localStorage.getItem('hp-driver-login') || savedDriver;
      if (loginHint) recoverActiveShift(loginHint, today, savedSession);
    }

    // Restore QR verification if cached for today
    try {
      const qrCached = JSON.parse(localStorage.getItem(QR_SESSION_KEY) || 'null');
      if (qrCached && qrCached.date === today && qrCached.depot) {
        setQrDepot(qrCached.depot);
        setQrState('verified');
      }
    } catch {}

    // Ask the server whether this session is still allowed. The portal restores itself
    // from localStorage without checking anything, so a driver retired while signed in
    // would sit here looking logged in with every call failing underneath him. Retiring
    // someone now revokes their cookie server-side, and this turns that into a clean
    // sign-out with a reason instead of a portal that just stops working.
    //
    // Only a definite 403 signs anyone out — an offline phone or a hiccup must never
    // throw a working driver back to the login screen mid-shift.
    if (restoredFromLocal) {
      fetch('/.netlify/functions/portal-me', { method: 'POST', credentials: 'include', body: '{}' })
        .then((r) => (r.status === 403 ? r.json().catch(() => ({})) : null))
        .then((d) => {
          if (!d) return;
          logout();
          // Deliberately generic: this same 403 covers an ordinary expired cookie. The
          // specific "your login has been closed" belongs at the door, in portal-auth,
          // where we know it's a retired driver and not just a stale session.
          setLoginError('Your session has ended. Please sign in again.');
        })
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  const persist = useCallback((s, st, sub) => {
    try {
      const withOdo = { ...s, odoStart: s.odoStart || odoStartRef.current || '' };
      localStorage.setItem(SESSION_KEY, JSON.stringify({ ...withOdo, shiftState: st, _sub: sub ?? null }));
    } catch {}
  }, []);

  // Keep the typed start odometer in the saved session even before Start Shift is
  // pressed, so a reload on the pre-shift screen doesn't lose the start km.
  useEffect(() => {
    if (authState === 'authed' && shiftState === 'ready' && shift?.driver) {
      persist({ ...shift, odoStart }, 'ready');
    }
  }, [odoStart, authState, shiftState, shift, persist]);

  // Load the driver's default (Fleet) vehicle so the shift page can show it and
  // the end-of-shift kms / vehicle-swap know the starting rego.
  useEffect(() => {
    const drv = shift?.driver;
    if (authState !== 'authed' || !drv) return;
    fetch('/.netlify/functions/appsscript', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getMyVan', login: drv }),
    }).then(r => r.json()).then(d => {
      if (d && d.success && d.vehicle && d.vehicle.rego) {
        // latestOdo comes through so the odometer fields can check what's typed
        // against what this van last read (see utils/odoCheck).
        setShiftVehicle({
          rego: d.vehicle.rego, make: d.vehicle.make || '', model: d.vehicle.model || '',
          lastOdo: (d.latestOdo && d.latestOdo.odometer) || 0,
          lastOdoDate: (d.latestOdo && d.latestOdo.date) || '',
        });
      }
    }).catch(() => {});
  }, [authState, shift?.driver]);

  // Pending-lease count for the My Van tab red dot (cleared once signed/declined).
  useEffect(() => {
    if (authState !== 'authed' || !shift?.driver) { setLeasePending(0); return; }
    let alive = true;
    fetch('/.netlify/functions/vehicle-contract-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getMyContracts' }),
    }).then(r => r.json()).then(d => {
      if (alive && d && d.success) setLeasePending((d.contracts || []).length);
    }).catch(() => {});
    return () => { alive = false; };
  }, [authState, shift?.driver]);

  // Accident reports the office has asked more about — My Van red dot (cleared on answer).
  useEffect(() => {
    if (authState !== 'authed' || !shift?.driver) { setAccidentPending(0); return; }
    let alive = true;
    fetch('/.netlify/functions/driver-records-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'listDriverRecords', recordType: 'accident' }),
    }).then(r => r.json()).then(d => {
      if (alive && d && d.success) setAccidentPending((d.records || []).filter(openRequest).length);
    }).catch(() => {});
    return () => { alive = false; };
  }, [authState, shift?.driver]);

  // Pending detail-request for the My Shift tab red dot (cleared once submitted).
  useEffect(() => {
    if (authState !== 'authed' || !shift?.driver) { setDetailsPending(0); return; }
    let alive = true;
    fetch('/.netlify/functions/driver-details-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getMyRequest' }),
    }).then(r => r.json()).then(d => {
      if (alive && d && d.success) setDetailsPending(d.request ? 1 : 0);
    }).catch(() => {});
    return () => { alive = false; };
  }, [authState, shift?.driver]);

  // Outstanding scanner asset check for the Scanner tab red dot. The card itself
  // reports the live count once opened; this is what makes the driver open it.
  useEffect(() => {
    if (authState !== 'authed' || !shift?.driver) { setAssetCheck(0); return; }
    let alive = true;
    fetch('/.netlify/functions/scanners-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'myAudit' }),
    }).then(r => r.json()).then(d => {
      if (alive && d && d.success) setAssetCheck(d.request && d.request.status !== 'done' ? 1 : 0);
    }).catch(() => {});
    return () => { alive = false; };
  }, [authState, shift?.driver]);

  // Unopened-document count for the My Docs tab red dot (cleared once opened).
  useEffect(() => {
    if (authState !== 'authed' || !shift?.driver) { setDocsUnread(0); return; }
    let alive = true;
    fetch('/.netlify/functions/driver-docs-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getMyDocs' }),
    }).then(r => r.json()).then(d => {
      if (alive && d && d.success) {
        setDocsUnread(d.unread || 0);
        setFinesUnread((d.documents || []).filter(x => x.category === 'Fines' && !x.pay && x.status !== 'opened').length);
        setTollsToPay((d.documents || []).filter(x => x.pay && !(x.pay.payment && (x.pay.payment.status === 'paid' || x.pay.payment.status === 'check'))));
        // Inclusive Sydney calendar days — shown the whole window, read or not.
        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Sydney' });
        setDocBanners((d.documents || []).filter(x => x.homeBanner && x.homeBanner.from <= today && today <= x.homeBanner.to));
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, [authState, shift?.driver, portalTab]);

  // Outstanding-retraining count for the Training tab red dot (cleared once passed).
  // Prefetched so the badge shows before the driver opens the tab.
  useEffect(() => {
    if (authState !== 'authed' || !shift?.driver) { setTrainingDue(0); return; }
    let alive = true;
    fetch('/.netlify/functions/driver-training-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getMyTraining' }),
    }).then(r => r.json()).then(d => {
      if (alive && d && d.success) { setTrainingDue(d.outstanding || 0); }
    }).catch(() => {});
    return () => { alive = false; };
  }, [authState, shift?.driver]);

  // DMS attention count for the My Docs tab red dot. Populated proactively (before
  // the driver opens My Docs) and counted REQUIRED-only, matching what the on-file
  // section actually shows — non-required docs are hidden, so they never nag.
  useEffect(() => {
    if (authState !== 'authed' || !shift?.driver) { setDmsAttention(0); return; }
    let alive = true;
    fetch('/.netlify/functions/driver-dms-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getMyDms' }),
    }).then(r => r.json()).then(d => {
      if (!alive || !d || !d.success) return;
      const attn = (d.documents || []).filter(x => x.required && (x.renewalRequested || x.status === 'expired' || x.status === 'expiring')).length;
      setDmsAttention(attn);
    }).catch(() => {});
    return () => { alive = false; };
  }, [authState, shift?.driver]);

  // ── Server-side session backup (fire-and-forget) ──────────────────────────
  // Saves active shift state to Apps Script so it can be recovered if
  // the browser clears localStorage mid-shift.
  const backupSession = useCallback((s, st) => {
    if (!s?.driver || !s?.date) return;
    fetch('/.netlify/functions/appsscript', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action:     'saveShiftSession',
        driver:     s.driver,
        date:       s.date,
        name:       s.name || s.driver,
        shiftStart: s.shiftStart || '',
        odoStart:   s.odoStart || odoStartRef.current || '',
        vehicleRego: s.vehicleRego || '',
        breakStart: s.breakStart || '',
        breakEnd:   s.breakEnd   || '',
        breaksJson: JSON.stringify(getBreaks(s)),
        shiftState: st,
      }),
    }).catch(() => {}); // silent — localStorage is primary
  }, []);

  // ── Periodic re-sync ──────────────────────────────────────────────────────
  // Re-back-up the running shift to the server every few minutes while on shift.
  // Self-heals a failed start-of-shift backup (otherwise a driver who logged on
  // through a signal blip and never took a break would stay invisible on Log-On
  // Check all day). shiftRef always holds the latest shift for the interval.
  const shiftRef = useRef(shift);
  useEffect(() => { shiftRef.current = shift; }, [shift]);
  useEffect(() => {
    if (authState !== 'authed' || !(shiftState === 'on_shift' || shiftState === 'on_break')) return undefined;
    const id = setInterval(() => {
      const s = shiftRef.current;
      if (s && s.driver && s.date) backupSession(s, shiftState);
    }, 180000); // every 3 min
    return () => clearInterval(id);
  }, [authState, shiftState, backupSession]);

  // ── Restore an in-progress shift from the Active_Shifts server backup ──────
  // Used on page load when localStorage is empty AND after a manual
  // logout→login (previously only the page-load path existed, so logging out
  // mid-shift lost the running shift). Returns true if a shift was restored.
  async function recoverActiveShift(driver, today, hint) {
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getShiftSession', driver, date: today }),
      });
      const data = await res.json();
      if (!(data.success && data.session && data.session.shiftStart)) return false;
      // The server shift backup doesn't store the driver's team, so preserve whatever
      // the last local session held — otherwise a mid-shift reload wipes route/routes
      // and the Time Away tab wrongly shows "not set up for your area". (The Time Away
      // tab also re-fetches the team from /portal-me as a further backstop.)
      const prevSaved = hint || (() => { try { return JSON.parse(localStorage.getItem(SESSION_KEY) || 'null'); } catch { return null; } })();
      const s = {
        driver:     data.session.driver,
        name:       data.session.name,
        route:      prevSaved?.route || '',
        routes:     prevSaved?.routes || (prevSaved?.route ? [prevSaved.route] : []),
        date:       data.session.date,
        shiftStart: data.session.shiftStart,
        odoStart:   data.session.odoStart || '',  // carry it so later saves don't drop it
        vehicleRego: data.session.vehicleRego || '', // current van (set if swapped mid-shift)
        breakStart: data.session.breakStart || null,
        breakEnd:   data.session.breakEnd   || null,
        breaks:     (() => {
          try { const b = JSON.parse(data.session.breaksJson || data.session.breaks || 'null'); if (Array.isArray(b)) return b; } catch {}
          return data.session.breakStart ? [{ start: data.session.breakStart, end: data.session.breakEnd || null }] : [];
        })(),
      };
      const st = data.session.shiftState || 'on_shift';
      setShift(s);
      setShiftState(st);
      if (data.session.odoStart) setOdoStart(String(data.session.odoStart));
      setAuthState('authed');
      // Re-persist to localStorage so next load is instant
      try {
        localStorage.setItem(SESSION_KEY, JSON.stringify({ ...s, shiftState: st, _sub: null }));
        localStorage.setItem('hp-driver-login', s.driver);
      } catch {}
      return true;
    } catch {
      return false;
    }
  }

  // ── Fetch existing submission for today ───────────────────────────────────
  async function checkExistingSubmission(driver, date) {
    try {
      const res  = await fetch(`/.netlify/functions/driver-logs?driver=${encodeURIComponent(driver)}&date=${date}`);
      const data = await res.json();
      if (!data.success || !data.rows.length) return null;
      // Find the most recent non-superseded row
      return data.rows.find(r => r.status !== 'SUPERSEDED') || null;
    } catch {
      return null;
    }
  }

  // ── Login ─────────────────────────────────────────────────────────────────
  async function handleLogin(e) {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const res  = await fetch('/.netlify/functions/portal-auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: loginForm.login.trim(), pin: loginForm.pin.trim() }),
      });
      const data = await res.json();
      if (!data.ok) {
        setLoginError(data.error || 'Incorrect ID or PIN.');
      } else {
        const today = localDateStr();
        const s = {
          driver: data.login, name: data.name,
          route: data.route || '',
          routes: data.routes || (data.route ? [data.route] : []), // team(s) for Time Away tab
          phone: data.phone || '',                                  // for time-away confirmation SMS
          date: today,
          shiftStart: null, breakStart: null, breakEnd: null,
        };
        setShift(s);
        setAuthState('authed');
        // Save login hint so server recovery can identify the driver on reload
        try { localStorage.setItem('hp-driver-login', data.login); } catch {}
        // Show welcome guide on first ever login for this driver
        const welcomed = localStorage.getItem(WELCOME_KEY + '_' + data.login);
        if (!welcomed && !ADMIN_PORTAL_LOGINS.has(data.login)) setShowWelcome(true);

        // Restore QR verification if cached for today
        try {
          const qrCached = JSON.parse(localStorage.getItem(QR_SESSION_KEY) || 'null');
          if (qrCached && qrCached.date === today && qrCached.depot) {
            setQrDepot(qrCached.depot);
            setQrState('verified');
          }
        } catch {}

        // Check if there's already a submission for today
        const active = await checkExistingSubmission(data.login, today);
        if (active) {
          setExistingSub(active);
          const st = statusToShiftState(active.status);
          setShiftState(st);
          persist(s, st, active);
        } else {
          // No submission yet — restore an in-progress shift from the server
          // backup (covers logout→login mid-shift; without this the running
          // shift start time was lost and had to be restarted).
          const recovered = await recoverActiveShift(data.login, today, s);
          if (!recovered) {
            setShiftState('ready');
            persist(s, 'ready');
          }
        }
      }
    } catch {
      setLoginError('Could not connect. Check your internet connection and try again.');
    } finally {
      setLoginLoading(false);
    }
  }

  // ── Odometer OCR ─────────────────────────────────────────────────────────
  // The photo is re-encoded and downscaled BEFORE it leaves the phone — the same
  // rule fileToB64 already applies to every other camera in this page, and the one
  // camera that had been left sending the raw file. Sending a 12 MP iPhone JPEG
  // whole broke this three ways at once (Ben Pena, 27 Aug 2026 — photo taken, the
  // screen went blank, the reading never stuck):
  //   1. memory — a ~4 MB file read through FileReader becomes a ~5.5 MB data URL,
  //      a second copy after the split and a third inside JSON.stringify. That spike,
  //      right as iOS hands the tab back from the camera, is enough for Safari to
  //      discard the page. It reloads blank and the un-typed reading is gone.
  //   2. size — if the tab survived, the body could clear Netlify's 6 MB function
  //      limit, and a 413's body isn't JSON, so resp.json() threw and the driver was
  //      told "connection error" for a photo that was fine.
  //   3. rotation — the raw file carries iPhone EXIF orientation, so a portrait shot
  //      reached the model sideways and came back UNCLEAR.
  // 1600px at q0.8 is already more than the model resolves, and ~200 KB on the wire.
  async function handleOdoPhoto(e, which) {
    const file = e.target.files?.[0];
    if (!file) return;
    setOdoOcrLoading(true);
    setOdoOcrError('');
    setOdoOcrResult(null);
    try {
      // Downscale first and unconditionally — fileToB64 decides by file.type, and a
      // camera file can arrive with an empty type, which would drop it onto the raw
      // path this fix exists to avoid. It stays as the fallback for the browsers where
      // createImageBitmap isn't available.
      const ab = await uprightJpegBytes(file, 1600, 0.8);
      const { base64, contentType } = ab
        ? { base64: abToB64(ab), contentType: 'image/jpeg' }
        : await fileToB64(file);
      if (!base64) throw new Error('empty');
      if (base64.length > MAX_ODO_B64) {
        setOdoOcrError('That photo is too large to send — please retake it.');
        return;
      }
      const resp = await fetch('/.netlify/functions/ocr-odometer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageBase64: base64, mediaType: contentType }),
      });
      // A rejected body (413) or a gateway error answers in HTML, not JSON — read it
      // as text first so the driver gets "couldn't read it", never a parser crash.
      const raw = await resp.text();
      let data = null;
      try { data = JSON.parse(raw); } catch { data = null; }
      if (!data) {
        setOdoOcrError(resp.status === 413
          ? 'That photo was too large to send — please retake it.'
          : 'Could not read the photo — please try again or enter it manually.');
        return;
      }
      if (data.success) {
        setOdoOcrResult(data.reading);
        if (which === 'start') stashPendingOdo(data.reading);
      } else {
        setOdoOcrError(data.unclear ? 'Could not read the number clearly — please try again or enter it manually.' : (data.error || 'Read failed'));
      }
    } catch {
      setOdoOcrError('Connection error — please try again.');
    } finally {
      setOdoOcrLoading(false);
    }
  }

  function confirmOdoReading(which) {
    if (which === 'start') {
      // Just populate the field — driver still presses START SHIFT themselves
      setOdoStart(String(odoOcrResult));
      clearPendingOdo();
      setOdoOcrResult(null);
      setOdoOcrError('');
      setStartOdoCapturing(false);
    } else {
      setOdoEnd(String(odoOcrResult));
      setOdoCapturing(false);
      setOdoOcrResult(null);
      setShiftState('finishing'); persist(shift, 'finishing');
      backupSession(shift, 'finishing'); // afternoon check can see they reached the finish screen
    }
  }

  function skipOdoCapture(which) {
    setOdoCapturing(false);
    setOdoOcrResult(null);
    setOdoOcrError('');
    if (which === 'start') {
      clearPendingOdo();
      setStartOdoCapturing(false);
    } else {
      setShiftState('finishing'); persist(shift, 'finishing');
      backupSession(shift, 'finishing');
    }
  }

  // ── Shift control ─────────────────────────────────────────────────────────
  function startShift() {
    // Odometer now captured via text field on pre-shift screen — go straight to shift
    startShiftDirect();
  }

  function startShiftDirect() {
    // Persist the start odometer onto the shift so it survives reloads/recovery and
    // the day's total km (end − start) can always be calculated at finish.
    const s = { ...shift, shiftStart: new Date().toISOString(), odoStart };
    setShift(s); setShiftState('on_shift'); persist(s, 'on_shift');
    backupSession(s, 'on_shift');
    // Audit the opening odometer against the van (non-fatal).
    const oStart = Number(odoStart) || 0;
    if (oStart > 0) {
      fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'recordShiftStartKms', driver: s.driver, date: s.date, odometer: oStart, rego: s.vehicleRego || shiftVehicle?.rego || '' }),
      }).catch(() => {});
    }
    setScannerCapture(null); setScannerDone(false); setScannerSkipped(false);
    setCarryOver(null); setCarrySel({}); setCarryPoAck(false);
  }
  function startBreak() {
    const breaks = [...getBreaks(shift), { start: new Date().toISOString(), end: null }];
    // Mirror legacy fields: breakStart = first break, breakEnd null while on break.
    const s = { ...shift, breaks, breakStart: breaks[0].start, breakEnd: null };
    setShift(s); setShiftState('on_break'); persist(s, 'on_break');
    backupSession(s, 'on_break');
  }
  function endBreak() {
    const arr = getBreaks(shift);
    const breaks = arr.map((b, i) =>
      (i === arr.length - 1 && !b.end) ? { ...b, end: new Date().toISOString() } : b);
    // Mirror legacy fields: first start / last end for the record + admin display.
    const s = {
      ...shift, breaks,
      breakStart: breaks[0]?.start || '',
      breakEnd:   breaks[breaks.length - 1]?.end || '',
    };
    setShift(s); setShiftState('on_shift'); persist(s, 'on_shift');
    backupSession(s, 'on_shift');
  }
  // Hard DoE close-out gate: if any DoE collection scheduled for this driver today
  // is still unscanned, block finishing the shift until it's done in the Tasks tab.
  // Returns true when blocked (caller should stop). Re-fetches so the check is fresh.
  async function doeBlocksFinish() {
    const driver = shift?.driver;
    if (!driver) return false;
    const today    = localDateStr();
    // Re-pull the permanent assignments so a swap made during the shift still
    // gates (or releases) the right driver at finish time.
    await refreshDoeAssignments();
    const schedule = effectiveDoESchools(driver, today, doeRelief);
    if (schedule.length === 0) return false;

    let confirmed = doeConfirmed;
    try {
      const res = await fetch('/.netlify/functions/doe-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getDoEConfirmations', driver, date: today }),
      });
      const d = await res.json();
      if (d && d.success) {
        confirmed = new Map((d.rows || []).map(r => [r.school, { collected: (r.quantity || 0) > 0, reason: r.reason || '' }]));
        setDoeConfirmed(confirmed);
      }
    } catch { /* fall back to in-memory state */ }

    const outstanding = schedule.filter(p => !(confirmed && confirmed.has(p.school)));
    if (outstanding.length === 0) return false;

    const names = outstanding.map(p => p.school).join(', ');
    setFinishBlockMsg(`Display & confirm the DoE collection for ${names} in the Tasks tab before finishing your shift.`);
    setPortalTab('tasks');
    return true;
  }

  async function goToFinish() {
    setFinishBlockMsg('');
    // Belt-and-braces: ensure the start odometer is populated before the finish
    // screen so drivers never have to re-enter it. If state was lost (e.g. iOS
    // killed the tab mid-shift), recover from the shift object / ref / localStorage.
    if (!odoStart) {
      let recovered = shift?.odoStart || odoStartRef.current || '';
      if (!recovered) {
        try {
          const saved = JSON.parse(localStorage.getItem(SESSION_KEY) || 'null');
          if (saved && saved.date === shift?.date && saved.odoStart) recovered = saved.odoStart;
        } catch {}
      }
      if (recovered) setOdoStart(String(recovered));
    }
    if (await doeBlocksFinish()) return;
    // Odo-optional drivers skip the odometer-photo prompt and go straight to the
    // finishing form (where the km fields are optional).
    const toOdoOrFinish = () => {
      if (odoOptional) { setShiftState('finishing'); persist(shift, 'finishing'); backupSession(shift, 'finishing'); }
      else { setOdoOcrResult(null); setOdoOcrError(''); setOdoCapturing('finish'); }
    };
    if (finishGeoExempt || !finishZone.finishZoneEnabled) {
      toOdoOrFinish();
      return;
    }
    setFinishGeoState('checking');
    navigator.geolocation.getCurrentPosition(
      pos => {
        const { latitude, longitude } = pos.coords;
        const dist = haversineDistance(latitude, longitude, finishZone.finishZoneLat, finishZone.finishZoneLng);
        const coords = { lat: latitude, lng: longitude };
        setFinishGpsCoords(coords);
        setFinishGeoDistance(Math.round(dist));
        if (dist <= finishZone.finishZoneRadiusM) {
          setFinishGeoState('ok');
          toOdoOrFinish();
        } else {
          setFinishGeoState('outside');
        }
      },
      err => setFinishGeoState(err.code === 1 ? 'denied' : 'error'),
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }

  // ── Carry-over parcels helpers ────────────────────────────────────────────
  function toggleCarryReason(key) {
    setCarrySel(prev => {
      const next = { ...prev };
      if (next[key]) delete next[key];
      else next[key] = { count: key === 'missing' ? '1' : '', note: '', photoUrls: [], uploading: false, photoError: '', consignment: '', articleId: '', actionTaken: '' };
      return next;
    });
    if (key === 'address') setCarryPoAck(false);
    setSubmitError('');
  }
  function updateCarry(key, patch) {
    setCarrySel(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }
  // Photo goes to the same Drive store as scanner photos (visible on the admin
  // Scanner Photos page), tagged with the carry-over reason in the note.
  async function uploadCarryPhoto(key, label, file) {
    updateCarry(key, { uploading: true, photoError: '' });
    try {
      // A barcode shot is kept sharper so the OCR can still read the long article number.
      const base64 = await compressImage(file, key === 'missing' ? 900 : 350);
      const res = await fetch(SCANNER_API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'upload', driver: shift.driver, driverName: shift.name, date: localDateStr(), imageData: base64, contentType: 'image/jpeg', note: `Carry-over: ${label}` }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'upload failed');
      // Store the STABLE storage path (fileId), not the 1h signed viewUrl — the
      // carry-over record is re-signed server-side at read/email time, so the AP
      // report link never expires (older builds baked in a viewUrl that died in 1h).
      const ref = data.metadata?.fileId || (data.key ? data.key + '.jpg' : '') || data.metadata?.viewUrl || 'uploaded';
      const read = key === 'missing' ? parseScannerBarcode(data.metadata?.ocrText) : {};
      setCarrySel(prev => {
        const cur = prev[key] || {};
        return { ...prev, [key]: {
          ...cur, uploading: false, photoError: '', photoUrls: [...(cur.photoUrls || []), ref],
          // Fill only what's still empty: never overwrite what the driver typed.
          consignment: cur.consignment || read.consignment || '',
          articleId: cur.articleId || read.articleId || '',
        } };
      });
    } catch (e) {
      // Name the actual fault: a phone that can't re-encode the photo is not a
      // signal problem, and telling the driver to move somewhere with bars sends
      // them chasing the wrong thing.
      updateCarry(key, { uploading: false, photoError: 'Could not send the photo — ' + (e.message || 'check your signal') + '. Try again.' });
    }
  }

  // ── Submit new shift ──────────────────────────────────────────────────────
  // Change vehicle mid-shift (breakdown / hire replacement) without losing the
  // start time. Records the new van + its current odometer (new km baseline).
  async function submitVehicleChange() {
    const rego = (vehChangeRego || '').trim().toUpperCase();
    const odo  = Number(vehChangeOdo) || 0;
    if (!rego) return;
    setVehChanging(true);
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'changeShiftVehicle', driver: shift.driver, date: shift.date, rego, odometer: odo, by: shift.driver }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Could not change vehicle');
      // New van becomes the current vehicle; its odometer is the new km baseline.
      const updated = { ...shift, vehicleRego: rego, ...(odo > 0 ? { odoStart: odo } : {}) };
      setShift(updated);
      setShiftVehicle({ rego, make: '', model: '', lastOdo: odo > 0 ? odo : 0, lastOdoDate: shift?.date || '' });
      if (odo > 0) { setOdoStart(String(odo)); odoStartRef.current = String(odo); setOdoStartKept(false); }
      persist(updated, shiftState, existingSub);
      setShowVehChange(false); setVehChangeRego(''); setVehChangeOdo('');
    } catch (e) {
      alert(e.message);
    } finally {
      setVehChanging(false);
    }
  }

  async function submitShift() {
    const oStart = Number(odoStart) || 0;
    const oEnd   = Number(odoEnd)   || 0;
    if (odoOptional) {
      // Odometer not required. But if either reading is entered (conveying
      // parcels), require both and that finish ≥ start.
      if ((odoStart || odoEnd) && (!odoStart || !odoEnd)) {
        setSubmitError('Enter both start and finish km, or leave both blank.'); return;
      }
      if (odoStart && odoEnd && oEnd < oStart) {
        setSubmitError('Finish odometer must be at least the start odometer.'); return;
      }
    } else {
      if (!odoStart || !odoEnd) { setSubmitError('Please enter both odometer readings.'); return; }
      if (!oStart || !oEnd)     { setSubmitError('Odometer readings must be numbers.'); return; }
      if (oEnd <= oStart)       { setSubmitError('End odometer must be greater than start odometer.'); return; }
    }
    if (oStart > 0 && oEnd > oStart && (oEnd - oStart) > MAX_DAILY_KM) {
      setSubmitError(`That works out to ${(oEnd - oStart).toLocaleString()} km in one shift, which can't be right. ` +
        'Please re-check the digits of your finish odometer reading.');
      return;
    }
    // Short of the outright refusal above, a day longer than any round goes is
    // queried rather than blocked — the driver answers the amber prompt on the
    // readings and submits either way (utils/odoCheck).
    if (odoEndBlocked) {
      setSubmitError(odoEndChk.message);
      return;
    }
    if (!scannerSatisfied) {
      setSubmitError('Photograph (or upload a screenshot of) your scanner screen before submitting — it’s required.');
      scannerSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (!carrySatisfied) {
      setSubmitError(carryOver === null
        ? 'Answer the carry-over parcels question before submitting.'
        : 'Complete the carry-over parcels report — each reason needs a parcel count' +
          (carryEntries.some(r => r.photo && !(carrySel[r.key].photoUrls && carrySel[r.key].photoUrls.length > 0)) ? ', photo' : '') +
          (carrySel.address && !carryPoAck ? ', and the Post Office transfer tick' : '') + '.');
      carrySectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true); setSubmitError('');

    // Single-button flow: send the held scanner photo first, then finalise the shift.
    // If the upload fails we stop here — we never finalise a shift without its photo.
    if (!scannerDone && scannerCapture) {
      try {
        const base64 = scannerCapture.file
          ? await compressImage(scannerCapture.file)
          : (scannerCapture.dataUrl.split(',')[1] || '');
        const up = await fetch(SCANNER_API, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'upload', driver: shift.driver, driverName: shift.name, date: localDateStr(), imageData: base64, contentType: 'image/jpeg', note: 'End of shift' }),
        });
        const upData = await up.json();
        if (!upData.ok) throw new Error(upData.error || 'upload failed');
        setScannerDone(true); setScannerCapture(null);
      } catch (e) {
        setSubmitError('Could not send your scanner photo — ' + (e.message || 'check your signal') +
          '. Your shift was NOT submitted. Retake it, or take it in your own Camera app and use “Upload / screenshot”.');
        setSubmitting(false);
        return;
      }
    }

    const finishISO  = new Date().toISOString();
    const totalKm    = (oStart > 0 && oEnd >= oStart) ? oEnd - oStart : 0;
    const shiftMs    = new Date(finishISO) - new Date(shift.shiftStart);
    // Sum ALL completed breaks (multiple breaks supported).
    const breaks     = getBreaks(shift).filter(b => b.start && b.end);
    const breakMs    = sumBreakMs(breaks);
    const totalHours = Math.round((Math.max(0, shiftMs - breakMs) / 3600000) * 100) / 100;
    const breakMinutes = Math.round(breakMs / 60000);

    // Legacy display columns: first break start / last break end (the span).
    const bStart = breaks.length ? formatTimeStr(breaks[0].start) : '';
    const bEnd   = breaks.length ? formatTimeStr(breaks[breaks.length - 1].end) : '';

    try {
      const payload = {
        action: 'logDriverShift',
        driver: shift.driver, name: shift.name, date: shift.date,
        shiftStart: formatTimeStr(shift.shiftStart),
        breakStart: bStart, breakEnd: bEnd,
        breakCount: breaks.length, breakMinutes,
        finishTime: formatTimeStr(finishISO),
        odoStart: oStart, odoEnd: oEnd, totalKm, totalHours,
        submittedAt: finishISO,
        finishLat: finishGpsCoords?.lat || '',
        finishLng: finishGpsCoords?.lng || '',
        vehicleRego: shift.vehicleRego || shiftVehicle?.rego || '',
        // A shift finished without its required photo says so ON the shift, with
        // the phone's own reason, so the office can chase the photo rather than
        // discovering the gap weeks later on a report.
        ...(scannerNoPhoto ? { managerNote: NO_PHOTO_PREFIX + (scannerFailure || 'camera would not take the photo') } : {}),
      };
      const res  = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Submission failed. Please try again.');

      // Record the carry-over parcels report (non-fatal — the shift is already logged).
      const carryRows = carryOver === 'yes'
        ? carryEntries.map(r => {
            const urls = carrySel[r.key].photoUrls || [];
            const e = carrySel[r.key];
            const ids = r.incident ? [e.consignment, e.articleId].map(x => (x || '').trim()).filter(Boolean).join(' / ') : '';
            return {
              reason: r.label,
              count: Number(e.count) || 0,
              note: (ids ? ids + ': ' : '') + (e.note || '').trim(),
              photoUrl: urls[0] || '',
              photoUrls: urls,
            };
          })
        : [{ reason: 'None — all delivered/transferred', count: 0, note: '', photoUrl: '', photoUrls: [] }];
      fetch('/.netlify/functions/carryover-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'saveCarryOver', driver: shift.driver, name: shift.name, date: shift.date, rows: carryRows, submittedAt: finishISO }),
      }).catch(() => {});
      // Missing parcel → incident report for the AP email (the carry_over row above is the fallback).
      if (carryOver === 'yes' && carrySel.missing) {
        const m = carrySel.missing;
        fetch('/.netlify/functions/carryover-sb', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'reportMissingParcel', date: shift.date, incident: {
            name: shift.name, count: Number(m.count) || 1,
            consignment: (m.consignment || '').trim(), articleId: (m.articleId || '').trim(),
            whatHappened: (m.note || '').trim(), actionTaken: (m.actionTaken || '').trim(),
            photos: (m.photoUrls || []).map(path => ({ path, caption: 'Driver: scanner showing the parcel barcode' })),
          } }),
        }).catch(() => {});
      }

      // Auto-record the closing odometer to the vehicle's log so My Van's
      // next-service-due stays current (non-fatal — the shift is already logged).
      if (oEnd > 0) {
        fetch('/.netlify/functions/appsscript', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'recordShiftEndKms', driver: shift.driver, date: shift.date, odometer: oEnd, rego: shift.vehicleRego || shiftVehicle?.rego || '' }),
        }).catch(() => {});
      }

      // Build the existingSub record to display status screen
      const sub = {
        date: shift.date, driver: shift.driver, name: shift.name,
        shiftStart: formatTimeStr(shift.shiftStart),
        breakStart: bStart, breakEnd: bEnd,
        finishTime: formatTimeStr(finishISO),
        odoStart: oStart, odoEnd: oEnd, totalKm, totalHours,
        submittedAt: finishISO, status: 'PENDING', type: 'ORIGINAL', managerNotes: '',
      };
      setExistingSub(sub);
      const final = { ...shift, finishTime: finishISO, odoStart: oStart, odoEnd: oEnd, totalKm, totalHours };
      setShift(final);
      setShiftState('pending_review');
      persist(final, 'pending_review', sub);
      // Clear server backup — shift is now fully recorded in Driver_Logs
      fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'clearShiftSession', driver: shift.driver, date: shift.date }),
      }).catch(() => {});
    } catch (err) {
      setSubmitError(err.message || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Start amendment ───────────────────────────────────────────────────────
  function startAmendment() {
    if (!existingSub) return;
    setAmendForm({
      shiftStart:  existingSub.shiftStart  || '',
      breakStart:  existingSub.breakStart  || '',
      breakEnd:    existingSub.breakEnd    || '',
      finishTime:  existingSub.finishTime  || '',
      odoStart:    String(existingSub.odoStart || ''),
      odoEnd:      String(existingSub.odoEnd   || ''),
      reason:      '',
    });
    setAmendError('');
    setShiftState('amending');
    persist(shift, 'amending', existingSub);
  }

  // ── Submit amendment ──────────────────────────────────────────────────────
  async function submitAmendment() {
    const { shiftStart, finishTime, odoStart: aOdoStart, odoEnd: aOdoEnd, reason } = amendForm;
    if (!shiftStart || !finishTime) { setAmendError('Shift start and finish time are required.'); return; }
    if (!aOdoStart || !aOdoEnd)     { setAmendError('Both odometer readings are required.'); return; }
    const oStart = Number(aOdoStart);
    const oEnd   = Number(aOdoEnd);
    if (!oStart || !oEnd)  { setAmendError('Odometer readings must be valid numbers.'); return; }
    if (oEnd <= oStart)    { setAmendError('End odometer must be greater than start odometer.'); return; }
    if ((oEnd - oStart) > MAX_DAILY_KM) {
      setAmendError(`That works out to ${(oEnd - oStart).toLocaleString()} km in one shift, which can't be right. ` +
        'Please re-check the digits of the finish odometer reading.');
      return;
    }
    // Same day-distance query as the finish screen, so an amendment can't quietly
    // reinstate the readings the original submission was stopped for. An amendment
    // already carries a written reason, so saying so there is the confirmation.
    const amendDayChk = checkDayDistance({ odoStart: aOdoStart, odoEnd: aOdoEnd });
    if (!amendDayChk.ok && !/\bkm\b/i.test(reason)) {
      setAmendError(`${amendDayChk.message} If it is right, say so in your reason (mention "km") and submit again.`);
      return;
    }
    if (!reason.trim())    { setAmendError('Please provide a reason for the amendment.'); return; }

    setAmendSubmitting(true); setAmendError('');
    const submittedAt = new Date().toISOString();
    const totalKm     = oEnd - oStart;
    const totalHours  = calcTotalHoursFromHHMM(shiftStart, finishTime, amendForm.breakStart, amendForm.breakEnd);

    try {
      const payload = {
        action: 'amendDriverShift',
        originalRef: existingSub?.submittedAt || '',
        driver: shift.driver, name: shift.name, date: shift.date,
        shiftStart, breakStart: amendForm.breakStart, breakEnd: amendForm.breakEnd,
        finishTime, odoStart: oStart, odoEnd: oEnd, totalKm, totalHours,
        submittedAt, amendmentReason: reason.trim(),
      };
      const res  = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Amendment failed. Please try again.');

      const newSub = {
        ...payload, status: 'PENDING', type: 'AMENDMENT', managerNotes: '',
        amendmentReason: reason.trim(),
      };
      setExistingSub(newSub);
      setShiftState('amendment_sent');
      persist(shift, 'amendment_sent', newSub);
    } catch (err) {
      setAmendError(err.message || 'Could not submit amendment. Please try again.');
    } finally {
      setAmendSubmitting(false);
    }
  }

  // ── Geofence check ────────────────────────────────────────────────────────
  async function checkGeofence() {
    if (geoExempt) {            // exempt logins skip the GPS prompt entirely
      setGeoState('in_range');
      return;
    }
    setGeoState('checking');
    // If the load-time settings fetch failed we'd otherwise compare against the
    // hardcoded fallback pins forever — retry it on every (re-)check.
    let gs = geofenceSettings;
    if (!settingsLiveRef.current) {
      const fresh = await loadGeofenceSettings();
      if (fresh) gs = fresh;
    }
    if (!gs.geofenceEnabled) {
      setGeoState('in_range');
      return;
    }
    if (!navigator.geolocation) {
      setGeoState('error');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        // Merge any admin-saved coordinate overrides over the hardcoded defaults
        const activeDepots = DEPOTS.map(d => {
          const pin = gs.depotPins?.[d.name.toLowerCase()];
          return pin ? { ...d, lat: pin.lat, lng: pin.lng } : d;
        });
        let nearest = null;
        let minDist = Infinity;
        activeDepots.forEach(d => {
          const dist = haversineDistance(latitude, longitude, d.lat, d.lng);
          if (dist < minDist) { minDist = dist; nearest = d; }
        });
        // Credit the fix its own stated error margin (capped) so a driver at the
        // depot with a coarse fix isn't falsely bounced — but a genuinely-distant
        // fix still fails. We show the raw distance; only the pass/fail is lenient.
        const slack = Math.min(pos.coords.accuracy || 0, GEO_ACCURACY_SLACK_MAX_M);
        setNearestDepot(nearest);
        setGeoDistance(Math.round(minDist));
        setGeoState(minDist - slack <= gs.geofenceRadiusM ? 'in_range' : 'out_of_range');
      },
      (err) => {
        setGeoState(err.code === 1 ? 'denied' : 'error');
      },
      // maximumAge:0 — a manual Re-check must force a fresh fix, not replay the
      // stale cached position that just failed.
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }

  // Auto-check whenever the driver lands on the ready screen
  useEffect(() => {
    if (authState === 'authed' && shiftState === 'ready') {
      checkGeofence();
    }
  }, [authState, shiftState]); // eslint-disable-line

  // Re-check once settings have loaded in case the user restored a session before the fetch completed
  useEffect(() => {
    if (settingsLoaded && authState === 'authed' && shiftState === 'ready') {
      checkGeofence();
    }
  }, [settingsLoaded]); // eslint-disable-line

  // ── QR Override polling ───────────────────────────────────────────────────
  // While driver is stuck on QR screen, poll every 15 seconds for a manager override.
  useEffect(() => {
    if (!geofenceSettings.qrRequired) return; // sorters-only gate; never polls for drivers
    if (authState !== 'authed' || shiftState !== 'ready') return;
    if (qrState === 'verified' || qrState === 'override') return;
    if (!shift?.driver) return;

    const today = localDateStr();
    let cancelled = false;

    async function checkOverride() {
      try {
        const res = await fetch('/.netlify/functions/qr-override', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'check', driver: shift.driver, date: today }),
        });
        const data = await res.json();
        if (!cancelled && data.ok && data.granted) {
          setQrState('override');
          setQrDepot({ name: data.depot || 'Depot', id: 'override' });
          localStorage.setItem(QR_SESSION_KEY, JSON.stringify({ depot: { name: data.depot || 'Depot', id: 'override' }, date: today }));
        }
      } catch {}
    }

    checkOverride(); // immediate check on mount
    const interval = setInterval(checkOverride, 15000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [authState, shiftState, qrState, shift?.driver]); // eslint-disable-line

  // ── QR token verification ─────────────────────────────────────────────────
  async function verifyQrToken(token) {
    setQrState('checking');
    try {
      const res  = await fetch('/.netlify/functions/depot-verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (data.valid && data.depot) {
        setQrDepot(data.depot);
        setQrState('verified');
        localStorage.setItem(QR_SESSION_KEY, JSON.stringify({ depot: data.depot, date: localDateStr() }));
      } else {
        setQrState('invalid');
      }
    } catch {
      setQrState('error');
    }
  }

  // Handle ?dv=TOKEN in URL (QR code scan redirects here)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token  = params.get('dv');
    if (!token) return;
    // Strip the token from the URL immediately (don't leave it in browser history)
    window.history.replaceState({}, '', window.location.pathname);
    // If already verified today, skip network call
    try {
      const qrCached = JSON.parse(localStorage.getItem(QR_SESSION_KEY) || 'null');
      if (qrCached && qrCached.date === localDateStr() && qrCached.depot) {
        setQrDepot(qrCached.depot);
        setQrState('verified');
        return;
      }
    } catch {}
    verifyQrToken(token);
  }, []); // eslint-disable-line

  // ── Logout ────────────────────────────────────────────────────────────────
  function logout() {
    localStorage.removeItem(SESSION_KEY);
    setAuthState('login'); setShift(null); setShiftState('ready');
    setLoginForm({ login: '', pin: '' }); setLoginError('');
    setOdoStart(''); setOdoEnd(''); setSubmitError('');
    setExistingSub(null);
    setAmendForm({ shiftStart:'', breakStart:'', breakEnd:'', finishTime:'', odoStart:'', odoEnd:'', reason:'' });
    setAmendError('');
    setGeoState('idle'); setGeoDistance(null); setNearestDepot(null);
    setQrState('idle'); setQrDepot(null);
    setPortalTab('shift'); setHistoryData(null);
  }

  // ── Load history (with localStorage cache) ───────────────────────────────
  async function loadHistory(driver, force = false) {
    // Cut on the driver's own pay calendar (28-day contractor grid, or the
    // 15th→14th wage run) so the fetched window covers both period buttons.
    const { current, previous } = historyPeriods(driver);
    const end       = current.end;
    const prevStart = previous.start;   // one full period back, so 'Last Period' has data too
    const cacheKey = `hp-history-${driver}-${prevStart}-${end}`;
    const CACHE_TTL = 10 * 60 * 1000; // 10 minutes

    // Show cached data immediately if available and not forcing refresh
    if (!force) {
      try {
        const cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
        if (cached && Date.now() - cached.ts < CACHE_TTL) {
          setHistoryData(cached.data);
          setHistoryLoading(false);
          return;
        }
      } catch {}
    }

    setHistoryLoading(true);
    setHistoryError('');
    try {
      const res  = await fetch(
        `/.netlify/functions/driver-history?driver=${encodeURIComponent(driver)}&periodStart=${prevStart}&periodEnd=${end}`
      );
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Could not load history');
      setHistoryData(data);
      try { localStorage.setItem(cacheKey, JSON.stringify({ data, ts: Date.now() })); } catch {}
    } catch (err) {
      setHistoryError(err.message || 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  }

  useEffect(() => {
    if (portalTab === 'history' && shift?.driver && !historyData && !historyLoading) {
      loadHistory(shift.driver);
    }
  }, [portalTab]); // eslint-disable-line

  // ── Elapsed calculations (supports multiple breaks) ───────────────────────
  const shiftBreaks   = getBreaks(shift);
  const breaksCount   = shiftBreaks.length;
  const openBreak     = shiftBreaks.find(b => b.start && !b.end);
  const completedBreakMs = sumBreakMs(shiftBreaks.filter(b => b.end)); // finished breaks only
  // Current break timer (on_break screen) vs total break taken (on_shift screen)
  const currentBreakElapsed = openBreak ? now - new Date(openBreak.start) : 0;
  const breakElapsed  = openBreak ? currentBreakElapsed : completedBreakMs;
  const totalBreakTaken = completedBreakMs;
  const workedSoFar = shift?.shiftStart
    ? Math.max(0, now - new Date(shift.shiftStart) - completedBreakMs - currentBreakElapsed)
    : 0;
  const kmPreview = odoStart && odoEnd && Number(odoEnd) > Number(odoStart)
    ? Number(odoEnd) - Number(odoStart) : null;

  // ── Odometer sanity (utils/odoCheck) ──────────────────────────────────────
  // The opening reading is checked against what the van last read; the closing one
  // against the opening, because the day's distance is what catches a driver whose
  // readings have been wrong from the very first one. Warnings only — the driver
  // takes the suggestion or confirms their number, and either way carries on.
  const odoStartChk = checkOdometer({
    reading: odoStart,
    lastKnown: shiftVehicle && shiftVehicle.lastOdo,
    lastDate: shiftVehicle && shiftVehicle.lastOdoDate,
    date: shift?.date, rego: shift?.vehicleRego || (shiftVehicle && shiftVehicle.rego),
  });
  const odoStartBlocked = !odoStartChk.ok && !odoStartKept;
  const odoEndChk = checkDayDistance({ odoStart, odoEnd });
  const odoEndBlocked = !odoEndChk.ok && !odoEndKept;
  // Same rule as the van check: a changed reading is a new question, so a
  // confirmation never carries over to a number the driver has since edited.
  useEffect(() => { setOdoStartKept(false); }, [odoStart]);
  useEffect(() => { setOdoEndKept(false); }, [odoEnd]);

  // Amendment preview
  const amendKmPreview = amendForm.odoStart && amendForm.odoEnd &&
    Number(amendForm.odoEnd) > Number(amendForm.odoStart)
    ? Number(amendForm.odoEnd) - Number(amendForm.odoStart) : null;
  const amendHoursPreview = amendForm.shiftStart && amendForm.finishTime
    ? calcTotalHoursFromHHMM(amendForm.shiftStart, amendForm.finishTime, amendForm.breakStart, amendForm.breakEnd)
    : null;

  // ── Task gating (Tasks tab visibility + badge) ────────────────────────────
  const isPortalAdmin   = ADMIN_PORTAL_LOGINS.has(shift?.driver);
  const driverUpper     = (shift?.driver || '').toUpperCase();
  const showSortScan    = SORT_SCAN_LOGINS.has(driverUpper);
  const vanCheckExempt  = VAN_CHECK_EXEMPT.has(driverUpper);   // walkers — no van to check
  const isDoEDriver     = DOE_POINTS_LIVE.some(p => p.school !== '(TBA)' && p.drivers.map(d => d.toUpperCase()).includes(driverUpper));
  const doeSchedule     = shift?.driver ? effectiveDoESchools(shift.driver, localDateStr(), doeRelief) : [];
  const hasDoEToday     = doeSchedule.length > 0;
  const doeOutstanding  = hasDoEToday && doeConfirmed
    ? doeSchedule.some(p => !doeConfirmed.has(p.school))
    : false;
  // DoE drivers always see the Tasks tab (so the holiday/return banner reaches them).
  const showTasksTab    = isPortalAdmin || depotActive || hasDoEToday || isDoEDriver;
  // Personal to-do list: owner (BOUVENGP2) plus anyone granted access from the Dashboard.
  const todoOwner       = (todoAccess?.owner || 'BOUVENGP2').toUpperCase();
  const canUseTodo      = driverUpper === todoOwner || (todoAccess?.allowed || []).map(x => x.toUpperCase()).includes(driverUpper);

  // ── Team-leader nominated functions (Settings → Team Leader Access) ────────
  const myFeatures    = featureAccess[driverUpper] || {};
  const tlAfternoon   = !!myFeatures.afternoon_check;
  const tlLoggedOn    = !!myFeatures.logged_on;
  const tlLeave       = !!myFeatures.leave_calendar;
  const tlStatus      = !!myFeatures.status;
  const tlRoutesScope = ['all', 'team', 'driver'].includes(myFeatures.driver_routes) ? myFeatures.driver_routes : '';
  const tlTeam        = TEAM_HISTORY[driverUpper] || null; // {scope, areas, metrics} for team scoping
  // Data scope for Afternoon Check / Logged-On / Status / Leave. Prefer the scope
  // stored on the grant (derived from the TL's Team_Leaders route at push time),
  // then fall back to the hardcoded map, then all teams.
  const tlAreas       = myFeatures.areas || (tlTeam && tlTeam.areas) || 'ALL';

  // ── Portal tabs ───────────────────────────────────────────────────────────
  // One source of truth for both navigations: the scrolling strip and the home
  // hub map over this same list, so a gated tab appears — or doesn't — in both.
  const portalTabs = [
    { key: 'shift',   label: 'My Shift',   group: 'today', icon: <Clock size={15} />, badge: detailsPending > 0, badgeColor: 'var(--hp-red)',
      alert: 'The office asked you to complete your details' },
    { key: 'safety',  label: 'Safety',     group: 'know',  icon: <ShieldAlert size={15} />, badge: safetyMustRead > 0, badgeColor: 'var(--hp-red)',
      alert: `${safetyMustRead} safety notice${safetyMustRead === 1 ? '' : 's'} to read` },
    { key: 'customerservice', label: 'Customer Service', group: 'today', icon: <Headphones size={15} />, badge: (csPending + reqPending + msInfo.todo) > 0, badgeColor: 'var(--hp-red)',
      alert: [
        csPending ? `${csPending} customer case${csPending === 1 ? '' : 's'} waiting on your answer` : '',
        msInfo.todo ? `${msInfo.todo} mail monitor${msInfo.todo === 1 ? '' : 's'} to answer for today` : '',
        reqPending ? `${reqPending} customer request${reqPending === 1 ? '' : 's'} to read` : '',
      ].filter(Boolean).join(' · ') },
    ...(soInfo.total > 0 || soLeader ? [{ key: 'standingorders', label: 'Standing Orders', group: 'today', icon: <Signpost size={15} />, badge: soInfo.pending > 0, badgeColor: 'var(--hp-red)',
      alert: `${soInfo.pending} standing order${soInfo.pending === 1 ? '' : 's'} to read` }] : []),
    ...(vanCheckExempt ? [] : [{ key: 'inspection', label: 'Van Check', group: 'today', icon: <ClipboardCheck size={15} /> }]),
    { key: 'onboard', label: 'Onboard',    group: 'today', icon: <Package size={15} /> },
    // Everyone gets their own history. Portal admins ALSO get All Drivers — this
    // used to be an either/or, which left the two admin logins (BOUVENGJ among
    // them) with no way to see their own completed shifts and hours at all.
    ...(ADMIN_PORTAL_LOGINS.has(shift?.driver)
      ? [{ key: 'summary', label: 'All Drivers', group: 'admin', icon: <Users size={15} /> }]
      : []
    ),
    { key: 'history', label: 'My History', group: 'me', icon: <BarChart2 size={15} /> },
    { key: 'rcti',    label: 'My RCTIs',   group: 'me',    icon: <FileText size={15} /> },
    { key: 'routes',  label: 'My Routes',  group: 'know',  icon: <Route size={15} /> },
    { key: 'findaddr', label: 'Find Address', group: 'today', icon: <MapPin size={15} /> },
    ...(isPortalAdmin ? [{ key: 'floorplan', label: 'Floor Plan', group: 'admin', icon: <LayoutGrid size={15} /> }] : []),
    { key: 'myvan',   label: 'My Van',     group: 'me',    icon: <Truck size={15} />, badge: (leasePending + accidentPending) > 0, badgeColor: 'var(--hp-red)',
      alert: [
        accidentPending ? `The office needs more on your accident report${accidentPending > 1 ? 's' : ''}` : '',
        leasePending ? `${leasePending} lease agreement${leasePending === 1 ? '' : 's'} to sign` : '',
      ].filter(Boolean).join(' · ') },
    { key: 'procedures', label: 'Procedures', group: 'know', icon: <BookOpen size={15} /> },
    { key: 'training', label: 'Training',  group: 'know',  icon: <GraduationCap size={15} />, badge: trainingDue > 0, badgeColor: 'var(--hp-red)',
      alert: `${trainingDue} training module${trainingDue === 1 ? '' : 's'} to complete` },
    { key: 'mydocs',  label: 'My Docs',    group: 'me',    icon: <FolderOpen size={15} />, badge: docsUnread > 0 || docReqPending > 0 || dmsAttention > 0 || bagPending > 0, badgeColor: 'var(--hp-red)',
      alert: [
        docsUnread   > 0 ? `${docsUnread} new document${docsUnread === 1 ? '' : 's'}` : '',
        docReqPending > 0 ? `${docReqPending} document${docReqPending === 1 ? '' : 's'} to upload` : '',
        dmsAttention  > 0 ? `${dmsAttention} ID document${dmsAttention === 1 ? '' : 's'} need attention` : '',
        bagPending    > 0 ? 'Delivery bag: are you interested?' : '',
      ].filter(Boolean).join(' · ') },
    { key: 'timeaway', label: 'Time Away', group: 'me',    icon: <Calendar size={15} /> },
    ...(canUseTodo ? [{ key: 'todos', label: 'To-Do', group: 'me', icon: <CheckSquare size={15} /> }] : []),
    ...(showTasksTab ? [{ key: 'tasks', label: 'Tasks', group: 'today', icon: <Package size={15} />, badge: doeOutstanding, badgeColor: 'var(--hp-red)',
      alert: 'A school collection still needs confirming' }] : []),
    { key: 'scanner', label: 'Scanner',    group: 'today', icon: <Camera size={15} />, badge: assetCheck > 0, badgeColor: 'var(--hp-red)',
      alert: 'The office asked for a photo of your scanner\u2019s home screen' },
    ...(isPortalAdmin ? [{ key: 'onboard_team', label: 'Tallies', group: 'admin', icon: <BarChart2 size={15} /> }] : []),
    ...(tlAfternoon ? [{ key: 'tl_afternoon', label: 'Afternoon', group: 'team', icon: <Flag size={15} /> }] : []),
    ...(tlLoggedOn  ? [{ key: 'tl_loggedon',  label: 'Logged On', group: 'team', icon: <Users size={15} /> }] : []),
    ...(tlLeave     ? [{ key: 'tl_leave',     label: 'Leave',     group: 'team', icon: <Calendar size={15} /> }] : []),
    ...(tlRoutesScope ? [{ key: 'tl_routes',  label: 'Team Routes', group: 'team', icon: <Route size={15} /> }] : []),
    ...(tlStatus    ? [{ key: 'tl_status',    label: 'Status',    group: 'team', icon: <Activity size={15} /> }] : []),
    ...(CARRY_REPORT_LOGINS.has(driverUpper) ? [{ key: 'carryreport', label: 'Report', group: 'team', icon: <Send size={15} /> }] : []),
    ...(DEPOT_ASSIST_LOGINS.has(driverUpper) ? [{ key: 'depots_assist', label: 'Team', group: 'team', icon: <Users size={15} /> }] : []),
    ...(AUDIT_VIEWER_LOGINS.has(driverUpper) ? [{ key: 'parcel_audit', label: 'Parcel Audit', group: 'team', icon: <PackageSearch size={15} /> }] : []),
    ...(showSortScan ? [{ key: 'sortscan', label: 'Sort to Outlet', group: 'today', icon: <ScanLine size={15} /> }] : []),
    ...(isPortalAdmin ? [{ key: 'nightsheet', label: 'Night Sheet', group: 'admin', icon: <FileText size={15} /> }] : []),
    ...(isPortalAdmin ? [{ key: 'applicants', label: 'Applicants', group: 'admin', icon: <UserPlus size={15} /> }] : []),
  ];

  // Home-hub pilot state. hubOn === false leaves every driver on the tab strip.
  const hubPilot   = NAV_HUB_LOGINS.has(driverUpper);
  const hubOn      = hubPilot && navMode === 'hub';
  const activeTab  = portalTabs.find(t => t.key === portalTab) || null;
  const toggleNav  = () => {
    const next = navMode === 'hub' ? 'tabs' : 'hub';
    setNavMode(next);
    try { localStorage.setItem(NAV_MODE_KEY, next); } catch {}
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      {/* New-version banner — one tap reloads onto the latest bundle */}
      {updateAvailable && (
        <button
          onClick={() => window.location.reload()}
          style={{ width: '100%', padding: '12px 16px', border: 'none', background: 'var(--hp-gold)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          <RefreshCw size={15} /> App update available — tap to refresh
        </button>
      )}
      {/* Team-leader access changed mid-session — apply without a full reload */}
      {authState === 'authed' && accessUpdated && (
        <button
          onClick={applyAccessUpdate}
          style={{ width: '100%', padding: '12px 16px', border: 'none', background: '#3B82F6', color: '#fff', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
        >
          <RefreshCw size={15} /> Your portal access was updated — tap to apply
        </button>
      )}
      {/* Visa holders who haven't consented to a VEVO check get asked on open */}
      {authState === 'authed' && driverUpper && <VevoConsentPrompt login={driverUpper} />}
      {/* Header */}
      <div style={S.header}>
        <span style={S.brand}>HOLLYPAUL</span>
        {authState === 'authed' ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <SelfAvatar login={driverUpper} size={34} />
            {hubPilot && (
              <button
                onClick={toggleNav}
                title="Switch between the home hub and the tab strip"
                style={{ background: 'none', border: '1px solid var(--hp-border)', borderRadius: 999, color: 'var(--hp-gold)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: 'var(--font-body)' }}
              >
                <LayoutGrid size={13} /> {navMode === 'hub' ? 'Hub' : 'Tabs'}
              </button>
            )}
            <a href="/driver-guide.html" target="_blank" rel="noreferrer"
              style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.8rem', fontFamily: 'var(--font-body)', textDecoration: 'none' }}>
              <HelpCircle size={15} /> Guide
            </a>
            <button
              onClick={logout}
              style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.875rem', fontFamily: 'var(--font-body)' }}
            >
              <LogOut size={16} /> Logout
            </button>
          </div>
        ) : (
          <span style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>Driver Portal</span>
        )}
      </div>

      {/* Tab bar — the scrolling strip every driver still uses. The home-hub pilot
          replaces it with a Back bar (the hub itself is the navigation). */}
      {authState === 'authed' && !hubOn && (
        <div className="hp-tabbar" style={{ width: '100%', display: 'flex', flexWrap: 'nowrap', borderBottom: '2px solid var(--hp-border)', background: 'var(--hp-surface)' }}>
          {portalTabs.map(tab => (
            <button
              key={tab.key}
              onClick={() => setPortalTab(tab.key)}
              style={{
                flex: '1 0 auto', whiteSpace: 'nowrap', padding: '12px 14px', border: 'none', cursor: 'pointer',
                position: 'relative',
                fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.8rem',
                letterSpacing: '0.06em', textTransform: 'uppercase',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                background: 'none',
                color:         portalTab === tab.key ? 'var(--hp-gold)'  : 'var(--hp-dim)',
                borderBottom:  portalTab === tab.key ? '2px solid var(--hp-gold)' : '2px solid transparent',
                marginBottom:  -2,
              }}
            >
              {tab.icon}{tab.label}
              {tab.badge && (
                <span style={{ position: 'absolute', top: 6, right: 'calc(50% - 28px)', width: 8, height: 8, borderRadius: 999, background: tab.badgeColor || 'var(--hp-gold)' }} />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Hub Back bar — every screen except the hub itself gets one way home. */}
      {authState === 'authed' && hubOn && portalTab !== 'hub' && (
        <button
          onClick={() => setPortalTab('hub')}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            padding: '11px 14px', border: 'none', borderBottom: '1px solid var(--hp-border)',
            background: 'var(--hp-surface)', color: 'var(--hp-gold)',
            fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.8rem',
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}
        >
          <ChevronLeft size={17} />
          Home
          <span style={{ marginLeft: 'auto', color: 'var(--hp-dim)', fontWeight: 700 }}>
            {activeTab ? activeTab.label : ''}
          </span>
        </button>
      )}

      {/* Content */}
      <div style={{ width: '100%', maxWidth: 440, padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* ── LOGIN ──────────────────────────────────────────────────────── */}
        {authState === 'login' && (
          <>
            <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--hp-text)', letterSpacing: '0.04em' }}>
                SHIFT LOG IN
              </div>
              <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', marginTop: 6 }}>
                {formatDateDisplay(localDateStr())}
              </div>
            </div>
            <div style={S.card}>
              <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <div>
                  <label style={S.label}>Driver ID</label>
                  <input style={S.input} type="text" placeholder="e.g. SMITHJ"
                    autoCapitalize="characters" autoCorrect="off"
                    value={loginForm.login}
                    onChange={e => setLoginForm(f => ({ ...f, login: e.target.value }))}
                    required
                  />
                </div>
                <div>
                  <label style={S.label}>PIN</label>
                  <input
                    style={{ ...S.input, letterSpacing: '0.3em', fontSize: '1.4rem' }}
                    type="password" inputMode="numeric" maxLength={6} placeholder="····"
                    value={loginForm.pin}
                    onChange={e => setLoginForm(f => ({ ...f, pin: e.target.value }))}
                    required
                  />
                </div>
                {loginError && (
                  <div style={S.errorBox}>
                    <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                    {loginError}
                  </div>
                )}
                <button type="submit" disabled={loginLoading}
                  style={{ ...S.bigBtn('gold'), opacity: loginLoading ? 0.6 : 1 }}>
                  {loginLoading ? 'Checking...' : 'LOG IN'}
                </button>
              </form>
            </div>
          </>
        )}

        {/* ── Detail request banner (admin asked driver to complete details) ── */}
        {authState === 'authed' && shift && (
          <DriverDetailsCard shift={shift} onCountChange={setDetailsPending} />
        )}

        {/* ── Toll notice banner (the driver pays it; stays until a receipt is in) ── */}
        {authState === 'authed' && shift && tollsToPay.length > 0 && portalTab !== 'mydocs' && (() => {
          const t = tollsToPay.slice().sort((a, b) => (a.pay.daysLeft ?? 99) - (b.pay.daysLeft ?? 99))[0];
          const d = t.pay.daysLeft;
          const red = d != null && d <= 3;
          const amt = t.fine && t.fine.amount != null ? Number(t.fine.amount).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' }) : '';
          const when = d == null ? '' : d < 0 ? `OVERDUE by ${-d} day${d === -1 ? '' : 's'}` : d === 0 ? 'due TODAY' : `due in ${d} day${d === 1 ? '' : 's'}`;
          return (
            <div style={{ width: '100%', marginBottom: 14, padding: '14px 16px', borderRadius: 12, background: red ? 'rgba(220,38,38,0.12)' : 'rgba(245,158,11,0.10)', border: `${red ? 2 : 1}px solid ${red ? '#dc2626' : '#d97706'}`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <AlertTriangle size={22} color={red ? '#dc2626' : '#d97706'} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{tollsToPay.length === 1 ? `Toll notice to pay — ${amt} ${when}` : `${tollsToPay.length} toll notices to pay — the next is ${when}`}</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>You need to pay this one yourself, then upload the receipt. Open My Docs for how.</div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => setPortalTab('mydocs')}>How to pay</button>
            </div>
          );
        })()}

        {/* ── Penalty notice banner (office sent a fine; stays until it's opened) ── */}
        {authState === 'authed' && shift && finesUnread > 0 && portalTab !== 'mydocs' && (
          <div style={{ width: '100%', marginBottom: 14, padding: '14px 16px', borderRadius: 12, background: 'rgba(245,158,11,0.10)', border: '1px solid #d97706', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <AlertTriangle size={22} color="#d97706" style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{finesUnread === 1 ? 'A penalty notice has been sent to you' : `${finesUnread} penalty notices have been sent to you`}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>Do not pay it — open it in My Docs to read why.</div>
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => setPortalTab('mydocs')}>Open My Docs</button>
          </div>
        )}

        {/* ── Office notice banner (a My Docs letter pinned to the home screen for its dates) ── */}
        {authState === 'authed' && shift && portalTab !== 'mydocs' && docBanners.map(b => {
          const day = iso => new Date(iso + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
          const pending = b.status !== 'opened' || (b.requiresAck && !b.acknowledgedAt);
          return (
            <div key={b.id} style={{ width: '100%', marginBottom: 14, padding: '14px 16px', borderRadius: 12, background: 'rgba(245,158,11,0.10)', border: `${pending ? 2 : 1}px solid #d97706`, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <AlertTriangle size={22} color="#d97706" style={{ flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{b.title}</div>
                {b.homeBanner.text && <div style={{ fontSize: '0.85rem', marginTop: 2 }}>{b.homeBanner.text}</div>}
                <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>
                  {day(b.homeBanner.from)} to {day(b.homeBanner.to)}{pending ? (b.requiresAck ? ' · please read and acknowledge' : ' · please read') : ''}
                </div>
              </div>
              <button className="btn btn-primary btn-sm" onClick={() => setPortalTab('mydocs')}>Read in My Docs</button>
            </div>
          );
        })}

        {/* ── Document-upload request banner (office asked for a document) ── */}
        {authState === 'authed' && shift && (
          <DocUploadRequestCard shift={shift} onCountChange={setDocReqPending} />
        )}

        {/* ── DoE cover banner (office assigned / moved a school collection) ── */}
        {authState === 'authed' && shift && <DoECoverCard shift={shift} />}

        {/* ── WELCOME GUIDE (first login only) ──────────────────────────── */}
        {showWelcome && shift && (
          <WelcomeGuide name={shift.name} driver={shift.driver} onDone={() => {
            try { localStorage.setItem(WELCOME_KEY + '_' + shift.driver, '1'); } catch {}
            setShowWelcome(false);
          }} />
        )}

        {/* ── HISTORY TAB ────────────────────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'history' && (
          TEAM_HISTORY[shift.driver]
            ? <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Team / Mine toggle — own per-day vs team totals */}
                <div style={{ display: 'flex', gap: 6, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 4 }}>
                  {[['mine', 'Mine'], ['team', 'Team']].map(([k, label]) => (
                    <button key={k} onClick={() => setHistoryMode(k)}
                      style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.04em', fontFamily: 'var(--font-body)',
                        background: historyMode === k ? 'var(--hp-gold)' : 'transparent',
                        color: historyMode === k ? 'var(--hp-black)' : 'var(--hp-dim)' }}>
                      {label === 'Mine' ? 'My Days' : 'Team Totals'}
                    </button>
                  ))}
                </div>
                {historyMode === 'team' ? (
                  <>
                    <TeamHistoryView shift={shift} config={TEAM_HISTORY[shift.driver]} />
                    <AfternoonCheckView areas={TEAM_HISTORY[shift.driver].areas} />
                  </>
                ) : (
                  <DriverHistoryView
                    shift={shift}
                    historyData={historyData}
                    historyLoading={historyLoading}
                    historyError={historyError}
                    historyView={historyView}
                    setHistoryView={setHistoryView}
                    onRefresh={() => { setHistoryData(null); loadHistory(shift.driver, true); }}
                  />
                )}
              </div>
            : <DriverHistoryView
                shift={shift}
                historyData={historyData}
                historyLoading={historyLoading}
                historyError={historyError}
                historyView={historyView}
                setHistoryView={setHistoryView}
                onRefresh={() => { setHistoryData(null); loadHistory(shift.driver, true); }}
              />
        )}

        {/* ── ROUTES TAB ─────────────────────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'rcti' && (
          <RctiHistoryView driver={shift.driver} name={shift.name} />
        )}

        {authState === 'authed' && shift && portalTab === 'routes' && (
          <DriverRoutesPortalView shift={shift} />
        )}

        {/* ── MY VAN TAB ─────────────────────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'myvan' && (
          <div style={{ width: '100%' }}>
            <LeaseSignCard shift={shift} onCountChange={setLeasePending} />
            <SignedLeasesCard shift={shift} />
            <MyVanPortalTab shift={shift} onAccidentCountChange={setAccidentPending} />
          </div>
        )}

        {/* ── MY DOCS TAB (received docs + upload + on-file compliance docs) ── */}
        {authState === 'authed' && shift && portalTab === 'mydocs' && (
          <div style={{ width: '100%' }}>
            <BagEoiCard variant="docs" onPendingChange={setBagPending} />
            <MyDocsPortalTab
              shift={shift}
              onCountChange={setDocsUnread}
              showOnFile
              onDmsCountChange={setDmsAttention}
            />
          </div>
        )}

        {/* ── DELIVERY PROCEDURES TAB ────────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'procedures' && (
          <DeliveryProceduresTab />
        )}

        {/* ── SAFETY TAB (focus topics · who to call · hazards · report) ─── */}
        {authState === 'authed' && shift && portalTab === 'safety' && (
          <SafetyTab shift={shift} onMustReadChange={setSafetyMustRead} />
        )}

        {/* ── TRAINING / RETRAINING TAB ──────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'training' && (
          <TrainingPortalTab shift={shift} onCountChange={setTrainingDue} />
        )}

        {/* ── REQUEST TIME AWAY TAB ──────────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'timeaway' && (
          // Oversight users (owner/admins + team leaders) aren't tied to a single
          // delivery team, so they'd otherwise hit the "not set up for your area"
          // block. Let them choose which team to book their own leave under.
          <LeaveRequestPortal shift={shift} oversight={isPortalAdmin || !!tlTeam} />
        )}

        {/* ── MY TO-DO TAB ───────────────────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'todos' && canUseTodo && (
          <div style={{ width: '100%' }}>
            <ToDoList login={driverUpper} title="My to-do" footerNote="Private to you · synced with your Dashboard" />
          </div>
        )}

        {/* ── ALL DRIVERS SUMMARY (admin only) ──────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'summary' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <AfternoonCheckView areas={(TEAM_HISTORY[shift.driver] && TEAM_HISTORY[shift.driver].areas) || 'ALL'} />
            {TEAM_HISTORY[shift.driver] && (
              <TeamHistoryView shift={shift} config={TEAM_HISTORY[shift.driver]} />
            )}
            <AdminDailySummary />
          </div>
        )}

        {/* ── TASKS TAB (Depot Bags + DoE) ───────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'tasks' && (
          <TasksPortalTab
            shift={shift}
            depotActive={depotActive || isPortalAdmin}
            doeRelief={doeRelief}
            doeConfirmed={doeConfirmed}
            onDoeConfirmed={(school, info = { collected: true, reason: '' }) => setDoeConfirmed(prev => {
              const next = new Map(prev || []);
              next.set(school, info);
              return next;
            })}
          />
        )}

        {/* ── SCANNER TAB ────────────────────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'scanner' && (
          <ScannerPortalTab shift={shift} onAssetCheck={setAssetCheck} />
        )}

        {/* ── TEAM LEADER: AFTERNOON CHECK ───────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'tl_afternoon' && tlAfternoon && (
          <AfternoonCheckView areas={tlAreas} />
        )}

        {/* ── TEAM LEADER: LOGGED-ON DRIVERS ─────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'tl_loggedon' && tlLoggedOn && (
          <LoggedOnPortalView areas={tlAreas} />
        )}

        {/* ── TEAM LEADER: LEAVE CALENDAR ────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'tl_leave' && tlLeave && (
          <div style={{ width: '100%' }}>
            <iframe
              key={driverUpper + (Array.isArray(tlAreas) ? tlAreas.join(',') : tlAreas)}
              title="Leave Calendar"
              src={`/roster-calendar.html?theme=${(typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme')) || 'dark'}${
                (Array.isArray(tlAreas) && tlAreas.length === 1 && AREA_LABELS[tlAreas[0]])
                  ? `&teamLock=${encodeURIComponent(AREA_LABELS[tlAreas[0]])}` : ''
              }`}
              style={{ display: 'block', width: '100%', height: 'calc(100vh - 140px)', minHeight: 520, border: 'none', borderRadius: 'var(--radius)' }}
            />
          </div>
        )}

        {/* ── TEAM LEADER: DRIVER ROUTES (scoped) ────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'tl_routes' && tlRoutesScope && (
          <DriverRoutesPortalView shift={shift} accessScope={tlRoutesScope} />
        )}

        {/* ── TEAM LEADER: STATUS PAGE ───────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'tl_status' && tlStatus && (
          <StatusPortalView areas={tlAreas} />
        )}

        {authState === 'authed' && shift && portalTab === 'carryreport' && CARRY_REPORT_LOGINS.has(driverUpper) && (
          <CarryOverReportTab shift={shift} />
        )}

        {/* ── ASTORGACV: VIEW BOTH BROADWAY DRIVERS' FULL HISTORY ─────────── */}
        {authState === 'authed' && shift && portalTab === 'depots_assist' && DEPOT_ASSIST_LOGINS.has(driverUpper) && (
          <DepotAssistView shift={shift} />
        )}

        {/* ── PARCEL AUDIT (view only) — lookups + XLSX/PDF, no reassign ──── */}
        {authState === 'authed' && shift && portalTab === 'parcel_audit' && AUDIT_VIEWER_LOGINS.has(driverUpper) && (
          <ParcelAuditPage viewOnly />
        )}

        {/* ── ONBOARD SUMMARY SCAN — device SUMMARY screen → onboard_scans ── */}
        {authState === 'authed' && shift && portalTab === 'inspection' && !vanCheckExempt && (
          <InspectionTab driver={driverUpper} onDone={() => setPortalTab('onboard')} odoStart={odoStart} setOdoStart={setOdoStart} />
        )}

        {/* ── STANDING ORDERS TAB ─────────────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'standingorders' && (
          <StandingOrdersPanel onInfo={setSoInfo} leader={soLeader} />
        )}

        {authState === 'authed' && shift && portalTab === 'customerservice' && (
          <CustomerServiceTab driver={driverUpper} onCount={setCsPending} initialSub={csSub}
            msCount={msInfo.todo} hasMonitors={msInfo.any} onMonitors={applyMs} />
        )}

        {authState === 'authed' && shift && portalTab === 'onboard' && (
          <>
            <CustomerCaseNudge count={csPending} onGo={() => setPortalTab('customerservice')} />
            <OnboardScanTab driver={driverUpper} exempt={vanCheckExempt} onGoInspection={() => setPortalTab('inspection')} />
          </>
        )}

        {/* ── ONBOARD TEAM TALLIES (admin only) ──────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'onboard_team' && isPortalAdmin && (
          <OnboardTeamView />
        )}

        {/* ── SORT TO OUTLET (scan a label → chute/outlet) ─────────────────── */}
        {authState === 'authed' && shift && portalTab === 'sortscan' && showSortScan && (
          <ChuteLayoutTab />
        )}

        {/* ── FIND ADDRESS (scan/type a label → driver + floor bay) ────────── */}
        {authState === 'authed' && shift && portalTab === 'findaddr' && (
          <PortalAddressScan />
        )}

        {/* ── DEPOT FLOOR PLAN (Paul & Jayda only) ─────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'floorplan' && isPortalAdmin && (
          <DepotMapPage />
        )}

        {/* ── NIGHT SHEET SCAN (admin only) — AP paper sheet → AP Sheets ──── */}
        {authState === 'authed' && shift && portalTab === 'nightsheet' && isPortalAdmin && (
          <NightSheetScanTab />
        )}

        {/* ── APPLICANTS TAB (admin only) ─────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'applicants' && isPortalAdmin && (
          <ApplicantsView
            addNotification={(msg) => { try { window.alert(msg); } catch (e) {} }}
            recordedBy={driverUpper}
          />
        )}

        {/* ── HOME HUB (pilot) ───────────────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'hub' && (() => {
          // Anything carrying a red dot is lifted out of its group and pinned to the
          // top as a full-width row that says WHY, not just that something is wrong.
          // The block disappears the moment the last one is cleared. My Shift is left
          // out — its dot rides on the hero immediately below.
          const attention    = portalTabs.filter(t => t.badge && t.key !== 'shift');
          const attentionKey = new Set(attention.map(t => t.key));
          return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>

            {attention.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--hp-red)' }}>
                  <span style={{ width: 9, height: 9, borderRadius: 999, background: 'var(--hp-red)' }} />
                  Needs your attention
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {attention.map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setPortalTab(tab.key)}
                      style={{
                        width: '100%', cursor: 'pointer', textAlign: 'left',
                        background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.35)',
                        borderRadius: 'var(--radius-lg)', padding: '12px 14px',
                        display: 'flex', alignItems: 'center', gap: 12,
                        color: 'var(--hp-text)', fontFamily: 'var(--font-body)',
                      }}
                    >
                      <span style={{ color: 'var(--hp-red)', display: 'flex' }}>
                        {React.cloneElement(tab.icon, { size: 20 })}
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: 'block', fontSize: '0.85rem', fontWeight: 700 }}>{tab.label}</span>
                        {tab.alert && (
                          <span style={{ display: 'block', fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 1 }}>{tab.alert}</span>
                        )}
                      </span>
                      <ChevronRight size={17} color="var(--hp-dim)" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Hero — the shift screen, one tap away, showing where the day is up to. */}
            <button
              onClick={() => setPortalTab('shift')}
              style={{
                width: '100%', textAlign: 'left', cursor: 'pointer',
                background: 'linear-gradient(160deg, rgba(201,168,76,0.16), rgba(201,168,76,0.02) 70%)',
                border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)',
                padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 8,
                fontFamily: 'var(--font-body)', position: 'relative',
              }}
            >
              <span style={{
                alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase',
                padding: '4px 10px', borderRadius: 999,
                background: shiftState === 'on_shift' ? 'rgba(34,197,94,0.14)'
                          : shiftState === 'on_break' ? 'rgba(245,158,11,0.14)'
                          : 'rgba(201,168,76,0.14)',
                color:      shiftState === 'on_shift' ? 'var(--hp-green)'
                          : shiftState === 'on_break' ? 'var(--hp-amber)'
                          : 'var(--hp-gold)',
              }}>
                ● {shiftState === 'on_shift' ? 'On shift'
                  : shiftState === 'on_break' ? 'On break'
                  : shiftState === 'finishing' ? 'Finishing up'
                  : existingSub ? 'Shift submitted' : 'Not started'}
              </span>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.9rem', letterSpacing: '0.04em', color: 'var(--hp-text)', lineHeight: 1 }}>
                MY SHIFT
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
                {shift.name} · {formatDateDisplay(shift.date)}
              </div>
              {detailsPending > 0 && (
                <span style={{ position: 'absolute', top: 14, right: 14, width: 9, height: 9, borderRadius: 999, background: 'var(--hp-red)' }} />
              )}
            </button>

            {/* Tiles, grouped. Same list the tab strip renders, minus My Shift. */}
            {HUB_GROUPS.map(([g, groupLabel]) => {
              const items = portalTabs.filter(t => t.group === g && t.key !== 'shift' && !attentionKey.has(t.key));
              if (!items.length) return null;
              return (
                <div key={g} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--hp-dim)' }}>
                    {groupLabel}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                    {items.map(tab => (
                      <button
                        key={tab.key}
                        onClick={() => setPortalTab(tab.key)}
                        style={{
                          position: 'relative', cursor: 'pointer',
                          background: 'var(--hp-card)', border: '1px solid var(--hp-border)',
                          borderRadius: 'var(--radius-lg)', padding: '14px 6px 12px',
                          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
                          color: 'var(--hp-text)', fontFamily: 'var(--font-body)',
                          fontSize: '0.68rem', fontWeight: 600, lineHeight: 1.25, textAlign: 'center',
                          minHeight: 84,
                        }}
                      >
                        <span style={{ color: 'var(--hp-gold)', display: 'flex' }}>
                          {React.cloneElement(tab.icon, { size: 22 })}
                        </span>
                        {tab.label}
                        {tab.badge && (
                          <span style={{ position: 'absolute', top: 9, right: 9, width: 9, height: 9, borderRadius: 999, background: tab.badgeColor || 'var(--hp-gold)' }} />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          );
        })()}

        {/* ── AUTHED SCREENS ─────────────────────────────────────────────── */}
        {authState === 'authed' && shift && portalTab === 'shift' && (
          <>
            {/* Pending subcontractor agreement to sign (shows only when one is pending) */}
            <ContractorAgreementCard shift={shift} />

            {/* Advance agreement awaiting signature. Sits beside the subcontractor
                agreement because it is the same kind of thing: a document he must read
                and sign before money moves. Renders nothing when none is pending. */}
            <AdvanceAgreementCard shift={shift} />

            {/* Safety Focus — ONE topic, three lines, in the driver's language. Sits above
                the shift card because it has to be read before the run, not after it.
                Collapses to a slim green line once acknowledged for the current focus. */}
            <SafetyFocusCard shift={shift} onOpenSafety={() => setPortalTab('safety')} onMustReadChange={setSafetyMustRead} onBriefsDueChange={setBriefsDue} />

            {/* Training the office has assigned — on the home screen, not just behind the tab. */}
            <TrainingDueCard driver={driverUpper} onCountChange={setTrainingDue} onGoTraining={() => setPortalTab('training')} />

            {/* Delivery bag expression of interest — asked here, not just by text, so it
                isn't glanced over. Gone once answered (changeable in My Docs) or closed. */}
            <BagEoiCard variant="home" onPendingChange={setBagPending} />

            {/* Melbourne Cup sweep notice — posted by Paul, gone when the race starts. */}
            <CupNoticeCard />

            {/* Driver badge */}
            <div style={{ ...S.card, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Logged in as</div>
                <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--hp-text)', marginTop: 2 }}>{shift.name}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Date</div>
                <div style={{ fontWeight: 600, fontSize: '0.9rem', color: 'var(--hp-gold)', marginTop: 2 }}>
                  {formatDateDisplay(shift.date)}
                </div>
              </div>
            </div>

            {/* ── ODOMETER CAMERA CAPTURE ── */}
            {odoCapturing && (
              <div style={S.card}>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.3rem', color: 'var(--hp-text)', letterSpacing: '0.04em', marginBottom: 6 }}>
                    {odoCapturing === 'start' ? 'START ODOMETER' : 'FINISH ODOMETER'}
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', lineHeight: 1.5 }}>
                    Take a photo of the odometer and we'll read the number automatically.
                  </div>
                  <a href="/finish-guide.html" target="_blank" rel="noreferrer"
                    style={{ display: 'inline-block', marginTop: 8, fontSize: '0.78rem', fontWeight: 700, color: 'var(--hp-gold)', textDecoration: 'none' }}>
                    ▶ Watch how — 30 sec guide
                  </a>
                </div>

                {/* Camera input — auto-opens rear camera on mobile */}
                {!odoOcrLoading && !odoOcrResult && (
                  <label style={{ display: 'block', cursor: 'pointer' }}>
                    <input
                      type="file" accept="image/*" capture="environment"
                      style={{ display: 'none' }}
                      onChange={e => handleOdoPhoto(e, odoCapturing)}
                    />
                    <div style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                      gap: 14, padding: '32px 20px',
                      background: 'rgba(201,168,76,0.06)', border: '2px dashed rgba(201,168,76,0.35)',
                      borderRadius: 'var(--radius-lg)', marginBottom: 16,
                    }}>
                      <Camera size={44} color="var(--hp-gold)" />
                      <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--hp-gold)' }}>TAP TO TAKE PHOTO</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>Point camera at the odometer display</div>
                    </div>
                  </label>
                )}

                {/* Loading */}
                {odoOcrLoading && (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, padding: '32px 0', color: 'var(--hp-dim)' }}>
                    <div style={{ width: 40, height: 40, border: '3px solid var(--hp-border)', borderTopColor: 'var(--hp-gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    <div style={{ fontSize: '0.9rem' }}>Reading odometer…</div>
                  </div>
                )}

                {/* Result — confirm or retake */}
                {odoOcrResult && !odoOcrLoading && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 'var(--radius-lg)', padding: '20px', textAlign: 'center', marginBottom: 16 }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Odometer reading</div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '2.2rem', fontWeight: 700, color: 'var(--hp-green)' }}>{odoOcrResult.toLocaleString()}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 4 }}>km</div>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', textAlign: 'center', marginBottom: 14 }}>Does this look correct?</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      <button style={S.bigBtn('green')} onClick={() => confirmOdoReading(odoCapturing)}>
                        <CheckCircle size={20} /> Yes, that's correct
                      </button>
                      <label style={{ ...S.bigBtn('muted'), cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                        <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => handleOdoPhoto(e, odoCapturing)} />
                        <Camera size={18} /> Retake photo
                      </label>
                    </div>
                  </div>
                )}

                {/* Error */}
                {odoOcrError && !odoOcrLoading && (
                  <div style={{ ...S.errorBox, marginBottom: 14 }}>
                    <AlertCircle size={16} style={{ flexShrink: 0 }} /> {odoOcrError}
                  </div>
                )}

                {/* Manual entry fallback */}
                {!odoOcrLoading && !odoOcrResult && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textAlign: 'center', marginBottom: 10 }}>Or enter manually:</div>
                    <div style={{ display: 'flex', gap: 10 }}>
                      <input
                        style={{ ...S.input, fontFamily: 'var(--font-mono)', fontSize: '1.2rem', flex: 1 }}
                        type="number" inputMode="numeric" placeholder="e.g. 123456"
                        onChange={e => {
                          const v = parseInt(e.target.value);
                          if (v > 0) setOdoOcrResult(v);
                        }}
                      />
                      <button
                        style={{ ...S.bigBtn('green'), padding: '14px 20px', flexShrink: 0 }}
                        onClick={() => { if (odoOcrResult) confirmOdoReading(odoCapturing); }}
                      >
                        OK
                      </button>
                    </div>
                  </div>
                )}

                <button
                  style={{ marginTop: 20, background: 'none', border: 'none', color: 'var(--hp-dim)', fontSize: '0.8rem', cursor: 'pointer', width: '100%', textAlign: 'center', fontFamily: 'var(--font-body)', padding: '8px 0' }}
                  onClick={() => skipOdoCapture(odoCapturing)}
                >
                  Skip for now
                </button>
              </div>
            )}

            {/* ── READY ── */}
            {!odoCapturing && shiftState === 'ready' && (
              <div style={S.card}>
                <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--hp-text)', letterSpacing: '0.04em' }}>
                    READY TO START?
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', marginTop: 8 }}>
                    Press the button when you depart the depot
                  </div>
                </div>

                <MailStopperBanner driver={driverUpper} onGo={goMailMonitor} />

                {/* Geofence-exempt drivers (e.g. owners logging on/off elsewhere) skip location + QR */}
                {geoExempt && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16 }}>
                    <MapPin size={16} color="var(--hp-green)" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '0.85rem', color: 'var(--hp-dim)' }}>Location check not required for your account — start anywhere.</span>
                  </div>
                )}

                {!geoExempt && (<>
                {/* ── Geofence status ── */}
                {geofenceSettings.geofenceEnabled && geoState === 'checking' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16 }}>
                    <Navigation size={16} color="var(--hp-gold)" style={{ flexShrink: 0, animation: 'spin 1.5s linear infinite' }} />
                    <span style={{ fontSize: '0.875rem', color: 'var(--hp-dim)' }}>Checking your location…</span>
                  </div>
                )}

                {geofenceSettings.geofenceEnabled && geoState === 'in_range' && nearestDepot && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16 }}>
                    <MapPin size={16} color="var(--hp-green)" style={{ flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--hp-green)' }}>At {nearestDepot.name} depot</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>{geoDistance}m from gate · Good to go</div>
                    </div>
                  </div>
                )}

                {geofenceSettings.geofenceEnabled && geoState === 'out_of_range' && nearestDepot && (
                  <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                      <MapPin size={16} color="var(--hp-red)" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--hp-red)' }}>
                          {geoDistance >= 1000 ? `${(geoDistance / 1000).toFixed(1)} km` : `${geoDistance} m`} from {nearestDepot.name} depot
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 3 }}>
                          You must be within {geofenceSettings.geofenceRadiusM}m of a depot to start your shift.
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={checkGeofence}
                      style={{ width: '100%', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '8px', color: 'var(--hp-red)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, fontFamily: 'var(--font-body)' }}
                    >
                      Re-check location
                    </button>
                  </div>
                )}

                {geofenceSettings.geofenceEnabled && geoState === 'denied' && (
                  <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <AlertCircle size={16} color="var(--hp-red)" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--hp-red)' }}>Location access denied</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 3 }}>
                          Go to your browser settings, allow location for this site, then tap Re-check.
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={checkGeofence}
                      style={{ width: '100%', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '8px', color: 'var(--hp-red)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, fontFamily: 'var(--font-body)' }}
                    >
                      Re-check location
                    </button>
                  </div>
                )}

                {geofenceSettings.geofenceEnabled && geoState === 'error' && (
                  <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                      <AlertTriangle size={16} color="var(--hp-amber)" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--hp-amber)' }}>Could not get location</div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 3 }}>
                          GPS unavailable. Check your signal and try again.
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={checkGeofence}
                      style={{ width: '100%', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, padding: '8px', color: 'var(--hp-amber)', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 600, fontFamily: 'var(--font-body)' }}
                    >
                      Re-check location
                    </button>
                  </div>
                )}

                {/* ── QR status ── */}
                {geofenceSettings.qrRequired && qrState === 'checking' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16 }}>
                    <QrCode size={16} color="var(--hp-gold)" style={{ flexShrink: 0 }} />
                    <span style={{ fontSize: '0.875rem', color: 'var(--hp-dim)' }}>Verifying QR code…</span>
                  </div>
                )}

                {geofenceSettings.qrRequired && qrState === 'override' && (
                  <div style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.35)', borderRadius: 'var(--radius)', padding: '12px 16px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <CheckCircle size={18} color="var(--hp-green)" style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>
                      <div style={{ fontWeight: 700, color: 'var(--hp-green)', fontSize: '0.9rem' }}>Manager approved</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 2 }}>QR requirement waived by your manager. You can now start your shift.</div>
                    </div>
                  </div>
                )}

                {geofenceSettings.qrRequired && qrState === 'verified' && qrDepot && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.25)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16 }}>
                    <QrCode size={16} color="var(--hp-green)" style={{ flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--hp-green)' }}>QR verified — {qrDepot.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>Depot gate scan confirmed for today</div>
                    </div>
                  </div>
                )}

                {geofenceSettings.qrRequired && (qrState === 'idle' || qrState === 'invalid' || qrState === 'error') && (
                  <div style={{ background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius)', padding: '12px 14px', marginBottom: 16 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <QrCode size={16} color="var(--hp-amber)" style={{ flexShrink: 0, marginTop: 1 }} />
                      <div>
                        <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--hp-amber)' }}>
                          {qrState === 'invalid' ? 'Invalid QR code' : 'QR scan required'}
                        </div>
                        <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 3 }}>
                          {qrState === 'invalid'
                            ? 'That QR code was not recognised. Scan the QR code posted at the depot gate.'
                            : 'Scan the QR code posted at the depot gate to unlock Start Shift.'}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                </>)}

                {/* ── Start Odometer — text field + optional camera ── */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
                    Start Odometer (km)
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
                    <input
                      type="number"
                      inputMode="numeric"
                      value={odoStart}
                      onChange={e => setOdoStart(e.target.value)}
                      placeholder="Type km reading"
                      style={{
                        flex: 1, padding: '12px 14px', borderRadius: 'var(--radius)',
                        border: `1px solid ${odoStart ? 'rgba(34,197,94,0.5)' : 'var(--hp-border)'}`,
                        background: 'var(--hp-surface)', color: 'var(--hp-text)',
                        fontSize: '1.1rem', fontFamily: 'var(--font-body)', fontWeight: 600,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    />
                    <label style={{ flexShrink: 0, cursor: 'pointer' }}>
                      <input
                        type="file" accept="image/*" capture="environment"
                        style={{ display: 'none' }}
                        onChange={e => { setStartOdoCapturing(true); handleOdoPhoto(e, 'start'); }}
                      />
                      <div style={{
                        height: '100%', minHeight: 48, padding: '0 14px',
                        background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.4)',
                        borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center',
                        justifyContent: 'center', gap: 6, color: 'var(--hp-gold)',
                      }}>
                        <Camera size={18} />
                        <span style={{ fontSize: '0.72rem', fontWeight: 700 }}>PHOTO</span>
                      </div>
                    </label>
                  </div>

                  {/* Inline OCR status for start odometer */}
                  {startOdoCapturing && odoOcrLoading && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, padding: '10px 14px', background: 'rgba(201,168,76,0.08)', borderRadius: 'var(--radius)', border: '1px solid rgba(201,168,76,0.2)' }}>
                      <div style={{ width: 16, height: 16, border: '2px solid var(--hp-border)', borderTopColor: 'var(--hp-gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                      <span style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>Reading odometer from photo…</span>
                    </div>
                  )}
                  {startOdoCapturing && odoOcrResult && !odoOcrLoading && (
                    <div style={{ marginTop: 10, padding: '12px 14px', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 'var(--radius)' }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Reading detected</div>
                      <div style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--hp-green)', fontVariantNumeric: 'tabular-nums', marginBottom: 10 }}>{odoOcrResult.toLocaleString()} km</div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => confirmOdoReading('start')}
                          style={{ flex: 1, padding: '8px', background: 'var(--hp-green)', border: 'none', borderRadius: 8, color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                          ✓ Use this reading
                        </button>
                        <button onClick={() => { clearPendingOdo(); setOdoOcrResult(null); setStartOdoCapturing(false); }}
                          style={{ padding: '8px 12px', background: 'none', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-dim)', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                          Retake
                        </button>
                      </div>
                    </div>
                  )}
                  {startOdoCapturing && odoOcrError && !odoOcrLoading && (
                    <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, fontSize: '0.78rem', color: 'var(--hp-red)' }}>
                      {odoOcrError} — type the reading manually above.
                    </div>
                  )}

                  {!odoStart && !odoOcrLoading && (
                    <div style={{ fontSize: '0.72rem', color: odoOptional ? 'var(--hp-dim)' : 'var(--hp-amber)', marginTop: 5 }}>
                      {odoOptional
                        ? 'Optional — only enter your km reading if you are conveying parcels'
                        : 'Required — type or photograph your dashboard km reading'}
                    </div>
                  )}
                  {odoStart && odoStartChk.ok && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--hp-green)', marginTop: 5 }}>
                      ✓ Start odometer set — {Number(odoStart).toLocaleString()} km
                    </div>
                  )}
                  <OdoWarning check={odoStartChk} kept={odoStartKept}
                    onUse={(km) => { setOdoStart(String(km)); setOdoStartKept(false); }}
                    onKeep={() => setOdoStartKept(true)} />
                </div>

                {/* ── Start Shift button — geofence + odometer required ── */}
                {(() => {
                  const geoOk    = geoExempt || !geofenceSettings.geofenceEnabled || geoState === 'in_range';
                  const qrOk     = geoExempt || !geofenceSettings.qrRequired      || qrState === 'verified' || qrState === 'override';
                  const odoOk    = (odoOptional || (!!odoStart && Number(odoStart) > 0)) && !odoStartBlocked;
                  const canStart = geoOk && qrOk && odoOk;
                  return (
                    <button
                      style={{ ...S.bigBtn(canStart ? 'green' : 'muted'), opacity: canStart ? 1 : 0.45, cursor: canStart ? 'pointer' : 'not-allowed' }}
                      onClick={startShift}
                      disabled={!canStart}
                    >
                      <Play size={22} /> START SHIFT
                    </button>
                  );
                })()}
              </div>
            )}

            {/* ── ON SHIFT ── */}
            {/* Customer case alert — shown only once the driver is ON SHIFT, so it never
                distracts from starting the shift on the pre-start screen. */}
            {!odoCapturing && shiftState === 'on_shift' && (
              <CustomerCaseNudge count={csPending} onGo={() => setPortalTab('customerservice')} />
            )}
            {/* A customer's standing request they have not taken in yet. It sits here, in
                full, until they tap Got it — the case that raised one used to be resolved
                and filed before the driver ever opened it. It never blocks the shift; it
                just does not go away on its own. */}
            {!odoCapturing && shiftState === 'on_shift' && (
              <DeliveryRequestsBanner onCountChange={setReqPending} />
            )}
            {/* A standing order they have not read in its current wording — red, in full,
                until they tap Got it. Same rule as requests: never blocks the shift. */}
            {!odoCapturing && shiftState === 'on_shift' && (
              <StandingOrdersBanner onInfo={setSoInfo} />
            )}
            {/* Mail stoppers stay in view for the whole shift — one confirmed after the
                driver pressed Start used to be invisible until the Onboard gate. */}
            {!odoCapturing && shiftState === 'on_shift' && (
              <MailStopperBanner driver={driverUpper} onGo={goMailMonitor} />
            )}
            {/* Late in the shift with no summary scanned — tap through to the Onboard tab
                while there is still shift left to put it right. */}
            {!odoCapturing && shiftState === 'on_shift' && (
              <OnboardNudge shiftStart={shift?.shiftStart} now={now} scannedToday={obToday}
                onGo={() => setPortalTab('onboard')} />
            )}
            {!odoCapturing && shiftState === 'on_shift' && (
              <div style={S.card}>
                <div style={S.timerDisplay}>
                  <div style={S.timerNum}>{formatDuration(workedSoFar)}</div>
                  <div style={S.timerLabel}>Time on shift</div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>Shift started</span>
                    <span style={{ ...S.metaValue, fontFamily: 'var(--font-mono)' }}>{formatTimeStr(shift.shiftStart)}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>Vehicle</span>
                    <span style={{ ...S.metaValue, fontFamily: 'var(--font-mono)' }}>{(shift?.vehicleRego || shiftVehicle?.rego) || '—'}</span>
                  </div>
                  {breaksCount > 0 && (
                    <div style={S.metaRow}>
                      <span style={S.metaLabel}>{breaksCount === 1 ? 'Break taken' : `Breaks taken (${breaksCount})`}</span>
                      <span style={{ ...S.metaValue, fontFamily: 'var(--font-mono)' }}>{formatDuration(totalBreakTaken)}</span>
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <button style={S.bigBtn('amber')} onClick={startBreak}>
                    <Coffee size={20} /> {breaksCount > 0 ? 'TAKE ANOTHER BREAK' : 'TAKE BREAK'}
                  </button>

                  {/* Change vehicle mid-shift (breakdown / hire replacement) — keeps start time */}
                  {!showVehChange ? (
                    <button
                      style={{ ...S.bigBtn('muted'), fontSize: '0.82rem', padding: '12px' }}
                      onClick={() => { setShowVehChange(true); setVehChangeRego(shift?.vehicleRego || shiftVehicle?.rego || ''); setVehChangeOdo(''); }}
                    >
                      <Truck size={16} /> CHANGE VEHICLE
                    </button>
                  ) : (
                    <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14 }}>
                      <div style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--hp-text)', marginBottom: 8 }}>Change vehicle</div>
                      <div style={{ fontSize: '0.74rem', color: 'var(--hp-dim)', marginBottom: 12, lineHeight: 1.5 }}>
                        Breakdown or hire replacement? Enter the new van's rego and its current odometer — your start time is kept.
                      </div>
                      <label style={S.label}>New vehicle rego</label>
                      <input style={S.input} value={vehChangeRego} autoCapitalize="characters"
                        onChange={e => setVehChangeRego(e.target.value.toUpperCase())} placeholder="e.g. CK12AB or hire rego" />
                      <label style={{ ...S.label, marginTop: 12 }}>New van odometer (km)</label>
                      <input style={S.input} type="number" inputMode="numeric" value={vehChangeOdo}
                        onChange={e => setVehChangeOdo(e.target.value)} placeholder="current reading" />
                      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
                        <button style={{ ...S.bigBtn('gold'), flex: 1, fontSize: '0.85rem', padding: '12px' }}
                          disabled={vehChanging || !vehChangeRego.trim()} onClick={submitVehicleChange}>
                          {vehChanging ? 'Saving…' : 'CONFIRM CHANGE'}
                        </button>
                        <button style={{ ...S.bigBtn('muted'), flex: 1, fontSize: '0.85rem', padding: '12px' }}
                          disabled={vehChanging} onClick={() => setShowVehChange(false)}>
                          CANCEL
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Finish zone — checking */}
                  {finishGeoState === 'checking' && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, padding: '16px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontSize: '0.875rem' }}>
                      <div style={{ width: 18, height: 18, border: '2px solid var(--hp-border)', borderTopColor: 'var(--hp-gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 }} />
                      Checking your location…
                    </div>
                  )}

                  {/* Finish zone — outside */}
                  {finishGeoState === 'outside' && (
                    <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', padding: '14px 16px' }}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                        <AlertCircle size={18} color="var(--hp-red)" style={{ flexShrink: 0, marginTop: 1 }} />
                        <div>
                          <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--hp-red)', marginBottom: 3 }}>Outside delivery zone</div>
                          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', lineHeight: 1.5 }}>
                            You are {finishGeoDistance ? `${finishGeoDistance.toLocaleString()}m` : 'too far'} from the delivery area. You must finish your shift from within the delivery zone.
                          </div>
                        </div>
                      </div>
                      <button style={{ ...S.bigBtn('muted'), fontSize: '0.82rem', padding: '12px' }} onClick={goToFinish}>
                        <Navigation size={16} /> Try again
                      </button>
                    </div>
                  )}

                  {/* Finish zone — GPS denied or error */}
                  {(finishGeoState === 'denied' || finishGeoState === 'error') && (
                    <div style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', padding: '14px 16px', fontSize: '0.82rem', color: 'var(--hp-red)' }}>
                      <AlertCircle size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                      {finishGeoState === 'denied' ? 'Location access denied — enable GPS to finish your shift.' : 'Could not get your location. Please try again.'}
                    </div>
                  )}

                  {finishBlockMsg && (
                    <div style={{ background: 'rgba(201,168,76,0.1)', border: '1px solid rgba(201,168,76,0.45)', borderRadius: 'var(--radius)', padding: '14px 16px', fontSize: '0.85rem', color: 'var(--hp-text)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <School size={18} color="var(--hp-gold)" style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>{finishBlockMsg}</span>
                    </div>
                  )}

                  {finishGeoState !== 'checking' && finishGeoState !== 'outside' && (
                    <button style={S.bigBtn('red')} onClick={goToFinish}>
                      <Flag size={20} /> FINISH SHIFT
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* ── ON BREAK ── */}
            {!odoCapturing && shiftState === 'on_break' && (
              <div style={S.card}>
                <div style={S.timerDisplay}>
                  <div style={{ ...S.timerNum, color: 'var(--hp-amber)' }}>{formatDuration(breakElapsed)}</div>
                  <div style={S.timerLabel}>{breaksCount > 1 ? `Break #${breaksCount} duration` : 'Break duration'}</div>
                </div>
                <div style={{ marginBottom: 20 }}>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>Shift started</span>
                    <span style={{ ...S.metaValue, fontFamily: 'var(--font-mono)' }}>{formatTimeStr(shift.shiftStart)}</span>
                  </div>
                  {totalBreakTaken > 0 && (
                    <div style={S.metaRow}>
                      <span style={S.metaLabel}>Earlier breaks ({breaksCount - 1})</span>
                      <span style={{ ...S.metaValue, fontFamily: 'var(--font-mono)' }}>{formatDuration(totalBreakTaken)}</span>
                    </div>
                  )}
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>Break started</span>
                    <span style={{ ...S.metaValue, fontFamily: 'var(--font-mono)' }}>{formatTimeStr(openBreak?.start || shift.breakStart)}</span>
                  </div>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>Worked so far</span>
                    <span style={{ ...S.metaValue, fontFamily: 'var(--font-mono)' }}>{formatDuration(workedSoFar)}</span>
                  </div>
                </div>
                <button style={S.bigBtn('green')} onClick={endBreak}>
                  <Play size={20} /> END BREAK
                </button>
              </div>
            )}

            {/* ── FINISHING ── */}
            {shiftState === 'finishing' && (
              <div style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--hp-text)', letterSpacing: '0.04em' }}>
                    FINISH SHIFT
                  </div>
                  <a href="/finish-guide.html" target="_blank" rel="noreferrer"
                    style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--hp-gold)', textDecoration: 'none' }}>
                    ▶ Watch how
                  </a>
                </div>

                <CustomerCaseNudge count={csPending} onGo={() => setPortalTab('customerservice')} />
                <div style={{ marginBottom: 24 }}>
                  <div style={S.metaRow}>
                    <span style={S.metaLabel}>Start time</span>
                    <span style={{ ...S.metaValue, fontFamily: 'var(--font-mono)' }}>{formatTimeStr(shift.shiftStart)}</span>
                  </div>
                  {breaksCount > 0 && (
                    <div style={S.metaRow}>
                      <span style={S.metaLabel}>{breaksCount === 1 ? 'Break' : `Breaks (${breaksCount})`}</span>
                      <span style={{ ...S.metaValue, fontFamily: 'var(--font-mono)' }}>
                        {breaksCount === 1
                          ? `${formatTimeStr(shiftBreaks[0].start)} – ${formatTimeStr(shiftBreaks[0].end)}`
                          : `${formatDuration(totalBreakTaken)} total`}
                      </span>
                    </div>
                  )}
                  <div style={{ ...S.metaRow, borderBottom: 'none' }}>
                    <span style={S.metaLabel}>Finish time</span>
                    <span style={{ ...S.metaValue, fontFamily: 'var(--font-mono)', color: 'var(--hp-gold)' }}>NOW — {formatTimeStr(new Date().toISOString())}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>
                  {odoOptional && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '10px 12px' }}>
                      Odometer is optional for your account — leave blank if you didn't convey parcels, or enter start &amp; finish km if you did.
                    </div>
                  )}
                  <div>
                    <label style={S.label}>Odometer at Start (km){odoOptional ? ' — optional' : ''}</label>
                    <input style={{ ...S.input, fontFamily: 'var(--font-mono)', fontSize: '1.3rem' }}
                      type="number" inputMode="numeric" placeholder="e.g. 123456"
                      value={odoStart} onChange={e => setOdoStart(e.target.value)} />
                  </div>
                  <div>
                    <label style={S.label}>Odometer at Finish (km){odoOptional ? ' — optional' : ''}</label>
                    <input style={{ ...S.input, fontFamily: 'var(--font-mono)', fontSize: '1.3rem' }}
                      type="number" inputMode="numeric" placeholder="e.g. 123678"
                      value={odoEnd} onChange={e => setOdoEnd(e.target.value)} />
                  </div>
                  <OdoWarning check={odoEndChk} kept={odoEndKept}
                    onKeep={() => setOdoEndKept(true)} />
                  {kmPreview !== null && odoEndChk.ok && (
                    <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 'var(--radius)', padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', fontWeight: 600 }}>TOTAL KM</span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.3rem', fontWeight: 700, color: 'var(--hp-green)' }}>{kmPreview} km</span>
                    </div>
                  )}
                </div>
                {/* ── Carry-over parcels report ── */}
                <div ref={carrySectionRef} style={{ marginBottom: 16, border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.95rem', color: 'var(--hp-text)', marginBottom: 12 }}>
                    <Package size={18} color="var(--hp-gold)" />
                    Do you have any carry-over parcels? <span style={{ color: 'var(--hp-red)' }}>*</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <button
                      onClick={() => { setCarryOver('no'); setCarrySel({}); setCarryPoAck(false); setSubmitError(''); }}
                      style={{ padding: '14px 10px', borderRadius: 'var(--radius)', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer',
                        background: carryOver === 'no' ? 'rgba(52,211,153,0.15)' : 'var(--hp-surface)',
                        border: carryOver === 'no' ? '2px solid var(--hp-green)' : '1px solid var(--hp-border)',
                        color: carryOver === 'no' ? 'var(--hp-green)' : 'var(--hp-text)' }}
                    >
                      No — all delivered / transferred
                    </button>
                    <button
                      onClick={() => { setCarryOver('yes'); setSubmitError(''); }}
                      style={{ padding: '14px 10px', borderRadius: 'var(--radius)', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer',
                        background: carryOver === 'yes' ? 'rgba(245,158,11,0.15)' : 'var(--hp-surface)',
                        border: carryOver === 'yes' ? '2px solid var(--hp-amber)' : '1px solid var(--hp-border)',
                        color: carryOver === 'yes' ? 'var(--hp-amber)' : 'var(--hp-text)' }}
                    >
                      Yes — parcels remaining
                    </button>
                  </div>

                  {carryOver === 'yes' && (
                    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', fontWeight: 600 }}>
                        Select the reason(s) and how many parcels for each:
                      </div>
                      {CARRY_REASONS.map(r => {
                        const sel = carrySel[r.key];
                        return (
                          <div key={r.key} style={{ border: sel ? '1px solid rgba(201,168,76,0.5)' : '1px solid var(--hp-border)', borderRadius: 'var(--radius)', background: sel ? 'rgba(201,168,76,0.06)' : 'var(--hp-surface)' }}>
                            <button
                              onClick={() => toggleCarryReason(r.key)}
                              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '13px 14px', background: 'none', border: 'none', color: 'var(--hp-text)', fontWeight: 700, fontSize: '0.88rem', cursor: 'pointer', textAlign: 'left' }}
                            >
                              <span>{r.label}{r.photo ? ' 📷' : ''}</span>
                              <span style={{ fontSize: '1rem', color: sel ? 'var(--hp-gold)' : 'var(--hp-dim)', flexShrink: 0, marginLeft: 8 }}>{sel ? '✓' : '+'}</span>
                            </button>
                            {sel && (
                              <div style={{ padding: '0 14px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {r.key === 'address' && (
                                  <div style={{ background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.4)', borderRadius: 'var(--radius)', padding: '12px 14px', fontSize: '0.82rem', color: 'var(--hp-text)', lineHeight: 1.5 }}>
                                    <strong style={{ color: 'var(--hp-amber)' }}>📮 Transfer these to a Post Office</strong><br />
                                    Incorrect-address parcels should be transferred to a Post Office — please do not bring them back to the depot.
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, cursor: 'pointer', fontWeight: 700 }}>
                                      <input type="checkbox" checked={carryPoAck} onChange={e => setCarryPoAck(e.target.checked)} style={{ width: 20, height: 20, flexShrink: 0 }} />
                                      I have transferred (or will transfer) these to a Post Office
                                    </label>
                                  </div>
                                )}
                                {r.incident && (
                                  <div style={{ background: 'rgba(220,38,38,0.07)', border: '1px solid rgba(220,38,38,0.35)', borderRadius: 'var(--radius)', padding: '12px 14px', fontSize: '0.82rem', color: 'var(--hp-text)', lineHeight: 1.5 }}>
                                    <strong style={{ color: 'var(--hp-red)' }}>Report a missing parcel</strong><br />
                                    Open the parcel on your scanner (Task List or Handover to Retail) so the <strong>barcode number</strong> is on screen, then take a photo of the scanner. We fill in the numbers from the photo. Check them, then say what happened.
                                  </div>
                                )}
                                <div>
                                  <label style={S.label}>How many parcels?</label>
                                  <input style={{ ...S.input, fontFamily: 'var(--font-mono)' }}
                                    type="number" inputMode="numeric" min="1" placeholder="e.g. 2"
                                    value={sel.count} onChange={e => updateCarry(r.key, { count: e.target.value })} />
                                </div>
                                {r.note && (
                                  <div>
                                    <label style={S.label}>What happened?</label>
                                    <textarea
                                      style={{ ...S.input, minHeight: 64, resize: 'vertical', fontFamily: 'inherit' }}
                                      placeholder={r.incident ? 'e.g. Attempted delivery, could not deliver. I think I dropped it walking back to the van.' : 'Briefly describe the reason'}
                                      value={sel.note} onChange={e => updateCarry(r.key, { note: e.target.value })} />
                                  </div>
                                )}
                                {r.photo && (() => {
                                  const shots = sel.photoUrls || [];
                                  return (
                                  <div>
                                    {shots.length > 0 ? (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: 'var(--hp-green)', fontWeight: 700, marginBottom: 8 }}>
                                        <CheckCircle size={16} /> {shots.length} photo{shots.length > 1 ? 's' : ''} sent — received by management
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 6, fontWeight: 700 }}>Photo required</div>
                                    )}
                                    <div style={{ display: 'flex', gap: 8 }}>
                                      <label style={{ flex: 1, cursor: sel.uploading ? 'wait' : 'pointer' }}>
                                        <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} disabled={sel.uploading}
                                          onChange={e => { const f = e.target.files?.[0]; if (f) uploadCarryPhoto(r.key, r.label, f); e.target.value = ''; }} />
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px', background: 'rgba(201,168,76,0.07)', border: '2px dashed rgba(201,168,76,0.5)', borderRadius: 'var(--radius)', fontWeight: 700, fontSize: '0.85rem', color: 'var(--hp-gold)', opacity: sel.uploading ? 0.7 : 1 }}>
                                          <Camera size={16} /> {sel.uploading ? 'Sending…' : (shots.length > 0 ? 'Take another' : 'Take photo')}
                                        </div>
                                      </label>
                                      <label style={{ flex: 1, cursor: sel.uploading ? 'wait' : 'pointer' }}>
                                        <input type="file" accept="image/*" style={{ display: 'none' }} disabled={sel.uploading}
                                          onChange={e => { const f = e.target.files?.[0]; if (f) uploadCarryPhoto(r.key, r.label, f); e.target.value = ''; }} />
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '13px', background: 'rgba(201,168,76,0.07)', border: '2px dashed rgba(201,168,76,0.5)', borderRadius: 'var(--radius)', fontWeight: 700, fontSize: '0.85rem', color: 'var(--hp-gold)', opacity: sel.uploading ? 0.7 : 1 }}>
                                          <ImageIcon size={16} /> {sel.uploading ? 'Sending…' : (shots.length > 0 ? 'Upload another' : 'Upload photo')}
                                        </div>
                                      </label>
                                    </div>
                                    {sel.photoError && (
                                      <div style={{ fontSize: '0.78rem', color: 'var(--hp-red)', marginTop: 6 }}>{sel.photoError}</div>
                                    )}
                                  </div>
                                  );
                                })()}
                                {r.incident && (
                                  <>
                                    <div style={{ display: 'flex', gap: 8 }}>
                                      <div style={{ flex: 1 }}>
                                        <label style={S.label}>Consignment</label>
                                        <input style={{ ...S.input, fontFamily: 'var(--font-mono)' }} placeholder="e.g. KCE2046189"
                                          value={sel.consignment} onChange={e => updateCarry(r.key, { consignment: e.target.value.toUpperCase() })} />
                                      </div>
                                      <div style={{ flex: 1.4 }}>
                                        <label style={S.label}>Barcode / article number</label>
                                        <input style={{ ...S.input, fontFamily: 'var(--font-mono)' }} inputMode="numeric" placeholder="from the photo"
                                          value={sel.articleId} onChange={e => updateCarry(r.key, { articleId: e.target.value })} />
                                      </div>
                                    </div>
                                    <div>
                                      <label style={S.label}>What did you do to find it?</label>
                                      <textarea style={{ ...S.input, minHeight: 56, resize: 'vertical', fontFamily: 'inherit' }}
                                        placeholder="e.g. Went back and retraced my steps, could not find it."
                                        value={sel.actionTaken} onChange={e => updateCarry(r.key, { actionTaken: e.target.value })} />
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* ── Scanner photo ── */}
                <div ref={scannerSectionRef}>
                {scannerDone ? (
                  /* ── Done state ── */
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '16px', background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.35)', borderRadius: 'var(--radius)', marginBottom: 16 }}>
                    <CheckCircle size={32} color="var(--hp-green)" style={{ flexShrink: 0 }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--hp-green)' }}>Scanner photo sent</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 2 }}>Received by management ✓</div>
                    </div>
                  </div>
                ) : scannerCapture ? (
                  /* ── Preview state ── */
                  <div style={{ marginBottom: 16 }}>
                    <img src={scannerCapture.dataUrl} alt="Scanner" style={{ width: '100%', borderRadius: 'var(--radius)', background: '#000', display: 'block', maxHeight: 240, objectFit: 'contain' }} />
                    <button
                      onClick={() => setScannerCapture(null)}
                      style={{ marginTop: 10, width: '100%', padding: '14px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-text)', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}
                    >
                      ↺ Retake
                    </button>
                    <div style={{ marginTop: 10, fontSize: '0.8rem', color: 'var(--hp-dim)', display: 'flex', alignItems: 'flex-start', gap: 6, lineHeight: 1.5 }}>
                      <CheckCircle size={15} color="var(--hp-green)" style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>Photo ready — it’s sent automatically when you tap <strong style={{ color: 'var(--hp-text)' }}>SUBMIT SHIFT</strong> below.</span>
                    </div>
                  </div>
                ) : scannerNoPhoto ? (
                  /* ── Finishing without a photo (camera fault) ── */
                  <div style={{ padding: '14px 16px', background: 'rgba(245,158,11,0.09)', border: '1px solid var(--hp-amber)', borderRadius: 'var(--radius)', marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--hp-amber)' }}>Finishing without the scanner photo</span>
                      <button onClick={() => { setScannerNoPhoto(false); setScannerFailure(''); }}
                        style={{ background: 'none', border: 'none', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', flexShrink: 0 }}>
                        Try again
                      </button>
                    </div>
                    <div style={{ fontSize: '0.79rem', color: 'var(--hp-dim)', marginTop: 6, lineHeight: 1.55 }}>
                      The office will see the reason on your shift. <strong style={{ color: 'var(--hp-text)' }}>Send them the photo</strong> when
                      you can — they can attach it to today for you.
                    </div>
                  </div>
                ) : scannerSkipped ? (
                  /* ── Skipped state ── */
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', marginBottom: 16 }}>
                    <span style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>No scanner used today — no photo required</span>
                    <button onClick={() => setScannerSkipped(false)} style={{ background: 'none', border: 'none', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                      + Add photo
                    </button>
                  </div>
                ) : (
                  /* ── Capture state — live in-app camera (getUserMedia) + upload fallback ── */
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ textAlign: 'center', marginBottom: 12 }}>
                      <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--hp-text)' }}>
                        Photograph your scanner {!scannerOptional && <span style={{ color: 'var(--hp-red)' }}>*</span>}
                      </div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginTop: 4 }}>
                        Tap <strong>Take photo</strong>, point at the PUD Main Menu and snap — your phone's side/volume buttons work as the shutter too.
                      </div>
                    </div>
                    <CameraCapture
                      nativeFirst
                      guide="document"
                      maxEdge={1400}
                      quality={0.85}
                      value={null}
                      onCapture={dataUrl => setScannerCapture({ dataUrl, file: null })}
                      onClear={() => setScannerCapture(null)}
                      onFailure={setScannerFailure}
                    />
                    {/* Only drawn once the camera has actually failed. */}
                    {scannerFailure && !scannerNoPhoto && (
                      <div style={{ marginTop: 12, padding: '14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.35)', borderRadius: 'var(--radius)' }}>
                        <div style={{ fontWeight: 800, fontSize: '0.9rem', color: 'var(--hp-red)', display: 'flex', alignItems: 'center', gap: 7 }}>
                          <AlertTriangle size={16} /> Camera won&rsquo;t take the photo?
                        </div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 6, lineHeight: 1.55 }}>
                          Try <strong style={{ color: 'var(--hp-text)' }}>Upload / screenshot</strong> first — take the picture in your
                          phone&rsquo;s own Camera app, then choose it here. If that won&rsquo;t work either, finish your shift without it
                          and <strong style={{ color: 'var(--hp-text)' }}>send the photo to the office</strong> — they&rsquo;ll attach it.
                          Your shift is recorded either way.
                        </div>
                        <button
                          onClick={() => { setScannerNoPhoto(true); setSubmitError(''); }}
                          style={{ marginTop: 12, width: '100%', padding: '13px', background: 'var(--hp-surface)', border: '1px solid var(--hp-red)', borderRadius: 'var(--radius)', color: 'var(--hp-red)', fontWeight: 800, fontSize: '0.88rem', cursor: 'pointer' }}>
                          Finish without the photo — my camera is broken
                        </button>
                      </div>
                    )}
                    {scannerOptional && (
                      <button
                        onClick={() => { setScannerSkipped(true); setSubmitError(''); }}
                        style={{ marginTop: 10, width: '100%', padding: '12px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}
                      >
                        No scanner used today
                      </button>
                    )}
                  </div>
                )}
                </div>

                {submitError && (
                  <div style={{ ...S.errorBox, marginBottom: 16 }}>
                    <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                    {submitError}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {!scannerSatisfied && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: 'var(--hp-gold)', fontWeight: 600 }}>
                      <Camera size={15} />
                      {scannerOptional
                        ? 'Add a scanner photo/screenshot, or tap “No scanner used today”.'
                        : 'Scanner photo required before you can submit.'}
                    </div>
                  )}
                  {!carrySatisfied && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: 'var(--hp-gold)', fontWeight: 600 }}>
                      <Package size={15} />
                      {carryOver === null
                        ? 'Answer the carry-over parcels question above.'
                        : 'Finish the carry-over report — reason(s), parcel counts and any required photos.'}
                    </div>
                  )}

                  {/* Always tappable (except mid-submit): a blocked tap explains
                      what's missing and scrolls to it — a dead grey button looked
                      "done" to drivers, who walked away mid-finish. */}
                  <button style={{ ...S.bigBtn('green'), opacity: (submitting || !scannerSatisfied || !carrySatisfied) ? 0.55 : 1 }}
                    onClick={submitShift} disabled={submitting}>
                    <Gauge size={20} />
                    {submitting ? 'Submitting...' : 'SUBMIT SHIFT'}
                  </button>
                  <button
                    style={{ ...S.bigBtn('muted'), fontSize: '0.875rem', padding: '14px' }}
                    onClick={() => { setShiftState('on_shift'); persist(shift, 'on_shift'); setScannerCapture(null); }}
                    disabled={submitting}
                  >Back — still working</button>
                </div>
              </div>
            )}

            {/* ── PENDING REVIEW ── */}
            {shiftState === 'pending_review' && existingSub && (
              <div style={S.card}>
                <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
                  <Clock size={48} style={{ color: 'var(--hp-amber)', marginBottom: 12 }} />
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--hp-text)', letterSpacing: '0.04em' }}>
                    PENDING REVIEW
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', marginTop: 8 }}>
                    Your shift has been submitted and is awaiting manager approval
                  </div>
                </div>
                <SubmissionSummary sub={existingSub} />
                <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button style={S.bigBtn('amber')} onClick={startAmendment}>
                    <Edit3 size={18} /> REQUEST AMENDMENT
                  </button>
                  <p style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--hp-dim)', margin: 0 }}>
                    Need to correct a mistake? Request an amendment for manager review.
                  </p>
                </div>
              </div>
            )}

            {/* ── APPROVED ── */}
            {shiftState === 'approved' && existingSub && (
              <div style={S.card}>
                <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
                  <CheckCircle size={48} style={{ color: 'var(--hp-green)', marginBottom: 12 }} />
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--hp-text)', letterSpacing: '0.04em' }}>
                    SHIFT APPROVED
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', marginTop: 8 }}>
                    Your shift has been approved by management
                  </div>
                </div>
                <SubmissionSummary sub={existingSub} />
                <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button
                    style={{ ...S.bigBtn('muted'), fontSize: '0.875rem', padding: '14px', color: 'var(--hp-dim)' }}
                    onClick={startAmendment}
                  >
                    <Edit3 size={16} /> Request Amendment
                  </button>
                  <p style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--hp-dim)', margin: 0 }}>
                    Only request an amendment if there is a genuine error.
                  </p>
                </div>
              </div>
            )}

            {/* ── REJECTED ── */}
            {shiftState === 'rejected' && existingSub && (
              <div style={S.card}>
                <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
                  <XCircle size={48} style={{ color: 'var(--hp-red)', marginBottom: 12 }} />
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--hp-text)', letterSpacing: '0.04em' }}>
                    SHIFT REJECTED
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', marginTop: 8 }}>
                    Your manager has rejected this shift submission
                  </div>
                </div>
                {existingSub.managerNotes && (
                  <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', padding: '14px 16px', marginBottom: 20 }}>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-red)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Manager Note</div>
                    <div style={{ fontSize: '0.9rem', color: 'var(--hp-text)' }}>{existingSub.managerNotes}</div>
                  </div>
                )}
                <SubmissionSummary sub={existingSub} />
                <div style={{ marginTop: 20 }}>
                  <button style={S.bigBtn('amber')} onClick={startAmendment}>
                    <Edit3 size={18} /> RESUBMIT WITH CORRECTIONS
                  </button>
                </div>
              </div>
            )}

            {/* ── AMENDING ── */}
            {shiftState === 'amending' && (
              <div style={S.card}>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--hp-text)', letterSpacing: '0.04em' }}>
                    REQUEST AMENDMENT
                  </div>
                  <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', marginTop: 6 }}>
                    Correct the details below. Your manager will review the changes.
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>
                  {/* Times section */}
                  <div style={{ background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '16px' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Shift Times</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={{ ...S.label, marginBottom: 4 }}>Shift Start *</label>
                        <input style={S.timeInput} type="time"
                          value={amendForm.shiftStart}
                          onChange={e => setAmendForm(f => ({ ...f, shiftStart: e.target.value }))}
                          required />
                      </div>
                      <div>
                        <label style={{ ...S.label, marginBottom: 4 }}>Finish Time *</label>
                        <input style={S.timeInput} type="time"
                          value={amendForm.finishTime}
                          onChange={e => setAmendForm(f => ({ ...f, finishTime: e.target.value }))}
                          required />
                      </div>
                      <div>
                        <label style={{ ...S.label, marginBottom: 4 }}>Break Start</label>
                        <input style={S.timeInput} type="time"
                          value={amendForm.breakStart}
                          onChange={e => setAmendForm(f => ({ ...f, breakStart: e.target.value }))} />
                      </div>
                      <div>
                        <label style={{ ...S.label, marginBottom: 4 }}>Break End</label>
                        <input style={S.timeInput} type="time"
                          value={amendForm.breakEnd}
                          onChange={e => setAmendForm(f => ({ ...f, breakEnd: e.target.value }))} />
                      </div>
                    </div>
                  </div>

                  {/* Odometers section */}
                  <div style={{ background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '16px' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Odometer</div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                      <div>
                        <label style={{ ...S.label, marginBottom: 4 }}>Start (km) *</label>
                        <input style={{ ...S.timeInput, fontSize: '1rem' }}
                          type="number" inputMode="numeric" placeholder="Start km"
                          value={amendForm.odoStart}
                          onChange={e => setAmendForm(f => ({ ...f, odoStart: e.target.value }))} />
                      </div>
                      <div>
                        <label style={{ ...S.label, marginBottom: 4 }}>Finish (km) *</label>
                        <input style={{ ...S.timeInput, fontSize: '1rem' }}
                          type="number" inputMode="numeric" placeholder="Finish km"
                          value={amendForm.odoEnd}
                          onChange={e => setAmendForm(f => ({ ...f, odoEnd: e.target.value }))} />
                      </div>
                    </div>
                    {/* Preview */}
                    {(amendKmPreview !== null || amendHoursPreview !== null) && (
                      <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                        {amendKmPreview !== null && (
                          <div style={{ flex: 1, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 6, padding: '8px 12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.65rem', color: 'var(--hp-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Total KM</div>
                            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.1rem', fontWeight: 700, color: 'var(--hp-green)' }}>{amendKmPreview} km</div>
                          </div>
                        )}
                        {amendHoursPreview !== null && amendHoursPreview > 0 && (
                          <div style={{ flex: 1, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 6, padding: '8px 12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '0.65rem', color: 'var(--hp-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Hours</div>
                            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.1rem', fontWeight: 700, color: 'var(--hp-green)' }}>{amendHoursPreview}h</div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Reason */}
                  <div>
                    <label style={S.label}>Reason for Amendment *</label>
                    <textarea
                      rows={3}
                      placeholder="e.g. Forgot to log break — took 30 min break at 11:00"
                      value={amendForm.reason}
                      onChange={e => setAmendForm(f => ({ ...f, reason: e.target.value }))}
                      style={{ width: '100%', background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-text)', fontSize: '0.9rem', padding: '12px 14px', outline: 'none', resize: 'vertical', fontFamily: 'var(--font-body)', WebkitAppearance: 'none' }}
                    />
                  </div>
                </div>

                {amendError && (
                  <div style={{ ...S.errorBox, marginBottom: 16 }}>
                    <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                    {amendError}
                  </div>
                )}

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <button
                    style={{ ...S.bigBtn('purple'), opacity: amendSubmitting ? 0.6 : 1, color: '#fff' }}
                    onClick={submitAmendment}
                    disabled={amendSubmitting}
                  >
                    <Send size={18} />
                    {amendSubmitting ? 'Submitting...' : 'SUBMIT AMENDMENT'}
                  </button>
                  <button
                    style={{ ...S.bigBtn('muted'), fontSize: '0.875rem', padding: '14px' }}
                    onClick={() => {
                      const st = statusToShiftState(existingSub?.status || 'PENDING');
                      setShiftState(st);
                      persist(shift, st, existingSub);
                    }}
                    disabled={amendSubmitting}
                  >Cancel</button>
                </div>
              </div>
            )}

            {/* ── AMENDMENT SENT ── */}
            {shiftState === 'amendment_sent' && existingSub && (
              <div style={S.card}>
                <div style={{ textAlign: 'center', padding: '8px 0 20px' }}>
                  <CheckCircle size={48} style={{ color: '#a78bfa', marginBottom: 12 }} />
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.6rem', color: 'var(--hp-text)', letterSpacing: '0.04em' }}>
                    AMENDMENT SUBMITTED
                  </div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', marginTop: 8 }}>
                    Your correction is awaiting manager review
                  </div>
                </div>
                <SubmissionSummary sub={existingSub} />
                {existingSub.amendmentReason && (
                  <div style={{ marginTop: 16, background: 'rgba(139,92,246,0.1)', border: '1px solid rgba(139,92,246,0.25)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
                    <div style={{ fontSize: '0.7rem', color: '#a78bfa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>Your Reason</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--hp-text)' }}>{existingSub.amendmentReason}</div>
                  </div>
                )}
                <button style={{ ...S.bigBtn('gold'), marginTop: 20 }} onClick={logout}>
                  <LogOut size={18} /> LOG OUT
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Driver History View ───────────────────────────────────────────────────────
// ── Afternoon check: who's still on shift / hasn't logged depots ──────────────
function AfternoonCheckView({ areas }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const res = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getAfternoonCheck', date: localDateStr(), areas }),
      });
      const d = await res.json();
      if (!d.success) throw new Error(d.error || 'Could not load check');
      setData(d);
    } catch (e) { setErr(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, [areas]);
  useEffect(() => { load(); }, [load]);

  const card = { background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', padding: 20 };
  const real  = (data?.unfinished || []).filter(u => !u.isStaff);
  const staff = (data?.unfinished || []).filter(u => u.isStaff);

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--hp-gold)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Clock size={16} /> Afternoon Check
        </div>
        <button onClick={load} style={{ background: 'none', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-dim)', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', fontFamily: 'var(--font-body)' }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--hp-dim)', fontSize: '0.85rem' }}>
          <div style={{ width: 30, height: 30, border: '3px solid var(--hp-border)', borderTopColor: 'var(--hp-gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 10px' }} />
          Checking…
        </div>
      )}
      {err && !loading && (
        <div style={{ color: 'var(--hp-red)', fontSize: '0.82rem', display: 'flex', gap: 8, alignItems: 'center' }}>
          <AlertCircle size={15} /> {err}
        </div>
      )}

      {data && !loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
            <CheckCircle size={13} color="var(--hp-green)" style={{ verticalAlign: 'middle', marginRight: 6 }} />
            {data.finishedCount} shift{data.finishedCount !== 1 ? 's' : ''} finished today
          </div>

          {/* Finished — with finish times */}
          {(data.finished || []).length > 0 && (
            <div>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
                Finished ({data.finished.length})
              </div>
              {data.finished.map(f => (
                <div key={f.login} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '7px 10px', background: 'rgba(52,211,153,0.06)', border: '1px solid rgba(52,211,153,0.22)', borderRadius: 8, marginBottom: 5 }}>
                  <span style={{ fontSize: '0.84rem', color: 'var(--hp-text)' }}>
                    <strong>{f.login}</strong>{f.isStaff ? <span style={{ color: 'var(--hp-dim)', fontSize: '0.7rem' }}> (staff)</span> : ''}
                  </span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--hp-green)', fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                    {f.finishTime}<span style={{ color: 'var(--hp-dim)', fontWeight: 600, marginLeft: 8 }}>{f.area || ''}</span>
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Still on shift */}
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              Still on shift — not finished ({real.length})
            </div>
            {real.length === 0 ? (
              <div style={{ fontSize: '0.82rem', color: 'var(--hp-green)', fontWeight: 600 }}>✓ All drivers have finished</div>
            ) : real.map(u => (
              <div key={u.login} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 8, marginBottom: 6 }}>
                <span style={{ fontSize: '0.84rem', color: 'var(--hp-text)' }}>
                  <strong>{u.login}</strong>{u.name && u.name !== u.login ? ` · ${u.name}` : ''}
                </span>
                <span style={{ fontSize: '0.72rem', color: 'var(--hp-amber)', fontWeight: 700 }}>
                  {u.area || '—'}{u.state === 'on_break' ? ' · on break' : u.state === 'finishing' ? ' · at finish screen' : ''}
                </span>
              </div>
            ))}
            {staff.length > 0 && (
              <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 4 }}>
                Also logged in (staff/admin): {staff.map(u => u.login).join(', ')}
              </div>
            )}
          </div>

          {/* Depot not logged */}
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
              Depot bags not logged ({data.depotMissing.length})
            </div>
            {data.depotMissing.length === 0 ? (
              <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
                {data.depotLoggedCount > 0
                  ? '✓ All active depot drivers have logged'
                  : 'No depot roster configured (set active depot drivers in Direct Entry → Depots).'}
              </div>
            ) : data.depotMissing.map(m => (
              <div key={m.login} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: 'rgba(96,165,250,0.08)', border: '1px solid rgba(96,165,250,0.25)', borderRadius: 8, marginBottom: 6 }}>
                <span style={{ fontSize: '0.84rem', color: 'var(--hp-text)' }}><strong>{m.login}</strong></span>
                <span style={{ fontSize: '0.72rem', color: '#60a5fa', fontWeight: 700 }}>{m.area || m.route || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Team-leader / admin aggregated period totals ──────────────────────────────
function TeamHistoryView({ shift, config }) {
  const [byDate, setByDate]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [view, setView]       = useState('period');

  const { start: periodStart, end: periodEnd } = currentPeriodDates();
  const { start: prevPeriodStart, end: prevPeriodEnd } = previousPeriodDates();
  const today   = isoToday();
  const lwdDate = lastWorkingDay();
  // Floor at the fetched window (prevPeriodStart), NOT periodStart: at the start of a
  // new period the last working day still sits in the previous period, so clamping to
  // periodStart would push weekStart past weekEnd → an inverted, empty range.
  const weekStart = addDays(lwdDate, -6) < prevPeriodStart ? prevPeriodStart : addDays(lwdDate, -6);
  const weekEnd   = lwdDate;

  const load = useCallback(async (force) => {
    const cacheKey = `hp-teamhist-${shift.driver}-${prevPeriodStart}-${periodEnd}`;
    if (!force) {
      try {
        const c = JSON.parse(localStorage.getItem(cacheKey) || 'null');
        if (c && Date.now() - c.ts < 10 * 60 * 1000) { setByDate(c.byDate); setLoading(false); return; }
      } catch {}
    }
    setLoading(true); setError('');
    try {
      const res = await fetch('/.netlify/functions/team-totals-sb', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'getTeamTotals', areas: config.areas, periodStart: prevPeriodStart, periodEnd }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Could not load totals');
      setByDate(data.byDate || {});
      try { localStorage.setItem(cacheKey, JSON.stringify({ byDate: data.byDate || {}, ts: Date.now() })); } catch {}
    } catch (e) { setError(e.message || 'Failed to load totals'); }
    finally { setLoading(false); }
  }, [shift.driver, prevPeriodStart, periodEnd, config.areas]);

  useEffect(() => { load(false); }, [load]);

  const sumRange = (from, to) => {
    const acc = { parcels: 0, express: 0, depots: 0, doe: 0, days: 0 };
    Object.keys(byDate || {}).forEach(d => {
      if (d < from || d > to) return;
      const v = byDate[d] || {};
      acc.parcels += v.parcels || 0;
      acc.express += v.express || 0;
      acc.depots  += v.depots  || 0;
      acc.doe     += v.doe     || 0;
      if ((v.parcels || 0) + (v.express || 0) + (v.depots || 0) + (v.doe || 0) > 0) acc.days += 1;
    });
    return acc;
  };

  const lwdLabel = (() => {
    const diff = Math.round((new Date(today) - new Date(lwdDate)) / 86400000);
    if (diff === 1) return 'Yesterday';
    return `Last ${new Date(lwdDate + 'T12:00:00').toLocaleDateString('en-AU', { weekday: 'long' })}`;
  })();

  const ranges = {
    yesterday:  [lwdDate, lwdDate],
    week:       [weekStart, weekEnd],
    period:     [periodStart, periodEnd],
    lastperiod: [prevPeriodStart, prevPeriodEnd],
  };
  const [rFrom, rTo] = ranges[view];
  const totals = sumRange(rFrom, rTo);

  const card = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', padding: '20px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Scope badge */}
      <div style={{ ...card, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Team totals</div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--hp-text)', marginTop: 2 }}>{config.scope}</div>
        </div>
        <button onClick={() => load(true)} style={{ background: 'none', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-dim)', padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.72rem', fontFamily: 'var(--font-body)' }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {/* Period toggle */}
      <div style={{ display: 'flex', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', padding: 4, gap: 3 }}>
        {[
          { key: 'yesterday',  label: lwdLabel },
          { key: 'week',       label: 'Last 7 Days' },
          { key: 'period',     label: 'This Period' },
          { key: 'lastperiod', label: 'Last Period' },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setView(key)} style={{
            flex: 1, padding: '10px 2px', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer',
            fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em',
            background: view === key ? 'var(--hp-gold)' : 'none',
            color:      view === key ? 'var(--hp-black)' : 'var(--hp-dim)',
          }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ textAlign: 'center', fontSize: '0.78rem', color: 'var(--hp-dim)' }}>
        {formatShortDate(rFrom)}{rFrom !== rTo ? ` → ${formatShortDate(rTo)}` : ''}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--hp-dim)' }}>
          <div style={{ width: 36, height: 36, border: '3px solid var(--hp-border)', borderTopColor: 'var(--hp-gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          Loading team totals…
        </div>
      )}

      {error && !loading && (
        <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 'var(--radius)', padding: '14px 16px', color: 'var(--hp-red)', fontSize: '0.875rem', display: 'flex', gap: 10, alignItems: 'center' }}>
          <AlertCircle size={16} style={{ flexShrink: 0 }} /> {error}
          <button onClick={() => load(true)} style={{ marginLeft: 'auto', background: 'none', border: '1px solid var(--hp-red)', borderRadius: 4, color: 'var(--hp-red)', padding: '4px 10px', cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'var(--font-body)' }}>Retry</button>
        </div>
      )}

      {!loading && !error && (
        <>
          <div style={{ ...card }}>
            <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
              {view === 'yesterday' ? lwdLabel : view === 'week' ? 'Last 7 Days' : view === 'period' ? 'Period' : 'Last Period'} totals
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
              {config.metrics.map(m => {
                const meta = METRIC_META[m];
                return (
                  <div key={m} style={{ background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '14px 12px', textAlign: 'center' }}>
                    <div style={{ fontSize: '0.62rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{meta.label}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.5rem', fontWeight: 800, color: meta.color, marginTop: 4 }}>
                      {Math.round(totals[m]).toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ marginTop: 12, fontSize: '0.72rem', color: 'var(--hp-dim)', textAlign: 'center' }}>
              {totals.days} day{totals.days !== 1 ? 's' : ''} with activity
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ── ASTORGACV: view either Broadway driver's full history (parcels, hours, km, depot) ──
const DEPOT_ASSIST_LOGINS = new Set(['ASTORGACV']);
// View-only Parcel Audit tab. The real gate is AUDIT_VIEWERS in parcel-audit-sb.js — mirror it.
const AUDIT_VIEWER_LOGINS = new Set(['ASTORGACV']);
const DEPOT_ASSIST_DRIVERS = [
  { login: 'SOTOASTOL2', name: 'Luis Rey Soto' },
  { login: 'SOTOASTOA',  name: 'Andrea Astorga' },
];

const ASSIST_TASK_TYPES = ['Depots', 'Late Exp Shuttle', 'Parcel Delivery', 'Other'];

function DriverPicker({ value, onPick }) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      {DEPOT_ASSIST_DRIVERS.map(d => (
        <button key={d.login} onClick={() => onPick(d)}
          style={{ flex: 1, padding: '10px 8px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem', fontFamily: 'var(--font-body)', lineHeight: 1.35,
            background: value.login === d.login ? 'var(--hp-gold)' : 'var(--hp-bg)', color: value.login === d.login ? 'var(--hp-black)' : 'var(--hp-dim)' }}>
          {d.name}<br /><span style={{ fontSize: '0.66rem', opacity: 0.85, fontFamily: 'var(--font-mono)' }}>{d.login}</span>
        </button>
      ))}
    </div>
  );
}

function DepotAssistView({ shift }) {
  const [mode, setMode] = useState('view'); // 'view' | 'log'

  // ── View state ──
  const [sel, setSel]         = useState(DEPOT_ASSIST_DRIVERS[0]);
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  const [view, setView]       = useState('yesterday');

  const load = useCallback((login) => {
    setLoading(true); setErr('');
    const { current, previous } = historyPeriods(login);
    const end       = current.end;
    const prevStart = previous.start;
    fetch(`/.netlify/functions/driver-history?driver=${encodeURIComponent(login)}&periodStart=${prevStart}&periodEnd=${end}`)
      .then(r => r.json())
      .then(d => { if (!d.success) throw new Error(d.error || 'Could not load'); setData(d); })
      .catch(e => setErr(e.message || 'Could not load history'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { setData(null); load(sel.login); }, [sel, load]);

  // ── Log-assist form state ──
  const [logDriver, setLogDriver] = useState(DEPOT_ASSIST_DRIVERS[0]);
  const [logDate, setLogDate]     = useState(isoToday());
  const [taskType, setTaskType]   = useState('Depots');
  const [bags, setBags]           = useState('');
  const [comment, setComment]     = useState('');
  const [busy, setBusy]           = useState(false);
  const [logErr, setLogErr]       = useState('');
  const [logOk, setLogOk]         = useState('');

  async function submitAssist() {
    setLogErr(''); setLogOk('');
    if (!logDate) { setLogErr('Pick the date you assisted.'); return; }
    if (taskType === 'Depots') {
      const n = parseInt(bags, 10);
      if (isNaN(n) || n < 0) { setLogErr('Enter the number of depot bags.'); return; }
    }
    if (taskType === 'Other' && !comment.trim()) { setLogErr('Add a comment describing the task.'); return; }
    setBusy(true);
    try {
      // 1) Depots → credit the driver via depot-sb (Supabase + sheet mirror) so it
      //    flows into their totals AND the sheet the driver-history reads.
      if (taskType === 'Depots') {
        const n = parseInt(bags, 10);
        const saveBags = (amend) => fetch('/.netlify/functions/depot-sb', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'saveDepotBags', driver: logDriver.login, date: logDate, tally: n, ...(amend ? { amend: true } : {}) }),
        }).then(r => r.json());
        let dres = await saveBags(false);
        // One tally per driver per day — replacing an existing entry needs an explicit OK.
        if (!dres.success && dres.duplicate) {
          if (!window.confirm(`${logDriver.name} already has ${dres.existingTally} depot bag${dres.existingTally === 1 ? '' : 's'} logged for ${logDate}.\n\nReplace it with ${n}?`)) {
            throw new Error(`Not saved — ${logDriver.login} already has ${dres.existingTally} bags for that day.`);
          }
          dres = await saveBags(true);
        }
        if (!dres.success) throw new Error(dres.error || 'Could not save the depot bags.');
      }
      // 2) Record the assist (helper, task type, comment) in the Assists log.
      const ares = await fetch('/.netlify/functions/appsscript', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'logAssist', helper: shift.driver, driver: logDriver.login, date: logDate,
          taskType, comment: comment.trim(), tally: taskType === 'Depots' ? parseInt(bags, 10) : '' }),
      }).then(r => r.json());
      if (!ares.success) throw new Error(ares.error || 'Could not record the assist.');

      setLogOk(`Logged: ${taskType} for ${logDriver.name}${taskType === 'Depots' ? ` · ${parseInt(bags, 10)} bags credited` : ''}.`);
      setBags(''); setComment('');
      if (sel.login === logDriver.login) { setData(null); load(sel.login); } // refresh so credited bags show
    } catch (e) {
      setLogErr(e.message || 'Failed. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const inputStyle = { width: '100%', padding: '11px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.95rem', fontFamily: 'var(--font-body)', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--hp-dim)', marginBottom: 6, fontWeight: 700 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%' }}>
      {/* Mode toggle */}
      <div style={{ display: 'flex', gap: 6, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 4 }}>
        {[['view', 'View history'], ['log', 'Log an assist']].map(([k, label]) => (
          <button key={k} onClick={() => setMode(k)}
            style={{ flex: 1, padding: '10px', borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: '0.82rem', fontFamily: 'var(--font-body)',
              background: mode === k ? 'var(--hp-gold)' : 'transparent', color: mode === k ? 'var(--hp-black)' : 'var(--hp-dim)' }}>
            {label}
          </button>
        ))}
      </div>

      {mode === 'view' ? (
        <>
          <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 14 }}>
            <div style={labelStyle}>View driver</div>
            <DriverPicker value={sel} onPick={setSel} />
          </div>
          <DriverHistoryView
            shift={{ driver: sel.login, name: sel.name }}
            historyData={data}
            historyLoading={loading}
            historyError={err}
            historyView={view}
            setHistoryView={setView}
            onRefresh={() => { setData(null); load(sel.login); }}
          />
        </>
      ) : (
        <div style={{ background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 18, display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', lineHeight: 1.5 }}>
            Record a task you assisted with. For <strong style={{ color: 'var(--hp-text)' }}>Depots</strong>, the bags are credited to that driver for the day.
          </div>
          <div>
            <label style={labelStyle}>Driver assisted</label>
            <DriverPicker value={logDriver} onPick={setLogDriver} />
          </div>
          <div>
            <label style={labelStyle}>Date</label>
            <input type="date" value={logDate} max={isoToday()} onChange={e => { setLogDate(e.target.value); setLogOk(''); }} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Task type</label>
            <select value={taskType} onChange={e => { setTaskType(e.target.value); setLogErr(''); setLogOk(''); }} style={inputStyle}>
              {ASSIST_TASK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {taskType === 'Depots' && (
            <div>
              <label style={labelStyle}>Depot bags</label>
              <input type="number" inputMode="numeric" min="0" value={bags} onChange={e => { setBags(e.target.value); setLogOk(''); }} placeholder="e.g. 12" style={{ ...inputStyle, fontFamily: 'var(--font-mono)', fontSize: '1.2rem' }} />
            </div>
          )}
          <div>
            <label style={labelStyle}>Comment{taskType === 'Other' ? ' *' : ' (optional)'}</label>
            <textarea value={comment} onChange={e => { setComment(e.target.value); setLogOk(''); }} placeholder={taskType === 'Other' ? 'Describe the task…' : 'Any note…'} style={{ ...inputStyle, minHeight: 64, resize: 'vertical' }} />
          </div>
          {logErr && <div style={{ color: 'var(--hp-red)', fontSize: '0.83rem', display: 'flex', gap: 8, alignItems: 'flex-start' }}><AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} /> {logErr}</div>}
          {logOk  && <div style={{ color: 'var(--hp-green)', fontSize: '0.85rem', fontWeight: 700, display: 'flex', gap: 8, alignItems: 'center' }}><CheckCircle size={16} /> {logOk}</div>}
          <button onClick={submitAssist} disabled={busy}
            style={{ width: '100%', padding: 14, borderRadius: 'var(--radius)', border: 'none', cursor: busy ? 'default' : 'pointer', fontWeight: 800, fontSize: '0.95rem', fontFamily: 'var(--font-body)', background: 'var(--hp-gold)', color: 'var(--hp-black)', opacity: busy ? 0.6 : 1 }}>
            {busy ? 'Saving…' : 'Log assist'}
          </button>
        </div>
      )}
    </div>
  );
}

function DriverHistoryView({ shift, historyData, historyLoading, historyError, historyView, setHistoryView, onRefresh }) {
  // Which pay calendar this login's periods are cut on — the 28-day contractor
  // grid, or the 15th→14th monthly wage run. Everything below (the two period
  // buttons, their week grouping and their labels) follows from this.
  const { wageRun, current, previous } = historyPeriods(shift?.driver);
  const { start: periodStart, end: periodEnd } = current;
  const { start: prevPeriodStart, end: prevPeriodEnd } = previous;
  const periodWord     = wageRun ? 'Pay Run' : 'Period';
  const periodWordLow  = wageRun ? 'pay run' : 'period';
  const today = isoToday();
  const lwdDate = lastWorkingDay(); // last Mon–Fri working day

  // Last 7 days — anchored to last working day so weekends don't skew it
  // Floor at the fetched window (prevPeriodStart), NOT periodStart: at the start of a
  // new period the last working day still sits in the previous period, so clamping to
  // periodStart would push weekStart past weekEnd → an inverted, empty range.
  const weekStart = addDays(lwdDate, -6) < prevPeriodStart ? prevPeriodStart : addDays(lwdDate, -6);
  const weekEnd   = lwdDate;

  const shifts       = historyData?.shifts       || [];
  const parcelsByDate = historyData?.parcelsByDate || {};

  // Build day objects from union of shift dates + parcel dates
  function buildDays(fromDate, toDate) {
    const allDates = new Set([
      ...shifts.filter(s => s.date >= fromDate && s.date <= toDate).map(s => s.date),
      ...Object.keys(parcelsByDate).filter(d => d >= fromDate && d <= toDate),
    ]);
    return [...allDates].sort().map(date => {
      const s = shifts.find(s => s.date === date) || null;
      const p = parcelsByDate[date] || null;
      const tot = totalParcels(p);
      return {
        date,
        shiftStart:      s?.shiftStart  || '',
        breakStart:      s?.breakStart  || '',
        breakEnd:        s?.breakEnd    || '',
        finishTime:      s?.finishTime  || '',
        totalKm:         s?.totalKm     || 0,
        totalHours:      s?.totalHours  || 0,
        status:          s?.status      || '',
        parcels:         p,
        totalParcelsDay: tot,
        pph: s?.totalHours > 0 && tot > 0
          ? Math.round((tot / s.totalHours) * 10) / 10 : null,
      };
    });
  }

  const lwdShift   = shifts.find(s => s.date === lwdDate);
  const lwdParcels = parcelsByDate[lwdDate] || null;
  const lwdTotal   = totalParcels(lwdParcels);
  const lwdPPH     = lwdShift?.totalHours > 0 && lwdTotal > 0
    ? Math.round((lwdTotal / lwdShift.totalHours) * 10) / 10 : null;
  // Friendly label: "Yesterday" or "Last Friday" etc.
  const lwdLabel = (() => {
    const diff = Math.round((new Date(today) - new Date(lwdDate)) / 86400000);
    if (diff === 1) return 'Yesterday';
    const dayName = new Date(lwdDate + 'T12:00:00').toLocaleDateString('en-AU', { weekday: 'long' });
    return `Last ${dayName}`;
  })();

  const weekDays       = buildDays(weekStart, weekEnd);
  const periodDays     = buildDays(periodStart, periodEnd);
  const prevPeriodDays = buildDays(prevPeriodStart, prevPeriodEnd);

  function sumDays(days) {
    // "Parcels" is parcel-type ONLY — express / DoE / depot are their own totals
    // (previously they were all rolled into "parcels", double-counting express).
    return days.reduce((acc, d) => ({
      days:       acc.days + 1,
      hours:      Math.round((acc.hours + (d.totalHours || 0)) * 100) / 100,
      km:         Math.round((acc.km    + (d.totalKm    || 0)) * 10)  / 10,
      parcels:    acc.parcels + Math.round(d.parcels?.parcel  || 0),
      express:    acc.express + Math.round(d.parcels?.express || 0),
      doe:        acc.doe     + Math.round(d.parcels?.doe     || 0),
      depot:      acc.depot   + Math.round(d.parcels?.depot   || 0),
      pphHours:   Math.round((acc.pphHours + (d.totalParcelsDay > 0 && d.totalHours > 0 ? d.totalHours : 0)) * 100) / 100,
    }), { days: 0, hours: 0, km: 0, parcels: 0, express: 0, doe: 0, depot: 0, pphHours: 0 });
  }

  // Group a period into weeks. Walks to the end of the window rather than
  // assuming four: the contractor grid is exactly 28 days, but a wage run is a
  // calendar month (30-31 days), and a fixed four would drop its last few days.
  function weeksOf(fromDate, toDate) {
    const weeks = [];
    for (let i = 0; i < 6; i++) {
      const wStart = addDays(fromDate, i * 7);
      if (wStart > toDate) break;
      const wEndRaw = addDays(fromDate, i * 7 + 6);
      const wEnd    = wEndRaw > toDate ? toDate : wEndRaw;
      const days    = buildDays(wStart, wEnd);
      if (days.length > 0) weeks.push({ wStart, wEnd, days, sum: sumDays(days) });
    }
    return weeks;
  }
  function periodWeeks() { return weeksOf(periodStart, periodEnd); }

  const statusColor = st => st === 'APPROVED' ? 'var(--hp-green)' : st === 'REJECTED' ? 'var(--hp-red)' : 'var(--hp-amber)';
  const card = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', padding: '20px' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Driver badge */}
      <div style={{ ...card, padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Viewing history for</div>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--hp-text)', marginTop: 2 }}>{shift.name}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{periodWord}</div>
          <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--hp-gold)', marginTop: 2 }}>{formatShortDate(periodStart)} → {formatShortDate(periodEnd)}</div>
        </div>
      </div>

      {/* View toggle */}
      <div style={{ display: 'flex', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', padding: 4, gap: 3 }}>
        {[
          { key: 'yesterday',  label: lwdLabel },
          { key: 'week',       label: 'Last 7 Days' },
          { key: 'period',     label: `This ${periodWord}` },
          { key: 'lastperiod', label: `Last ${periodWord}` },
        ].map(({ key, label }) => (
          <button key={key} onClick={() => setHistoryView(key)} style={{
            flex: 1, padding: '10px 2px', border: 'none', borderRadius: 'var(--radius)', cursor: 'pointer',
            fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.04em',
            background: historyView === key ? 'var(--hp-gold)' : 'none',
            color:      historyView === key ? 'var(--hp-black)' : 'var(--hp-dim)',
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {historyLoading && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--hp-dim)' }}>
          <div style={{ width: 36, height: 36, border: '3px solid var(--hp-border)', borderTopColor: 'var(--hp-gold)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
          Loading your history…
        </div>
      )}

      {/* Error */}
      {historyError && !historyLoading && (
        <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 'var(--radius)', padding: '14px 16px', color: 'var(--hp-red)', fontSize: '0.875rem', display: 'flex', gap: 10, alignItems: 'center' }}>
          <AlertCircle size={16} style={{ flexShrink: 0 }} /> {historyError}
          <button onClick={onRefresh} style={{ marginLeft: 'auto', background: 'none', border: '1px solid var(--hp-red)', borderRadius: 4, color: 'var(--hp-red)', padding: '4px 10px', cursor: 'pointer', fontSize: '0.75rem', fontFamily: 'var(--font-body)' }}>Retry</button>
        </div>
      )}

      {/* ── YESTERDAY / LAST WORKING DAY view ── */}
      {!historyLoading && !historyError && historyView === 'yesterday' && (
        <>
          <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', textAlign: 'center', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {formatShortDate(lwdDate)}
          </div>
          {/* Shift card — only shown if shift was submitted via portal */}
          {lwdShift ? (
            <div style={card}>
              <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>{lwdLabel}'s Shift</div>
              <StatGrid items={[
                { label: 'Start',  value: lwdShift.shiftStart || '—' },
                { label: 'Finish', value: lwdShift.finishTime || '—' },
                { label: 'Hours',  value: lwdShift.totalHours ? `${lwdShift.totalHours}h` : '—', color: 'var(--hp-green)' },
                { label: 'KM',     value: lwdShift.totalKm    ? `${lwdShift.totalKm} km`  : '—', color: 'var(--hp-green)' },
              ]} />
              {lwdShift.breakStart && (
                <div style={{ marginTop: 12, fontSize: '0.8rem', color: 'var(--hp-dim)', textAlign: 'center' }}>
                  Break: {lwdShift.breakStart} – {lwdShift.breakEnd || '?'}
                </div>
              )}
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, padding: '3px 10px', borderRadius: 4,
                  background: lwdShift.status === 'APPROVED' ? 'rgba(34,197,94,.12)' : lwdShift.status === 'REJECTED' ? 'rgba(239,68,68,.12)' : 'rgba(245,158,11,.12)',
                  color: statusColor(lwdShift.status) }}>
                  {lwdShift.status}
                </span>
              </div>
            </div>
          ) : (
            <div style={{ ...card, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>No shift hours recorded for {lwdLabel.toLowerCase()}.</div>
            </div>
          )}

          {/* Parcels card — always shown if parcel data exists */}
          <div style={card}>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>{lwdLabel}'s Parcels</div>
            {lwdTotal === 0 ? (
              <div style={{ textAlign: 'center', color: 'var(--hp-dim)', fontSize: '0.875rem', padding: '8px 0' }}>No parcel data recorded</div>
            ) : (
              <>
                <StatGrid items={[
                  { label: 'Parcel',  value: Math.round(lwdParcels?.parcel  || 0) },
                  { label: 'Express', value: Math.round(lwdParcels?.express || 0) },
                  { label: 'DoE',     value: Math.round(lwdParcels?.doe     || 0) },
                  { label: 'Depot',   value: Math.round(lwdParcels?.depot   || 0) },
                ]} />
                <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: 'rgba(34,197,94,.08)', border: '1px solid rgba(34,197,94,.2)', borderRadius: 8 }}>
                  <div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Total Parcels</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.6rem', fontWeight: 700, color: 'var(--hp-green)' }}>{lwdTotal}</div>
                  </div>
                  {lwdPPH != null && (
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Parcels / Hr</div>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.6rem', fontWeight: 700, color: 'var(--hp-gold)' }}>{lwdPPH}</div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </>
      )}

      {/* ── WEEK view ── */}
      {!historyLoading && !historyError && historyView === 'week' && (
        <>
          {(() => {
            const wSum = sumDays(weekDays);
            return (
              <>
                <div style={card}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
                    Last 7 Days — {formatShortDate(weekStart)} to {formatShortDate(weekEnd)}
                  </div>
                  <StatGrid items={[
                    { label: 'Days',    value: wSum.days },
                    { label: 'Hours',   value: `${wSum.hours}h`,      color: 'var(--hp-green)' },
                    { label: 'KM',      value: `${wSum.km} km`,        color: 'var(--hp-green)' },
                    { label: 'Parcels', value: wSum.parcels || '—',    color: 'var(--hp-gold)' },
                    { label: 'Express', value: wSum.express || '—',    color: 'var(--hp-amber)' },
                    ...(wSum.doe   ? [{ label: 'DoE',   value: wSum.doe,   color: '#a78bfa' }] : []),
                    ...(wSum.depot ? [{ label: 'Depot', value: wSum.depot, color: '#60a5fa' }] : []),
                  ]} />
                </div>
                <div style={card}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Day by Day</div>
                  {weekDays.length === 0 ? (
                    <div style={{ textAlign: 'center', color: 'var(--hp-dim)', fontSize: '0.875rem', padding: '16px 0' }}>No shifts recorded this week yet</div>
                  ) : (
                    weekDays.map(d => <DayRow key={d.date} day={d} />)
                  )}
                </div>
              </>
            );
          })()}
        </>
      )}

      {/* ── PERIOD view ── */}
      {!historyLoading && !historyError && historyView === 'period' && (
        <>
          {(() => {
            const pSum = sumDays(periodDays);
            const weeks = periodWeeks();
            return (
              <>
                <div style={card}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>{periodWord} Totals</div>
                  <StatGrid items={[
                    { label: 'Days',    value: pSum.days },
                    { label: 'Hours',   value: `${pSum.hours}h`,      color: 'var(--hp-green)' },
                    { label: 'KM',      value: `${pSum.km} km`,        color: 'var(--hp-green)' },
                    { label: 'Parcels', value: pSum.parcels || '—',    color: 'var(--hp-gold)' },
                    { label: 'Express', value: pSum.express || '—',    color: 'var(--hp-amber)' },
                    ...(pSum.doe   ? [{ label: 'DoE',   value: pSum.doe,   color: '#a78bfa' }] : []),
                    ...(pSum.depot ? [{ label: 'Depot', value: pSum.depot, color: '#60a5fa' }] : []),
                  ]} />
                  {pSum.parcels > 0 && pSum.pphHours > 0 && (
                    <div style={{ marginTop: 14, padding: '10px 16px', background: 'rgba(201,168,76,.08)', border: '1px solid rgba(201,168,76,.2)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', fontWeight: 600 }}>Avg Parcels / Hr</span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.2rem', fontWeight: 700, color: 'var(--hp-gold)' }}>
                        {Math.round((pSum.parcels / pSum.pphHours) * 10) / 10} p/hr
                      </span>
                    </div>
                  )}
                </div>
                {weeks.map((w, i) => (
                  <div key={i} style={card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Week {i + 1} · {formatShortDate(w.wStart)} – {formatShortDate(w.wEnd)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--hp-gold)', fontWeight: 700 }}>
                        {w.sum.hours}h · {w.sum.km} km · {w.sum.parcels || 0} parcels
                      </div>
                    </div>
                    {w.days.map(d => <DayRow key={d.date} day={d} />)}
                  </div>
                ))}
                {periodDays.length === 0 && (
                  <div style={{ textAlign: 'center', color: 'var(--hp-dim)', fontSize: '0.875rem', padding: '24px 0' }}>No shifts recorded this {periodWordLow} yet</div>
                )}
              </>
            );
          })()}
        </>
      )}

      {/* ── LAST PERIOD view ── */}
      {!historyLoading && !historyError && historyView === 'lastperiod' && (
        <>
          {(() => {
            const lpSum = sumDays(prevPeriodDays);
            const weeks  = weeksOf(prevPeriodStart, prevPeriodEnd);
            return (
              <>
                <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', textAlign: 'center', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  {formatShortDate(prevPeriodStart)} → {formatShortDate(prevPeriodEnd)}
                </div>
                <div style={card}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>Last {periodWord} Totals</div>
                  <StatGrid items={[
                    { label: 'Days',    value: lpSum.days },
                    { label: 'Hours',   value: `${lpSum.hours}h`,     color: 'var(--hp-green)' },
                    { label: 'KM',      value: `${lpSum.km} km`,       color: 'var(--hp-green)' },
                    { label: 'Parcels', value: lpSum.parcels || '—',   color: 'var(--hp-gold)' },
                    { label: 'Express', value: lpSum.express || '—',   color: 'var(--hp-amber)' },
                    ...(lpSum.doe   ? [{ label: 'DoE',   value: lpSum.doe,   color: '#a78bfa' }] : []),
                    ...(lpSum.depot ? [{ label: 'Depot', value: lpSum.depot, color: '#60a5fa' }] : []),
                  ]} />
                  {lpSum.parcels > 0 && lpSum.pphHours > 0 && (
                    <div style={{ marginTop: 14, padding: '10px 16px', background: 'rgba(201,168,76,.08)', border: '1px solid rgba(201,168,76,.2)', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', fontWeight: 600 }}>Avg Parcels / Hr</span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: '1.2rem', fontWeight: 700, color: 'var(--hp-gold)' }}>
                        {Math.round((lpSum.parcels / lpSum.pphHours) * 10) / 10} p/hr
                      </span>
                    </div>
                  )}
                </div>
                {weeks.map((w, i) => (
                  <div key={i} style={card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Week {i + 1} · {formatShortDate(w.wStart)} – {formatShortDate(w.wEnd)}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--hp-gold)', fontWeight: 700 }}>
                        {w.sum.hours}h · {w.sum.km} km · {w.sum.parcels || 0} parcels
                      </div>
                    </div>
                    {w.days.map(d => <DayRow key={d.date} day={d} />)}
                  </div>
                ))}
                {prevPeriodDays.length === 0 && (
                  <div style={{ textAlign: 'center', color: 'var(--hp-dim)', fontSize: '0.875rem', padding: '24px 0' }}>No shifts recorded for last {periodWordLow}</div>
                )}
              </>
            );
          })()}
        </>
      )}

      {/* Refresh */}
      {!historyLoading && historyData && (
        <button onClick={onRefresh}
          style={{ background: 'none', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', padding: '10px', fontSize: '0.8rem', cursor: 'pointer', fontFamily: 'var(--font-body)', width: '100%' }}>
          Refresh
        </button>
      )}
    </div>
  );

  function DayRow({ day }) {
    return (
      <div style={{ padding: '12px 0', borderBottom: '1px solid var(--hp-border)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <div style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--hp-text)' }}>{formatShortDate(day.date)}</div>
          <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '2px 8px', borderRadius: 4,
            background: day.status === 'APPROVED' ? 'rgba(34,197,94,.12)' : day.status === 'REJECTED' ? 'rgba(239,68,68,.12)' : 'rgba(245,158,11,.12)',
            color: statusColor(day.status) }}>
            {day.status}
          </span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 4 }}>
          {[
            { lbl: 'Start',  val: day.shiftStart || '—' },
            { lbl: 'Finish', val: day.finishTime || '—' },
            { lbl: 'Hours',  val: day.totalHours ? `${day.totalHours}h` : '—' },
            { lbl: 'KM',     val: day.totalKm    ? `${day.totalKm}`    : '—' },
          ].map(({ lbl, val }) => (
            <div key={lbl} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.6rem', color: 'var(--hp-dim)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em' }}>{lbl}</div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', fontWeight: 700, color: 'var(--hp-text)', marginTop: 2 }}>{val}</div>
            </div>
          ))}
        </div>
        {day.totalParcelsDay > 0 && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {[
              { lbl: 'Parcel',  val: day.parcels?.parcel  },
              { lbl: 'Express', val: day.parcels?.express },
              { lbl: 'DoE',     val: day.parcels?.doe     },
              { lbl: 'Depot',   val: day.parcels?.depot   },
            ].filter(x => x.val > 0).map(({ lbl, val }) => (
              <div key={lbl} style={{ background: 'rgba(201,168,76,.08)', border: '1px solid rgba(201,168,76,.2)', borderRadius: 6, padding: '4px 10px', fontSize: '0.75rem', color: 'var(--hp-gold)', fontWeight: 700 }}>
                {lbl}: {Math.round(val)}
              </div>
            ))}
            {day.pph != null && (
              <div style={{ marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--hp-green)', fontWeight: 700, alignSelf: 'center' }}>
                {day.pph} p/hr
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  function StatGrid({ items }) {
    const cols = items.length === 4 ? 2 : items.length >= 5 ? 3 : items.length;
    return (
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 8 }}>
        {items.map(({ label, value, color }) => (
          <div key={label} style={{ background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '10px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: '0.58rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 3 }}>{label}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.1rem', fontWeight: 700, color: color || 'var(--hp-text)' }}>{value ?? '—'}</div>
          </div>
        ))}
      </div>
    );
  }
}

// ── Shared submission summary component ───────────────────────────────────────
function SubmissionSummary({ sub }) {
  const S2 = {
    row: {
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '10px 0', borderBottom: '1px solid var(--hp-border)',
    },
    lbl: { fontSize: '0.78rem', color: 'var(--hp-dim)', fontWeight: 500 },
    val: { fontSize: '0.9rem', color: 'var(--hp-text)', fontWeight: 600, fontFamily: 'var(--font-mono)' },
  };
  return (
    <div>
      <div style={S2.row}>
        <span style={S2.lbl}>Shift start</span>
        <span style={S2.val}>{sub.shiftStart || '—'}</span>
      </div>
      {sub.breakStart && (
        <div style={S2.row}>
          <span style={S2.lbl}>Break</span>
          <span style={S2.val}>{sub.breakStart} – {sub.breakEnd || '?'}</span>
        </div>
      )}
      <div style={S2.row}>
        <span style={S2.lbl}>Finish time</span>
        <span style={S2.val}>{sub.finishTime || '—'}</span>
      </div>
      <div style={S2.row}>
        <span style={S2.lbl}>Hours worked</span>
        <span style={{ ...S2.val, color: 'var(--hp-green)' }}>{sub.totalHours ? `${sub.totalHours} hrs` : '—'}</span>
      </div>
      <div style={{ ...S2.row, borderBottom: 'none' }}>
        <span style={S2.lbl}>Total KM</span>
        <span style={{ ...S2.val, color: 'var(--hp-green)' }}>{sub.totalKm ? `${sub.totalKm} km` : '—'}</span>
      </div>
    </div>
  );
}

// ── Team leader area map ──────────────────────────────────────────────────────
const TEAM_LEADER_AREAS = {
  SOTOCRUZL:  'CITY',
  SOTOASTOL2: 'BWAY',
  DONGB3:     'DARL',
};

const AREA_LABELS = { CITY: 'City', BWAY: 'Broadway', DARL: 'Darlinghurst' };

// Does an area belong to the team-leader's scope? areas is 'ALL' or an array of codes.
function inTeamAreas(areaCode, areas) {
  if (areas === 'ALL' || !areas) return true;
  return Array.isArray(areas) && areas.includes((areaCode || '').toUpperCase());
}

// ── Team Leader: Logged-On Drivers ─────────────────────────────────────────
// Reuses the engine's getLoginCheck; scoped to the team leader's own area(s).
function LoggedOnPortalView({ areas }) {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    fetch('/.netlify/functions/appsscript', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'getLoginCheck', date: localDateStr() }),
    }).then(r => r.json()).then(d => {
      if (cancelled) return;
      if (!d.success) throw new Error(d.error || 'Failed to load');
      const scoped = (d.rows || []).filter(r => areas === 'ALL' ? true : inTeamAreas(r.area, areas));
      setRows(scoped);
    }).catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [areas, reloadKey]);

  const onShift  = rows.filter(r => r.status === 'ON_SHIFT');
  const finished = rows.filter(r => r.status === 'FINISHED');
  const notOn    = rows.filter(r => r.status === 'NOT_LOGGED_ON');

  const stat = (n, label, color) => (
    <div style={{ flex: 1, textAlign: 'center', padding: '12px 8px', background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)' }}>
      <div style={{ fontSize: '1.6rem', fontWeight: 800, color }}>{n}</div>
      <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 }}>{label}</div>
    </div>
  );

  const list = (items, color, showTime) => items.map(r => (
    <div key={r.login} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 14px', background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', marginBottom: 6 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--hp-text)' }}>{r.name || r.login}</div>
        <div style={{ fontSize: '0.7rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>
          {r.login}{r.route ? ` · ${r.route}` : ''}{r.area ? ` · ${AREA_LABELS[r.area] || r.area}` : ''}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        {showTime && r.shiftStart && <div style={{ fontSize: '0.78rem', color, fontFamily: 'var(--font-mono)' }}>{r.shiftStart}{r.finishTime ? ` – ${r.finishTime}` : ''}</div>}
        {r.auth && <div style={{ fontSize: '0.66rem', color: '#f59e0b' }}>Authorised: {r.auth.reason}</div>}
      </div>
    </div>
  ));

  if (loading) return <div style={{ textAlign: 'center', color: 'var(--hp-dim)', padding: '40px 0', fontSize: '0.9rem' }}>Loading logged-on drivers…</div>;
  if (error)   return <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', padding: '16px', color: 'var(--hp-red)', fontSize: '0.875rem' }}>{error}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--hp-text)', letterSpacing: '0.04em' }}>Logged-On Drivers</div>
        <button onClick={() => setReloadKey(k => k + 1)} style={{ background: 'none', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', padding: '6px 12px' }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        {stat(onShift.length, 'On shift', 'var(--hp-gold)')}
        {stat(finished.length, 'Finished', 'var(--hp-green)')}
        {stat(notOn.length, 'Not on', 'var(--hp-red)')}
      </div>

      {onShift.length > 0 && <div><div style={S.sectionLabel}>On shift now</div>{list(onShift, 'var(--hp-gold)', true)}</div>}
      {notOn.length > 0 && <div><div style={S.sectionLabel}>Not logged on</div>{list(notOn, 'var(--hp-red)', false)}</div>}
      {finished.length > 0 && <div><div style={S.sectionLabel}>Finished</div>{list(finished, 'var(--hp-green)', true)}</div>}
      {rows.length === 0 && <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', textAlign: 'center', padding: '20px 0' }}>No drivers found for your team today.</div>}
    </div>
  );
}

// ── Team Leader: Status Page ───────────────────────────────────────────────
// Today's parcel volumes by driver (getSheet1Totals) merged with logged-on
// status (getLoginCheck), scoped to the team leader's own area(s).
function StatusPortalView({ areas }) {
  const [rows, setRows]       = useState([]);
  const [totals, setTotals]   = useState({ parcel: 0, express: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    const today = localDateStr();
    Promise.all([
      fetch('/.netlify/functions/appsscript', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getLoginCheck', date: today }) }).then(r => r.json()),
      fetch('/.netlify/functions/appsscript', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'getSheet1Totals', periodStart: today, periodEnd: today }) }).then(r => r.json()),
    ]).then(([lc, st]) => {
      if (cancelled) return;
      if (!lc.success) throw new Error(lc.error || 'Failed to load roster');
      const vols = (st && st.success && st.byDriver) ? st.byDriver : {};
      const scoped = (lc.rows || []).filter(r => areas === 'ALL' ? true : inTeamAreas(r.area, areas));
      const merged = scoped.map(r => {
        const v = vols[r.login] || { parcel: 0, express: 0, total: 0 };
        return { login: r.login, name: r.name, area: r.area, status: r.status, parcel: v.parcel || 0, express: v.express || 0, total: v.total || 0 };
      }).sort((a, b) => b.total - a.total);
      const agg = merged.reduce((acc, r) => ({ parcel: acc.parcel + r.parcel, express: acc.express + r.express, total: acc.total + r.total }), { parcel: 0, express: 0, total: 0 });
      setRows(merged); setTotals(agg);
    }).catch(e => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [areas, reloadKey]);

  const STATUS_META = {
    ON_SHIFT:      { label: 'On shift', color: 'var(--hp-gold)' },
    FINISHED:      { label: 'Finished', color: 'var(--hp-green)' },
    NOT_LOGGED_ON: { label: 'Not on',   color: 'var(--hp-red)' },
  };
  const num = n => Math.round(n).toLocaleString();
  const td = { padding: '9px 10px', borderTop: '1px solid var(--hp-border)', fontSize: '0.82rem', color: 'var(--hp-text)' };
  const th = { padding: '8px 10px', fontSize: '0.66rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' };

  if (loading) return <div style={{ textAlign: 'center', color: 'var(--hp-dim)', padding: '40px 0', fontSize: '0.9rem' }}>Loading status…</div>;
  if (error)   return <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', padding: '16px', color: 'var(--hp-red)', fontSize: '0.875rem' }}>{error}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--hp-text)', letterSpacing: '0.04em' }}>Status — Today</div>
          <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 4 }}>{num(totals.total)} parcels across {rows.length} driver{rows.length !== 1 ? 's' : ''}</div>
        </div>
        <button onClick={() => setReloadKey(k => k + 1)} style={{ background: 'none', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.78rem', padding: '6px 12px' }}>
          <RefreshCw size={13} /> Refresh
        </button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{ ...th, textAlign: 'left' }}>Driver</th>
              <th style={{ ...th, textAlign: 'left' }}>Status</th>
              <th style={th}>Parcels</th>
              <th style={th}>Express</th>
              <th style={th}>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => {
              const sm = STATUS_META[r.status] || { label: r.status, color: 'var(--hp-dim)' };
              return (
                <tr key={r.login}>
                  <td style={{ ...td }}>
                    <div style={{ fontWeight: 700 }}>{r.name || r.login}</div>
                    <div style={{ fontSize: '0.68rem', color: 'var(--hp-dim)', fontFamily: 'var(--font-mono)' }}>{r.login}{r.area ? ` · ${AREA_LABELS[r.area] || r.area}` : ''}</div>
                  </td>
                  <td style={td}><span style={{ color: sm.color, fontWeight: 700, fontSize: '0.75rem' }}>{sm.label}</span></td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{num(r.parcel)}</td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{num(r.express)}</td>
                  <td style={{ ...td, textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--hp-gold)' }}>{num(r.total)}</td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: 'var(--hp-dim)' }}>No drivers found for your team today.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Driver Routes Portal View ─────────────────────────────────────────────────
function DriverRoutesPortalView({ shift, accessScope }) {
  const [routes, setRoutes]         = useState([]);
  const [scope, setScope]           = useState('driver'); // 'driver' | 'team' | 'all'
  const [area, setArea]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [expanded, setExpanded]     = useState({});
  const [feedback, setFeedback]     = useState(null); // { type, route } — active form
  const [fbFields, setFbFields]     = useState({ street: '', suggestedDriver: '', note: '' });
  const [fbSending, setFbSending]   = useState(false);
  const [fbDone, setFbDone]         = useState(null); // success message

  useEffect(() => {
    async function load() {
      setLoading(true); setError('');
      try {
        const res = await fetch('/.netlify/functions/portal-routes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ login: shift.driver, ...(accessScope ? { scope: accessScope } : {}) }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error || 'Failed to load routes');
        setRoutes(data.routes || []);
        setScope(data.scope);
        setArea(data.area);
        // Default: expand all routes
        const exp = {};
        (data.routes || []).forEach(r => { exp[r.adamCode] = true; });
        setExpanded(exp);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [shift.driver, accessScope]);

  async function submitFeedback(e) {
    e.preventDefault();
    setFbSending(true);
    try {
      const res = await fetch('/.netlify/functions/route-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: feedback.type,
          login: shift.driver,
          driverName: shift.name,
          routeCode: feedback.route.adamCode,
          street: fbFields.street,
          suggestedDriver: fbFields.suggestedDriver,
          note: fbFields.note,
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || 'Send failed');
      setFbDone(feedback.type === 'wrong_driver'
        ? 'Wrong driver alert sent to management.'
        : 'Missing street alert sent to management.');
      setFeedback(null);
      setFbFields({ street: '', suggestedDriver: '', note: '' });
    } catch (e) {
      alert('Could not send alert: ' + e.message);
    } finally {
      setFbSending(false);
    }
  }

  const cardStyle = {
    background: 'var(--hp-card)',
    border: '1px solid var(--hp-border)',
    borderRadius: 'var(--radius)',
    overflow: 'hidden',
  };

  const headerStyle = {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px', cursor: 'pointer', userSelect: 'none',
  };

  const areaColor = { CITY: '#60a5fa', BWAY: '#34d399', DARL: '#a78bfa' };

  if (loading) return (
    <div style={{ textAlign: 'center', color: 'var(--hp-dim)', padding: '40px 0', fontSize: '0.9rem' }}>
      Loading your routes…
    </div>
  );

  if (error) return (
    <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 'var(--radius)', padding: '16px', color: 'var(--hp-red)', fontSize: '0.875rem' }}>
      {error}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Heading */}
      <div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--hp-text)', letterSpacing: '0.04em' }}>
          {scope === 'all' ? 'All Routes' : scope === 'team' ? `${AREA_LABELS[area] || area} Routes` : 'My Routes'}
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 4 }}>
          {scope === 'all'
            ? `Team leader view — all ${routes.length} route${routes.length !== 1 ? 's' : ''} across every area`
            : scope === 'team'
            ? `Team leader view — all ${routes.length} route${routes.length !== 1 ? 's' : ''} in ${AREA_LABELS[area] || area}`
            : `${routes.length} route${routes.length !== 1 ? 's' : ''} assigned to you`}
        </div>
      </div>

      {/* Success toast */}
      {fbDone && (
        <div style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 'var(--radius)', padding: '12px 16px', color: 'var(--hp-green)', fontSize: '0.875rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span><CheckCircle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />{fbDone}</span>
          <button onClick={() => setFbDone(null)} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}><XCircle size={14} /></button>
        </div>
      )}

      {routes.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--hp-dim)', padding: '24px 0', fontSize: '0.9rem' }}>
          No routes found for your login.
        </div>
      )}

      {/* Route cards */}
      {routes.map(route => {
        const isOpen = !!expanded[route.adamCode];
        const ac = areaColor[route.area] || 'var(--hp-gold)';
        return (
          <div key={route.adamCode} style={cardStyle}>
            {/* Card header */}
            <div style={headerStyle} onClick={() => setExpanded(ex => ({ ...ex, [route.adamCode]: !ex[route.adamCode] }))}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>
                    {scope === 'team' ? route.driverLogin : route.adamCode}
                  </span>
                  <span style={{ fontSize: '0.68rem', fontWeight: 700, color: ac, background: `${ac}22`, padding: '2px 7px', borderRadius: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    {route.area || '—'}
                  </span>
                </div>
                {scope === 'team' && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>{route.adamCode}</span>
                )}
                <span style={{ fontSize: '0.75rem', color: 'var(--hp-dim)' }}>
                  {route.streets.length} street{route.streets.length !== 1 ? 's' : ''}
                  {route.bayNumber ? ` · Bay ${route.bayNumber}` : ''}
                  {route.chute ? ` · Chute ${route.chute}` : ''}
                </span>
              </div>
              {isOpen ? <ChevronUp size={16} color="var(--hp-dim)" /> : <ChevronDown size={16} color="var(--hp-dim)" />}
            </div>

            {/* Street list */}
            {isOpen && (
              <div style={{ borderTop: '1px solid var(--hp-border)' }}>
                <ul style={{ margin: 0, padding: '10px 16px', listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {route.streets.map((street, i) => (
                    <li key={i} style={{ fontSize: '0.85rem', color: 'var(--hp-text)', display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0' }}>
                      <MapPin size={11} color="var(--hp-dim)" style={{ flexShrink: 0 }} />
                      {street}
                    </li>
                  ))}
                </ul>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: 8, padding: '8px 16px 14px', borderTop: '1px solid var(--hp-border)' }}>
                  <button
                    onClick={() => { setFeedback({ type: 'wrong_driver', route }); setFbFields({ street: '', suggestedDriver: '', note: '' }); setFbDone(null); }}
                    style={{ flex: 1, padding: '8px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius)', color: '#f59e0b', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, letterSpacing: '0.04em' }}
                  >
                    <AlertTriangle size={13} /> Wrong Driver
                  </button>
                  <button
                    onClick={() => { setFeedback({ type: 'missing', route }); setFbFields({ street: '', suggestedDriver: '', note: '' }); setFbDone(null); }}
                    style={{ flex: 1, padding: '8px', background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: 'var(--radius)', color: '#60a5fa', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5, letterSpacing: '0.04em' }}
                  >
                    <MapPin size={13} /> Missing Street
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Feedback modal */}
      {feedback && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div style={{ background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0', padding: '24px 20px 32px', width: '100%', maxWidth: 480 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--hp-text)' }}>
                {feedback.type === 'wrong_driver' ? '⚠️ Wrong Driver Alert' : '📍 Missing Street Alert'}
              </div>
              <button onClick={() => setFeedback(null)} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}>
                <XCircle size={20} />
              </button>
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: 16 }}>
              Route: <strong style={{ color: 'var(--hp-text)' }}>{feedback.route.driverLogin} — {feedback.route.adamCode}</strong>
            </div>
            <form onSubmit={submitFeedback} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
                  {feedback.type === 'wrong_driver' ? 'Which street?' : 'Missing street or address'}
                </label>
                <input
                  style={{ width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '10px 12px', color: 'var(--hp-text)', fontSize: '0.9rem', fontFamily: 'var(--font-body)', boxSizing: 'border-box' }}
                  placeholder={feedback.type === 'wrong_driver' ? 'e.g. King St' : 'e.g. 45 Smith St'}
                  value={fbFields.street}
                  onChange={e => setFbFields(f => ({ ...f, street: e.target.value }))}
                  required
                />
              </div>
              {feedback.type === 'wrong_driver' && (
                <div>
                  <label style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
                    Should belong to (driver ID or name)
                  </label>
                  <input
                    style={{ width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '10px 12px', color: 'var(--hp-text)', fontSize: '0.9rem', fontFamily: 'var(--font-body)', boxSizing: 'border-box' }}
                    placeholder="e.g. SMITHJ or unknown"
                    value={fbFields.suggestedDriver}
                    onChange={e => setFbFields(f => ({ ...f, suggestedDriver: e.target.value }))}
                  />
                </div>
              )}
              <div>
                <label style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
                  Additional notes
                </label>
                <textarea
                  style={{ width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '10px 12px', color: 'var(--hp-text)', fontSize: '0.9rem', fontFamily: 'var(--font-body)', minHeight: 70, resize: 'vertical', boxSizing: 'border-box' }}
                  placeholder="Any extra detail…"
                  value={fbFields.note}
                  onChange={e => setFbFields(f => ({ ...f, note: e.target.value }))}
                />
              </div>
              <button
                type="submit"
                disabled={fbSending}
                style={{ padding: '12px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 700, fontSize: '0.875rem', cursor: fbSending ? 'not-allowed' : 'pointer', opacity: fbSending ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <Send size={15} /> {fbSending ? 'Sending…' : 'Send Alert'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Carry-Over Report Tab ─────────────────────────────────────────────────────
// Daily carry-over parcels report + one-tap email to Australia Post.
// Visible to CARRY_REPORT_LOGINS; To/CC/auto-send editable by portal admins.
// paul@hollypaul.com is always BCC'd server-side.
function CarryOverReportTab({ shift }) {
  const driver  = (shift?.driver || '').toUpperCase();
  const isAdmin = ADMIN_PORTAL_LOGINS.has(driver);
  const [date, setDate]             = useState(localDateStr());
  const [loading, setLoading]       = useState(true);
  const [rows, setRows]             = useState([]);
  const [status, setStatus]         = useState(null);  // { to, cc, autoSend, sent }
  const [sending, setSending]       = useState(false);
  const [sendConfirm, setSendConfirm] = useState(false);
  const [resendConfirm, setResendConfirm] = useState(false); // admin correction double-confirm
  const [result, setResult]         = useState(null);
  const [cfgEdit, setCfgEdit]       = useState(null);  // { to, cc, autoSend } while editing
  const [cfgSaving, setCfgSaving]   = useState(false);
  const [rowEdit, setRowEdit]       = useState(null);  // { driver, reason, value } while editing a count
  const [rowEditBusy, setRowEditBusy] = useState(false);

  const api = body => fetch('/.netlify/functions/carryover-sb', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  }).then(r => r.json());

  const load = useCallback(() => {
    setLoading(true); setResult(null); setSendConfirm(false);
    Promise.all([
      api({ action: 'getCarryOverByDate', date }),
      api({ action: 'getCarryOverEmailStatus', date }),
    ]).then(([d, s]) => {
      setRows((d && d.rows) || []);
      if (s && s.success) setStatus(s);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const isNone  = r => (r.reason || '').toLowerCase().startsWith('none');
  const carried = rows.filter(r => !isNone(r));
  const clear   = [...new Set(rows.filter(isNone).map(r => r.driver))].sort();
  const total   = carried.reduce((s, r) => s + (r.count || 0), 0);

  // Send the report. `correction` = an admin re-send after a report already
  // went out (passes override:true so the server-side once-per-day lock allows it).
  async function doSend(correction = false) {
    if (correction) { if (!resendConfirm) { setResendConfirm(true); return; } }
    else            { if (!sendConfirm)   { setSendConfirm(true);   return; } }
    setSending(true); setResult(null);
    try {
      const d = await fetch('/.netlify/functions/carryover-email', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, by: driver, override: correction }),
      }).then(r => r.json());
      if (!d.ok) {
        // Someone else already sent it (e.g. this portal was stale) — lock the UI.
        if (d.alreadySent && d.sent) setStatus(st => ({ ...st, sent: d.sent }));
        throw new Error(d.error || 'Send failed — try again.');
      }
      setResult({ ok: true, msg: `${correction ? 'Correction sent' : 'Sent'} to ${d.to}${d.cc ? ` (cc ${d.cc})` : ''}` });
      setStatus(st => ({ ...st, sent: { sentAt: d.sentAt, by: driver, to: d.to } }));
    } catch (err) {
      setResult({ ok: false, msg: err.message });
    } finally {
      setSending(false); setSendConfirm(false); setResendConfirm(false);
    }
  }

  async function saveCfg() {
    setCfgSaving(true);
    try {
      const d = await api({ action: 'saveCarryOverConfig', to: cfgEdit.to, cc: cfgEdit.cc, autoSend: cfgEdit.autoSend });
      if (d.success) { setStatus(st => ({ ...st, to: d.to, cc: d.cc, autoSend: d.autoSend })); setCfgEdit(null); }
    } catch {} finally { setCfgSaving(false); }
  }

  // Review correction before sending: update one row's parcel count (0 removes).
  async function saveRowCount() {
    const c = parseInt(rowEdit.value);
    if (isNaN(c) || c < 0) { setRowEdit(null); return; }
    setRowEditBusy(true);
    try {
      const d = await api({ action: 'setCarryOverCount', date, driver: rowEdit.driver, reason: rowEdit.reason, count: c });
      if (d.success) { setRows(d.rows || []); setRowEdit(null); }
    } catch {} finally { setRowEditBusy(false); }
  }

  const chip = (text, color) => (
    <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '4px 10px', borderRadius: 999, background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', color }}>{text}</span>
  );

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.15rem', color: 'var(--hp-text)', letterSpacing: '0.04em' }}>
          CARRY-OVER REPORT
        </div>
        <input type="date" value={date} onChange={e => setDate(e.target.value)}
          style={{ ...S.input, width: 'auto', padding: '8px 10px', fontSize: '0.85rem' }} />
      </div>

      {loading ? (
        <div style={{ color: 'var(--hp-dim)', fontSize: '0.85rem', padding: '12px 0' }}>Loading…</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
            {chip(`${total} parcel${total === 1 ? '' : 's'} carried over`, total > 0 ? 'var(--hp-amber)' : 'var(--hp-dim)')}
            {chip(`${clear.length} all clear`, 'var(--hp-green)')}
            {rows.length === 0 && chip('no reports yet', 'var(--hp-dim)')}
          </div>

          {carried.map((r, i) => {
            const isEditing = rowEdit && rowEdit.driver === r.driver && rowEdit.reason === r.reason;
            return (
              <div key={r.driver + r.reason + i} style={{ border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '10px 14px', marginBottom: 8, background: 'var(--hp-surface)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)' }}>
                  <span>{r.driver}</span>
                  {isEditing ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input type="number" inputMode="numeric" min="0" autoFocus value={rowEdit.value}
                        onChange={e => setRowEdit(re => ({ ...re, value: e.target.value }))}
                        onKeyDown={e => { if (e.key === 'Enter') saveRowCount(); if (e.key === 'Escape') setRowEdit(null); }}
                        style={{ width: 64, padding: '5px 8px', borderRadius: 'var(--radius)', border: '1px solid var(--hp-gold)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '0.9rem', fontFamily: 'var(--font-mono)' }} />
                      <button onClick={saveRowCount} disabled={rowEditBusy}
                        style={{ background: 'none', border: 'none', color: 'var(--hp-green)', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer', padding: 0 }}>
                        {rowEditBusy ? '…' : '✓'}
                      </button>
                      <button onClick={() => setRowEdit(null)}
                        style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', padding: 0 }}>✕</button>
                    </span>
                  ) : (
                    <span style={{ color: 'var(--hp-amber)' }}>
                      {r.count} parcel{r.count === 1 ? '' : 's'}
                      <button
                        onClick={() => setRowEdit({ driver: r.driver, reason: r.reason, value: String(r.count) })}
                        title="Correct this count before sending (0 removes the row)"
                        style={{ background: 'none', border: 'none', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.75rem', cursor: 'pointer', marginLeft: 6, padding: 0 }}
                      >✎</button>
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '0.8rem', color: (r.reason || '').startsWith('Incorrect') ? 'var(--hp-amber)' : 'var(--hp-dim)', marginTop: 2 }}>
                  {r.reason}{r.note ? ` — ${r.note}` : ''}
                  {r.photo && (r.photo).split('\n').filter(Boolean).map((u, i, a) => (
                    <React.Fragment key={i}> · <a href={u} target="_blank" rel="noreferrer" style={{ color: 'var(--hp-gold)', fontWeight: 600 }}>{a.length > 1 ? `photo ${i + 1}` : 'photo'}</a></React.Fragment>
                  ))}
                </div>
              </div>
            );
          })}
          {clear.length > 0 && (
            <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', margin: '4px 0 14px' }}>
              <strong style={{ color: 'var(--hp-green)' }}>All delivered/transferred:</strong> {clear.join(', ')}
            </div>
          )}

          {/* Send */}
          <div style={{ borderTop: '1px solid var(--hp-border)', paddingTop: 14, marginTop: 6 }}>
            <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 10 }}>
              {status?.sent
                ? <span style={{ color: 'var(--hp-green)', fontWeight: 700 }}>✓ Sent {formatTimeStr(status.sent.sentAt)} by {status.sent.by} to {status.sent.to}</span>
                : 'Not sent for this date yet.'}
              {status?.to ? <span> · Recipient: <strong style={{ color: 'var(--hp-text)' }}>{status.to}</strong></span> : ''}
            </div>
            {!status?.to && (
              <div style={{ ...S.errorBox, marginBottom: 10 }}>
                No recipient set — {isAdmin ? 'enter the Australia Post email below.' : 'ask an admin to set the Australia Post email address.'}
              </div>
            )}

            {status?.sent ? (
              /* LOCKED — one report per day already went to Australia Post */
              <>
                <div style={{ background: 'rgba(52,211,153,0.10)', border: '1px solid rgba(52,211,153,0.30)', borderRadius: 'var(--radius)', padding: '12px 14px', fontSize: '0.82rem', color: 'var(--hp-text)', lineHeight: 1.55 }}>
                  🔒 This day's carry-over report has already been sent to Australia Post
                  {status.sent.by ? <> by <strong>{status.sent.by}</strong></> : ''}. To avoid confusing AP, only one report goes out per day.
                </div>
                {isAdmin ? (
                  <>
                    <button
                      onClick={() => doSend(true)}
                      disabled={sending || !status?.to}
                      style={{ ...S.bigBtn(resendConfirm ? 'red' : 'gold'), marginTop: 12, opacity: (sending || !status?.to) ? 0.5 : 1 }}
                    >
                      <Send size={18} />
                      {sending ? 'Sending…' : resendConfirm ? 'TAP AGAIN — THIS REPLACES THE REPORT AP ALREADY RECEIVED' : 'RE-SEND A CORRECTION'}
                    </button>
                    {resendConfirm && (
                      <button onClick={() => setResendConfirm(false)}
                        style={{ width: '100%', marginTop: 8, padding: '10px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                        Cancel
                      </button>
                    )}
                    <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)', marginTop: 8 }}>
                      Admin only — use this solely to correct an error in the report already sent.
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: '0.76rem', color: 'var(--hp-dim)', marginTop: 10 }}>
                    Need a correction sent? Ask Paul or Jayda — only an admin can re-send.
                  </div>
                )}
              </>
            ) : (
              <button
                onClick={() => doSend(false)}
                disabled={sending || !status?.to}
                style={{ ...S.bigBtn(sendConfirm ? 'red' : 'gold'), opacity: (sending || !status?.to) ? 0.5 : 1 }}
              >
                <Send size={18} />
                {sending ? 'Sending…' : sendConfirm ? 'TAP AGAIN TO CONFIRM SEND' : 'SEND TO AUSTRALIA POST'}
              </button>
            )}

            {result && (
              <div style={{ fontSize: '0.82rem', fontWeight: 700, marginTop: 10, color: result.ok ? 'var(--hp-green)' : 'var(--hp-red)' }}>
                {result.ok ? '✓ ' : '✗ '}{result.msg}
              </div>
            )}
          </div>

          {/* Admin settings */}
          {isAdmin && (
            <div style={{ borderTop: '1px solid var(--hp-border)', paddingTop: 14, marginTop: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email settings</span>
                {!cfgEdit && (
                  <button onClick={() => setCfgEdit({ to: status?.to || '', cc: status?.cc || '', autoSend: !!status?.autoSend })}
                    style={{ background: 'none', border: 'none', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
                    ✎ Edit
                  </button>
                )}
              </div>
              {cfgEdit ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 10 }}>
                  <div>
                    <label style={S.label}>To (Australia Post)</label>
                    <input style={S.input} type="email" placeholder="ap.contact@auspost.com.au"
                      value={cfgEdit.to} onChange={e => setCfgEdit(c => ({ ...c, to: e.target.value }))} />
                  </div>
                  <div>
                    <label style={S.label}>CC (optional)</label>
                    <input style={S.input} type="email" value={cfgEdit.cc} onChange={e => setCfgEdit(c => ({ ...c, cc: e.target.value }))} />
                  </div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.85rem', color: 'var(--hp-text)', fontWeight: 600, cursor: 'pointer' }}>
                    <input type="checkbox" checked={cfgEdit.autoSend} onChange={e => setCfgEdit(c => ({ ...c, autoSend: e.target.checked }))} style={{ width: 18, height: 18 }} />
                    Auto-send when all drivers have finished (after 3pm)
                  </label>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={saveCfg} disabled={cfgSaving}
                      style={{ flex: 1, padding: '12px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.85rem', cursor: 'pointer' }}>
                      {cfgSaving ? 'Saving…' : '✓ Save settings'}
                    </button>
                    <button onClick={() => setCfgEdit(null)}
                      style={{ flex: 1, padding: '12px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 8, lineHeight: 1.6 }}>
                  To: {status?.to || '— not set —'}<br />
                  CC: {status?.cc || '—'} · BCC: paul@hollypaul.com (always)<br />
                  Auto-send: <strong style={{ color: status?.autoSend ? 'var(--hp-green)' : 'var(--hp-dim)' }}>{status?.autoSend ? 'ON' : 'OFF'}</strong>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Scanner Portal Tab ────────────────────────────────────────────────────────
// Allows driver to photograph their scanner screen and upload with optional note.
// Images are compressed client-side to ~300KB before upload.

const SCANNER_API = '/.netlify/functions/scanner-photo';
// Marker on driver_logs.manager_notes for a shift finished without its scanner
// photo. Shift Approvals keys off it to label the note as a camera fault the
// office should chase, not as a rejection reason (which is what that field
// otherwise holds). Keep the two in step — see ShiftApprovalsPage.
const NO_PHOTO_PREFIX = 'NO SCANNER PHOTO — ';

async function scannerApi(body) {
  const res = await fetch(SCANNER_API, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

// A canvas re-encode that never answers is worse than one that fails: the caller
// sits on "Sending…" with no error and the driver decides the app is broken.
// 12s covers a slow phone re-encoding a 12 MP photo and nothing longer.
const COMPRESS_TIMEOUT_MS = 12000;
function compressImage(file, maxSizeKB = 350) {
  return Promise.race([
    compressImageInner(file, maxSizeKB),
    new Promise((_, reject) => setTimeout(() => reject(new Error('your phone took too long to prepare the photo')), COMPRESS_TIMEOUT_MS)),
  ]);
}
function compressImageInner(file, maxSizeKB = 350) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        // Scale down if large
        const maxDim = 1400;
        if (width > maxDim || height > maxDim) {
          if (width > height) { height = Math.round(height * maxDim / width); width = maxDim; }
          else                { width  = Math.round(width  * maxDim / height); height = maxDim; }
        }
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        // Try quality from 0.8 down until size is acceptable
        let quality = 0.82;
        let dataUrl;
        do {
          dataUrl = canvas.toDataURL('image/jpeg', quality);
          quality -= 0.08;
        } while (dataUrl.length > maxSizeKB * 1024 * 1.37 && quality > 0.2);
        const b64 = dataUrl.split(',')[1] || '';
        // "data:," is what a canvas that ran out of memory hands back. Sending it
        // stores a zero-byte photo and reports success.
        if (b64.length < 512) { reject(new Error('this phone could not prepare the photo')); return; }
        resolve(b64);
      };
      img.onerror = () => reject(new Error('this phone could not open that image'));
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── My Van — driver vehicle asset (service, fuel, fines, accidents, documents) ─
const MYVAN_SECTIONS = [
  { key: 'overview', label: 'Overview' },
  { key: 'compliance', label: 'Rego & Insurance' },
  { key: 'service', label: 'Service' },
  { key: 'fuel', label: 'Fuel' },
  { key: 'fines', label: 'Fines' },
  { key: 'accident', label: 'Accidents' },
  { key: 'other', label: 'Other' },
];
const rawFileToB64 = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve({ base64: String(r.result).split(',')[1] || '', contentType: file.type || 'application/octet-stream', name: file.name });
  r.onerror = reject;
  r.readAsDataURL(file);
});
const abToB64 = (ab) => {
  const bytes = new Uint8Array(ab);
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  return btoa(bin);
};
// Read a picked file for upload. IMAGES ARE RE-ENCODED, never sent raw: a phone photo
// is 3–5 MB and base64 inflates ~33%, so one or two pages blew Netlify's 6 MB function
// body limit and the ENTIRE save failed — the record's text saved, the images didn't,
// and the driver just saw an error. That silently cost us the photos on a real accident
// claim. uprightJpegBytes downscales AND bakes in iPhone EXIF rotation, so pages stop
// arriving sideways too. PDFs pass through untouched.
const fileToB64 = async (file) => {
  if (!file.type || !file.type.startsWith('image/')) return rawFileToB64(file);
  const jpgName = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
  const ab = await uprightJpegBytes(file, 1600, 0.8);
  if (ab) return { base64: abToB64(ab), contentType: 'image/jpeg', name: jpgName };
  // createImageBitmap can throw on some iOS/Safari versions (the imageOrientation option
  // isn't supported), so uprightJpegBytes returns null. Do NOT fall straight through to the
  // raw full-res photo — a 3–4 MB camera JPEG (doubled once it's sent as primary + page 1)
  // blows Netlify's 6 MB body limit and the whole save fails. Fall back to the classic
  // <img>+canvas downscale (compressImage) first, which works on those older browsers; only
  // if THAT fails too do we send raw.
  try {
    const b64 = await compressImage(file, 900);
    if (b64) return { base64: b64, contentType: 'image/jpeg', name: jpgName };
  } catch (_) { /* fall through to raw */ }
  return rawFileToB64(file);
};
const money2 = n => '$' + (Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtD = iso => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || ''); if (!m) return iso || '—'; const M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return `${+m[3]} ${M[+m[2]-1]} ${m[1]}`; };
// Compliance docs the driver manages (rego carries the renewal flow).
const COMPLIANCE_DOCS = [
  { type: 'rego_doc',      label: 'Registration',     fleetField: 'rego_exp' },
  { type: 'ctp',           label: 'CTP (Green Slip)',  fleetField: null },
  { type: 'insurance_doc', label: 'Vehicle Insurance', fleetField: 'insurance_exp' },
];

// OCR: Claude reads a scanned vehicle doc and returns its type + expiry (reuses the
// Service & Repairs invoice-extraction pattern: key from get-claude-key → Anthropic API).
async function extractDocFields(fileObj) {
  const keyResp = await fetch('/.netlify/functions/get-claude-key');
  const { key } = await keyResp.json();
  if (!key) throw new Error('OCR key unavailable');
  const isPdf = (fileObj.contentType === 'application/pdf') || /\.pdf$/i.test(fileObj.name || '');
  const docBlock = isPdf
    ? { type: 'document', source: { type: 'base64', media_type: 'application/pdf', data: fileObj.base64 } }
    : { type: 'image', source: { type: 'base64', media_type: fileObj.contentType || 'image/jpeg', data: fileObj.base64 } };
  const SCHEMA = {
    type: 'object', additionalProperties: false,
    properties: {
      docType:      { type: 'string', enum: ['registration', 'ctp', 'insurance', 'other'], description: 'Document type' },
      expiryDate:   { type: 'string', description: 'Expiry / due / period-end date as YYYY-MM-DD. Empty string if none.' },
      rego:         { type: 'string', description: 'Vehicle registration / plate, uppercase no spaces. Empty if none.' },
      insurer:      { type: 'string', description: 'Insurer / issuer name. Empty if none.' },
      policyNumber: { type: 'string', description: 'Policy or registration number. Empty if none.' },
    },
    required: ['docType', 'expiryDate', 'rego', 'insurer', 'policyNumber'],
  };
  const resp = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'x-api-key': key, 'anthropic-version': '2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' },
    body: JSON.stringify({
      model: 'claude-opus-4-8', max_tokens: 800,
      output_config: { format: { type: 'json_schema', schema: SCHEMA } },
      messages: [{ role: 'user', content: [docBlock, { type: 'text', text: `Today is ${new Date().toISOString().slice(0,10)}. This is an Australian vehicle document — a registration renewal, a CTP "green slip", or an insurance certificate of currency. Extract the document type, the expiry / due / period-end date, the rego, the insurer/issuer, and the policy/registration number.` }] }],
    }),
  });
  if (!resp.ok) throw new Error('OCR failed (' + resp.status + ')');
  const result = await resp.json();
  const raw = (result.content || []).filter(b => b.type === 'text').map(b => b.text).join('');
  return JSON.parse(raw);
}

// Reusable upload control: Take photo (camera) · Upload (library/files) · Scan
// (camera, for multi-page docs). Shows page thumbnails with remove. onPick(file).
function DocCapture({ pages, onPick, onRemove, scanning, ocr }) {
  const handle = (e) => { const f = e.target.files && e.target.files[0]; if (f) onPick(f); if (e.target) e.target.value = ''; };
  const btn = { padding: '9px 10px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-card)', color: 'var(--hp-text)', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5, flex: 1 };
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label style={btn}>📷 Take photo<input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handle} /></label>
        <label style={btn}>🖼 Upload<input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} onChange={handle} /></label>
        <label style={btn}>📄 Scan{ocr ? '' : ' page'}<input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handle} /></label>
      </div>
      {scanning && <div style={{ fontSize: 12, color: 'var(--hp-gold)', marginTop: 6 }}>Reading document…</div>}
      {pages && pages.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
          {pages.map((p, i) => (
            <div key={i} style={{ position: 'relative', width: 54, height: 54, borderRadius: 6, overflow: 'hidden', border: '1px solid var(--hp-border)', background: 'var(--hp-card)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {(p.contentType || '').indexOf('pdf') >= 0
                ? <span style={{ fontSize: 10, color: 'var(--hp-dim)', fontWeight: 700 }}>PDF</span>
                : <img src={'data:' + (p.contentType || 'image/jpeg') + ';base64,' + p.base64} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
              <button onClick={() => onRemove(i)} style={{ position: 'absolute', top: 0, right: 0, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', width: 18, height: 18, fontSize: 11, cursor: 'pointer', lineHeight: 1, padding: 0 }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Accident claim questions ──────────────────────────────────────────────────
// One renderer for the claim questions (src/utils/accidentClaim.js), used by the Report
// form and by the office's "we need more" card. `val` = { claim, op, location,
// description }; photo questions take DocCapture pages via `photos` / `plate`.
function AccidentFieldInputs({ keys, val, set, photos, setPhotos, plate, setPlate, inp, lbl, missing }) {
  const miss = new Set(missing || []);
  const read = (f) => (f.where === 'claim' ? (val.claim || {})[f.key] : f.where === 'op' ? (val.op || {})[f.opKey] : val[f.where]) || '';
  const write = (f, v) => set(f, f.upper ? v.toUpperCase() : v);
  const pill = (on) => ({ flex: 1, padding: '9px 10px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
    border: '1px solid ' + (on ? 'var(--hp-gold)' : 'var(--hp-border)'), background: on ? 'rgba(212,175,55,0.15)' : 'var(--hp-card)', color: 'var(--hp-text)' });
  return keys.map((k) => {
    const f = fieldByKey(k);
    if (!f) return null;
    const label = <label style={{ ...lbl, color: miss.has(k) ? 'var(--hp-red)' : lbl.color }}>{f.label}{miss.has(k) ? ' *' : ''}</label>;
    if (f.kind === 'photo') {
      const pages = k === 'platePhoto' ? plate : photos;
      const setPages = k === 'platePhoto' ? setPlate : setPhotos;
      return (
        <div key={k}>{label}
          <DocCapture pages={pages} onPick={async (file) => { try { const fo = await fileToB64(file); setPages(ps => [...ps, fo]); } catch (e) { /* unreadable file — ignore */ } }}
            onRemove={i => setPages(ps => ps.filter((_, j) => j !== i))} />
        </div>
      );
    }
    if (f.kind === 'yesno') {
      const v = read(f);
      return (
        <div key={k}>{label}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={pill(v === 'yes')} onClick={() => write(f, 'yes')}>Yes</button>
            <button type="button" style={pill(v === 'no')} onClick={() => write(f, 'no')}>No</button>
          </div>
        </div>
      );
    }
    if (f.kind === 'hit') {
      return (
        <div key={k}>{label}
          <select style={inp} value={read(f)} onChange={e => write(f, e.target.value)}>
            <option value="">Choose…</option>
            {HIT_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      );
    }
    if (f.kind === 'textarea') {
      return <div key={k}>{label}<textarea style={{ ...inp, minHeight: 64, resize: 'vertical' }} value={read(f)} placeholder={f.placeholder || ''} onChange={e => write(f, e.target.value)} /></div>;
    }
    return <div key={k}>{label}<input style={inp} type={f.kind === 'time' ? 'time' : 'text'} value={read(f)} placeholder={f.placeholder || ''} onChange={e => write(f, e.target.value)} /></div>;
  });
}

// Set one answer in a { claim, op, location, description } bag.
const setClaimVal = (bag, f, v) => (
  f.where === 'claim' ? { ...bag, claim: { ...(bag.claim || {}), [f.key]: v } }
    : f.where === 'op' ? { ...bag, op: { ...(bag.op || {}), [f.opKey]: v } }
      : { ...bag, [f.where]: v }
);

// The office asked for more on an accident report. A red card at the top of My Van with
// exactly the questions they ticked; answers are written onto the same report, photos
// appended, and the office is told (with anything still left blank).
function AccidentRequestCard({ record, onDone, inp, lbl, gold }) {
  const req = openRequest(record);
  // The address box starts with what's on file, so a missing street number is an edit, not a retype.
  const [bag, setBag] = useState(() => ({ claim: {}, op: {}, location: record.location || '', description: '' }));
  const [photos, setPhotos] = useState([]);
  const [plate, setPlate] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  if (!req) return null;
  const keys = (req.fields || []).map(f => f.key).filter(k => fieldByKey(k));
  const draft = draftRecord({ ...bag, photos: photos.length, plate: plate.length, base: record });
  const blank = keys.filter(k => !valueOf(draft, k) || (k === 'address' && !/\d/.test(valueOf(draft, k))));

  const submit = async () => {
    if (blank.length && !window.confirm(`You haven't answered: ${blank.map(k => fieldByKey(k).label).join(', ')}.\n\nSend what you have anyway?`)) return;
    setBusy(true); setErr('');
    try {
      const call = async (action, payload) => {
        const r = await fetch('/.netlify/functions/driver-records-sb', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) });
        const d = await r.json();
        if (!d.success) throw new Error(d.error || 'failed');
        return d;
      };
      const files = [...plate.map(p => ({ ...p, label: 'Number plate' })), ...photos.map(p => ({ ...p, label: 'Damage' }))];
      for (let i = 0; i < files.length; i += 6) {
        await call('addRecordDocuments', { id: record.id, documents: files.slice(i, i + 6).map(p => ({ label: p.label, base64: p.base64, contentType: p.contentType })) });
      }
      await call('answerAccidentInfo', {
        id: record.id, claim: bag.claim, otherParty: bag.op, location: bag.location, description: bag.description,
        stillMissing: blank.map(k => fieldByKey(k).label),
      });
      onDone();
    } catch (e) { setErr('Could not send — ' + e.message); }
    setBusy(false);
  };

  return (
    <div style={{ background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.45)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', width: '100%', maxWidth: 440, boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ width: 9, height: 9, borderRadius: 999, background: 'var(--hp-red)', flexShrink: 0 }} />
        <div style={{ fontWeight: 800, fontSize: 15 }}>The office needs more on your accident</div>
      </div>
      <div style={{ fontSize: 12.5, color: 'var(--hp-dim)', marginBottom: 10 }}>
        {fmtD(record.date)}{record.location ? ` · ${record.location}` : ''} — “{(record.description || '').slice(0, 80)}”
      </div>
      {req.note && <div style={{ fontSize: 13, color: 'var(--hp-text)', background: 'var(--hp-card)', borderRadius: 8, padding: '8px 10px', marginBottom: 12, whiteSpace: 'pre-wrap' }}>{req.note}</div>}
      <div style={{ fontSize: 12, color: 'var(--hp-dim)', marginBottom: 10 }}>The insurer won't take the claim without these. Answer what you can.</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <AccidentFieldInputs keys={keys} val={bag} set={(f, v) => setBag(b => setClaimVal(b, f, v))}
          photos={photos} setPhotos={setPhotos} plate={plate} setPlate={setPlate} inp={inp} lbl={lbl} />
        {err && <div style={{ fontSize: 12.5, color: 'var(--hp-red)' }}>{err}</div>}
        <button style={{ ...gold, opacity: busy ? 0.6 : 1 }} onClick={submit} disabled={busy}>{busy ? 'Sending…' : 'Send to the office'}</button>
      </div>
    </div>
  );
}

function MyVanPortalTab({ shift, onAccidentCountChange }) {
  const driver = shift.driver;
  const callAS = async (action, payload) => {
    const res = await fetch('/.netlify/functions/appsscript', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) });
    return res.json();
  };
  // Like callAS but targets a specific Netlify function — for actions migrated off
  // Apps Script onto Supabase (fleet-sb, vehicle-service-sb).
  const callFn = async (url, action, payload) => {
    const res = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action, ...payload }) });
    return res.json();
  };
  const [van, setVan] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState('overview');
  const [odo, setOdo] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [form, setForm] = useState(null);

  const [odoHistory, setOdoHistory] = useState([]);
  // Supabase service records the driver is allowed to see (operator-visibility only,
  // resolved server-side to the van they currently operate). Merged with van.services.
  const [opServices, setOpServices] = useState([]);
  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(''), 2500); };
  const load = async () => {
    setLoading(true);
    try {
      const [v, r, os] = await Promise.all([
        callAS('getMyVan', { login: driver }),
        callFn('/.netlify/functions/driver-records-sb', 'listDriverRecords', { driver }),
        callFn('/.netlify/functions/vehicle-service-sb', 'getOperatorService', { login: driver }),
      ]);
      if (v.success) setVan(v);
      if (r.success) {
        setRecords(r.records || []);
        // Keeps the My Van red dot in step the moment an answer is sent.
        if (onAccidentCountChange) onAccidentCountChange((r.records || []).filter(x => x.recordType === 'accident' && openRequest(x)).length);
      }
      setOpServices((os && os.success && os.records) ? os.records : []);
      // Odometer audit for this van (timestamped readings — start/finish/swap/manual).
      const rego = v && v.vehicle && v.vehicle.rego;
      if (rego) {
        const h = await callAS('getOdometerLog', { rego });
        if (h.success) setOdoHistory(h.rows || []);
      }
    } catch (e) { flash('Could not load — ' + e.message); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [driver]);

  const today = new Date().toISOString().slice(0, 10);
  const veh = van && van.vehicle;
  const curOdo = (van && van.latestOdo && van.latestOdo.odometer) || 0;
  const nextKm = (van && van.nextServiceKm) || 0;
  const days = veh && veh.serviceDue ? Math.ceil((new Date(veh.serviceDue) - new Date(today)) / 86400000) : null;
  const kmLeft = nextKm > 0 && curOdo > 0 ? nextKm - curOdo : null;
  const dateSoon = days != null && days <= 30;
  const kmSoon = kmLeft != null && kmLeft <= 1000;
  const serviceAlert = dateSoon || kmSoon;

  // Format an odometer-audit row's when (prefer the timestamp, else the date).
  const odoWhen = (h) => {
    if (h.timestamp) {
      const dt = new Date(h.timestamp);
      if (!isNaN(dt)) return dt.toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false });
    }
    return fmtD(h.date);
  };

  const saveOdo = async () => {
    const v = parseFloat(odo);
    if (!v || !veh) return;
    setBusy('odo');
    const d = await callAS('logOdometer', { rego: veh.rego, odometer: v, date: today, driver, source: 'Driver update (My Van)', notes: 'Driver update (My Van)' });
    setBusy(''); setOdo('');
    if (d.success) { flash('Odometer updated'); load(); } else flash('Could not update odometer');
  };

  const delRecord = async (id) => {
    setRecords(prev => prev.filter(r => r.id !== id));
    await callFn('/.netlify/functions/driver-records-sb', 'deleteDriverRecord', { id });
  };

  const downloadAll = async () => {
    setBusy('zip');
    const d = await callFn('/.netlify/functions/driver-records-sb', 'zipDriverDocs', { driver, rego: veh ? veh.rego : '' });
    setBusy('');
    if (d.success && d.downloadUrl) window.open(d.downloadUrl, '_blank');
    else flash(d.error || 'Nothing to download yet');
  };

  // styles
  const card = { background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', width: '100%', maxWidth: 440 };
  const gold = { padding: '10px 16px', borderRadius: 8, border: 'none', background: 'var(--hp-gold)', color: '#1a1a1a', fontWeight: 700, fontSize: 14, cursor: 'pointer' };
  const ghost = { padding: '7px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-card)', color: 'var(--hp-text)', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 };
  const inp = { width: '100%', background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 8, color: 'var(--hp-text)', fontSize: 15, padding: '10px 12px', outline: 'none', boxSizing: 'border-box', fontFamily: 'var(--font-body)' };
  const lbl = { fontSize: 11, fontWeight: 700, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5, display: 'block' };
  const Pill = ({ label, iso, warnDays = 45 }) => {
    const dd = iso ? Math.ceil((new Date(iso) - new Date(today)) / 86400000) : null;
    const c = dd == null ? 'var(--hp-dim)' : dd < 0 ? 'var(--hp-red)' : dd <= warnDays ? 'var(--hp-amber)' : 'var(--hp-green)';
    return (
      <div style={{ flex: 1, minWidth: 120, background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '8px 10px' }}>
        <div style={{ fontSize: 10.5, color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '.05em' }}>{label}</div>
        <div style={{ fontSize: 13, fontWeight: 700, color: c }}>{iso ? fmtD(iso) : '—'}</div>
        {dd != null && <div style={{ fontSize: 10.5, color: c }}>{dd < 0 ? `${Math.abs(dd)}d overdue` : `in ${dd}d`}</div>}
      </div>
    );
  };

  const fuel = records.filter(r => r.recordType === 'fuel');
  const yr = today.slice(0, 4);
  const mo = today.slice(0, 7);
  const qtr = Math.floor((new Date(today).getMonth()) / 3);
  const inQ = r => (r.date || '').slice(0, 4) === yr && Math.floor((parseInt((r.date || '0-0').slice(5, 7)) - 1) / 3) === qtr;
  const fuelMonth = fuel.filter(r => (r.date || '').slice(0, 7) === mo).reduce((s, r) => s + (r.amount || 0), 0);
  const fuelQtr = fuel.filter(inQ).reduce((s, r) => s + (r.amount || 0), 0);
  const fuelYear = fuel.filter(r => (r.date || '').slice(0, 4) === yr).reduce((s, r) => s + (r.amount || 0), 0);

  // Compliance docs + expiry resolution (rego/insurance from Fleet, CTP from the doc)
  const latestOf = (t) => records.filter(r => r.recordType === t).sort((a, b) => (b.date || '').localeCompare(a.date || ''))[0];
  const expiryFor = (c) => {
    const rec = latestOf(c.type);
    const fromDoc = rec && rec.meta && rec.meta.expiry;
    if (c.fleetField === 'rego_exp') return (veh && veh.regoExp) || fromDoc || '';
    if (c.fleetField === 'insurance_exp') return (veh && veh.insuranceExp) || fromDoc || '';
    return fromDoc || '';
  };
  const regoDays = (veh && veh.regoExp) ? Math.ceil((new Date(veh.regoExp) - new Date(today)) / 86400000) : null;
  const regoRenewWindow = regoDays != null && regoDays <= 28 && regoDays >= -14; // 4wk before → 2wk after

  // ── add-record form ──
  const openForm = (recordType) => setForm({ recordType, date: today, amount: '', description: '', location: '', status: 'open', provider: '', odometer: '', nextServiceDate: '', nextServiceKm: '', litres: '', file: null, fileName: '', expiry: '', insurer: '', policyNumber: '', pages: [], scanning: false, op: { name: '', licence: '', rego: '', insurer: '', phone: '' }, claim: { vanRego: (veh && veh.rego) || '' }, plate: [], tried: false });
  const isComplianceForm = (t) => t === 'rego_doc' || t === 'ctp' || t === 'insurance_doc';
  // Add a captured/uploaded/scanned file as a page. For compliance docs, OCR the
  // first page to pre-fill the expiry / insurer / policy.
  const addFile = async (file, ocrFirst) => {
    if (!file) return;
    let fo;
    try { fo = await fileToB64(file); } catch (e) { flash('Could not read file'); return; }
    const isFirst = (form && form.pages ? form.pages.length : 0) === 0;
    if (ocrFirst && isFirst) {
      setForm(f => ({ ...f, scanning: true }));
      let ext = {};
      try { ext = await extractDocFields(fo); } catch (e) { flash('Added; OCR could not read it — enter details by hand'); }
      setForm(f => ({ ...f, scanning: false, pages: [...f.pages, fo], expiry: ext.expiryDate || f.expiry, insurer: ext.insurer || f.insurer, policyNumber: ext.policyNumber || f.policyNumber }));
    } else {
      setForm(f => ({ ...f, pages: [...f.pages, fo] }));
    }
  };
  // What a new accident report still needs before it can be saved (see utils/accidentClaim).
  const accidentGaps = (f) => missingForClaim(draftRecord({ location: f.location, description: f.description, claim: f.claim, op: f.op, photos: (f.pages || []).length, plate: (f.plate || []).length }));
  const submitForm = async () => {
    if (!form) return;
    if (form.recordType === 'accident') {
      const gaps = accidentGaps(form);
      if (gaps.length) {
        setForm(f => ({ ...f, tried: true }));
        window.alert('The insurer needs these before we can claim:\n\n• ' + gaps.map(k => fieldByKey(k).label).join('\n• '));
        return;
      }
    }
    setBusy('save');
    try {
      const primary = (form.pages || [])[0] || null;
      // Page 1 is sent as `photoBase64` (the record's primary image); do NOT also repeat it in
      // `documents` or the backend stores it twice and the request body is needlessly doubled —
      // that doubling is what pushed a single full-res camera photo over Netlify's 6 MB limit.
      const docs = (form.pages || []).slice(1).map((p, i) => ({ label: 'Page ' + (i + 2), base64: p.base64, contentType: p.contentType }))
        .concat((form.recordType === 'accident' ? (form.plate || []) : []).map(p => ({ label: 'Number plate', base64: p.base64, contentType: p.contentType })));
      // Safety net: never fire a request we know will exceed Netlify's 6 MB body limit — that
      // fails the whole save with an opaque error. Base64 chars ≈ body bytes; keep headroom for
      // the surrounding JSON. Leave the form open so the driver can retake / drop a page.
      const b64len = (primary ? primary.base64.length : 0) + docs.reduce((s, d) => s + (d.base64 ? d.base64.length : 0), 0);
      if (b64len > 5.5 * 1024 * 1024) {
        flash('Receipt too large to upload — retake the photo, or remove extra pages, and try again.');
        setBusy('');
        return;
      }
      if (isComplianceForm(form.recordType)) {
        const cfg = COMPLIANCE_DOCS.find(c => c.type === form.recordType);
        await callFn('/.netlify/functions/driver-records-sb', 'saveDriverRecord', { driver, recordType: form.recordType, date: form.date, description: form.description || (cfg ? cfg.label : ''), meta: { expiry: form.expiry, insurer: form.insurer, policyNumber: form.policyNumber }, by: driver, photoBase64: primary ? primary.base64 : '', contentType: primary ? primary.contentType : '', documents: docs });
        if (cfg && cfg.fleetField && form.expiry && veh) await callFn('/.netlify/functions/fleet-sb', 'setVehicleExpiry', { rego: veh.rego, field: cfg.fleetField, iso: form.expiry });
      } else if (form.recordType === 'service') {
        const rec = await callFn('/.netlify/functions/vehicle-service-sb', 'saveVehicleService', { record: { rego: veh ? veh.rego : '', type: form.category || 'Scheduled Service', status: 'Completed', dateOut: form.date, odometer: parseFloat(form.odometer) || 0, provider: form.provider, description: form.description, cost: parseFloat(form.amount) || 0, nextServiceDate: form.nextServiceDate, nextServiceKm: parseFloat(form.nextServiceKm) || 0, visibility: 'operator' } });
        if (rec.success && primary) await callFn('/.netlify/functions/vehicle-service-sb', 'saveVehicleServiceFile', { recordId: rec.id, rego: veh ? veh.rego : '', pdfBase64: primary.base64, filename: 'service' + ((primary.contentType || '').indexOf('pdf') >= 0 ? '.pdf' : '.jpg'), contentType: primary.contentType });
      } else {
        const meta = form.recordType === 'accident' ? { otherParty: form.op, claim: form.claim } : (form.recordType === 'fuel' ? { litres: parseFloat(form.litres) || 0, odometer: parseFloat(form.odometer) || 0 } : {});
        await callFn('/.netlify/functions/driver-records-sb', 'saveDriverRecord', { driver, recordType: form.recordType, date: form.date, amount: parseFloat(form.amount) || 0, status: form.status, description: form.description, location: form.location, notes: '', meta, by: driver, photoBase64: primary ? primary.base64 : '', contentType: primary ? primary.contentType : '', documents: docs });
      }
      setForm(null); flash('Saved'); load();
    } catch (e) { flash('Save failed — ' + e.message); }
    setBusy('');
  };

  // Attach photos to a record that already exists. Uploaded in small batches: each
  // image is ~260 KB after re-encoding, and Netlify's function body limit is 6 MB, so
  // batching keeps a 10-photo accident well inside it instead of failing as one blob.
  const addPhotosToRecord = async (id, files) => {
    const imgs = files.filter(f => f.type && f.type.startsWith('image/'));
    if (!imgs.length) { flash('Pick photos to add'); return; }
    setBusy('photos:' + id);
    try {
      const BATCH = 6;
      let added = 0;
      for (let i = 0; i < imgs.length; i += BATCH) {
        const slice = imgs.slice(i, i + BATCH);
        const documents = [];
        for (const f of slice) {
          const fo = await fileToB64(f); // re-encodes; keeps us under the body limit
          documents.push({ label: 'Photo', base64: fo.base64, contentType: fo.contentType });
        }
        const r = await callFn('/.netlify/functions/driver-records-sb', 'addRecordDocuments', { id, documents });
        if (!r.success) throw new Error(r.error || 'upload failed');
        added += r.added || slice.length;
      }
      flash(`${added} photo${added > 1 ? 's' : ''} added`);
      load();
    } catch (e) {
      flash('Could not add photos — ' + e.message);
    }
    setBusy('');
  };

  const recordRow = (r) => (
    <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--hp-border)', borderRadius: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, color: 'var(--hp-text)', fontWeight: 600 }}>{r.description || r.category || r.recordType}{r.amount ? ` · ${money2(r.amount)}` : ''}</div>
        <div style={{ fontSize: 11.5, color: 'var(--hp-dim)' }}>{fmtD(r.date)}{r.location ? ` · ${r.location}` : ''}{r.status && r.status !== 'open' ? ` · ${r.status}` : ''}{r.meta && r.meta.otherParty && r.meta.otherParty.name ? ` · vs ${r.meta.otherParty.name}` : ''}</div>
      </div>
      {r.viewUrl && <a href={r.viewUrl} target="_blank" rel="noreferrer" style={ghost}>View</a>}
      {r.downloadUrl && <a href={r.downloadUrl} target="_blank" rel="noreferrer" style={{ ...ghost, padding: '7px 9px' }}><Download size={13} /></a>}
      <button onClick={() => delRecord(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)', padding: 4 }}><Trash2 size={15} /></button>
    </div>
  );

  // An accident, with its photos and a way to add more. A report filed without photos
  // (or whose photos failed to upload) is the common case — the insurer needs them, so
  // say so plainly rather than leave a silent gap.
  const accidentRow = (r) => {
    const photos = r.documents || [];
    const busyKey = 'photos:' + r.id;
    return (
      <div key={r.id} style={{ border: '1px solid var(--hp-border)', borderRadius: 10, padding: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13.5, color: 'var(--hp-text)', fontWeight: 600 }}>{fmtD(r.date)}{r.location ? ` · ${r.location}` : ''}</div>
            {/* Accidents are kept per driver, not per van, so a loaner's accident still lists under whatever van they hold now — say which van it was. */}
            {valueOf(r, 'vanRego') && <div style={{ fontSize: 11.5, color: 'var(--hp-gold)', fontWeight: 700, marginTop: 2, fontFamily: 'monospace' }}>Van {valueOf(r, 'vanRego').toUpperCase()}</div>}
            <div style={{ fontSize: 12.5, color: 'var(--hp-dim)', marginTop: 3 }}>{r.description}</div>
            {r.meta && r.meta.otherParty && r.meta.otherParty.name && (
              <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginTop: 4 }}>
                Other party: {r.meta.otherParty.name}{r.meta.otherParty.rego ? ` · ${r.meta.otherParty.rego}` : ''}
              </div>
            )}
          </div>
          <button onClick={() => delRecord(r.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--hp-dim)', padding: 4 }}><Trash2 size={15} /></button>
        </div>

        {photos.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
            {photos.map((p, i) => (
              <a key={i} href={p.viewUrl} target="_blank" rel="noreferrer">
                <img src={p.thumbUrl || p.viewUrl} alt={p.label || `photo ${i + 1}`} style={{ width: 74, height: 74, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--hp-border)' }} />
              </a>
            ))}
          </div>
        )}

        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {/* TWO inputs, deliberately. iOS honours `capture` by forcing the camera and
              REMOVING the photo-library option — so a driver adding photos after the
              fact (they're already in her reel) could only re-take them, never upload.
              "Choose from photos" drops `capture` so the OS shows Photo Library, and
              keeps `multiple` so she can pick several at once. "Take photo" keeps the
              camera for at-the-scene use. */}
          <label style={{ ...gold, cursor: busy === busyKey ? 'default' : 'pointer', opacity: busy === busyKey ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <input type="file" accept="image/*" multiple style={{ display: 'none' }} disabled={busy === busyKey}
              onChange={e => { addPhotosToRecord(r.id, Array.from(e.target.files || [])); e.target.value = ''; }} />
            <ImageIcon size={14} /> {busy === busyKey ? 'Uploading…' : photos.length ? 'Add more photos' : 'Choose from photos'}
          </label>
          <label style={{ ...ghost, cursor: busy === busyKey ? 'default' : 'pointer', opacity: busy === busyKey ? 0.6 : 1 }}>
            <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} disabled={busy === busyKey}
              onChange={e => { addPhotosToRecord(r.id, Array.from(e.target.files || [])); e.target.value = ''; }} />
            <Camera size={13} /> Take photo
          </label>
          <span style={{ fontSize: 11.5, color: photos.length ? 'var(--hp-dim)' : 'var(--hp-red)' }}>
            {photos.length ? `${photos.length} photo${photos.length > 1 ? 's' : ''} attached` : 'No photos yet — the insurer needs them'}
          </span>
        </div>
      </div>
    );
  };

  if (loading) return <div style={{ ...card, color: 'var(--hp-dim)' }}>Loading your van…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, width: '100%', maxWidth: 440 }}>
      {msg && <div style={{ ...card, padding: '10px 14px', background: 'rgba(34,197,94,0.1)', borderColor: 'rgba(34,197,94,0.3)', color: 'var(--hp-text)', fontSize: 13 }}>{msg}</div>}

      {records.filter(r => r.recordType === 'accident' && openRequest(r)).map(r => (
        <AccidentRequestCard key={r.id + (r.meta.infoRequest.requestedAt || '')} record={r} inp={inp} lbl={lbl} gold={gold}
          onDone={() => { flash('Sent — thank you'); load(); }} />
      ))}

      {/* Vehicle header */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <Truck size={18} color="var(--hp-gold)" />
          <div style={{ fontWeight: 800, fontSize: 16 }}>My Van</div>
        </div>
        {veh ? (
          <>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--hp-text)' }}>{[veh.year, veh.make, veh.model].filter(Boolean).join(' ') || 'Vehicle'}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: 'var(--hp-gold)', marginBottom: 12 }}>{veh.rego}{veh.owner ? ` · ${veh.owner}` : ''}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Pill label="Rego" iso={veh.regoExp} />
              <Pill label="Insurance" iso={veh.insuranceExp} />
              <Pill label="Service due" iso={veh.serviceDue} warnDays={30} />
            </div>
          </>
        ) : (
          <div style={{ fontSize: 13, color: 'var(--hp-dim)' }}>No company van is assigned to your login. Your fuel, fines, accident and other records are still tracked here.</div>
        )}
      </div>

      {/* Service-due alert */}
      {serviceAlert && (
        <div style={{ ...card, background: 'rgba(245,158,11,0.12)', borderColor: 'rgba(245,158,11,0.4)' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--hp-amber)', fontWeight: 700, fontSize: 14 }}>
            <AlertTriangle size={16} /> Service due soon
          </div>
          <div style={{ fontSize: 13, color: 'var(--hp-text)', marginTop: 4 }}>
            {dateSoon && <>By date: <strong>{fmtD(veh.serviceDue)}</strong>{days >= 0 ? ` (in ${days} days)` : ` (${Math.abs(days)} days overdue)`}. </>}
            {kmSoon && <>By km: <strong>{kmLeft >= 0 ? `${kmLeft.toLocaleString()} km left` : `${Math.abs(kmLeft).toLocaleString()} km over`}</strong> (next at {nextKm.toLocaleString()} km). </>}
            Book it in soon.
          </div>
        </div>
      )}

      <VanWatchCard rego={veh && veh.rego} odo={curOdo} />

      {/* Registration renewal — only 4wk before → 2wk after the rego expiry */}
      {regoRenewWindow && veh && (
        <div style={{ ...card, background: 'rgba(201,168,76,0.12)', borderColor: 'rgba(201,168,76,0.5)' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', color: 'var(--hp-gold)', fontWeight: 700, fontSize: 14 }}><AlertTriangle size={16} /> Registration renewal</div>
          <div style={{ fontSize: 13, color: 'var(--hp-text)', margin: '4px 0 10px' }}>Rego expires {fmtD(veh.regoExp)}{regoDays >= 0 ? ` (in ${regoDays} days)` : ` (${Math.abs(regoDays)} days ago)`}. Renewed it? Scan the new papers — we'll read the new expiry and update your record.</div>
          <button style={gold} onClick={() => openForm('rego_doc')}>Confirm registration renewed</button>
        </div>
      )}

      {/* Section chips */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {MYVAN_SECTIONS.map(s => (
          <button key={s.key} onClick={() => { setSection(s.key); setForm(null); }} style={{ padding: '6px 13px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', border: '1px solid ' + (section === s.key ? 'var(--hp-gold)' : 'var(--hp-border)'), background: section === s.key ? 'rgba(201,168,76,0.15)' : 'var(--hp-surface)', color: section === s.key ? 'var(--hp-gold)' : 'var(--hp-dim)' }}>{s.label}</button>
        ))}
        <button onClick={downloadAll} disabled={busy === 'zip'} style={{ ...ghost, marginLeft: 'auto' }}><Download size={13} /> {busy === 'zip' ? 'Zipping…' : 'Download all'}</button>
      </div>

      {/* ── OVERVIEW ── */}
      {section === 'overview' && (
        <>
          <div style={card}>
            <div style={lbl}>Current odometer</div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <input style={{ ...inp, flex: 1 }} type="number" inputMode="numeric" placeholder={curOdo ? curOdo.toLocaleString() + ' km' : 'Enter current km'} value={odo} onChange={e => setOdo(e.target.value)} />
              <button style={{ ...gold, opacity: busy === 'odo' ? 0.6 : 1 }} onClick={saveOdo} disabled={busy === 'odo' || !veh}>Update</button>
            </div>
            <div style={{ fontSize: 12, color: 'var(--hp-dim)' }}>
              {curOdo ? `Last reading ${curOdo.toLocaleString()} km${van.latestOdo.date ? ' on ' + fmtD(van.latestOdo.date) : ''}.` : 'No odometer reading yet.'}
              {nextKm > 0 && curOdo > 0 && <> Next service at {nextKm.toLocaleString()} km — {kmLeft.toLocaleString()} km to go.</>}
            </div>
          </div>

          {/* Odometer audit — every reading with who/when/why */}
          {odoHistory.length > 0 && (
            <div style={{ ...card, marginTop: 12 }}>
              <div style={lbl}>Odometer history</div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {odoHistory.slice(0, 15).map((h, i, arr) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, padding: '8px 0', borderBottom: i < Math.min(arr.length, 15) - 1 ? '1px solid var(--hp-border)' : 'none' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--hp-text)', fontFamily: 'var(--font-mono)' }}>{Number(h.odometer).toLocaleString()} km</div>
                      <div style={{ fontSize: 11.5, color: 'var(--hp-dim)' }}>{h.source || 'reading'}{h.driver ? ' · ' + h.driver : ''}</div>
                    </div>
                    <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', textAlign: 'right', whiteSpace: 'nowrap' }}>{odoWhen(h)}</div>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 11, color: 'var(--hp-dim)', marginTop: 10 }}>Every reading is logged with who entered it and when — shift start, finish, vehicle swap or manual update.</div>
            </div>
          )}
        </>
      )}

      {/* ── REGO & INSURANCE ── */}
      {section === 'compliance' && (
        <div style={card}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Registration & insurance documents</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {COMPLIANCE_DOCS.map(c => {
              const iso = expiryFor(c);
              const dd = iso ? Math.ceil((new Date(iso) - new Date(today)) / 86400000) : null;
              const col = dd == null ? 'var(--hp-dim)' : dd < 0 ? 'var(--hp-red)' : dd <= 45 ? 'var(--hp-amber)' : 'var(--hp-green)';
              const rec = latestOf(c.type);
              return (
                <div key={c.type} style={{ border: '1px solid var(--hp-border)', borderRadius: 8, padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--hp-text)' }}>{c.label}</div>
                      <div style={{ fontSize: 12, color: col }}>{iso ? `Expires ${fmtD(iso)}${dd != null ? (dd < 0 ? ` · ${Math.abs(dd)}d overdue` : ` · in ${dd}d`) : ''}` : 'No expiry on file'}</div>
                    </div>
                    {rec && rec.viewUrl && <a href={rec.viewUrl} target="_blank" rel="noreferrer" style={ghost}>View</a>}
                    <button style={ghost} onClick={() => openForm(c.type)}><Plus size={13} /> {rec ? 'Update' : 'Add'}</button>
                  </div>
                  {rec && rec.documents && rec.documents.length > 1 && <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginTop: 6 }}>{rec.documents.length} pages on file</div>}
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginTop: 10 }}>Scan a document and we'll read the expiry date for you. Add subsequent pages to the same record.</div>
        </div>
      )}

      {/* ── SERVICE ── */}
      {section === 'service' && (() => {
        // van.services = legacy sheet; opServices = Supabase records exposed to this
        // driver. Merge and de-dupe by id (Supabase copy wins, it carries the flag).
        const byId = {};
        (van.services || []).forEach(s => { if (s && s.id != null) byId[s.id] = s; });
        (opServices || []).forEach(s => { if (s && s.id != null) byId[s.id] = s; });
        const services = Object.values(byId)
          .sort((a, b) => (b.dateOut || b.bookedDate || '').localeCompare(a.dateOut || a.bookedDate || ''));
        return (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>Service & maintenance</div>
            <button style={gold} onClick={() => openForm('service')}><Plus size={14} /> Invoice</button>
          </div>
          {services.length === 0 ? <div style={{ fontSize: 13, color: 'var(--hp-dim)' }}>No service records yet.</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {services.map(s => (
                <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1px solid var(--hp-border)', borderRadius: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 600 }}>{s.type || 'Service'}{s.cost ? ` · ${money2(s.cost)}` : ''}</div>
                    <div style={{ fontSize: 11.5, color: 'var(--hp-dim)' }}>{fmtD(s.dateOut || s.bookedDate)}{s.provider ? ` · ${s.provider}` : ''}{s.odometer ? ` · ${s.odometer.toLocaleString()} km` : ''}</div>
                  </div>
                  {s.invoicePdfUrl && <a href={s.invoicePdfUrl} target="_blank" rel="noreferrer" style={ghost}>Invoice</a>}
                </div>
              ))}
            </div>
          )}
        </div>
        );
      })()}

      {/* ── FUEL ── */}
      {section === 'fuel' && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>Fuel receipts</div>
            <button style={gold} onClick={() => openForm('fuel')}><Plus size={14} /> Receipt</button>
          </div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[['This month', fuelMonth], ['This quarter', fuelQtr], ['This year', fuelYear]].map(([l, v]) => (
              <div key={l} style={{ flex: 1, background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 8, padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: 10.5, color: 'var(--hp-dim)', textTransform: 'uppercase' }}>{l}</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--hp-text)' }}>{money2(v)}</div>
              </div>
            ))}
          </div>
          {fuel.length === 0 ? <div style={{ fontSize: 13, color: 'var(--hp-dim)' }}>No fuel receipts yet.</div> : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{fuel.map(recordRow)}</div>}
        </div>
      )}

      {/* ── FINES & OFFENCES ── */}
      {section === 'fines' && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>Fines & offences</div>
            <button style={ghost} onClick={() => openForm('parking_fine')}><Plus size={13} /> Parking</button>
            <button style={ghost} onClick={() => openForm('traffic_offence')}><Plus size={13} /> Traffic</button>
          </div>
          {records.filter(r => r.recordType === 'parking_fine' || r.recordType === 'traffic_offence').length === 0
            ? <div style={{ fontSize: 13, color: 'var(--hp-dim)' }}>No fines or offences recorded.</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{records.filter(r => r.recordType === 'parking_fine' || r.recordType === 'traffic_offence').map(recordRow)}</div>}
        </div>
      )}

      {/* ── ACCIDENTS ── */}
      {section === 'accident' && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>Accidents & incidents</div>
            <button style={gold} onClick={() => openForm('accident')}><Plus size={14} /> Report</button>
          </div>
          {records.filter(r => r.recordType === 'accident').length === 0
            ? <div style={{ fontSize: 13, color: 'var(--hp-dim)' }}>No accidents recorded.</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{records.filter(r => r.recordType === 'accident').map(accidentRow)}</div>}
        </div>
      )}

      {/* ── OTHER ── */}
      {section === 'other' && (
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 700, fontSize: 14, flex: 1 }}>Other items</div>
            <button style={gold} onClick={() => openForm('other')}><Plus size={14} /> Add</button>
          </div>
          {records.filter(r => r.recordType === 'other').length === 0
            ? <div style={{ fontSize: 13, color: 'var(--hp-dim)' }}>Nothing else recorded.</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{records.filter(r => r.recordType === 'other').map(recordRow)}</div>}
        </div>
      )}

      {/* ── ADD-RECORD FORM (modal) ── */}
      {form && (
        <div onClick={() => setForm(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--hp-surface)', borderTopLeftRadius: 16, borderTopRightRadius: 16, width: '100%', maxWidth: 480, maxHeight: '92vh', overflowY: 'auto', padding: '18px 18px 28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
              <div style={{ fontWeight: 800, fontSize: 16, flex: 1, textTransform: 'capitalize' }}>{isComplianceForm(form.recordType) ? ((COMPLIANCE_DOCS.find(c => c.type === form.recordType) || {}).label || 'Document') : form.recordType.replace('_', ' ')}</div>
              <button onClick={() => setForm(null)} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', fontSize: 22, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div><label style={lbl}>Date</label><input style={inp} type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
              {isComplianceForm(form.recordType) && (<>
                <div>
                  <label style={lbl}>Document — photo, upload or scan</label>
                  <DocCapture pages={form.pages} scanning={form.scanning} ocr onPick={f => addFile(f, true)} onRemove={i => setForm(fm => ({ ...fm, pages: fm.pages.filter((_, j) => j !== i) }))} />
                  <div style={{ fontSize: 11.5, color: 'var(--hp-dim)', marginTop: 4 }}>We'll read the expiry from the first page — or fill it in below. Add more pages for multi-page docs.</div>
                </div>
                <div><label style={lbl}>Expiry date</label><input style={inp} type="date" value={form.expiry} onChange={e => setForm({ ...form, expiry: e.target.value })} /></div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}><label style={lbl}>Insurer / issuer</label><input style={inp} value={form.insurer} onChange={e => setForm({ ...form, insurer: e.target.value })} /></div>
                  <div style={{ flex: 1 }}><label style={lbl}>Policy / ref #</label><input style={inp} value={form.policyNumber} onChange={e => setForm({ ...form, policyNumber: e.target.value })} /></div>
                </div>
              </>)}
              {!isComplianceForm(form.recordType) && (<>
              {form.recordType === 'service' && (<>
                <div><label style={lbl}>Provider</label><input style={inp} value={form.provider} onChange={e => setForm({ ...form, provider: e.target.value })} placeholder="Workshop" /></div>
                <div><label style={lbl}>Odometer (km)</label><input style={inp} type="number" value={form.odometer} onChange={e => setForm({ ...form, odometer: e.target.value })} /></div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}><label style={lbl}>Next service date</label><input style={inp} type="date" value={form.nextServiceDate} onChange={e => setForm({ ...form, nextServiceDate: e.target.value })} /></div>
                  <div style={{ flex: 1 }}><label style={lbl}>Next service km</label><input style={inp} type="number" value={form.nextServiceKm} onChange={e => setForm({ ...form, nextServiceKm: e.target.value })} /></div>
                </div>
              </>)}
              {form.recordType === 'fuel' && (
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}><label style={lbl}>Litres</label><input style={inp} type="number" value={form.litres} onChange={e => setForm({ ...form, litres: e.target.value })} /></div>
                  <div style={{ flex: 1 }}><label style={lbl}>Odometer (km)</label><input style={inp} type="number" value={form.odometer} onChange={e => setForm({ ...form, odometer: e.target.value })} /></div>
                </div>
              )}
              {(form.recordType === 'parking_fine' || form.recordType === 'traffic_offence' || form.recordType === 'accident') && (
                <div><label style={lbl}>{form.recordType === 'accident' ? 'Full address incl. street number (or corner of two streets)' : 'Location'}</label><input style={inp} value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder={form.recordType === 'accident' ? 'e.g. 42 Brougham Street, Potts Point — or Corner of Victoria St and Orwell St' : 'Where'} /></div>
              )}
              {(form.recordType !== 'accident') && (
                <div><label style={lbl}>Amount ($)</label><input style={inp} type="number" inputMode="decimal" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} /></div>
              )}
              <div><label style={lbl}>{form.recordType === 'service' ? 'Work done' : 'Description'}</label><input style={inp} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              {(form.recordType === 'parking_fine' || form.recordType === 'traffic_offence') && (
                <div><label style={lbl}>Status</label>
                  <select style={inp} value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                    <option value="open">Open</option><option value="paid">Paid</option><option value="disputed">Disputed</option>
                  </select>
                </div>
              )}
              {form.recordType === 'accident' && (() => {
                // The claim questions. Which ones show depends on what was hit; the rest of
                // the other driver's details are always offered, required only for a moving vehicle.
                const hit = form.claim.hit || '';
                const vehicle = !hit || hit === 'parked_car' || hit === 'moving_vehicle';
                const keys = ['time', 'hit', 'vanRego', 'ourDamage',
                  ...(hit !== 'nothing' ? ['theirDamage'] : []),
                  ...(vehicle ? ['otherRego', 'platePhoto'] : []),
                  ...(!hit || hit === 'parked_car' ? ['noteLeft'] : []),
                  ...(hit === 'moving_vehicle' ? ['otherName', 'otherPhone', 'otherLicence', 'otherInsurer'] : []),
                  'injuries', 'police', 'witnesses'];
                const bag = { claim: form.claim, op: form.op, location: form.location, description: form.description };
                return (
                  <div style={{ border: '1px solid var(--hp-border)', borderRadius: 10, padding: '12px', display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--hp-dim)' }}>FOR THE INSURANCE CLAIM</div>
                    <AccidentFieldInputs keys={keys} val={bag} inp={inp} lbl={lbl}
                      missing={form.tried ? accidentGaps(form) : []}
                      set={(f, v) => setForm(fm => { const b = setClaimVal({ claim: fm.claim, op: fm.op, location: fm.location, description: fm.description }, f, v); return { ...fm, ...b }; })}
                      photos={form.pages} setPhotos={fn => setForm(fm => ({ ...fm, pages: fn(fm.pages) }))}
                      plate={form.plate} setPlate={fn => setForm(fm => ({ ...fm, plate: fn(fm.plate || []) }))} />
                  </div>
                );
              })()}
              <div>
                <label style={{ ...lbl, color: form.recordType === 'accident' && form.tried && !(form.pages || []).length ? 'var(--hp-red)' : lbl.color }}>{form.recordType === 'service' ? 'Invoice — photo, upload or scan' : form.recordType === 'accident' ? 'Photos of the damage *' : 'Photo / document — take, upload or scan'}</label>
                <DocCapture pages={form.pages} onPick={f => addFile(f, false)} onRemove={i => setForm(fm => ({ ...fm, pages: fm.pages.filter((_, j) => j !== i) }))} />
              </div>
              </>)}
              <button style={{ ...gold, opacity: busy === 'save' ? 0.6 : 1, marginTop: 4 }} onClick={submitForm} disabled={busy === 'save'}>{busy === 'save' ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Scanner asset check ───────────────────────────────────────────────────────
// The office asks; the driver photographs the handheld's home screen; we read the
// asset number off the device itself and the driver — who is holding it — confirms
// the reading before it goes anywhere. That confirmation is the point: OCR gets a
// digit wrong occasionally, and a wrong asset number files the wrong device against
// a person. The full illustrated guide lives at /scanner-home-screen.html.
function ScannerAssetCard({ driver, onPendingChange }) {
  const [request, setRequest] = useState(null);
  const [loaded, setLoaded]   = useState(false);
  const [capture, setCapture] = useState(null);
  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState('');
  const [asset, setAsset]     = useState('');
  const [saved, setSaved]     = useState(false);

  const call = async (body) => {
    const res = await fetch('/.netlify/functions/scanners-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    const j = await res.json();
    if (!j.success) throw new Error(j.error || 'Request failed');
    return j;
  };

  const refresh = useCallback(() => {
    call({ action: 'myAudit' })
      .then((j) => {
        setRequest(j.request);
        setAsset((j.request && j.request.confirmed && j.request.confirmed.asset)
          || (j.request && j.request.ocr && j.request.ocr.assetNumber) || '');
        if (onPendingChange) onPendingChange(j.request && j.request.status !== 'done' ? 1 : 0);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [onPendingChange]);
  useEffect(() => { refresh(); }, [refresh, driver]);

  if (!loaded || !request || request.status === 'done') return null;

  const ocr = request.ocr;
  const model = (request.confirmed && request.confirmed.model) || (ocr && ocr.model) || 'TC58e';
  const replied = request.status === 'submitted';

  const send = async () => {
    if (!capture) return;
    setBusy(true); setError('');
    try {
      const j = await call({ action: 'auditSubmit', base64: capture.split(',')[1] || '', contentType: 'image/jpeg' });
      setCapture(null);
      setAsset((j.ocr && j.ocr.assetNumber) || '');
      refresh();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true); setError('');
    try {
      await call({ action: 'auditConfirm', asset: asset.trim(), model });
      setSaved(true);
      refresh();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const box = {
    background: 'var(--hp-card)', border: '1px solid rgba(201,168,76,0.45)',
    borderRadius: 'var(--radius)', padding: 16, marginBottom: 16,
  };
  const stepList = { margin: '8px 0 0', paddingLeft: 20, fontSize: '0.84rem', color: 'var(--hp-dim)', lineHeight: 1.7 };

  return (
    <div style={box}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Camera size={17} color="var(--hp-gold)" />
        <strong style={{ fontSize: '1rem' }}>Scanner check</strong>
      </div>
      <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', lineHeight: 1.6 }}>
        The office needs to record which scanner you are carrying. Send one photo of the scanner&rsquo;s
        <strong style={{ color: 'var(--hp-text)' }}> home screen</strong> — the screen with the asset number on it.
      </div>
      {request.note && (
        <div style={{ marginTop: 8, fontSize: '0.84rem', padding: '8px 10px', borderRadius: 8, background: 'rgba(201,168,76,0.10)' }}>
          {request.note}
        </div>
      )}

      <ol style={stepList}>
        <li>Press the <strong style={{ color: 'var(--hp-text)' }}>round dot</strong> at the bottom of the scanner — the circle between the triangle and the square.</li>
        <li>The Australia Post <strong style={{ color: 'var(--hp-text)' }}>Pick Up &amp; Delivery</strong> screen appears. Under the logo are three short lines: the date and battery, a long number, and the model.</li>
        <li>Photograph the whole screen so all three lines are readable, and send it below.</li>
        <li>When you&rsquo;re done, press the <strong style={{ color: 'var(--hp-text)' }}>Scan It PUD</strong> tile to go back to the live screen.</li>
      </ol>
      <a href="/scanner-home-screen.html" target="_blank" rel="noreferrer"
        style={{ display: 'inline-block', marginTop: 8, fontSize: '0.82rem', color: 'var(--hp-gold)' }}>
        See the picture guide →
      </a>

      {!replied && (
        <div style={{ marginTop: 14 }}>
          <CameraCapture
            nativeFirst guide="document" maxEdge={1800} quality={0.85}
            value={capture}
            onCapture={(dataUrl) => { setCapture(dataUrl); setError(''); }}
            onClear={() => setCapture(null)}
          />
          {capture && (
            <button onClick={send} disabled={busy}
              style={{ marginTop: 12, width: '100%', padding: 14, background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.95rem', cursor: busy ? 'wait' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
              {busy ? <><RefreshCw size={16} style={{ animation: 'spin 0.9s linear infinite' }} /> Reading the screen… (~10s)</> : <><Send size={16} /> Send the photo</>}
            </button>
          )}
        </div>
      )}

      {replied && (
        <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 'var(--radius)', background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.35)', fontSize: '0.85rem' }}>
          <div style={{ marginBottom: 8 }}>
            Photo received{request.ocr && request.ocr.deviceDate ? ` — your scanner says ${request.ocr.deviceDate}` : ''}.
          </div>
          {request.ocrError && (
            <div style={{ color: '#f59e0b', marginBottom: 8 }}>
              We couldn&rsquo;t read the number automatically. Type it in below — it&rsquo;s the long number on its own line.
            </div>
          )}
          <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', marginBottom: 4 }}>
            Asset number ({model}) — check this matches your screen:
          </div>
          <input value={asset} onChange={(e) => { setAsset(e.target.value); setSaved(false); }}
            inputMode="numeric" autoComplete="off"
            style={{ width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid var(--hp-border)', background: 'var(--hp-bg)', color: 'var(--hp-text)', fontSize: '1.05rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', letterSpacing: '0.04em' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button onClick={confirm} disabled={busy || !asset.trim()}
              style={{ flex: 1, minWidth: 150, padding: 12, background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.9rem', cursor: busy ? 'wait' : 'pointer' }}>
              {request.confirmed && saved ? 'Confirmed ✓' : request.confirmed ? 'Confirm again' : 'Yes, that’s my scanner'}
            </button>
            <button onClick={() => { setRequest({ ...request, status: 'pending' }); setCapture(null); }} disabled={busy}
              style={{ padding: 12, background: 'transparent', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontSize: '0.85rem', cursor: 'pointer' }}>
              Take another photo
            </button>
          </div>
          {request.confirmed && (
            <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--hp-dim)' }}>
              Sent to the office. Nothing else to do — you can go back to work.
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.4)', borderRadius: 'var(--radius)', color: '#ef4444', fontSize: '0.82rem' }}>
          {error}
        </div>
      )}
    </div>
  );
}

function ScannerPortalTab({ shift, onAssetCheck }) {
  const [photos, setPhotos]       = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [note, setNote]           = useState('');
  const [preview, setPreview]     = useState(null);   // { dataUrl, file }
  const [uploadDone, setUploadDone] = useState(null); // success message
  const [error, setError]         = useState('');

  const today = localDateStr();

  // Load today's already-submitted photos
  useEffect(() => {
    async function load() {
      setLoadingList(true);
      try {
        const data = await scannerApi({ action: 'list', driver: shift.driver, date: today });
        if (data.ok) setPhotos(data.photos || []);
      } catch {}
      finally { setLoadingList(false); }
    }
    load();
  }, [shift.driver, today]);

  const handleUpload = async () => {
    if (!preview) return;
    setUploading(true); setError('');
    try {
      const base64 = preview.file
        ? await compressImage(preview.file)
        : (preview.dataUrl.split(',')[1] || '');
      const data = await scannerApi({
        action:      'upload',
        driver:      shift.driver,
        driverName:  shift.name,
        date:        today,
        imageData:   base64,
        contentType: 'image/jpeg',
        note:        note.trim(),
      });
      if (!data.ok) throw new Error(data.error);
      setPhotos(prev => [data.metadata, ...prev]);
      setPreview(null);
      setNote('');
      setUploadDone('Photo submitted successfully.');
    } catch (e) {
      setError('Upload failed: ' + e.message);
    } finally {
      setUploading(false);
    }
  };

  const cardStyle = {
    background: 'var(--hp-card)',
    border: '1px solid var(--hp-border)',
    borderRadius: 'var(--radius)',
    padding: '16px',
  };

  const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false }) : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* An outstanding asset check comes first — it's a request, not a routine. */}
      <ScannerAssetCard driver={shift.driver} onPendingChange={onAssetCheck} />

      {/* Heading */}
      <div>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.4rem', color: 'var(--hp-text)', letterSpacing: '0.04em' }}>
          Scanner Photo
        </div>
        <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 4 }}>
          Photograph your scanner screen at the end of your shift
        </div>
      </div>

      {/* Instructions */}
      <div style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 'var(--radius)', padding: '12px 14px', fontSize: '0.82rem', color: 'var(--hp-dim)', lineHeight: 1.6 }}>
        📱 Show the <strong style={{ color: 'var(--hp-text)' }}>PUD Main Menu</strong> screen — make sure the driver name, Unsent Data count, and Task List are clearly visible. If you have remaining tasks, also photograph the Task List screen.
      </div>

      {/* Success toast */}
      {uploadDone && (
        <div style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 'var(--radius)', padding: '12px 16px', color: 'var(--hp-green)', fontSize: '0.875rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span><CheckCircle size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />{uploadDone}</span>
          <button onClick={() => setUploadDone(null)} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer' }}><XCircle size={14} /></button>
        </div>
      )}

      {/* Preview + upload */}
      {preview ? (
        <div style={cardStyle}>
          <img src={preview.dataUrl} alt="Preview" style={{ width: '100%', borderRadius: 6, maxHeight: 300, objectFit: 'contain', background: '#000' }} />
          <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
                Note (optional — e.g. "Task list", "Unsent data issue")
              </label>
              <input
                style={{ width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '9px 12px', color: 'var(--hp-text)', fontSize: '0.88rem', boxSizing: 'border-box' }}
                placeholder="e.g. End of shift — all clear"
                value={note}
                onChange={e => setNote(e.target.value)}
              />
            </div>
            {error && <div style={{ color: '#ef4444', fontSize: '0.82rem' }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => { setPreview(null); setNote(''); setError(''); }}
                style={{ flex: 1, padding: '11px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}
              >
                Retake
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                style={{ flex: 2, padding: '11px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 700, fontSize: '0.875rem', cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                <Send size={15} /> {uploading ? 'Uploading…' : 'Submit Photo'}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', marginBottom: 10, textAlign: 'center' }}>
            Tap <strong>Open camera</strong>, point at the PUD Main Menu and Capture.
          </div>
          <CameraCapture
            guide="document"
            maxEdge={1400}
            quality={0.85}
            value={null}
            onCapture={dataUrl => { setPreview({ dataUrl, file: null }); setNote(''); setUploadDone(null); setError(''); }}
            onClear={() => setPreview(null)}
          />
        </div>
      )}

      {/* Today's submitted photos */}
      <div>
        <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
          Submitted today ({photos.length})
        </div>
        {loadingList && <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>Loading…</div>}
        {!loadingList && photos.length === 0 && (
          <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', textAlign: 'center', padding: '16px 0' }}>
            No photos submitted yet today.
          </div>
        )}
        {photos.map((p, i) => (
          <div key={p.key || i} style={{ ...cardStyle, marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 32, height: 32, background: p.flags?.hasIssues ? 'rgba(239,68,68,0.12)' : 'rgba(52,211,153,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Camera size={14} color={p.flags?.hasIssues ? '#ef4444' : '#34d399'} />
              </div>
              <div>
                <div style={{ fontSize: '0.82rem', color: 'var(--hp-text)', fontWeight: 600 }}>
                  Photo {i + 1} — {fmtTime(p.uploadedAt)}
                </div>
                {p.note && <div style={{ fontSize: '0.72rem', color: 'var(--hp-dim)' }}>{p.note}</div>}
                {p.flags?.hasIssues && (
                  <div style={{ fontSize: '0.7rem', color: '#ef4444', marginTop: 2 }}>
                    {p.flags.hasUnsentIssue ? `Unsent: ${p.flags.unsentData}` : ''}
                    {p.flags.hasUnsentIssue && p.flags.hasTasksRemaining ? ' · ' : ''}
                    {p.flags.hasTasksRemaining ? `Tasks: ${p.flags.taskListCount}` : ''}
                  </div>
                )}
              </div>
            </div>
            <CheckCircle size={16} color={p.flags?.hasIssues ? '#ef4444' : '#34d399'} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── NSW School Terms (mirrors DirectEntryPage) ────────────────────────────────
const NSW_TERMS = [
  ['2026-01-28','2026-04-09'],['2026-04-27','2026-07-03'],
  ['2026-07-20','2026-09-25'],['2026-10-12','2026-12-18'],
  ['2027-01-27','2027-04-01'],['2027-04-19','2027-07-02'],
  ['2027-07-19','2027-09-24'],['2027-10-11','2027-12-17'],
];
function isSchoolTerm(iso) {
  return NSW_TERMS.some(([s,e]) => iso >= s && iso <= e);
}
// Next term start strictly after the given date (for the holiday "resumes" banner).
function nextTermStartISO(iso) {
  const starts = NSW_TERMS.map(([s]) => s).filter(s => s > iso).sort();
  return starts[0] || '';
}

// DoE collection points — driver → schools
const DOE_POINTS = [
  { qr:'SPB000000553865', school:'Fort Street Public School',     drivers:['FERNANDEB3'] },
  { qr:'SPB000000600323', school:'Conservatorium',                drivers:['SOTOASTOC'] },
  { qr:'SPB000000600316', school:'Observatory Hill',              drivers:['RONAS'] },
  { qr:'SPB000000600460', school:'Bridge Road School',            drivers:['BAGNATOA'] },
  { qr:'SPB000000600613', school:'Forest Lodge Public School',    drivers:['SHARLANDS2'] },
  { qr:'SPB000000600644', school:'Glebe Public School',           drivers:['GARCIARIP'] },
  { qr:'SPB000000600408', school:'RPA Public School',             drivers:['BAGNATOA'] },
  { qr:'SPB000000600545', school:'Syd Sec Coll Blackwattle Bay',  drivers:['TUMURO'] },
  { qr:'SPB000000600606', school:'Ultimo Public',                 drivers:['MEDINACOK'] },
  { qr:'SPB000000600200', school:'Bourke St Public School',       drivers:['GALLEGUIL','KIMJ8'] },
  { qr:'SPB000000600217', school:'Crown St Public School',        drivers:['MEDINAHEM2'] },
  { qr:'SPB000000600194', school:'Darlinghurst Public School',    drivers:['KIMJ8'] },
  { qr:'SPB000000623681', school:'Plunket St Public School',      drivers:['KIMJ8','NGUYENHAT'] },
  { qr:'SPB000000553872', school:'Syd Distance Ed',               drivers:['KIMJ8','ZHOUM5'] },
  { qr:'SPB000000567718', school:'Inner Sydney School',           drivers:['CAVIERESD2'] },
];
const INNER_SYDNEY_SCHOOL = 'Inner Sydney School';
// Live assignments from the DoE_Assignments sheet (admin "Permanent driver
// change" writes there). The hardcoded DOE_POINTS above is only the offline /
// fetch-failure fallback. Updated once per session via getDoEAssignments.
let DOE_POINTS_LIVE = DOE_POINTS;
// Local pre-generated QR PNG (works offline). Falls back to the online generator
// only if the bundled image is missing (see <img onError> below).
function doeQrImageUrl(code) {
  return `/doe-qr/${code}.png`;
}
function doeQrFallbackUrl(code) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=320x320&data=${encodeURIComponent(code)}&format=png&margin=4`;
}
function getDoESchoolsForDriver(login, isoDate) {
  if (!isSchoolTerm(isoDate)) return [];
  const day = new Date(isoDate + 'T12:00:00').getDay(); // 0=Sun,1=Mon,2=Tue,4=Thu
  const isMon = day === 1, isTue = day === 2, isThu = day === 4;
  if (!isMon && !isTue && !isThu) return [];
  // Day rule follows the SCHOOL (Inner Sydney = Mon+Thu, others Tue+Thu) so a
  // permanent driver swap keeps the school's collection days.
  return DOE_POINTS_LIVE
    .filter(p => p.school !== '(TBA)')
    .filter(p => p.drivers.map(d => d.toUpperCase()).includes(login.toUpperCase()))
    .filter(p => (p.school === INNER_SYDNEY_SCHOOL ? (isMon || isThu) : (isTue || isThu)));
}

// Effective schedule for a login on a date, applying relief reassignments.
// reliefList: [{ school, qr, relief, original }] for that date (from getDoERelief).
// - drops schools reassigned away from this login
// - adds schools where this login is the relief
function effectiveDoESchools(login, isoDate, reliefList) {
  const up = (login || '').toUpperCase();
  const relief = reliefList || [];
  const base = getDoESchoolsForDriver(login, isoDate).filter(p => {
    const r = relief.find(x => x.school === p.school);
    return !(r && r.relief && r.relief.toUpperCase() !== up); // taken over by someone else
  });
  const reliefPoints = relief
    .filter(r => r.relief && r.relief.toUpperCase() === up)
    .map(r => DOE_POINTS_LIVE.find(p => p.school === r.school))
    .filter(Boolean);
  const byQr = {};
  base.concat(reliefPoints).forEach(p => { byQr[p.qr] = p; });
  return Object.values(byQr);
}

// ── Period helper (reuse from top of file) ────────────────────────────────────
function currentPeriodEndISO() {
  const { end } = currentPeriodDates();
  return end;
}

// ── Tasks Portal Tab ──────────────────────────────────────────────────────────
function TasksPortalTab({ shift, depotActive, doeRelief, doeConfirmed, onDoeConfirmed }) {
  const today      = localDateStr();
  const payPeriod  = currentPeriodEndISO();
  const doeSchools = effectiveDoESchools(shift.driver, today, doeRelief);
  const isSchoolDay = doeSchools.length > 0;

  // Depot bags state
  const [bagInput, setBagInput]     = useState('');
  const [bagBusy, setBagBusy]       = useState(false);
  const [bagDone, setBagDone]       = useState(false);
  const [bagTotal, setBagTotal]     = useState(null);
  const [bagError, setBagError]     = useState('');
  // One tally per day: todayTally is what's already logged for today (null = none
  // yet); the input form only shows when there's no entry, or in explicit amend mode.
  const [todayTally, setTodayTally] = useState(null);
  const [amendMode, setAmendMode]   = useState(false);

  // DoE state — confirmed map (school → {collected, reason}) comes from the parent
  const doneSchools = doeConfirmed || new Map();
  const [showQrFor, setShowQrFor]   = useState(null);  // the DoE point whose QR is on screen
  const [missFor, setMissFor]       = useState(null);  // the DoE point being marked "couldn't collect"
  const [scanBusy, setScanBusy]     = useState(false);
  const [scanError, setScanError]   = useState('');

  const MISS_REASONS = ['School closed', 'Gate locked / no access', 'No articles to collect', 'Could not attend (breakdown/illness)', 'Other'];

  const appsApi = async (body) => {
    const res = await fetch('/.netlify/functions/appsscript', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  };
  // Depot bags + DoE confirmations migrated to Supabase (depot-sb / doe-sb).
  const depotApi = async (body) => {
    const res = await fetch('/.netlify/functions/depot-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  };
  const doeApi = async (body) => {
    const res = await fetch('/.netlify/functions/doe-sb', {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return res.json();
  };

  // Load existing depot bag total for the period + whether today is already logged
  useEffect(() => {
    if (!depotActive) return;
    depotApi({ action: 'getDepotBagsTotals', driver: shift.driver, payPeriod })
      .then(d => {
        if (!d.success) return;
        setBagTotal(d.total);
        const mine = (d.rows || []).filter(r => r.date === today);
        if (mine.length) setTodayTally(mine.reduce((s, r) => s + (r.tally || 0), 0));
      })
      .catch(() => {});
  }, [shift.driver, depotActive]); // eslint-disable-line

  async function submitDepotBags(e) {
    e.preventDefault();
    const n = parseInt(bagInput);
    if (isNaN(n) || n < 0) return;
    const amending = todayTally !== null;
    setBagBusy(true); setBagError('');
    try {
      const d = await depotApi({
        action: 'saveDepotBags', driver: shift.driver, name: shift.name,
        date: today, payPeriod, tally: n, amend: amending,
      });
      if (!d.success) {
        // Race: something was logged since we loaded — show it and switch to amend flow.
        if (d.duplicate) {
          setTodayTally(d.existingTally);
          setAmendMode(false);
          throw new Error(`A tally of ${d.existingTally} is already logged for today. Tap Amend to change it.`);
        }
        throw new Error(d.error);
      }
      setBagDone(true);
      setBagTotal(prev => (prev === null ? null : prev - (amending ? (todayTally || 0) : 0) + n));
      setTodayTally(n);
      setAmendMode(false);
      setBagInput('');
    } catch (err) {
      setBagError(err.message || 'Failed to save. Please try again.');
    } finally {
      setBagBusy(false);
    }
  }

  // Driver displays the school QR on their phone, scans it with their AP scanner gun,
  // then confirms here — which records the collection and clears the close-out gate.
  // collected=false records a "couldn't collect" with a reason (Quantity 0).
  async function confirmDoeScanned(point, collected = true, reason = '') {
    setScanBusy(true); setScanError('');
    try {
      const d = await doeApi({
        action: 'saveDoEConfirmation', driver: shift.driver, name: shift.name,
        date: today, school: point.school, qr: point.qr, payPeriod,
        collected, reason,
      });
      if (d.success) {
        onDoeConfirmed(point.school, { collected, reason });
        setShowQrFor(null); setMissFor(null);
      } else setScanError(d.error || 'Could not save. Please try again.');
    } catch {
      setScanError('Could not save. Please try again.');
    } finally {
      setScanBusy(false);
    }
  }

  const card = { background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: 16 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* DoE QR display overlay — driver scans this with their AP scanner gun */}
      {showQrFor && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.94)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px', paddingTop: 'env(safe-area-inset-top)' }}>
          <button onClick={() => setShowQrFor(null)} aria-label="Close" style={{ position: 'absolute', top: 'calc(16px + env(safe-area-inset-top))', right: 18, background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 999, width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <XCircle size={22} color="#fff" />
          </button>
          <div style={{ color: '#fff', fontWeight: 800, fontSize: '1.05rem', textAlign: 'center', marginBottom: 4 }}>{showQrFor.school}</div>
          <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem', textAlign: 'center', marginBottom: 18 }}>Scan this QR with your AP scanner</div>
          <div style={{ background: '#fff', padding: 16, borderRadius: 16 }}>
            <img src={doeQrImageUrl(showQrFor.qr)} alt={`QR for ${showQrFor.school}`} width={280} height={280} style={{ display: 'block' }}
              onError={(e) => { if (!e.target.dataset.fb) { e.target.dataset.fb = '1'; e.target.src = doeQrFallbackUrl(showQrFor.qr); } }} />
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', color: 'rgba(255,255,255,0.85)', fontSize: '0.9rem', marginTop: 14, letterSpacing: '0.02em' }}>{showQrFor.qr}</div>
          {scanError && <div style={{ color: '#fca5a5', fontSize: '0.82rem', marginTop: 12, textAlign: 'center' }}>{scanError}</div>}
          <button
            onClick={() => confirmDoeScanned(showQrFor)}
            disabled={scanBusy}
            style={{ marginTop: 24, width: '100%', maxWidth: 320, padding: '15px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.95rem', cursor: scanBusy ? 'not-allowed' : 'pointer', opacity: scanBusy ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
          >
            <CheckCircle size={18} /> {scanBusy ? 'Saving…' : "Done — I've scanned it"}
          </button>
        </div>
      )}

      {/* ── DoE section ── */}
      {isSchoolDay && (
        <div style={{ ...card, borderColor: 'rgba(201,168,76,0.4)', background: 'rgba(201,168,76,0.05)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <School size={18} color="var(--hp-gold)" />
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--hp-text)' }}>DoE Collection Today</div>
          </div>
          <div style={{ fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 12 }}>
            Display each school's QR, scan it with your AP scanner, then confirm. You can't finish your shift until all are confirmed.
          </div>
          {scanError && !showQrFor && <div style={{ color: '#ef4444', fontSize: '0.82rem', marginBottom: 10 }}>{scanError}</div>}
          {doeConfirmed === null ? (
            <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)' }}>Loading…</div>
          ) : doeSchools.map(point => {
            const entry  = doneSchools.get(point.school);
            const done   = doneSchools.has(point.school);
            const missed = done && entry && entry.collected === false;
            const picking = missFor && missFor.school === point.school;
            const bg = missed ? 'rgba(239,68,68,0.07)' : done ? 'rgba(52,211,153,0.08)' : 'var(--hp-surface)';
            const bd = missed ? 'rgba(239,68,68,0.3)' : done ? 'rgba(52,211,153,0.35)' : 'var(--hp-border)';
            return (
              <div key={point.qr} style={{ marginBottom: 12, padding: 14, background: bg, border: `1px solid ${bd}`, borderRadius: 'var(--radius)' }}>
                <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--hp-text)', marginBottom: 4 }}>{point.school}</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem', color: 'var(--hp-dim)', marginBottom: 10 }}>{point.qr}</div>
                {done ? (
                  missed ? (
                    <div style={{ color: 'var(--hp-red)', fontWeight: 700, fontSize: '0.85rem' }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={15} /> Couldn't collect</span>
                      {entry.reason && <div style={{ fontWeight: 500, color: 'var(--hp-dim)', fontSize: '0.8rem', marginTop: 3 }}>{entry.reason}</div>}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--hp-green)', fontWeight: 700, fontSize: '0.875rem' }}>
                      <CheckCircle size={16} /> Confirmed — collection done
                    </div>
                  )
                ) : picking ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginBottom: 2 }}>Why couldn't you collect?</div>
                    {MISS_REASONS.map(reason => (
                      <button key={reason}
                        onClick={() => {
                          if (reason === 'Other') {
                            const note = window.prompt('Briefly, what happened?');
                            if (note && note.trim()) confirmDoeScanned(point, false, note.trim());
                          } else confirmDoeScanned(point, false, reason);
                        }}
                        disabled={scanBusy}
                        style={{ width: '100%', padding: '11px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-text)', fontWeight: 600, fontSize: '0.85rem', cursor: scanBusy ? 'not-allowed' : 'pointer', textAlign: 'left' }}
                      >{reason}</button>
                    ))}
                    <button onClick={() => setMissFor(null)} disabled={scanBusy}
                      style={{ width: '100%', padding: '9px', background: 'transparent', border: 'none', color: 'var(--hp-dim)', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}>Cancel</button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <button
                      onClick={() => { setScanError(''); setShowQrFor(point); }}
                      style={{ width: '100%', padding: '13px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                    >
                      <QrCode size={18} /> Display QR code
                    </button>
                    <button
                      onClick={() => { setScanError(''); confirmDoeScanned(point); }}
                      disabled={scanBusy}
                      style={{ width: '100%', padding: '12px', background: 'transparent', border: '1px solid var(--hp-green)', borderRadius: 'var(--radius)', color: 'var(--hp-green)', fontWeight: 800, fontSize: '0.9rem', cursor: scanBusy ? 'not-allowed' : 'pointer', opacity: scanBusy ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                    >
                      <CheckCircle size={18} /> {scanBusy ? 'Saving…' : 'Collected'}
                    </button>
                    <button
                      onClick={() => { setScanError(''); setMissFor(point); }}
                      disabled={scanBusy}
                      style={{ width: '100%', padding: '9px', background: 'transparent', border: 'none', color: 'var(--hp-dim)', fontWeight: 600, fontSize: '0.8rem', cursor: scanBusy ? 'not-allowed' : 'pointer', textDecoration: 'underline' }}
                    >
                      Couldn't collect
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!isSchoolDay && (() => {
        const onHoliday = !isSchoolTerm(today);
        const resumeISO = onHoliday ? nextTermStartISO(today) : '';
        return (
          <div style={{ ...card, textAlign: 'center', padding: '18px 16px',
            ...(onHoliday ? { borderColor: 'rgba(201,168,76,0.4)', background: 'rgba(201,168,76,0.05)' } : {}) }}>
            <School size={22} color={onHoliday ? 'var(--hp-gold)' : 'var(--hp-dim)'} style={{ marginBottom: 8 }} />
            {onHoliday ? (
              <>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--hp-text)' }}>School holidays — no DoE collections</div>
                {resumeISO && (
                  <div style={{ fontSize: '0.82rem', color: 'var(--hp-dim)', marginTop: 4 }}>
                    Collections resume {formatDateDisplay(resumeISO)}.
                  </div>
                )}
              </>
            ) : (
              <div style={{ fontSize: '0.85rem', color: 'var(--hp-dim)' }}>No DoE collection scheduled for today.</div>
            )}
          </div>
        );
      })()}

      {/* ── Depot Bags section — only for active depot-roster drivers ── */}
      {depotActive && (
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <ShoppingBag size={18} color="var(--hp-gold)" />
          <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--hp-text)' }}>Depot Bags</div>
          {bagTotal !== null && (
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '1rem', color: 'var(--hp-gold)' }}>
              {bagTotal} this period
            </span>
          )}
        </div>

        {bagDone && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--hp-green)', fontWeight: 600, fontSize: '0.875rem', marginBottom: 12 }}>
            <CheckCircle size={15} /> Saved successfully
          </div>
        )}

        {/* Already submitted today — locked until the driver explicitly amends */}
        {todayTally !== null && !amendMode ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px', background: 'rgba(52,211,153,0.08)', border: '1px solid rgba(52,211,153,0.35)', borderRadius: 'var(--radius)' }}>
              <CheckCircle size={18} color="var(--hp-green)" />
              <div style={{ fontSize: '0.9rem', color: 'var(--hp-text)' }}>
                <strong>Submitted for today: {todayTally} bag{todayTally === 1 ? '' : 's'}</strong>
                <div style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', marginTop: 2 }}>Only one tally per day. Wrong number? Amend it below.</div>
              </div>
            </div>
            {bagError && <div style={{ color: '#ef4444', fontSize: '0.82rem' }}>{bagError}</div>}
            <button
              type="button"
              onClick={() => { setAmendMode(true); setBagInput(String(todayTally)); setBagDone(false); setBagError(''); }}
              style={{ padding: '11px', background: 'transparent', border: '1px solid var(--hp-gold)', borderRadius: 'var(--radius)', color: 'var(--hp-gold)', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}
            >
              Amend today's tally
            </button>
          </div>
        ) : (
        <form onSubmit={submitDepotBags} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {amendMode && (
            <div style={{ fontSize: '0.8rem', color: 'var(--hp-gold)', fontWeight: 600 }}>
              Amending — this replaces today's tally of {todayTally}.
            </div>
          )}
          <div>
            <label style={{ fontSize: '0.75rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
              How many depot bags today?
            </label>
            <input
              type="number"
              inputMode="numeric"
              min="0"
              max="99"
              value={bagInput}
              onChange={e => { setBagInput(e.target.value); setBagDone(false); setBagError(''); }}
              placeholder="e.g. 3"
              style={{ width: '100%', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '12px', color: 'var(--hp-text)', fontSize: '1.4rem', fontFamily: 'var(--font-mono)', textAlign: 'center', boxSizing: 'border-box' }}
            />
          </div>
          {bagError && <div style={{ color: '#ef4444', fontSize: '0.82rem' }}>{bagError}</div>}
          <button
            type="submit"
            disabled={bagBusy || bagInput === '' || parseInt(bagInput) < 0}
            style={{ padding: '13px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.9rem', cursor: bagBusy || bagInput === '' ? 'not-allowed' : 'pointer', opacity: bagBusy || bagInput === '' ? 0.6 : 1 }}
          >
            {bagBusy ? 'Saving…' : amendMode ? 'Save Amended Tally' : 'Submit Depot Bags'}
          </button>
          {amendMode ? (
            <button
              type="button"
              onClick={() => { setAmendMode(false); setBagInput(''); setBagError(''); }}
              disabled={bagBusy}
              style={{ padding: '11px', background: 'transparent', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontWeight: 700, fontSize: '0.82rem', cursor: bagBusy ? 'not-allowed' : 'pointer' }}
            >
              Cancel — keep {todayTally}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => { setBagInput('0'); setBagError(''); }}
              disabled={bagBusy}
              style={{ padding: '11px', background: 'transparent', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontWeight: 700, fontSize: '0.82rem', cursor: bagBusy ? 'not-allowed' : 'pointer' }}
            >
              No depots today — set 0
            </button>
          )}
        </form>
        )}
      </div>
      )}
    </div>
  );
}

// ── Admin Daily Summary (driver portal) ──────────────────────────────────────
function AdminDailySummary() {
  const [date, setDate]   = useState(localDateStr());
  const [rows, setRows]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshed, setRefreshed] = useState('');

  function prevDay() { setDate(d => addDays(d, -1)); }
  function nextDay() { const n = addDays(date, 1); if (n <= localDateStr()) setDate(n); }

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await fetch('/.netlify/functions/appsscript', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'getSheet1Totals', periodStart: date, periodEnd: date }),
        });
        const data = await res.json();
        if (cancelled) return;
        const byDriver = data.byDate?.[date] || {};
        const list = Object.entries(byDriver)
          .map(([login, v]) => ({ login, parcel: Math.round(v.parcel||0), express: Math.round(v.express||0), total: Math.round(v.total||0) }))
          .filter(r => r.total > 0)
          .sort((a, b) => b.total - a.total);
        setRows(list);
        setRefreshed(new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false }));
      } catch {}
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    return () => { cancelled = true; };
  }, [date]);

  const totals = rows.reduce((a, r) => ({ p: a.p + r.parcel, e: a.e + r.express, t: a.t + r.total }), { p:0, e:0, t:0 });

  const isToday = date === localDateStr();

  const [y, m, d] = date.split('-').map(Number);
  const label = new Date(y, m-1, d).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* Date nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
        <button onClick={prevDay} style={{ background: 'none', border: 'none', color: 'var(--hp-dim)', cursor: 'pointer', padding: 4, fontSize: '1.2rem', lineHeight: 1 }}>‹</button>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--hp-text)' }}>{label}</div>
          {isToday && <div style={{ fontSize: '0.65rem', color: 'var(--hp-gold)', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 1 }}>TODAY</div>}
          {refreshed && <div style={{ fontSize: '0.65rem', color: 'var(--hp-dim)', marginTop: 1 }}>Updated {refreshed}</div>}
        </div>
        <button onClick={nextDay} disabled={isToday} style={{ background: 'none', border: 'none', color: isToday ? 'var(--hp-muted)' : 'var(--hp-dim)', cursor: isToday ? 'default' : 'pointer', padding: 4, fontSize: '1.2rem', lineHeight: 1 }}>›</button>
      </div>

      {/* Totals */}
      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
          {[['Parcels', totals.p, 'var(--hp-text)'], ['Express', totals.e, '#f59e0b'], ['Total', totals.t, 'var(--hp-gold)']].map(([lbl, val, color]) => (
            <div key={lbl} style={{ background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '10px 6px', textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 800, color, lineHeight: 1 }}>{val}</div>
              <div style={{ fontSize: '0.62rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.06em', marginTop: 3 }}>{lbl}</div>
            </div>
          ))}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--hp-dim)', fontSize: '0.875rem' }}>Loading…</div>
      )}

      {/* Empty */}
      {!loading && rows.length === 0 && (
        <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--hp-dim)', fontSize: '0.875rem' }}>
          No parcel data for this date.
        </div>
      )}

      {/* Driver rows */}
      {!loading && rows.map((r, i) => (
        <div key={r.login} style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'var(--hp-card)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', padding: '12px 14px' }}>
          <div style={{ width: 22, fontSize: '0.7rem', fontWeight: 700, color: 'var(--hp-dim)', flexShrink: 0, textAlign: 'center' }}>{i+1}</div>
          <div style={{ flex: 1, fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '0.85rem', color: 'var(--hp-text)' }}>{r.login}</div>
          <div style={{ textAlign: 'center', minWidth: 44 }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: 'var(--hp-text)', lineHeight: 1 }}>{r.parcel}</div>
            <div style={{ fontSize: '0.58rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 1 }}>Prcl</div>
          </div>
          <div style={{ textAlign: 'center', minWidth: 44 }}>
            <div style={{ fontSize: '1.1rem', fontWeight: 800, color: r.express > 0 ? '#f59e0b' : 'var(--hp-muted)', lineHeight: 1 }}>{r.express || '—'}</div>
            <div style={{ fontSize: '0.58rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 1 }}>Exp</div>
          </div>
          <div style={{ textAlign: 'center', minWidth: 50 }}>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, color: 'var(--hp-gold)', lineHeight: 1 }}>{r.total}</div>
            <div style={{ fontSize: '0.58rem', color: 'var(--hp-dim)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 1 }}>Total</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Welcome Guide (first login) ───────────────────────────────────────────────
const GUIDE_STEPS = [
  {
    icon: '👋',
    title: 'Welcome to the Hollypaul Driver Portal',
    body: 'This app is your personal tool for managing your shifts, checking your parcel counts, and staying on top of your work.',
  },
  {
    icon: '▶️',
    title: 'Starting your shift',
    body: 'Tap MY SHIFT when you arrive at the depot. Once your GPS confirms you\'re at the depot, enter your start odometer reading and press START SHIFT.',
  },
  {
    icon: '🏁',
    title: 'Finishing your shift',
    body: 'Press FINISH SHIFT, enter your end odometer reading, photograph your scanner screen, then tap SUBMIT SHIFT. That\'s it — your hours are sent for approval.',
  },
  {
    icon: '📦',
    title: 'Check your parcel counts',
    body: 'Tap MY HISTORY any time to see how many parcels and express items you delivered — yesterday, last 7 days, this pay period, or last pay period.',
  },
  {
    icon: '📋',
    title: 'Tasks — DoE & Depot Bags',
    body: 'On your scheduled DoE days the TASKS tab will show which school to collect from. Tap Confirm when done. Also enter your depot bag count here at the end of your run.',
  },
  {
    icon: '✅',
    title: 'You\'re all set!',
    body: 'If you ever have trouble — can\'t scan the QR, app not working — call your manager. They can unlock your Start Shift remotely in seconds.',
  },
];

function WelcomeGuide({ name, driver, onDone }) {
  const [step, setStep] = useState(0);
  const current = GUIDE_STEPS[step];
  const isLast  = step === GUIDE_STEPS.length - 1;
  const firstName = (name || driver).split(/\s+/)[0];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 500, display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: '0 0 0 0' }}>
      <div style={{ background: 'var(--hp-card)', borderRadius: '20px 20px 0 0', width: '100%', maxWidth: 480, padding: '28px 24px 36px', boxShadow: '0 -8px 40px rgba(0,0,0,0.4)' }}>

        {/* Progress dots */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 24 }}>
          {GUIDE_STEPS.map((_, i) => (
            <div key={i} style={{ width: i === step ? 20 : 8, height: 8, borderRadius: 4, background: i === step ? 'var(--hp-gold)' : i < step ? 'rgba(201,168,76,0.4)' : 'var(--hp-border)', transition: 'all 0.2s' }} />
          ))}
        </div>

        {/* Content */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: '3rem', marginBottom: 14, lineHeight: 1 }}>{current.icon}</div>
          <div style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--hp-text)', marginBottom: 12, lineHeight: 1.3 }}>
            {step === 0 ? `Hi ${firstName}! ${current.title}` : current.title}
          </div>
          <div style={{ fontSize: '0.9rem', color: 'var(--hp-dim)', lineHeight: 1.65 }}>
            {current.body}
          </div>
        </div>

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10 }}>
          {step > 0 && (
            <button onClick={() => setStep(s => s - 1)} style={{ flex: 1, padding: '14px', background: 'var(--hp-surface)', border: '1px solid var(--hp-border)', borderRadius: 'var(--radius)', color: 'var(--hp-dim)', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
              Back
            </button>
          )}
          <button
            onClick={() => isLast ? onDone() : setStep(s => s + 1)}
            style={{ flex: 2, padding: '14px', background: 'var(--hp-gold)', border: 'none', borderRadius: 'var(--radius)', color: 'var(--hp-black)', fontWeight: 800, fontSize: '0.95rem', cursor: 'pointer' }}
          >
            {isLast ? 'Get Started →' : 'Next →'}
          </button>
        </div>

        {/* Skip */}
        {!isLast && (
          <button onClick={onDone} style={{ display: 'block', width: '100%', marginTop: 12, background: 'none', border: 'none', color: 'var(--hp-muted)', fontSize: '0.78rem', cursor: 'pointer', padding: '4px' }}>
            Skip guide
          </button>
        )}
      </div>
    </div>
  );
}
